/**
 * IntelligentContentParser - Parse and structure pasted D&D content
 *
 * This utility analyzes pasted text content and intelligently determines:
 * - What type of D&D content it is (spell, item, action, feat, etc.)
 * - How it should be structured for our data model
 * - Where it should be placed in character/monster data
 *
 * Supports parsing:
 * - Spells (from various sources, including stat blocks)
 * - Equipment/Items (weapons, armor, magical items)
 * - Actions/Attacks (monster actions, character attacks)
 * - Features/Traits (class features, racial traits, feats)
 * - Character stat blocks
 * - Monster stat blocks
 *
 * @date_of_change 2025-09-18 - Initial implementation of intelligent D&D content parser
 */

// date_of_change: 2026-03-01
// rule_reference: Node ESM interop — explicit extension avoids ERR_MODULE_NOT_FOUND when imported via file://
import ContentValidator from './ContentValidator.js';
import { normalizeEquipmentInventoryEntry } from './EquipmentState.js';
import {
  DND_BEYOND_SOURCE_FORMAT,
  getDndBeyondCharacterData,
  mapDndBeyondCharacter
} from './DndBeyondCharacterAdapter.js';

class IntelligentContentParser {

  /**
   * Main parsing function - analyzes text and returns structured data
   * @param {string} text - The pasted text content
   * @param {object} context - Context about where this is being pasted (character/monster, field type)
   * @returns {object} - Parsed result with type, confidence, and structured data
   */
  static parseContent(text, context = {}) {
    if (!text || typeof text !== 'string') {
      return { type: 'unknown', confidence: 0, data: null, error: 'No content provided' };
    }
    const cleanText = text.trim();

    // @date_of_change 2025-12-16 - Try JSON parsing first since it's unambiguous
    // Structured JSON character/NPC data should be detected before text-based parsers
    const jsonResult = this.parseStructuredJSON(cleanText, context);
    if (jsonResult && jsonResult.confidence >= 0.9) {
      return jsonResult;
    }

    // Try different parsing strategies in order of likelihood
    const parsers = [
      this.parseSpell,
      this.parseAction,
      this.parseItem,
      this.parseFeature,
      this.parseCharacterStatBlock,
      this.parseMonsterStatBlock,
      this.parseAbilityScore,
      this.parseSimpleList
    ].filter(fn => typeof fn === 'function'); // @date_of_change 2025-10-12 - Guard against undefined entries

    let bestResult = { type: 'unknown', confidence: 0, data: {} };

    for (const parser of parsers) {
      try {
        const result = parser.call(this, cleanText, context);
        if (result && result.confidence > bestResult.confidence) {
          const safeResult = {
            type: result.type || 'unknown',
            confidence: result.confidence ?? 0,
            data: result.data || {},
            suggestions: result.suggestions || [],
            warnings: result.warnings || [],
            validation: result.validation || null
          };
          bestResult = safeResult;
        }
      } catch (error) {
        const parserName = (typeof parser === 'function' && parser.name) ? parser.name : '(unknown)';
        console.warn(`[IntelligentContentParser] Parser failed:`, parserName, error); // @date_of_change 2025-10-12 - Avoid accessing name on undefined
      }
    }

    // Validate the best result if we found something
    if (bestResult.type !== 'unknown') {
      try {
        const validation = ContentValidator.validateParsedContent(bestResult, context.entityType);

        // Add validation results to the parse result
        bestResult.validation = validation;

        // Reduce confidence if validation failed
        if (!validation.success) {
          bestResult.confidence *= 0.7; // Reduce confidence by 30%
          bestResult.warnings = validation.errors.concat(validation.warnings);
        } else if (validation.warnings.length > 0) {
          bestResult.confidence *= 0.9; // Slight reduction for warnings
          bestResult.warnings = validation.warnings;
        }

        // Add validation suggestions
        const suggestions = ContentValidator.getValidationSuggestions(bestResult, context.entityType) || [];
        if (suggestions.length > 0) {
          bestResult.suggestions = (bestResult.suggestions || []).concat(suggestions);
        }

      } catch (validationError) {
        console.warn('[IntelligentContentParser] Validation failed:', validationError);
        bestResult.warnings = ['Content validation failed - please review carefully'];
      }
    }

    return bestResult;
  }

  static getStructuredJsonIndicators(entityJson = {}) {
    const characterIndicators = [
      'class_levels', 'ability_scores', 'armor_class', 'hit_points',
      'deliverable', 'spellcasting', 'saving_throws', 'skill_proficiencies'
    ];
    const simpleIndicators = ['abilities', 'actions', 'class', 'level', 'race'];
    return {
      hasCharacterIndicators: characterIndicators.some((key) => key in entityJson),
      hasSimpleIndicators: simpleIndicators.filter((key) => key in entityJson).length >= 2
    };
  }

  static sanitizeJSONCandidateText(text) {
    let sanitized = String(text || '').trim();
    if (!sanitized) return '';

    sanitized = sanitized.replace(/^\uFEFF/, '');
    sanitized = sanitized
      .replace(/[“”]/g, '"')
      .replace(/[‘’]/g, "'");
    sanitized = sanitized.replace(/,\s*([}\]])/g, '$1');

    return sanitized.trim();
  }

  static tryParseJSONLikeText(text) {
    const attempts = [];
    const raw = String(text || '').trim();
    const sanitized = this.sanitizeJSONCandidateText(raw);

    if (raw) attempts.push(raw);
    if (sanitized && sanitized !== raw) attempts.push(sanitized);

    for (const attempt of attempts) {
      try {
        return { value: JSON.parse(attempt), normalizedText: attempt };
      } catch (error) {
        // Try the next normalization pass.
      }
    }

    return null;
  }

  static extractBalancedJSONCandidates(text, options = {}) {
    const source = String(text || '');
    const minLength = Number.isFinite(options.minLength) ? options.minLength : 20;
    const maxCandidates = Number.isFinite(options.maxCandidates) ? options.maxCandidates : 8;
    const candidates = [];
    const stack = [];
    let stringQuote = null;
    let escape = false;

    const pushCandidate = (startIndex, endIndex) => {
      if (startIndex < 0 || endIndex <= startIndex) return;
      const candidateText = source.slice(startIndex, endIndex);
      if (candidateText.length < minLength) return;
      if (!(candidateText.includes('{') || candidateText.includes('[')) || !/[:\]}]/.test(candidateText)) return;
      candidates.push({
        source: 'embedded-json',
        text: candidateText.trim(),
        startIndex,
        endIndex
      });
    };

    for (let index = 0; index < source.length; index += 1) {
      const char = source[index];

      if (escape) {
        escape = false;
        continue;
      }

      if (stringQuote) {
        if (char === '\\') {
          escape = true;
        } else if (char === stringQuote) {
          stringQuote = null;
        }
        continue;
      }

      if (char === '"') {
        stringQuote = char;
        continue;
      }

      if (char === '{' || char === '[') {
        stack.push({ char, index });
        continue;
      }

      if (char === '}' || char === ']') {
        if (!stack.length) continue;
        const opener = stack[stack.length - 1];
        const isMatchingPair = (opener.char === '{' && char === '}') || (opener.char === '[' && char === ']');
        if (!isMatchingPair) {
          stack.length = 0;
          continue;
        }
        stack.pop();
        if (stack.length === 0) {
          pushCandidate(opener.index, index + 1);
          if (candidates.length >= maxCandidates) break;
        }
      }
    }

    return candidates;
  }

  static extractStructuredJSONCandidates(text) {
    const source = String(text || '');
    const trimmed = source.trim();
    const candidates = [];
    const seen = new Set();
    const addCandidate = (candidate) => {
      const value = String(candidate?.text || '').trim();
      if (!value) return;
      if (seen.has(value)) return;
      seen.add(value);
      candidates.push({
        source: candidate?.source || 'unknown',
        text: value,
        startIndex: candidate?.startIndex,
        endIndex: candidate?.endIndex
      });
    };

    if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
      addCandidate({ source: 'whole-document', text: trimmed, startIndex: 0, endIndex: source.length });
    }

    const fenceRe = /```(?:json|javascript|js)?\s*([\s\S]*?)```/gi;
    let fenceMatch;
    while ((fenceMatch = fenceRe.exec(source)) !== null) {
      addCandidate({
        source: 'fenced-json',
        text: fenceMatch[1],
        startIndex: fenceMatch.index,
        endIndex: fenceRe.lastIndex
      });
    }

    this.extractBalancedJSONCandidates(source).forEach(addCandidate);

    return candidates;
  }

  static isCanonicalCharacterWrapper(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const typeValue = String(value.type || value.entityType || value.assetType || '').toLowerCase();
    return ['character', 'npc'].includes(typeValue) && value.data && typeof value.data === 'object' && !Array.isArray(value.data);
  }

  static isCanonicalCharacterNode(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const canonicalIndicators = [
      'classes', 'abilities', 'hp', 'hitPoints', 'ac', 'combatStats',
      'spellcasting', 'equipment', 'inventory', 'biography', 'identity'
    ];
    return canonicalIndicators.filter((key) => value[key] !== undefined).length >= 3;
  }

  static enumerateStructuredJSONRoots(rootValue) {
    const roots = [];
    const queue = [{ value: rootValue, wrapper: null, path: '$', depth: 0 }];
    const visited = new WeakSet();
    const wrapperKeys = ['character', 'data', 'payload', 'entity', 'record', 'asset', 'content', 'sheet', 'characterData', 'characterSheet'];

    while (queue.length > 0) {
      const current = queue.shift();
      const { value, wrapper, path, depth } = current;
      if (!value || typeof value !== 'object') continue;

      if (Array.isArray(value)) {
        if (depth < 3 && value.length === 1 && value[0] && typeof value[0] === 'object') {
          queue.push({
            value: value[0],
            wrapper,
            path: `${path}[0]`,
            depth: depth + 1
          });
        }
        continue;
      }

      if (visited.has(value)) continue;
      visited.add(value);
      roots.push({ value, wrapper, path });

      if (depth >= 3) continue;
      for (const key of wrapperKeys) {
        const child = value[key];
        if (child && typeof child === 'object') {
          queue.push({
            value: child,
            wrapper: value,
            path: `${path}.${key}`,
            depth: depth + 1
          });
        }
      }
    }

    return roots;
  }

  static normalizeCanonicalSpeed(speedValue, fallbackCombatSpeed) {
    const source = speedValue ?? fallbackCombatSpeed;
    if (source === undefined || source === null) return undefined;

    const toSpeedNumber = (value) => {
      if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
      const match = String(value || '').match(/-?\d+/);
      if (!match) return undefined;
      const parsed = Number(match[0]);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    if (typeof source === 'number' || typeof source === 'string') {
      const walk = toSpeedNumber(source);
      return walk !== undefined ? { walk } : undefined;
    }

    if (!source || typeof source !== 'object' || Array.isArray(source)) return undefined;

    const normalized = {};
    Object.entries(source).forEach(([key, value]) => {
      const parsed = toSpeedNumber(value);
      if (parsed !== undefined) {
        normalized[key] = parsed;
      }
    });

    return Object.keys(normalized).length ? normalized : undefined;
  }

  static mapCanonicalCharacterLikeObject(canonicalNode, wrapper = null, warnings = []) {
    if (!canonicalNode || typeof canonicalNode !== 'object' || Array.isArray(canonicalNode)) {
      return null;
    }

    const warn = (message) => {
      if (!Array.isArray(warnings)) return;
      if (typeof message !== 'string' || !message.trim()) return;
      warnings.push(message.trim());
    };
    const canonical = canonicalNode;
    const identityNode = canonical.identity && typeof canonical.identity === 'object' ? canonical.identity : {};
    const classesNode = Array.isArray(canonical.classes) ? canonical.classes : [];
    const primaryClass = classesNode[0] || {};
    const actionsNode = canonical.actions && typeof canonical.actions === 'object' ? canonical.actions : null;
    const hpNode = canonical.hp && typeof canonical.hp === 'object'
      ? canonical.hp
      : (canonical.hitPoints && typeof canonical.hitPoints === 'object' ? canonical.hitPoints : null);
    const acNode = canonical.ac && typeof canonical.ac === 'object' ? canonical.ac : null;
    const combatStats = canonical.combatStats && typeof canonical.combatStats === 'object' ? canonical.combatStats : {};
    const data = {};

    data.name = canonical.name || identityNode.name || wrapper?.id || 'Unnamed Character';

    const canonicalBackground = canonical.background ?? identityNode.background;
    if (typeof canonicalBackground === 'string' && canonicalBackground.trim()) {
      const classifiedBackground = this.classifyBackgroundValue(canonicalBackground);
      if (classifiedBackground.background) {
        data.background = classifiedBackground.background;
      }
      if (classifiedBackground.backstory) {
        data.biography = {
          ...(data.biography || {}),
          backstory: classifiedBackground.backstory
        };
      }
    }

    if (canonical.race ?? identityNode.race) {
      data.race = canonical.race ?? identityNode.race;
      data.species = canonical.race ?? identityNode.race;
    }
    if (canonical.species && !data.species) {
      data.species = canonical.species;
      data.race = canonical.species;
    }
    if (canonical.size) data.size = canonical.size;
    if (canonical.alignment ?? identityNode.alignment) data.alignment = canonical.alignment ?? identityNode.alignment;
    if (canonical.subclass) data.subclass = canonical.subclass;

    if (classesNode.length > 0) {
      data.classes = classesNode.map((entry) => ({
        ...entry,
        name: entry?.name || entry?.className || entry?.class || 'Unknown Class',
        level: Number.isFinite(Number(entry?.level)) ? Number(entry.level) : entry?.level
      }));
      data.class = primaryClass?.name || primaryClass?.className || primaryClass?.class;
      if (!data.subclass && primaryClass?.subclass) data.subclass = primaryClass.subclass;
    } else if (canonical.class) {
      data.class = canonical.class;
    }

    const canonicalLevel = Number.isFinite(Number(canonical.level))
      ? Number(canonical.level)
      : data.classes?.reduce((total, entry) => total + (Number.isFinite(Number(entry?.level)) ? Number(entry.level) : 0), 0);
    if (canonicalLevel) data.level = canonicalLevel;

    if (canonical.experience !== undefined) data.experience = canonical.experience;
    if (canonical.experiencePoints !== undefined && data.experience === undefined) data.experience = canonical.experiencePoints;
    if (canonical.inspiration !== undefined) data.inspiration = canonical.inspiration;

    if (canonical.abilities && typeof canonical.abilities === 'object' && !Array.isArray(canonical.abilities)) {
      data.abilities = { ...canonical.abilities };
    }

    if (acNode?.value !== undefined) {
      data.ac = {
        value: Number(acNode.value),
        notes: acNode.notes || ''
      };
    } else if (canonical.ac !== undefined && typeof canonical.ac !== 'object') {
      data.ac = { value: Number(canonical.ac), notes: '' };
    } else if (combatStats.armorClass !== undefined) {
      data.ac = { value: Number(combatStats.armorClass), notes: '' };
    }

    if (hpNode?.current !== undefined) data.hp = hpNode.current;
    if (hpNode?.maximum !== undefined) data.maxHp = hpNode.maximum;
    if (hpNode?.temporary !== undefined) data.tempHp = hpNode.temporary;
    if (hpNode?.formula) data.hitDice = hpNode.formula;

    const normalizedSpeed = this.normalizeCanonicalSpeed(canonical.speed, combatStats.speed);
    if (normalizedSpeed) data.speed = normalizedSpeed;
    if (canonical.initiative !== undefined) data.initiative = canonical.initiative;
    if (canonical.initiative === undefined && combatStats.initiative !== undefined) data.initiative = combatStats.initiative;

    if (canonical.skills) data.skills = canonical.skills;
    if (canonical.saves) data.savingThrows = canonical.saves;
    if (!data.savingThrows && canonical.savingThrows) data.savingThrows = canonical.savingThrows;
    if (canonical.features) data.features = canonical.features;
    if (canonical.classFeatures) data.classFeatures = canonical.classFeatures;
    if (canonical.feats) data.feats = canonical.feats;
    if (canonical.speciesTraits) data.speciesTraits = canonical.speciesTraits;
    if (canonical.classFeaturesText) data.classFeaturesText = canonical.classFeaturesText;
    if (canonical.speciesTraitsText) data.speciesTraitsText = canonical.speciesTraitsText;
    if (canonical.featsText) data.featsText = canonical.featsText;
    if (canonical.equipment) data.equipment = canonical.equipment;
    if (!data.equipment && canonical.inventory) data.equipment = canonical.inventory;
    if (canonical.inventory) data.inventory = canonical.inventory;
    if (canonical.items) data.items = canonical.items;

    // date_of_change: 2026-09-06
    // rule_reference: Canonical action buckets must contain discrete game options, never a flattened
    // character-sheet field dump that happened to include an attack bonus elsewhere in the text.
    const keepDiscreteActions = (entries) => (
      Array.isArray(entries) ? entries.filter((entry) => !this.isCharacterSheetMetadataDump(entry)) : null
    );
    const directActions = keepDiscreteActions(canonical.actions);
    if (directActions) data.actions = directActions;
    else {
      const nestedActions = keepDiscreteActions(actionsNode?.actions);
      if (nestedActions) data.actions = nestedActions;
    }
    const directBonusActions = keepDiscreteActions(canonical.bonusActions);
    if (directBonusActions) data.bonusActions = directBonusActions;
    else {
      const nestedBonusActions = keepDiscreteActions(actionsNode?.bonusActions);
      if (nestedBonusActions) data.bonusActions = nestedBonusActions;
    }
    const directReactions = keepDiscreteActions(canonical.reactions);
    if (directReactions) data.reactions = directReactions;
    else {
      const nestedReactions = keepDiscreteActions(actionsNode?.reactions);
      if (nestedReactions) data.reactions = nestedReactions;
    }

    if (canonical.spellcasting) data.spellcasting = canonical.spellcasting;
    if (canonical.spells) data.spells = canonical.spells;
    if (canonical.senses) data.senses = canonical.senses;
    if (canonical.passivePerception !== undefined) data.passivePerception = canonical.passivePerception;
    if (canonical.proficiencies) data.proficiencies = canonical.proficiencies;
    if (canonical.toolProficiencies) data.toolProficiencies = canonical.toolProficiencies;
    if (canonical.languages) data.languages = canonical.languages;
    if (canonical.appearance) data.appearance = canonical.appearance;
    if (canonical.biography) data.biography = canonical.biography;
    if (canonical.biographyMeta) data.biographyMeta = canonical.biographyMeta;
    if (canonical.notes) data.notes = canonical.notes;
    if (canonical.resistances) data.resistances = canonical.resistances;
    if (canonical.immunities) data.immunities = canonical.immunities;
    if (canonical.vulnerabilities) data.vulnerabilities = canonical.vulnerabilities;

    if (!data.abilities) {
      warn('No ability scores found in canonical JSON data - please add manually');
    }
    if (!data.maxHp && !data.hp) {
      warn('No hit points found in canonical JSON data - please add manually');
    }

    return data;
  }

  static scoreStructuredJsonCharacterData(data = {}, options = {}) {
    if (!data || typeof data !== 'object') return 0;
    const coreFieldCount = [
      data.name,
      data.abilities,
      data.ac,
      data.hp ?? data.maxHp,
      data.class ?? data.classes,
      data.level
    ].filter((value) => value !== undefined && value !== null && value !== '').length;
    const strongSectionCount = [
      data.spellcasting,
      data.spells,
      data.equipment,
      data.actions || data.bonusActions || data.reactions,
      data.features || data.classFeatures || data.feats || data.speciesTraits
    ].filter(Boolean).length;
    const wrapperBonus = options.isCanonicalWrapper ? 0.08 : 0;
    const embeddedBonus = options.isEmbeddedCandidate ? 0.02 : 0;
    return (coreFieldCount * 10) + (strongSectionCount * 4) + (wrapperBonus * 100) + (embeddedBonus * 100);
  }

  static mapStructuredJsonRoot(rootValue, wrapperValue = null, options = {}) {
    if (!rootValue || typeof rootValue !== 'object' || Array.isArray(rootValue)) {
      return null;
    }

    const warnings = [];
    const root = rootValue;
    const wrapper = wrapperValue && typeof wrapperValue === 'object' && !Array.isArray(wrapperValue)
      ? wrapperValue
      : null;
    const isCanonicalWrapper = this.isCanonicalCharacterWrapper(wrapper) || this.isCanonicalCharacterWrapper(root);
    let entityType = 'character';
    let characterData = null;

    // date_of_change: 2026-08-24
    // rule_reference: D&D Beyond character-service v5 is a source contract, not generic
    // structured JSON. Adapt it before canonical-node detection so nested race/class
    // definitions cannot become "[object Object]" and every multiclass entry survives.
    const dndBeyondCharacter = getDndBeyondCharacterData(root);

    // GAP-007 bug #6: resolve entity type — 'monster', 'npc', or 'character' — from the
    // canonical type/deliverable fields before falling back to 'character'.
    // date_of_change: 2026-05-20
    const resolveEntityType = (rawType) => {
      const t = String(rawType || '').toLowerCase().trim();
      if (t === 'monster') return 'monster';
      if (t === 'npc') return 'npc';
      return null; // caller provides default
    };

    if (dndBeyondCharacter) {
      characterData = mapDndBeyondCharacter(dndBeyondCharacter, warnings);
      entityType = 'character';
    } else if (this.isCanonicalCharacterWrapper(root)) {
      characterData = this.mapCanonicalCharacterLikeObject(root.data, root, warnings);
      entityType = resolveEntityType(root.type || root.entityType) ?? 'character';
    } else if (this.isCanonicalCharacterWrapper(wrapper) && wrapper?.data === root) {
      characterData = this.mapCanonicalCharacterLikeObject(root, wrapper, warnings);
      entityType = resolveEntityType(wrapper.type || wrapper.entityType) ?? 'character';
    } else if (this.isCanonicalCharacterNode(root)) {
      characterData = this.mapCanonicalCharacterLikeObject(root, wrapper, warnings);
      entityType = resolveEntityType(root.type || root.entityType) ?? 'character';
    } else {
      let entityJson = root;
      if (root.character && typeof root.character === 'object' && !Array.isArray(root.character)) {
        entityJson = { ...root.character };
        for (const [key, value] of Object.entries(root)) {
          if (key === 'character') continue;
          if (entityJson[key] === undefined) {
            entityJson[key] = value;
          }
        }
      }

      const { hasCharacterIndicators, hasSimpleIndicators } = this.getStructuredJsonIndicators(entityJson);
      if (!hasCharacterIndicators && !hasSimpleIndicators) {
        return null;
      }

      characterData = this.mapJSONToCanonical(entityJson, warnings);
      // date_of_change: 2026-05-20 GAP-007 #6: check deliverable and type for monster/npc before defaulting to character
      const explicitType = resolveEntityType(entityJson.deliverable ?? entityJson.type);
      const isMonster = explicitType === 'monster' ||
        (explicitType === null && entityJson.challenge_rating !== undefined && !entityJson.class_levels);
      entityType = isMonster ? 'monster' : (explicitType ?? 'character');
    }

    if (!characterData || typeof characterData !== 'object') {
      return null;
    }

    if (
      (entityType === 'character' || entityType === 'npc')
      && !options.skipTextFallback
      && characterData?.sourceFormat !== DND_BEYOND_SOURCE_FORMAT
    ) {
      characterData = this.augmentCharacterDataWithTextFallback(
        characterData,
        wrapper || root,
        warnings
      );
    }

    warnings.splice(0, warnings.length, ...this.reconcileStructuredJsonWarnings(characterData, warnings));
    characterData.type = entityType;
    const presentFields = ['name', 'abilities', 'ac', 'hp', 'maxHp'].filter((field) => characterData[field] !== undefined);
    const baseConfidence = characterData.sourceFormat === DND_BEYOND_SOURCE_FORMAT
      ? 0.99
      : (isCanonicalWrapper ? 0.94 : 0.9);
    const completenessBonus = (presentFields.length / 5) * 0.05;
    const confidence = Math.min(0.99, baseConfidence + completenessBonus + (options.isEmbeddedCandidate ? 0.01 : 0));

    return {
      type: entityType,
      confidence,
      data: characterData,
      suggestions: [
        `Import as ${entityType}`,
        'Merge with existing character'
      ],
      warnings,
      score: this.scoreStructuredJsonCharacterData(characterData, {
        isCanonicalWrapper,
        isEmbeddedCandidate: options.isEmbeddedCandidate
      })
    };
  }

  /**
   * Parse structured JSON character/NPC data
   * @date_of_change 2025-12-16 - Added to support pasting structured JSON character exports
   *
   * Handles:
   * - whole-document JSON
   * - fenced JSON blocks
   * - embedded JSON objects/arrays inside larger pasted text
   * - legacy structured SmartPaste exports
   * - canonical VCS saved-character wrappers ({ type: "character", data: { ... } })
   *
   * Maps to canonical schema fields expected by the system.
   */
  static parseStructuredJSON(text, context) {
    const candidates = this.extractStructuredJSONCandidates(text);
    if (!candidates.length) {
      return { type: 'unknown', confidence: 0 };
    }

    let bestResult = null;

    candidates.forEach((candidate) => {
      const parsedCandidate = this.tryParseJSONLikeText(candidate.text);
      if (!parsedCandidate) return;

      const roots = this.enumerateStructuredJSONRoots(parsedCandidate.value);
      roots.forEach(({ value, wrapper }) => {
        const mapped = this.mapStructuredJsonRoot(value, wrapper, {
          isEmbeddedCandidate: candidate.source !== 'whole-document'
        });
        if (!mapped) return;

        if (!bestResult) {
          bestResult = mapped;
          return;
        }

        const bestScore = (bestResult.score || 0) + (bestResult.confidence || 0);
        const candidateScore = (mapped.score || 0) + (mapped.confidence || 0);
        if (candidateScore > bestScore) {
          bestResult = mapped;
        }
      });
    });

    if (!bestResult) {
      return { type: 'unknown', confidence: 0 };
    }

    return {
      type: bestResult.type,
      confidence: bestResult.confidence,
      data: bestResult.data,
      suggestions: bestResult.suggestions,
      warnings: bestResult.warnings
    };
  }

  static formatStructuredSearchKey(key) {
    return String(key || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  static buildSearchableTextFromStructuredValue(value, options = {}) {
    if (!value || typeof value !== 'object') return '';

    const lines = [];
    const seen = new WeakSet();
    const maxDepth = Number.isFinite(options.maxDepth) ? options.maxDepth : 6;
    const maxLines = Number.isFinite(options.maxLines) ? options.maxLines : 1200;
    const ignoredKeys = new Set([
      'id',
      'schemaVersion',
      'schema_version',
      'version',
      'type',
      'exportDate',
      'created',
      'createdAt',
      'modified',
      'lastModified',
      'artwork',
      'imageUrl',
      'tokenAssetKey',
      'refId',
      'resolvedAtVersion',
      '_audit',
      '_meta',
      'audit',
      'compat',
      'metadata',
      'parserMeta',
      'parsedEntries',
      'parsedEntryCatalog',
      'provisionalEntryCatalog',
      'featuresList',
      'featsList',
      'speciesTraitsList',
      'featuresListStructured',
      'featsListStructured',
      'speciesTraitsListStructured',
      // date_of_change: 2026-05-02
      // rule_reference: These are pure narrative prose fields already mapped by mapJSONToCanonical.
      // Including them in searchable text causes the line-based parser (extractUncategorizedBlocks)
      // to misclassify narrative prose as equipment/features via keyword matching.
      'description',
      'appearance',
      'background',
      'backstory',
      'tactics',
      // All entries below are matched case-insensitively (see ignoredKeys.has check below)
      'personality traits',
      'personality_traits',
      'personality',
      'voice_mannerisms',
      'ideals',
      'bonds',
      'flaws',
      'goals',
      'fears',
      'quirks',
      'canon_update',
      'assumptions',
      'sources_used',
      'title'
    ]);
    const wrapperKeySet = new Set([
      'data',
      'character',
      'payload',
      'entity',
      'record',
      'asset',
      'content',
      'sheet',
      'characterdata',
      'charactersheet'
    ]);

    const appendLine = (labelParts, rawValue) => {
      const valueText = String(rawValue ?? '').trim();
      const labelText = labelParts
        .map((part) => this.formatStructuredSearchKey(part))
        .filter(Boolean)
        .slice(-3)
        .join(' ');
      if (!valueText || !labelText || lines.length >= maxLines) return;
      lines.push(`${labelText}: ${valueText}`);
    };
    const appendRawLine = (rawValue) => {
      const valueText = String(rawValue ?? '').trim();
      if (!valueText || lines.length >= maxLines) return;
      lines.push(valueText);
    };
    const appendKeyedLine = (label, rawValue) => {
      const labelText = String(label || '').trim();
      const valueText = String(rawValue ?? '').trim();
      if (!labelText || !valueText || lines.length >= maxLines) return;
      lines.push(`${labelText}: ${valueText}`);
    };
    const spellLikeKeys = new Set([
      'level',
      'level text',
      'school',
      'casting time',
      'range',
      'components',
      'duration',
      'description',
      'rules text',
      'spell',
      'save',
      'saving throw'
    ]);
    const spellFieldLabels = [
      ['levelText', ''],
      ['level_text', ''],
      ['school', 'School'],
      ['castingTime', 'Casting Time'],
      ['casting_time', 'Casting Time'],
      ['range', 'Range'],
      ['components', 'Components'],
      ['duration', 'Duration'],
      ['save', 'Save'],
      ['saving_throw', 'Saving Throw'],
      ['description', ''],
      ['rules_text', '']
    ];
    const descriptionLikeKeys = ['description', 'text', 'effect', 'notes'];
    const appendNamedStructuredEntry = (node) => {
      const displayName = ['name', 'title', 'label']
        .map((key) => node?.[key])
        .find((candidate) => typeof candidate === 'string' && candidate.trim());
      if (!displayName) return null;

      const looksStructuredEntry = Boolean(
        descriptionLikeKeys.some((key) => typeof node?.[key] === 'string' && node[key].trim())
        || node?.quantity !== undefined
        || node?.damage !== undefined
        || node?.damageType !== undefined
        || node?.armorClass !== undefined
        || node?.range !== undefined
        || node?.castingTime !== undefined
        || node?.duration !== undefined
        || node?.school !== undefined
        || node?.properties !== undefined
        || node?.source !== undefined
        || node?.prerequisite !== undefined
        || node?.uses
        || node?.type
      );
      if (!looksStructuredEntry) return null;

      const handledKeys = new Set(['name', 'title', 'label']);
      appendRawLine(displayName.trim());

      descriptionLikeKeys.forEach((key) => {
        const value = node?.[key];
        if (typeof value === 'string' && value.trim()) {
          appendRawLine(value.trim());
          handledKeys.add(key);
        }
      });

      if (typeof node?.source === 'string' && node.source.trim()) {
        appendKeyedLine('Source', node.source.trim());
        handledKeys.add('source');
      }

      if (typeof node?.prerequisite === 'string' && node.prerequisite.trim()) {
        appendKeyedLine('Prerequisite', node.prerequisite.trim());
        handledKeys.add('prerequisite');
      }

      if (node?.quantity !== undefined) {
        appendKeyedLine('Quantity', node.quantity);
        handledKeys.add('quantity');
      }

      if (node?.armorClass !== undefined) {
        appendKeyedLine('Armor Class', node.armorClass);
        handledKeys.add('armorClass');
      }

      if (node?.damage !== undefined) {
        const damageSummary = node?.damageType
          ? `${node.damage} ${node.damageType}`
          : node.damage;
        appendKeyedLine('Damage', damageSummary);
        handledKeys.add('damage');
        handledKeys.add('damageType');
      }

      if (node?.attackBonus !== undefined) {
        appendKeyedLine('Attack Bonus', node.attackBonus);
        handledKeys.add('attackBonus');
      }

      if (node?.toHit !== undefined) {
        appendKeyedLine('To Hit', node.toHit);
        handledKeys.add('toHit');
      }

      if (typeof node?.range === 'string' && node.range.trim()) {
        appendKeyedLine('Range', node.range.trim());
        handledKeys.add('range');
      }

      if (typeof node?.reach === 'string' && node.reach.trim()) {
        appendKeyedLine('Reach', node.reach.trim());
        handledKeys.add('reach');
      }

      if (Array.isArray(node?.properties) && node.properties.length > 0) {
        appendKeyedLine('Properties', node.properties.join(', '));
        handledKeys.add('properties');
      }

      if (node?.attunement === true || node?.requiresAttunement === true) {
        appendRawLine('Requires attunement');
        handledKeys.add('attunement');
        handledKeys.add('requiresAttunement');
      }

      if (typeof node?.type === 'string' && node.type.trim()) {
        appendKeyedLine('Type', node.type.trim());
        handledKeys.add('type');
      }

      if (node?.uses && typeof node.uses === 'object' && !Array.isArray(node.uses)) {
        const usesBits = [];
        if (node.uses.current !== undefined || node.uses.max !== undefined) {
          usesBits.push(`${node.uses.current ?? '?'} / ${node.uses.max ?? '?'}`);
        }
        if (node.uses.resetOn) usesBits.push(String(node.uses.resetOn));
        if (usesBits.length > 0) {
          appendKeyedLine('Uses', usesBits.join(' • '));
        }
        handledKeys.add('uses');
      }

      return handledKeys;
    };

    const visit = (node, labelParts = [], depth = 0) => {
      if (node === null || node === undefined || lines.length >= maxLines) return;
      if (depth > maxDepth) return;

      if (typeof node !== 'object') {
        appendLine(labelParts, node);
        return;
      }

      if (seen.has(node)) return;
      seen.add(node);

      if (Array.isArray(node)) {
        const primitiveValues = node.filter((entry) => entry === null || ['string', 'number', 'boolean'].includes(typeof entry));
        if (primitiveValues.length === node.length) {
          appendLine(labelParts, primitiveValues.join(', '));
          return;
        }

        node.forEach((entry, index) => {
          if (lines.length >= maxLines) return;
          const entryLabel = (entry && typeof entry === 'object')
            ? (entry.name || entry.title || entry.label || entry.id || `entry ${index + 1}`)
            : `entry ${index + 1}`;
          visit(entry, [...labelParts, entryLabel], depth + 1);
        });
        return;
      }

      const objectKeys = Object.keys(node).map((key) => this.formatStructuredSearchKey(key).toLowerCase());
      const isSpellLikeNode = objectKeys.some((key) => spellLikeKeys.has(key) || key.includes('spell'));
      const displayName = ['name', 'title', 'label'].map((key) => node[key]).find((candidate) => typeof candidate === 'string' && candidate.trim());
      let handledKeys = null;
      if (isSpellLikeNode && displayName) {
        appendRawLine(displayName);
        const appendSpellFields = (sourceNode, includeNarrative = true) => {
          spellFieldLabels.forEach(([fieldKey, label]) => {
            const isNarrativeField = fieldKey === 'description' || fieldKey === 'rules_text';
            if (!includeNarrative && isNarrativeField) return;
            const fieldValue = sourceNode?.[fieldKey];
            if (typeof fieldValue !== 'string' || !fieldValue.trim()) return;
            if (label) {
              appendRawLine(`${label}: ${fieldValue.trim()}`);
            } else {
              appendRawLine(fieldValue.trim());
            }
          });
        };
        Object.values(node)
          .filter((child) => child && typeof child === 'object' && !Array.isArray(child))
          .forEach((childNode) => appendSpellFields(childNode, false));
        appendSpellFields(node, true);
        handledKeys = new Set([
          'name',
          'title',
          'label',
          'level',
          'levelText',
          'level_text',
          'school',
          'castingTime',
          'casting_time',
          'range',
          'components',
          'duration',
          'description',
          'rules_text',
          'save',
          'saving_throw'
        ]);
      } else {
        handledKeys = appendNamedStructuredEntry(node);
      }

      Object.entries(node).forEach(([key, child]) => {
        if (ignoredKeys.has(key) || ignoredKeys.has(key.toLowerCase()) || lines.length >= maxLines) return;
        if (handledKeys?.has(key)) return;
        const normalizedKey = this.formatStructuredSearchKey(key);
        if (!normalizedKey) return;

        if (child === null || child === undefined) return;
        if (typeof child !== 'object') {
          appendLine([...labelParts, normalizedKey], child);
          return;
        }

        if (Array.isArray(child)) {
          const primitiveValues = child.filter((entry) => entry === null || ['string', 'number', 'boolean'].includes(typeof entry));
          if (primitiveValues.length === child.length) {
            appendLine([...labelParts, normalizedKey], primitiveValues.join(', '));
            return;
          }
        }

        const nextLabelParts = wrapperKeySet.has(normalizedKey.toLowerCase())
          ? labelParts
          : [...labelParts, normalizedKey];
        visit(child, nextLabelParts, depth + 1);
      });
    };

    visit(value);
    return lines.join('\n');
  }

  static mergeCharacterSpellData(existingSpells = {}, incomingSpells = {}) {
    const existing = (existingSpells && typeof existingSpells === 'object') ? existingSpells : {};
    const incoming = (incomingSpells && typeof incomingSpells === 'object') ? incomingSpells : {};
    const buildSpellKey = (entry) => [
      this.normalizeParsedEntryName(entry?.name),
      Number.isFinite(Number(entry?.level)) ? `level:${Number(entry.level)}` : '',
      entry?.prepared ? 'prepared' : '',
      entry?.alwaysPrepared ? 'alwaysPrepared' : '',
      entry?.innateFrequency || '',
      entry?.listType || ''
    ].filter(Boolean).join('|');
    const mergeSpellArray = (...lists) => {
      const merged = [];
      const indexByKey = new Map();
      lists
        .flatMap((list) => (Array.isArray(list) ? list : []))
        .forEach((entry) => {
          if (!entry) return;
          const key = buildSpellKey(entry) || this.normalizeParsedEntryName(entry?.name);
          if (!key) return;
          if (!indexByKey.has(key)) {
            indexByKey.set(key, merged.length);
            merged.push(entry);
            return;
          }
          const existingIndex = indexByKey.get(key);
          merged[existingIndex] = {
            ...merged[existingIndex],
            ...entry,
            parserMeta: this.mergeParserMeta(merged[existingIndex]?.parserMeta, entry?.parserMeta)
          };
        });
      return merged;
    };
    const mergeSpellMap = (leftMap = {}, rightMap = {}) => {
      const merged = {};
      const keys = new Set([
        ...Object.keys(leftMap || {}),
        ...Object.keys(rightMap || {})
      ]);
      keys.forEach((key) => {
        merged[key] = mergeSpellArray(leftMap?.[key], rightMap?.[key]);
      });
      return merged;
    };

    const merged = {
      ...existing,
      ...incoming,
      parserMeta: this.mergeParserMeta(existing.parserMeta, incoming.parserMeta)
    };

    if (existing.byLevel || incoming.byLevel) merged.byLevel = mergeSpellMap(existing.byLevel, incoming.byLevel);
    if (existing.prepared || incoming.prepared) merged.prepared = mergeSpellArray(existing.prepared, incoming.prepared);
    if (existing.atWill || incoming.atWill) merged.atWill = mergeSpellArray(existing.atWill, incoming.atWill);
    if (existing.known || incoming.known) merged.known = mergeSpellArray(existing.known, incoming.known);
    if (existing.innateByFrequency || incoming.innateByFrequency) merged.innateByFrequency = mergeSpellMap(existing.innateByFrequency, incoming.innateByFrequency);
    if (existing.alwaysPrepared || incoming.alwaysPrepared) merged.alwaysPrepared = mergeSpellMap(existing.alwaysPrepared, incoming.alwaysPrepared);
    if (existing.slots || incoming.slots) merged.slots = { ...(existing.slots || {}), ...(incoming.slots || {}) };
    if (merged.ability === undefined && incoming.ability !== undefined) merged.ability = incoming.ability;
    if (merged.saveDC === undefined && incoming.saveDC !== undefined) merged.saveDC = incoming.saveDC;
    if (merged.attackBonus === undefined && incoming.attackBonus !== undefined) merged.attackBonus = incoming.attackBonus;

    if (merged.byLevel && Object.keys(merged.byLevel).length === 0) delete merged.byLevel;
    if (Array.isArray(merged.prepared) && merged.prepared.length === 0) delete merged.prepared;
    if (Array.isArray(merged.atWill) && merged.atWill.length === 0) delete merged.atWill;
    if (Array.isArray(merged.known) && merged.known.length === 0) delete merged.known;
    if (merged.innateByFrequency && Object.keys(merged.innateByFrequency).length === 0) delete merged.innateByFrequency;
    if (merged.alwaysPrepared && Object.keys(merged.alwaysPrepared).length === 0) delete merged.alwaysPrepared;
    if (merged.slots && Object.keys(merged.slots).length === 0) delete merged.slots;

    return merged;
  }

  static hasStructuredSpellCoverage(spells = {}) {
    if (!spells || typeof spells !== 'object') return false;
    const hasByLevel = Object.values(spells.byLevel || {}).some((entries) => Array.isArray(entries) && entries.length > 0);
    const hasLegacyLevels = ['cantrips', 'level0', 'level1', 'level2', 'level3', 'level4', 'level5', 'level6', 'level7', 'level8', 'level9']
      .some((key) => Array.isArray(spells[key]) && spells[key].length > 0);
    const hasFlatLists = ['prepared', 'known', 'atWill'].some((key) => Array.isArray(spells[key]) && spells[key].length > 0);
    const hasAlwaysPrepared = Object.values(spells.alwaysPrepared || {}).some((entries) => Array.isArray(entries) && entries.length > 0);
    const hasInnate = Object.values(spells.innateByFrequency || {}).some((entries) => Array.isArray(entries) && entries.length > 0);
    return hasByLevel || hasLegacyLevels || hasFlatLists || hasAlwaysPrepared || hasInnate;
  }

  static countStructuredCharacterCoverage(data = {}) {
    if (!data || typeof data !== 'object') return 0;

    let coverage = 0;
    if (data.name) coverage += 1;
    if (data.class || (Array.isArray(data.classes) && data.classes.length > 0)) coverage += 1;
    if (data.level) coverage += 1;
    if (data.abilities && Object.keys(data.abilities).length > 0) coverage += 1;
    if (data.ac) coverage += 1;
    if (data.hp || data.maxHp) coverage += 1;
    if (this.hasStructuredSpellCoverage(data.spells)) coverage += 3;
    if (data.spellcasting && (data.spellcasting.ability || data.spellcasting.saveDC || data.spellcasting.attackBonus)) coverage += 1;
    if (Array.isArray(data.features) && data.features.length > 0) coverage += 1;
    if (Array.isArray(data.classFeatures) && data.classFeatures.length > 0) coverage += 1;
    if (Array.isArray(data.feats) && data.feats.length > 0) coverage += 1;
    if (Array.isArray(data.speciesTraits) && data.speciesTraits.length > 0) coverage += 1;
    if (Array.isArray(data.actions) && data.actions.length > 0) coverage += 1;
    if (Array.isArray(data.bonusActions) && data.bonusActions.length > 0) coverage += 1;
    if (Array.isArray(data.reactions) && data.reactions.length > 0) coverage += 1;
    if (
      (Array.isArray(data.equipment?.weapons) && data.equipment.weapons.length > 0)
      || (Array.isArray(data.equipment?.armor) && data.equipment.armor.length > 0)
      || (Array.isArray(data.equipment?.items) && data.equipment.items.length > 0)
      || (Array.isArray(data.equipment?.valuables) && data.equipment.valuables.length > 0)
    ) {
      coverage += 2;
    }
    if (data.proficiencies && Object.keys(data.proficiencies).length > 0) coverage += 1;
    if (Array.isArray(data.languages) && data.languages.length > 0) coverage += 1;
    if (data.biography && Object.keys(data.biography).length > 0) coverage += 1;

    return coverage;
  }

  static reconcileStructuredJsonWarnings(characterData = {}, warnings = []) {
    return (Array.isArray(warnings) ? warnings : []).filter((warning) => {
      const normalized = String(warning || '').toLowerCase();
      if (!normalized) return false;
      if ((characterData.hp !== undefined || characterData.maxHp !== undefined) && normalized.includes('no hit points found')) {
        return false;
      }
      if (characterData.abilities && Object.keys(characterData.abilities).length > 0 && normalized.includes('no ability scores found')) {
        return false;
      }
      if (characterData.ac && normalized.includes('armor class')) {
        return false;
      }
      return true;
    });
  }

  static mergeStructuredCharacterSupplement(baseData = {}, incomingData = {}) {
    if (!incomingData || typeof incomingData !== 'object') return baseData;

    const merged = { ...baseData };
    const dedupeStrings = (values = []) => {
      const seen = new Set();
      return (Array.isArray(values) ? values : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .filter((value) => {
          const normalized = value.toLowerCase();
          if (seen.has(normalized)) return false;
          seen.add(normalized);
          return true;
        });
    };
    const mergeStringArrays = (existing, incoming) => dedupeStrings([
      ...(Array.isArray(existing) ? existing : []),
      ...(Array.isArray(incoming) ? incoming : [])
    ]);
    const mergeScalarField = (field) => {
      if (merged[field] === undefined && incomingData[field] !== undefined) {
        merged[field] = incomingData[field];
      }
    };

    [
      'background',
      'race',
      'species',
      'class',
      'subclass',
      'level',
      'alignment',
      'abilities',
      'ac',
      'hp',
      'maxHp',
      'tempHp',
      'speed',
      'initiative',
      'passivePerception',
      'senses',
      'skills',
      'skillsDetailed',
      'savingThrows',
      'experience',
      'inspiration',
      'proficiencyBonus',
      'notes'
    ].forEach(mergeScalarField);

    if ((!Array.isArray(merged.classes) || merged.classes.length === 0) && Array.isArray(incomingData.classes) && incomingData.classes.length > 0) {
      merged.classes = incomingData.classes;
    }

    if (incomingData.biography || incomingData.biographyMeta) {
      merged.biography = {
        ...(incomingData.biography || {}),
        ...(merged.biography || {})
      };
      merged.biographyMeta = {
        ...(incomingData.biographyMeta || {}),
        ...(merged.biographyMeta || {})
      };
    }

    if (incomingData.languages || merged.languages) {
      merged.languages = mergeStringArrays(merged.languages, incomingData.languages);
      merged.languagesMeta = this.mergeParserMeta(incomingData.languagesMeta, merged.languagesMeta);
    }

    if (incomingData.toolProficiencies || merged.toolProficiencies) {
      merged.toolProficiencies = mergeStringArrays(merged.toolProficiencies, incomingData.toolProficiencies);
    }

    if (incomingData.proficiencies || merged.proficiencies) {
      const existing = merged.proficiencies && typeof merged.proficiencies === 'object' ? merged.proficiencies : {};
      const incoming = incomingData.proficiencies && typeof incomingData.proficiencies === 'object' ? incomingData.proficiencies : {};
      merged.proficiencies = {
        ...existing,
        armor: mergeStringArrays(existing.armor, incoming.armor),
        weapons: mergeStringArrays(existing.weapons, incoming.weapons),
        tools: mergeStringArrays(existing.tools, incoming.tools),
        languages: mergeStringArrays(existing.languages, incoming.languages),
        savingThrows: mergeStringArrays(existing.savingThrows, incoming.savingThrows)
      };
      merged.proficienciesMeta = this.mergeParserMeta(incomingData.proficienciesMeta, merged.proficienciesMeta);
    }

    if (incomingData.spells || incomingData.spellcasting) {
      merged.spells = this.mergeCharacterSpellData(merged.spells, incomingData.spells);
      this.normalizeSpellcastingStructures(merged);
      merged.spellcasting = {
        ...(incomingData.spellcasting || {}),
        ...(merged.spellcasting || {}),
        parserMeta: this.mergeParserMeta(incomingData.spellcasting?.parserMeta, merged.spellcasting?.parserMeta)
      };
    }

    ['features', 'classFeatures', 'feats', 'speciesTraits', 'actions', 'bonusActions', 'reactions'].forEach((kind) => {
      if (Array.isArray(incomingData[kind]) && incomingData[kind].length > 0) {
        merged[kind] = this.mergeCharacterEntriesForKind(kind, merged[kind], incomingData[kind]);
      }
    });

    if (incomingData.equipment && typeof incomingData.equipment === 'object') {
      const existingEquipment = merged.equipment && typeof merged.equipment === 'object' ? merged.equipment : {};
      merged.equipment = {
        ...existingEquipment,
        weapons: this.mergeCharacterEntriesForKind('equipment', existingEquipment.weapons, incomingData.equipment?.weapons),
        armor: this.mergeCharacterEntriesForKind('equipment', existingEquipment.armor, incomingData.equipment?.armor),
        items: this.mergeCharacterEntriesForKind('equipment', existingEquipment.items, incomingData.equipment?.items),
        valuables: this.mergeCharacterEntriesForKind('equipment', existingEquipment.valuables, incomingData.equipment?.valuables),
        currency: {
          ...(incomingData.equipment?.currency || {}),
          ...(existingEquipment.currency || {})
        }
      };
    }

    return merged;
  }

  static collectStructuredCharacterSupplements(searchableSource, baseData = {}) {
    if (!searchableSource || typeof searchableSource !== 'object') {
      return [];
    }

    const baseCoverage = this.countStructuredCharacterCoverage(baseData);
    const nestedRoots = this.enumerateStructuredJSONRoots(searchableSource);
    const supplements = [];
    const seenSignatures = new Set();

    nestedRoots.forEach(({ value, wrapper, path }) => {
      if (!value || value === searchableSource) return;

      const mapped = this.mapStructuredJsonRoot(value, wrapper, {
        isEmbeddedCandidate: true,
        skipTextFallback: true
      });
      if (!mapped || mapped.type !== 'character' || !mapped.data || typeof mapped.data !== 'object') {
        return;
      }

      const coverage = this.countStructuredCharacterCoverage(mapped.data);
      if (coverage < 3) return;

      const signature = [
        mapped.data.name || '',
        mapped.data.class || '',
        mapped.data.level || '',
        path
      ].join('|');
      if (seenSignatures.has(signature)) return;
      seenSignatures.add(signature);

      const addsCoverage = coverage > baseCoverage
        || this.hasStructuredSpellCoverage(mapped.data.spells)
        || (Array.isArray(mapped.data.actions) && mapped.data.actions.length > 0)
        || (Array.isArray(mapped.data.reactions) && mapped.data.reactions.length > 0)
        || (Array.isArray(mapped.data.bonusActions) && mapped.data.bonusActions.length > 0)
        || ((!(Array.isArray(baseData.features) && baseData.features.length > 0)) && Array.isArray(mapped.data.features) && mapped.data.features.length > 0)
        || ((!(Array.isArray(baseData.classFeatures) && baseData.classFeatures.length > 0)) && Array.isArray(mapped.data.classFeatures) && mapped.data.classFeatures.length > 0)
        || ((!(Array.isArray(baseData.feats) && baseData.feats.length > 0)) && Array.isArray(mapped.data.feats) && mapped.data.feats.length > 0)
        || ((!(Array.isArray(baseData.speciesTraits) && baseData.speciesTraits.length > 0)) && Array.isArray(mapped.data.speciesTraits) && mapped.data.speciesTraits.length > 0)
        || ((!baseData.proficiencies || Object.keys(baseData.proficiencies).length === 0) && mapped.data.proficiencies && Object.keys(mapped.data.proficiencies).length > 0)
        || ((!(Array.isArray(baseData.languages) && baseData.languages.length > 0)) && Array.isArray(mapped.data.languages) && mapped.data.languages.length > 0)
        || ((!baseData.biography || Object.keys(baseData.biography).length === 0) && mapped.data.biography && Object.keys(mapped.data.biography).length > 0);
      if (!addsCoverage) return;

      supplements.push({
        path,
        score: (mapped.score || 0) + coverage,
        data: mapped.data,
        warnings: Array.isArray(mapped.warnings) ? mapped.warnings : []
      });
    });

    return supplements.sort((left, right) => right.score - left.score);
  }

  static augmentCharacterDataWithTextFallback(baseData = {}, searchableSource, warnings = []) {
    if (!baseData || typeof baseData !== 'object' || !searchableSource || typeof searchableSource !== 'object') {
      return baseData;
    }

    let merged = { ...baseData };
    const structuredSupplements = this.collectStructuredCharacterSupplements(searchableSource, baseData);
    const hasStructuredSupplements = structuredSupplements.length > 0;
    structuredSupplements.forEach((supplement) => {
      merged = this.mergeStructuredCharacterSupplement(merged, supplement.data);
    });
    this.normalizeSpellcastingStructures(merged);

    // date_of_change: 2026-05-02
    // rule_reference: When the structured JSON already has strong coverage (core stats + at least one
    // content array), skip the text-fallback pipeline entirely. Running extractUncategorizedBlocks
    // on JSON-serialized text is the root cause of narrative prose being misclassified as equipment/
    // features — the JSON field values (description, appearance, tactics, etc.) hit keyword matchers
    // even though mapJSONToCanonical already handled them. Only fall through to text parsing when
    // structured coverage is genuinely weak (missing abilities, ac, or hp).
    // date_of_change: 2026-05-02
    // Only skip the text-fallback pipeline when both conditions hold:
    //   (a) the structured parse already has strong coverage (name+class+level+abilities+ac+hp = 6 pts, or abilities+ac+hp directly), AND
    //   (b) the structured parse already has spell coverage — otherwise irregular spell blocks
    //       (e.g. spellbook_blob.primary_spell) can only be recovered by the text-flattening path.
    const structuredCoverage = this.countStructuredCharacterCoverage(merged);
    const hasStrongStructuredCoverage = (structuredCoverage >= 5
      || (merged.abilities && merged.ac && (merged.hp || merged.maxHp)))
      && this.hasStructuredSpellCoverage(merged.spells);
    if (hasStrongStructuredCoverage) {
      return merged;
    }

    const searchableText = this.buildSearchableTextFromStructuredValue(searchableSource);
    if (!searchableText.trim()) {
      return merged;
    }

    const textResult = this.parseCharacterStatBlock(searchableText, { entityType: 'character' });
    const parsedData = textResult?.type === 'character' ? textResult.data : null;
    if (!parsedData || typeof parsedData !== 'object') {
      return merged;
    }

    const mergeScalarField = (field) => {
      if (merged[field] === undefined && parsedData[field] !== undefined) {
        merged[field] = parsedData[field];
      }
    };
    [
      'background',
      'race',
      'species',
      'subclass',
      'alignment',
      'abilities',
      'ac',
      'hp',
      'maxHp',
      'tempHp',
      'speed',
      'initiative',
      'passivePerception',
      'senses',
      'proficiencies',
      'toolProficiencies',
      'languages',
      'notes'
    ].forEach(mergeScalarField);

    if (parsedData.biography || parsedData.biographyMeta) {
      merged.biography = { ...(parsedData.biography || {}), ...(merged.biography || {}) };
      if (parsedData.biographyMeta || merged.biographyMeta) {
        merged.biographyMeta = {
          ...(parsedData.biographyMeta || {}),
          ...(merged.biographyMeta || {})
        };
      }
    }

    if (parsedData.spells || parsedData.spellcasting) {
      if (!this.hasStructuredSpellCoverage(merged.spells)) {
        merged.spells = this.mergeCharacterSpellData(merged.spells, parsedData.spells);
        this.normalizeSpellcastingStructures(merged);
      }
      merged.spellcasting = {
        ...(parsedData.spellcasting || {}),
        ...(merged.spellcasting || {}),
        parserMeta: this.mergeParserMeta(parsedData.spellcasting?.parserMeta, merged.spellcasting?.parserMeta)
      };
    }

    ['features', 'classFeatures', 'feats', 'speciesTraits', 'actions', 'bonusActions', 'reactions'].forEach((kind) => {
      if (Array.isArray(parsedData[kind]) && parsedData[kind].length > 0) {
        const existingEntries = Array.isArray(merged[kind]) ? merged[kind] : [];
        if (!hasStructuredSupplements && existingEntries.length === 0) {
          merged[kind] = this.mergeCharacterEntriesForKind(kind, parsedData[kind], merged[kind]);
        }
      }
    });

    if (!hasStructuredSupplements && parsedData.equipment && typeof parsedData.equipment === 'object') {
      const existingEquipment = (merged.equipment && typeof merged.equipment === 'object') ? merged.equipment : {};
      merged.equipment = {
        ...existingEquipment,
        weapons: Array.isArray(existingEquipment.weapons) && existingEquipment.weapons.length > 0
          ? existingEquipment.weapons
          : this.mergeCharacterEntriesForKind('equipment', parsedData.equipment?.weapons, existingEquipment.weapons),
        armor: Array.isArray(existingEquipment.armor) && existingEquipment.armor.length > 0
          ? existingEquipment.armor
          : this.mergeCharacterEntriesForKind('equipment', parsedData.equipment?.armor, existingEquipment.armor),
        items: Array.isArray(existingEquipment.items) && existingEquipment.items.length > 0
          ? existingEquipment.items
          : this.mergeCharacterEntriesForKind('equipment', parsedData.equipment?.items, existingEquipment.items),
        valuables: Array.isArray(existingEquipment.valuables) && existingEquipment.valuables.length > 0
          ? existingEquipment.valuables
          : this.mergeCharacterEntriesForKind('equipment', parsedData.equipment?.valuables, existingEquipment.valuables)
      };
    }

    if (Array.isArray(parsedData.warnings) && parsedData.warnings.length > 0) {
      warnings.push(...parsedData.warnings);
    }

    return merged;
  }

  /**
   * Map JSON fields to canonical schema format
   * @date_of_change 2025-12-16 - Added to transform structured JSON to canonical format
   */
  static mapJSONToCanonical(json, warnings = []) {
    const data = {};
    let routedBackgroundNarrative = '';
    const warn = (message) => {
      if (!Array.isArray(warnings)) return;
      if (typeof message !== 'string' || !message.trim()) return;
      warnings.push(message.trim());
    };

    const combatBlock = (json.combat && typeof json.combat === 'object' && !Array.isArray(json.combat)) ? json.combat : null;

    // Name
    data.name = json.name || json.canonical_name || json.title || 'Unnamed Character';

    // Race/Species
    if (json.race) data.race = json.race;
    if (json.species) data.species = json.species;

    // Size
    if (json.size) data.size = json.size;

    // Creature type
    if (json.creature_type) data.creatureType = json.creature_type;

    // Alignment
    if (json.alignment) data.alignment = json.alignment;

    // Description/Appearance
    if (json.description) data.description = json.description;
    if (json.appearance) data.appearance = json.appearance;
    if (json.background) {
      // date_of_change: 2026-03-20
      // rule_reference: Canonical D&D background is a short title (Soldier, Sage, Gladiator), not narrative prose.
      // Route prose-like background text into biography.backstory so SmartPaste doesn't stuff history into the header field.
      if (typeof json.background === 'string') {
        const classifiedBackground = this.classifyBackgroundValue(json.background);
        if (classifiedBackground.background) {
          data.background = classifiedBackground.background;
        }
        if (classifiedBackground.backstory) {
          routedBackgroundNarrative = classifiedBackground.backstory;
        }
      } else if (json.background && typeof json.background === 'object') {
        const backgroundTitle = json.background.name || json.background.title || json.background.label || json.background.background;
        const backgroundDescription = json.background.description || json.background.notes || json.background.story;
        const classifiedTitle = this.classifyBackgroundValue(backgroundTitle || '');
        if (classifiedTitle.background) {
          data.background = classifiedTitle.background;
        } else if (classifiedTitle.backstory) {
          routedBackgroundNarrative = classifiedTitle.backstory;
        }
        if (!routedBackgroundNarrative && typeof backgroundDescription === 'string' && backgroundDescription.trim()) {
          routedBackgroundNarrative = backgroundDescription.trim();
        }
      }
    }

    // Class levels - handle array format
    if (Array.isArray(json.class_levels) && json.class_levels.length > 0) {
      data.classes = json.class_levels.map(cl => ({
        name: cl.class || cl.name,
        // date_of_change: 2026-05-03
        // rule_reference: level may arrive as a string ("8") from some exporters; coerce to integer.
        level: typeof cl.level === 'number' ? cl.level : (parseInt(cl.level, 10) || 0),
        // date_of_change: 2026-05-03
        // rule_reference: AI exporters use various keys for subclass (archetype, subclass_name, path, domain, tradition).
        subclass: cl.subclass || cl.archetype || cl.subclass_name || cl.path || cl.domain || cl.tradition || cl.primal_path,
        notes: cl.notes
      }));
      // Set primary class and total level
      data.class = data.classes[0].name;
      data.subclass = data.classes[0].subclass;
      // date_of_change: 2026-05-03
      // rule_reference: All per-class levels are already coerced to integers above; safe arithmetic sum.
      data.level = data.classes.reduce((sum, c) => sum + (c.level || 0), 0);
      // If level summed to 0 (all undefined/unparseable) fall back to json.level
      if (data.level === 0 && json.level) {
        data.level = typeof json.level === 'number' ? json.level : (parseInt(json.level, 10) || 0);
      }
    } else if (typeof json.class_levels === 'string' && json.class_levels.trim()) {
      // date_of_change: 2026-01-03
      // rule_reference: SmartPaste structured JSON may encode multiclass data as a single string; parseMulticlassText preserves structured classes.
      const parsed = this.parseMulticlassText(json.class_levels);
      if (parsed && Array.isArray(parsed.classes) && parsed.classes.length > 0) {
        data.classes = parsed.classes.map(c => ({
          name: c.name,
          level: c.level,
          ...(c.subclass ? { subclass: c.subclass } : {})
        }));
        data.class = parsed.primaryClass || data.classes[0].name;
        if (parsed.primarySubclass || data.classes[0].subclass) {
          data.subclass = parsed.primarySubclass || data.classes[0].subclass;
        }
        data.level = parsed.totalLevel;
      } else {
        warn(`Unable to parse class_levels string; expected formats like "Fighter 5 / Druid 3", got: ${json.class_levels}`);
        // date_of_change: 2026-05-02
        // rule_reference: Fall back to json.class + json.level when class_levels string cannot be parsed.
        if (json.class) {
          data.class = json.class;
          data.level = (typeof json.level === 'number' ? json.level : parseInt(json.level, 10)) || 1;
          if (json.subclass) data.subclass = json.subclass;
        }
      }
    } else if (json.class) {
      data.class = json.class;
      data.level = (typeof json.level === 'number' ? json.level : parseInt(json.level, 10)) || 1;
      if (json.subclass) data.subclass = json.subclass;
    }

    // Ability scores - handle both formats
    if (json.ability_scores) {
      data.abilities = this.normalizeAbilityScores(json.ability_scores);
    } else if (json.abilities && typeof json.abilities === 'object' && !Array.isArray(json.abilities)) {
      // Check if 'abilities' is ability scores (has str/strength) vs features array
      const hasAbilityKeys = ['str', 'dex', 'con', 'int', 'wis', 'cha', 'strength', 'dexterity'].some(k => k in json.abilities);
      if (hasAbilityKeys) {
        data.abilities = this.normalizeAbilityScores(json.abilities);
      }
    }

    // date_of_change: 2026-01-03
    // rule_reference: SmartPaste structured JSON may provide armor class in multiple shapes; parse helper is shared across armor_class/ac fields.
    const parseAcEntry = (entry, entryPath) => {
      if (entry === null || entry === undefined) return null;

      if (typeof entry === 'number') {
        return { value: entry, notes: '' };
      }

      if (typeof entry === 'string') {
        const n = parseInt(entry, 10);
        if (Number.isFinite(n)) return { value: n, notes: '' };
        warn(`Invalid ${entryPath}: expected numeric string, got ${entry}`);
        return null;
      }

      if (typeof entry === 'object' && !Array.isArray(entry)) {
        const rawVal = entry.value ?? entry.ac;
        const n = (typeof rawVal === 'number') ? rawVal : parseInt(String(rawVal ?? ''), 10);
        if (!Number.isFinite(n)) {
          warn(`Invalid ${entryPath}.value: expected number, got ${String(rawVal)}`);
          return null;
        }
        const notes = (typeof entry.notes === 'string' ? entry.notes : '').trim();
        return { value: n, notes };
      }

      warn(`Invalid ${entryPath}: expected number, string, or { value }, got ${Array.isArray(entry) ? 'array' : typeof entry}`);
      return null;
    };

    const coerceNumber = (value) => {
      if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
      if (typeof value === 'string' && value.trim()) {
        const n = parseInt(value.trim(), 10);
        return Number.isFinite(n) ? n : undefined;
      }
      return undefined;
    };

    // Armor Class
    const armorClassSource = (json.armor_class !== undefined)
      ? json.armor_class
      : (combatBlock?.armor_class ?? combatBlock?.ac);
    if (armorClassSource !== undefined) {
      // date_of_change: 2026-01-03
      // rule_reference: SmartPaste structured JSON may provide armor_class as a number, { value, notes }, or an array; must not produce NaN.
      if (Array.isArray(armorClassSource)) {
        const parsed = armorClassSource
          .map((e, i) => parseAcEntry(e, `armor_class[${i}]`))
          .filter(Boolean);

        if (parsed.length > 0) {
          const mergedNotes = parsed
            .map(p => p.notes)
            .filter(n => typeof n === 'string' && n.trim())
            .join('; ')
            .trim();
          data.ac = { ...parsed[0], ...(mergedNotes ? { notes: mergedNotes } : {}) };
        } else {
          warn('armor_class array did not contain a parseable entry with a numeric value');
        }
      } else {
        const parsed = parseAcEntry(armorClassSource, 'armor_class');
        if (parsed) data.ac = parsed;
      }
    } else if (json.ac !== undefined) {
      const parsed = parseAcEntry(json.ac, 'ac');
      if (parsed) data.ac = parsed;
    }

    // Hit Points
    // date_of_change: 2026-05-03
    // rule_reference: Exporters use many key variants for max HP. Check all before falling back to hit_points.
    const hitPointsSource = json.hit_points
      || combatBlock?.hit_points
      || combatBlock?.hp
      || (json.max_hp !== undefined ? { maximum: json.max_hp, current: json.hp ?? json.max_hp } : null)
      || (json.hit_point_maximum !== undefined ? { maximum: json.hit_point_maximum, current: json.hp ?? json.hit_point_maximum } : null)
      || (json.hp_max !== undefined ? { maximum: json.hp_max, current: json.hp ?? json.hp_max } : null);
    if (hitPointsSource) {
      if (typeof hitPointsSource === 'object') {
        const maxCandidates = [hitPointsSource.maximum, hitPointsSource.max, hitPointsSource.average, hitPointsSource.current];
        const resolvedMax = maxCandidates.map(coerceNumber).find(v => v !== undefined);
        if (resolvedMax !== undefined) data.maxHp = resolvedMax;

        const currentCandidates = [hitPointsSource.current, hitPointsSource.average, resolvedMax];
        const resolvedCurrent = currentCandidates.map(coerceNumber).find(v => v !== undefined);

        if (hitPointsSource.formula) {
          const avg = resolvedMax ?? resolvedCurrent;
          data.hp = { average: avg !== undefined ? avg : null, formula: hitPointsSource.formula };
        } else if (resolvedCurrent !== undefined) {
          data.hp = resolvedCurrent;
        } else if (data.maxHp !== undefined) {
          data.hp = data.maxHp;
        }
      } else {
        const numericHp = coerceNumber(hitPointsSource);
        if (numericHp !== undefined) {
          data.maxHp = numericHp;
          data.hp = numericHp;
        } else {
          warn(`Invalid hit_points: expected number or object, got ${String(hitPointsSource)}`);
        }
      }
    } else if (json.hp !== undefined) {
      data.maxHp = typeof json.hp === 'object' ? (json.hp.max || json.hp.average) : parseInt(json.hp, 10);
      data.hp = json.hp;
    }

    // Hit Dice
    if (json.hit_dice) {
      data.hitDice = json.hit_dice;
    }

    // Speed - handle various formats
    if (json.speed) {
      if (typeof json.speed === 'object') {
        data.speed = {};
        for (const [mode, val] of Object.entries(json.speed)) {
          // Handle "30 ft." format
          const numMatch = String(val).match(/(\d+)/);
          if (numMatch) {
            data.speed[mode.toLowerCase()] = parseInt(numMatch[1], 10);
          }
        }
      } else {
        const numMatch = String(json.speed).match(/(\d+)/);
        if (numMatch) {
          data.speed = { walk: parseInt(numMatch[1], 10) };
        }
      }
    }

    // Proficiency Bonus
    if (json.proficiency_bonus !== undefined) {
      data.proficiencyBonus = parseInt(json.proficiency_bonus, 10);
    }

    // Languages
    if (Array.isArray(json.languages)) {
      data.languages = json.languages;
    }

    // Initiative / passive perception fallbacks from combat block
    if (combatBlock?.initiative !== undefined && data.initiative === undefined) {
      const initiativeVal = parseInt(String(combatBlock.initiative), 10);
      if (!Number.isNaN(initiativeVal)) data.initiative = initiativeVal;
    }
    if (combatBlock?.passive_perception !== undefined && data.passivePerception === undefined) {
      const passiveVal = parseInt(String(combatBlock.passive_perception), 10);
      if (!Number.isNaN(passiveVal)) data.passivePerception = passiveVal;
    }

    // Senses
    // date_of_change: 2026-01-03
    // rule_reference: SmartPaste structured JSON may provide senses as an array/object/string; preserve them into canonical data.senses.
    if (json.senses !== undefined && json.senses !== null) {
      const senses = {};

      const setSense = (senseKey, value, fieldPath) => {
        const n = (typeof value === 'number') ? value : parseInt(String(value ?? ''), 10);
        if (!Number.isFinite(n)) {
          warn(`Invalid ${fieldPath}: expected number for ${senseKey}, got ${String(value)}`);
          return;
        }
        senses[senseKey] = n;
      };

      const parseSenseLine = (line, fieldPath) => {
        if (typeof line !== 'string') {
          warn(`Invalid ${fieldPath}: expected string, got ${typeof line}`);
          return;
        }
        const m1 = line.match(/\b(darkvision|blindsight|tremorsense|truesight)\b\s*(\d+)\b/i);
        if (m1) {
          setSense(m1[1].toLowerCase(), m1[2], fieldPath);
        }
        const pp = line.match(/\bpassive\s*perception\b\s*(\d+)\b/i);
        if (pp) {
          setSense('passivePerception', pp[1], fieldPath);
        }
      };

      if (Array.isArray(json.senses)) {
        for (let i = 0; i < json.senses.length; i++) {
          const entry = json.senses[i];
          const entryPath = `senses[${i}]`;
          if (typeof entry === 'string') {
            parseSenseLine(entry, entryPath);
            continue;
          }
          if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            const name = (typeof entry.name === 'string')
              ? entry.name.trim()
              : (typeof entry.sense === 'string' ? entry.sense.trim() : '');
            const nameLower = name.toLowerCase();
            const range = entry.range ?? entry.distance ?? entry.value;
            if (nameLower === 'passive perception' || nameLower === 'passiveperception') {
              setSense('passivePerception', range, `${entryPath}.range`);
              continue;
            }
            if (['darkvision', 'blindsight', 'tremorsense', 'truesight'].includes(nameLower)) {
              setSense(nameLower, range, `${entryPath}.range`);
              continue;
            }
            warn(`Unsupported ${entryPath}: expected a sense like darkvision/blindsight or passive perception, got name=${String(name)}`);
            continue;
          }
          warn(`Invalid ${entryPath}: expected string or object, got ${Array.isArray(entry) ? 'array' : typeof entry}`);
        }
      } else if (typeof json.senses === 'string') {
        parseSenseLine(json.senses, 'senses');
      } else if (typeof json.senses === 'object') {
        for (const [k, v] of Object.entries(json.senses)) {
          const keyLower = k.toLowerCase();
          if (['darkvision', 'blindsight', 'tremorsense', 'truesight'].includes(keyLower)) {
            setSense(keyLower, v, `senses.${k}`);
          } else if (keyLower === 'passiveperception' || keyLower === 'passive_perception' || keyLower === 'passive perception') {
            setSense('passivePerception', v, `senses.${k}`);
          }
        }
      } else {
        warn(`Invalid senses: expected array/object/string, got ${typeof json.senses}`);
      }

      if (Object.keys(senses).length > 0) {
        data.senses = senses;
        if (typeof senses.passivePerception === 'number') {
          data.passivePerception = senses.passivePerception;
        }
      }
    }

    // Defenses (damage & condition traits)
    // date_of_change: 2026-01-03
    // rule_reference: SmartPaste structured JSON should preserve resistances/immunities/vulnerabilities/condition immunities.
    const coerceStringList = (value, fieldPath) => {
      if (value === null || value === undefined) return null;
      if (Array.isArray(value)) {
        const list = value
          .filter(v => typeof v === 'string')
          .map(v => v.trim())
          .filter(Boolean);
        if (list.length !== value.length) {
          warn(`Invalid ${fieldPath}: expected array of strings; non-string entries were ignored.`);
        }
        return list;
      }
      if (typeof value === 'string') {
        return value
          .split(/\s*,\s*/g)
          .map(s => s.trim())
          .filter(Boolean);
      }
      warn(`Invalid ${fieldPath}: expected array/string, got ${typeof value}`);
      return null;
    };

    const explicitProficiencies = (json.proficiencies && typeof json.proficiencies === 'object' && !Array.isArray(json.proficiencies))
      ? json.proficiencies
      : null;
    const armorProficiencies = coerceStringList(
      explicitProficiencies?.armor ?? explicitProficiencies?.armorTraining ?? json.armor_proficiencies ?? json.armor_training,
      'armor_proficiencies'
    );
    const weaponProficiencies = coerceStringList(
      explicitProficiencies?.weapons ?? explicitProficiencies?.weaponTraining ?? json.weapon_proficiencies ?? json.weapon_training,
      'weapon_proficiencies'
    );
    const proficiencyTools = coerceStringList(explicitProficiencies?.tools, 'proficiencies.tools');
    const proficiencyLanguages = coerceStringList(explicitProficiencies?.languages, 'proficiencies.languages');
    const proficiencySavingThrows = coerceStringList(
      explicitProficiencies?.savingThrows ?? explicitProficiencies?.saving_throws,
      'proficiencies.savingThrows'
    );

    if (
      armorProficiencies?.length
      || weaponProficiencies?.length
      || proficiencyTools?.length
      || proficiencyLanguages?.length
      || proficiencySavingThrows?.length
    ) {
      data.proficiencies = {
        ...(data.proficiencies || {}),
        ...(armorProficiencies?.length ? { armor: armorProficiencies } : {}),
        ...(weaponProficiencies?.length ? { weapons: weaponProficiencies } : {}),
        ...(proficiencyTools?.length ? { tools: proficiencyTools } : {}),
        ...(proficiencyLanguages?.length ? { languages: proficiencyLanguages } : {}),
        ...(proficiencySavingThrows?.length
          ? { savingThrows: proficiencySavingThrows.map((value) => this.normalizeAbilityName(value)) }
          : {})
      };
      if ((!Array.isArray(data.toolProficiencies) || data.toolProficiencies.length === 0) && proficiencyTools?.length) {
        data.toolProficiencies = [...proficiencyTools];
      }
      if ((!Array.isArray(data.languages) || data.languages.length === 0) && proficiencyLanguages?.length) {
        data.languages = [...proficiencyLanguages];
      }
    }

    const resistances = coerceStringList(json.damage_resistances ?? combatBlock?.resistances, 'damage_resistances');

    if (resistances && resistances.length) data.resistances = resistances;
    const immunities = coerceStringList(json.damage_immunities, 'damage_immunities');
    if (immunities && immunities.length) data.immunities = immunities;
    const vulnerabilities = coerceStringList(json.damage_vulnerabilities, 'damage_vulnerabilities');
    if (vulnerabilities && vulnerabilities.length) data.vulnerabilities = vulnerabilities;
    const conditionImmunities = coerceStringList(json.condition_immunities, 'condition_immunities');
    if (conditionImmunities && conditionImmunities.length) data.conditionImmunities = conditionImmunities;

    // Saving Throws - handle array of strings or objects
    // date_of_change: 2025-12-16
    // rule_reference: Handle both string format "Strength +6" and object format { name, value, notes }
    if (Array.isArray(json.saving_throws)) {
      const st = {};
      for (const save of json.saving_throws) {
        let abilityName, bonus;
        if (typeof save === 'object' && save !== null) {
          // Object format: { name: "Strength", value: "+6", notes: "..." }
          abilityName = save.name;
          const valMatch = String(save.value || '').match(/([+-]?\d+)/);
          bonus = valMatch ? parseInt(valMatch[1], 10) : null;
        } else {
          // String format: "Strength +6"
          const match = String(save).match(/(Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma|STR|DEX|CON|INT|WIS|CHA)\s*([+-]?\d+)/i);
          if (match) {
            abilityName = match[1];
            bonus = parseInt(match[2], 10);
          }
        }
        if (abilityName && bonus !== null) {
          const key = this.normalizeAbilityName(abilityName);
          st[key] = bonus;
        }
      }
      if (Object.keys(st).length > 0) data.savingThrows = st;
    } else if (json.saving_throws && typeof json.saving_throws === 'object') {
      data.savingThrows = json.saving_throws;
    }

    // Skill Proficiencies - handle array of strings or objects
    // date_of_change: 2025-12-16
    // rule_reference: Handle both string format "Athletics +6" and object format { name, value, notes }
    if (Array.isArray(json.skill_proficiencies)) {
      const skills = [];
      const skillsDetailed = [];
      for (const skill of json.skill_proficiencies) {
        let name, bonus;
        if (typeof skill === 'object' && skill !== null) {
          // Object format: { name: "Athletics", value: "+6", notes: "..." }
          name = skill.name;
          const valMatch = String(skill.value || '').match(/([+-]?\d+)/);
          bonus = valMatch ? parseInt(valMatch[1], 10) : null;
        } else {
          // String format: "Athletics +6"
          const match = String(skill).match(/([A-Za-z\s]+)\s*([+-]?\d+)?/);
          if (match) {
            name = match[1].trim();
            bonus = match[2] ? parseInt(match[2], 10) : null;
          }
        }
        if (name) {
          // date_of_change: 2026-02-12
          // rule_reference: D&D 5e (2024) - Detect expertise from notes, value (double PB), or explicit flag
          const notesStr = (typeof skill === 'object' && skill !== null) ? (skill.notes || '') : '';
          const hasExpertise = (typeof skill === 'object' && skill.expertise === true)
            || /expertise/i.test(notesStr)
            || /\(expertise\)/i.test(String(skill));
          skills.push(name);
          skillsDetailed.push({ name, proficient: true, expertise: hasExpertise, bonus });
        }
      }
      if (skills.length > 0) {
        data.skills = skills;
        data.skillsDetailed = skillsDetailed;
      }
    }

    // date_of_change: 2026-02-24
    // rule_reference: Structured JSON may include a `skills` object keyed by skill name => bonus; merge these safely.
    if (json.skills && typeof json.skills === 'object' && !Array.isArray(json.skills)) {
      const normalizeSkillName = (raw) => {
        if (!raw) return '';
        return String(raw)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (ch) => ch.toUpperCase())
          .trim();
      };

      const entries = [];
      for (const [rawName, bonusValue] of Object.entries(json.skills)) {
        const name = normalizeSkillName(rawName);
        if (!name) continue;
        const bonus = coerceNumber(bonusValue);
        if (bonus === undefined) {
          warn(`Invalid skills.${rawName}: expected numeric bonus, got ${String(bonusValue)}`);
          continue;
        }
        entries.push({ name, bonus });
      }

      if (entries.length > 0) {
        if (!Array.isArray(data.skills)) data.skills = [];
        if (!Array.isArray(data.skillsDetailed)) data.skillsDetailed = [];
        const existing = new Set(data.skillsDetailed.map(s => (s.name || '').toLowerCase()));
        for (const entry of entries) {
          const key = entry.name.toLowerCase();
          if (existing.has(key)) continue;
          data.skills.push(entry.name);
          data.skillsDetailed.push({ name: entry.name, proficient: true, expertise: false, bonus: entry.bonus });
          existing.add(key);
        }
      }
    }

    // date_of_change: 2026-03-19
    // rule_reference: Structured JSON imports must preserve explicit authored feature buckets instead of relying only on text heuristics.
    const normalizeStructuredNamedEntry = (entry, sourceSection, sourceLabel) => {
      if (typeof entry === 'string') {
        return {
          name: entry,
          description: entry,
          ...(sourceLabel ? { source: sourceLabel } : {}),
          sourceSection,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection,
            knowledgeSource: 'provided',
            parseConfidence: 1
          })
        };
      }
      if (!entry || typeof entry !== 'object') return null;
      const name = entry.name || entry.title || entry.header;
      if (!name) return null;
      return {
        ...entry,
        name,
        description: entry.description || entry.content || entry.text || entry.notes || name,
        ...(sourceLabel && !entry.source ? { source: sourceLabel } : {}),
        sourceSection: entry.sourceSection || sourceSection,
        parserMeta: this.mergeParserMeta(
          entry.parserMeta,
          {
            sourceParser: 'mapJSONToCanonical',
            sourceSection,
            knowledgeSource: 'provided',
            parseConfidence: 1
          }
        )
      };
    };

    const mergeStructuredEntries = (kind, entries, sourceSection, sourceLabel) => {
      const normalizedEntries = (Array.isArray(entries) ? entries : [])
        .map((entry) => normalizeStructuredNamedEntry(entry, sourceSection, sourceLabel))
        .filter(Boolean);
      if (normalizedEntries.length === 0) return;
      data[kind] = this.mergeCharacterEntriesForKind(kind, data[kind], normalizedEntries);
    };

    mergeStructuredEntries('classFeatures', json.class_features, 'classFeatures', 'Class');
    mergeStructuredEntries('classFeatures', json.subclass_features, 'classFeatures', 'Subclass');
    mergeStructuredEntries('speciesTraits', json.racial_features, 'speciesTraits', 'Species');
    mergeStructuredEntries('classFeatures', json.fighting_styles, 'classFeatures', 'Fighting Style');

    // Features/Abilities - auto-categorize into feats, class features, and traits
    // @date_of_change 2025-12-16 - Use categorizeFeatures to separate feats from class features from traits
    // date_of_change: 2026-02-12
    // rule_reference: Prefer _pipeline_stages abilities data when it has richer source fields than top-level.
    // The combat stage key is 'creator:_combat', not 'creator:_abilities'.
    const pipelineCombat = json._pipeline_stages?.['creator:_combat'];
    const pipelineAbilities = (pipelineCombat && Array.isArray(pipelineCombat.abilities))
      ? pipelineCombat.abilities
      : null;
    const abilitiesSource = (pipelineAbilities && pipelineAbilities.length > 0 && pipelineAbilities[0].source)
      ? pipelineAbilities
      : (Array.isArray(json.abilities) ? json.abilities : null);
    if (abilitiesSource) {
      const categorized = this.categorizeFeatures(abilitiesSource);
      
      // Class Features (from class levels)
      // date_of_change: 2026-04-27
      // rule_reference: Features from structured JSON must include parserMeta.rawText for SmartPaste UI preview consistency.
      if (categorized.classFeatures.length > 0) {
        data.features = categorized.classFeatures.map(a => ({
          name: a.name,
          description: a.description,
          source: a.source,
          uses: a.uses,
          notes: a.notes,
          actionType: 'passive',
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'features',
            rawText: a.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        }));
        data.classFeaturesText = categorized.classFeatures
          .map(a => `${a.name}${a.description ? ': ' + a.description : ''}`)
          .join('\n');
      }
      
      // Feats (character choices like Magic Initiate, War Caster, etc.)
      // date_of_change: 2026-04-27
      // rule_reference: Feats from structured JSON must include parserMeta.rawText for SmartPaste UI preview consistency.
      if (categorized.feats.length > 0) {
        data.feats = categorized.feats.map(a => ({
          name: a.name,
          description: a.description,
          source: a.source || 'Feat',
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'feats',
            rawText: a.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        }));
        data.featsText = categorized.feats
          .map(a => `${a.name}${a.description ? ': ' + a.description : ''}`)
          .join('\n');
      }
      
      // Species/Racial/Monster Traits (Regeneration, Spider Climb, etc.)
      // date_of_change: 2026-04-27
      // rule_reference: Species traits from structured JSON must include parserMeta.rawText for SmartPaste UI preview consistency.
      if (categorized.traits.length > 0) {
        data.speciesTraits = categorized.traits.map(a => ({
          name: a.name,
          description: a.description,
          source: a.source,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'speciesTraits',
            rawText: a.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        }));
        data.speciesTraitsText = categorized.traits
          .map(a => `${a.name}${a.description ? ': ' + a.description : ''}`)
          .join('\n');
      }
    }

    // date_of_change: 2026-02-12
    // rule_reference: D&D 5e (2024) - Auto-append standard class features from class_levels when the
    // external JSON omits them. Only features at or below the character's level are included.
    // This is a lightweight lookup; the GM can always edit the result.
    if (Array.isArray(json.class_levels) && json.class_levels.length > 0) {
      const stdFeatures = this.getStandardClassFeatures(json.class_levels);
      if (stdFeatures.length > 0) {
        // Deduplicate: skip any standard feature whose name already appears in data.features or data.classFeaturesText
        const existingNames = new Set();
        if (Array.isArray(data.features)) {
          data.features.forEach(f => existingNames.add((f.name || '').toLowerCase()));
        }
        const existingText = (data.classFeaturesText || '').toLowerCase();

        const newFeatures = stdFeatures.filter(f => {
          const nameLower = f.name.toLowerCase();
          return !existingNames.has(nameLower) && !existingText.includes(nameLower);
        });

        if (newFeatures.length > 0) {
          // Append to features array
          if (!Array.isArray(data.features)) data.features = [];
          data.features.push(...newFeatures.map(f => ({
            name: f.name,
            description: f.description,
            source: f.source,
            actionType: 'passive'
          })));

          // Append to classFeaturesText
          const newText = newFeatures
            .map(f => `${f.name}: ${f.description}`)
            .join('\n');
          data.classFeaturesText = data.classFeaturesText
            ? data.classFeaturesText + '\n' + newText
            : newText;
        }
      }
    }

    // date_of_change: 2025-12-16
    // rule_reference: Handle feats array for character feats
    if (Array.isArray(json.feats)) {
      data.feats = json.feats.map(f => {
        if (typeof f === 'string') return { name: f };
        return { name: f.name, description: f.description, source: f.source };
      });
      data.featsText = json.feats
        .map(f => typeof f === 'string' ? f : `${f.name}${f.description ? ': ' + f.description : ''}`)
        .join('\n');
    }

    // date_of_change: 2025-12-16
    // rule_reference: Handle species/racial traits
    if (Array.isArray(json.racial_traits)) {
      data.speciesTraitsText = json.racial_traits
        .map(t => typeof t === 'string' ? t : `${t.name}${t.description ? ': ' + t.description : ''}`)
        .join('\n');
    } else if (Array.isArray(json.species_traits)) {
      data.speciesTraitsText = json.species_traits
        .map(t => typeof t === 'string' ? t : `${t.name}${t.description ? ': ' + t.description : ''}`)
        .join('\n');
    }

    // date_of_change: 2025-12-16
    // rule_reference: Handle tool proficiencies
    if (Array.isArray(json.tool_proficiencies)) {
      data.toolProficiencies = json.tool_proficiencies.map(t => 
        typeof t === 'string' ? t : t.name
      );
    }

    // Actions
    // date_of_change: 2026-04-27
    // rule_reference: Actions from structured JSON must include parserMeta.rawText for SmartPaste UI preview consistency.
    // date_of_change: 2026-05-04
    // rule_reference: AI-generated JSON sometimes places saving throw stats, spellcasting metadata,
    // or NPC biography entries inside the actions array. Filter these out so they do not pollute
    // the action economy panel. Valid D&D actions have names that describe a combat/game maneuver.
    const ACTION_DISQUALIFIED_NAMES = new Set([
      'strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma',
      'str', 'dex', 'con', 'int', 'wis', 'cha',
      'saving throws', 'saving throw', 'spellcasting', 'skills', 'senses',
      'languages', 'challenge', 'proficiency bonus', 'passive perception'
    ]);
    const ACTION_DISQUALIFIED_PATTERNS = [
      // Names containing these substrings are disqualified
      /spellcasting/i,           // "Spellcasting (Druid)"
      /saving\s*throw/i,         // "Strength Saving Throw"
      /\bnotes\s*:/i,            // "actions +1 Longsword notes: ..."
      /^\s*actions\s+\+/i,       // "actions +1 Longsword..."
      /^\s*attack\s*with/i,      // "Attack with..."
      // Character/NPC names (2-3 capitalized words, not action names)
      /^(king|queen|prince|princess|lord|lady|sir|master|dame)\s+[a-z]+/i,
      // Ability score entries with value notation
      /\s*value\s*:\s*\+?\d+$/i,
      // Source/notes patterns that indicate metadata
      /\bsource\s*:/i,
      /\babilities\s+[^a-z]*\s+source\s*:/i
    ];
    const isDisqualifiedAction = (a) => {
      if (!a || !a.name) return true;
      if (this.isCharacterSheetMetadataDump(a)) return true;
      const nameLower = String(a.name).trim().toLowerCase();
      const nameRaw = String(a.name).trim();
      
      // Exact match disqualification
      if (ACTION_DISQUALIFIED_NAMES.has(nameLower)) return true;
      
      // Pattern-based disqualification (substring matching)
      for (const pattern of ACTION_DISQUALIFIED_PATTERNS) {
        if (pattern.test(nameRaw)) return true;
      }
      
      // Entries whose "name" is a long sentence (looks like bio prose / NPC description)
      const words = nameLower.split(/\s+/);
      if (words.length > 10) return true;
      
      // Short but obviously metadata entries (2-3 words that are ability scores)
      if (words.length <= 3) {
        const firstWord = words[0];
        if (ACTION_DISQUALIFIED_NAMES.has(firstWord)) return true;
      }
      
      // Entries that contain spellcasting metadata patterns in description but no attack/damage info
      const desc = String(a.description || a.notes || '').toLowerCase();
      const hasSpellcastingBlock = /spell\s*slots?\s*(1st|2nd|3rd|[1-9]th)|spellcasting\s+(ability|focus|attack|save\s*dc)/i.test(desc);
      const hasAttackInfo = /\d+d\d+|attack\s+roll|saving\s+throw|damage/i.test(desc);
      if (hasSpellcastingBlock && !hasAttackInfo) return true;
      
      // Description is just ability score assumptions/metadata
      if (/assumes\s+(fighter|wizard|cleric|rogue|druid|bard|paladin|ranger|sorcerer|warlock|monk|barbarian)/i.test(desc) 
          && !/melee|ranged|spell|attack|damage/i.test(desc)) {
        return true;
      }
      
      return false;
    };
    if (Array.isArray(json.actions)) {
      data.actions = json.actions
        .filter(a => !isDisqualifiedAction(a))
        .map(a => ({
          name: a.name,
          description: a.description,
          notes: a.notes,
          uses: a.uses,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'actions',
            rawText: a.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        }));
    }

    // Bonus Actions
    // date_of_change: 2026-04-27
    // rule_reference: Bonus actions from structured JSON must include parserMeta.rawText for SmartPaste UI preview consistency.
    // date_of_change: 2026-05-04
    // rule_reference: Apply same filtering as actions to remove bogus entries
    if (Array.isArray(json.bonus_actions)) {
      data.bonusActions = json.bonus_actions
        .filter(a => !isDisqualifiedAction(a))
        .map(a => ({
          name: a.name,
          description: a.description,
          notes: a.notes,
          uses: a.uses,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'bonusActions',
            rawText: a.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        }));
    }

    // Reactions
    // date_of_change: 2026-04-27
    // rule_reference: Reactions from structured JSON must include parserMeta.rawText for SmartPaste UI preview consistency.
    // date_of_change: 2026-05-04
    // rule_reference: Apply same filtering as actions to remove bogus entries
    if (Array.isArray(json.reactions)) {
      data.reactions = json.reactions
        .filter(a => !isDisqualifiedAction(a))
        .map(a => ({
          name: a.name,
          description: a.description,
          notes: a.notes,
          uses: a.uses,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'reactions',
            rawText: a.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        }));
    }

    // Spellcasting
    // date_of_change: 2026-02-12
    // rule_reference: D&D 5e (2024) spellcasting - handle camelCase/snake_case key variants and mine _pipeline_stages for richer spell data
    const structuredSpellcasting = (json.spellcasting && typeof json.spellcasting === 'object')
      ? json.spellcasting
      : {};
    const pipelineSpellStage = json._pipeline_stages?.['creator:_spellcasting'];
    const richSc = (pipelineSpellStage && typeof pipelineSpellStage === 'object')
      ? (
          pipelineSpellStage.spellcasting && typeof pipelineSpellStage.spellcasting === 'object'
            ? pipelineSpellStage.spellcasting
            : pipelineSpellStage
        )
      : null;
    const resolvedSpellcasting = (json.resolved_mechanics && typeof json.resolved_mechanics === 'object')
      ? json.resolved_mechanics
      : {};
    const resolvedRichSpellcasting = (richSc?.resolved_mechanics && typeof richSc.resolved_mechanics === 'object')
      ? richSc.resolved_mechanics
      : {};
    const pickDefined = (...values) => values.find((value) => value !== undefined && value !== null);
    const hasStructuredSpellData = Boolean(
      Object.keys(structuredSpellcasting).length > 0
      || richSc
      || pickDefined(
        json.spellcasting_ability,
        json.spellcastingAbility,
        json.spell_save_dc,
        json.spellSaveDC,
        json.spell_attack_bonus,
        json.spellAttackBonus,
        json.spell_slots,
        json.spellSlots,
        json.prepared_spells,
        json.preparedSpells,
        json.known_spells,
        json.knownSpells,
        json.spells_known,
        json.spellsKnown,
        json.cantrips,
        json.innate_spells,
        json.innateSpells,
        json.always_prepared_spells,
        json.alwaysPreparedSpells
      )
    );

    // date_of_change: 2026-03-19
    // rule_reference: Structured JSON imports may expose spellcasting as top-level keys or a direct creator:_spellcasting stage.
    // SmartPaste must normalize those authored fields into canonical spell/spellcasting data for preview, import, and sheet rendering.
    if (hasStructuredSpellData) {
      const sc = structuredSpellcasting;
      const toSpellNumber = (value) => {
        if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
        const parsed = parseInt(String(value ?? ''), 10);
        return Number.isNaN(parsed) ? undefined : parsed;
      };
      const normalizeSpellEntry = (spell, fallbackLevel = null, extra = {}) => {
        if (typeof spell === 'string') {
          return {
            name: spell,
            ...(fallbackLevel !== null ? { level: fallbackLevel } : {}),
            ...extra
          };
        }
        if (!spell || typeof spell !== 'object') return null;
        const name = spell.name || spell.title;
        if (!name) return null;
        return {
          ...spell,
          ...(fallbackLevel !== null && (spell.level === undefined || spell.level === null) ? { level: fallbackLevel } : {}),
          ...extra,
          name
        };
      };
      const normalizeSpellList = (spellList, fallbackLevel = null, extra = {}) => (
        (Array.isArray(spellList) ? spellList : [])
          .map((spell) => normalizeSpellEntry(spell, fallbackLevel, extra))
          .filter(Boolean)
      );

      const scSaveDC = pickDefined(
        sc.save_dc,
        sc.saveDc,
        sc.savedc,
        sc.spell_save_dc,
        sc.spellSaveDC,
        json.spell_save_dc,
        json.spellSaveDC,
        richSc?.save_dc,
        richSc?.saveDc,
        richSc?.spell_save_dc,
        richSc?.spellSaveDC,
        resolvedRichSpellcasting?.spell_save_dc,
        resolvedSpellcasting?.spell_save_dc
      );
      const scAttackBonus = pickDefined(
        sc.attack_bonus,
        sc.attackBonus,
        sc.spell_attack_bonus,
        sc.spellAttackBonus,
        json.spell_attack_bonus,
        json.spellAttackBonus,
        richSc?.attack_bonus,
        richSc?.attackBonus,
        richSc?.spell_attack_bonus,
        richSc?.spellAttackBonus,
        resolvedRichSpellcasting?.spell_attack_bonus,
        resolvedSpellcasting?.spell_attack_bonus
      );
      const scAbility = pickDefined(
        sc.ability,
        sc.spellcasting_ability,
        sc.spellcastingAbility,
        json.spellcasting_ability,
        json.spellcastingAbility,
        richSc?.ability,
        richSc?.spellcasting_ability,
        richSc?.spellcastingAbility,
        resolvedRichSpellcasting?.spellcasting_ability,
        resolvedSpellcasting?.spellcasting_ability
      );

      data.spellcasting = {
        ability: scAbility ? this.normalizeAbilityName(scAbility) : undefined,
        saveDC: toSpellNumber(scSaveDC),
        attackBonus: toSpellNumber(scAttackBonus),
        notes: pickDefined(sc.notes, richSc?.notes, json.spellcasting_notes, json.spellcastingNotes) ?? undefined
      };

      const spells = {
        ability: data.spellcasting.ability,
        saveDC: data.spellcasting.saveDC,
        attackBonus: data.spellcasting.attackBonus,
        slots: {},
        byLevel: {},
        prepared: [],
        known: [],
        atWill: [],
        innateByFrequency: {},
        alwaysPrepared: {}
      };

      const scCantrips = pickDefined(sc.cantrips, json.cantrips, richSc?.cantrips);
      if (Array.isArray(scCantrips)) {
        spells.byLevel[0] = normalizeSpellList(scCantrips, 0);
      }

      const scSlots = pickDefined(sc.spell_slots, sc.spellSlots, json.spell_slots, json.spellSlots, richSc?.spell_slots, richSc?.spellSlots);
      if (scSlots && typeof scSlots === 'object') {
        for (const [lvl, count] of Object.entries(scSlots)) {
          const levelNum = parseInt(String(lvl).replace(/\D/g, ''), 10);
          const slotCount = toSpellNumber(count);
          if (!Number.isNaN(levelNum) && levelNum > 0 && slotCount !== undefined) {
            spells.slots[levelNum] = { total: slotCount, used: 0 };
          }
        }
      }

      const scPrepared = pickDefined(
        sc.prepared_spells,
        sc.preparedSpells,
        json.prepared_spells,
        json.preparedSpells,
        richSc?.prepared_spells,
        richSc?.preparedSpells
      );
      if (Array.isArray(scPrepared)) {
        spells.prepared.push(...normalizeSpellList(scPrepared, null, { prepared: true }));
      } else if (scPrepared && typeof scPrepared === 'object') {
        for (const [lvl, spellList] of Object.entries(scPrepared)) {
          const levelNum = parseInt(String(lvl).replace(/\D/g, ''), 10);
          if (!Number.isNaN(levelNum) && Array.isArray(spellList)) {
            const normalized = normalizeSpellList(spellList, levelNum, { prepared: true });
            const existing = spells.byLevel[levelNum] || [];
            spells.byLevel[levelNum] = existing.concat(normalized);
            spells.prepared.push(...normalized);
          }
        }
      }

      const scKnown = pickDefined(
        sc.known_spells,
        sc.knownSpells,
        sc.spells_known,
        sc.spellsKnown,
        json.known_spells,
        json.knownSpells,
        json.spells_known,
        json.spellsKnown,
        richSc?.known_spells,
        richSc?.knownSpells,
        richSc?.spells_known,
        richSc?.spellsKnown
      );
      if (Array.isArray(scKnown) && scKnown.length > 0) {
        spells.known = normalizeSpellList(scKnown);
      } else if (scKnown && typeof scKnown === 'object') {
        for (const [lvl, spellList] of Object.entries(scKnown)) {
          const levelNum = parseInt(String(lvl).replace(/\D/g, ''), 10);
          if (!Number.isNaN(levelNum) && Array.isArray(spellList)) {
            const normalized = normalizeSpellList(spellList, levelNum);
            const existing = spells.byLevel[levelNum] || [];
            spells.byLevel[levelNum] = existing.concat(normalized);
            spells.known.push(...normalized);
          }
        }
      }

      const scAlwaysPrepared = pickDefined(
        sc.always_prepared_spells,
        sc.alwaysPreparedSpells,
        json.always_prepared_spells,
        json.alwaysPreparedSpells,
        richSc?.always_prepared_spells,
        richSc?.alwaysPreparedSpells
      );
      if (scAlwaysPrepared && typeof scAlwaysPrepared === 'object') {
        Object.entries(scAlwaysPrepared).forEach(([sourceKey, spellList]) => {
          if (!Array.isArray(spellList) || spellList.length === 0) return;
          spells.alwaysPrepared[sourceKey] = normalizeSpellList(
            spellList,
            null,
            { prepared: true, alwaysPrepared: true, preparationSource: sourceKey }
          );
          spells.prepared.push(...spells.alwaysPrepared[sourceKey]);
        });
      }

      const scInnate = pickDefined(
        sc.innate_spells,
        sc.innateSpells,
        json.innate_spells,
        json.innateSpells,
        richSc?.innate_spells,
        richSc?.innateSpells
      );
      if (scInnate && typeof scInnate === 'object') {
        for (const [key, spellList] of Object.entries(scInnate)) {
          if (!Array.isArray(spellList) || spellList.length === 0) continue;
          const normalized = normalizeSpellList(spellList, null, { innateFrequency: key });
          if (key.toLowerCase().trim().includes('will')) {
            spells.atWill.push(...normalized);
          } else {
            spells.innateByFrequency[key] = normalized;
          }
        }
      }

      data.spells = spells;
    }

    // ...
    // Equipment, magic items, and attunement
    // date_of_change: 2026-07-29
    // rule_reference: SmartPaste must retain the first-class inventory state used by the
    // character sheet instead of reducing rich item objects to name/description pairs.
    const equipmentSource = json.equipment ?? json.inventory;
    const mappedEquipment = {
      weapons: [],
      armor: [],
      items: [],
      valuables: [],
      currency: {}
    };
    const toEquipmentList = (value) => {
      if (Array.isArray(value)) return value;
      if (value === undefined || value === null) return [];
      if (typeof value === 'object') {
        const keys = Object.keys(value);
        if (keys.length > 0 && keys.every((key) => /^\d+$/.test(key))) {
          return Object.values(value);
        }
      }
      return [value];
    };
    const normalizeMappedEquipmentEntry = (value, defaults = {}) => {
      const sourceEntry = typeof value === 'string'
        ? { name: value }
        : (value && typeof value === 'object' && !Array.isArray(value) ? value : {});
      const normalized = normalizeEquipmentInventoryEntry({
        ...sourceEntry,
        type: sourceEntry.type
          || sourceEntry.kind
          || sourceEntry.category
          || sourceEntry.itemType
          || sourceEntry.item_type
          || defaults.type
          || 'item'
      }, defaults);
      const rawText = sourceEntry?.parserMeta?.rawText
        || normalized.name
        || String(value || '');

      return {
        ...normalized,
        parserMeta: this.mergeParserMeta(
          sourceEntry.parserMeta,
          this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'equipment',
            rawText,
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        )
      };
    };
    const getMappedEquipmentBucket = (entry, sourceBucket = '') => {
      if (sourceBucket === 'weapons') return 'weapons';
      if (sourceBucket === 'armor') return 'armor';
      if (sourceBucket === 'valuables') return 'valuables';
      if (entry.type === 'armor' || entry.type === 'shield') return 'armor';
      if (entry.type === 'valuable') return 'valuables';
      if (entry.type === 'weapon' && entry.equipped === true) return 'weapons';
      return 'items';
    };
    const appendMappedEquipmentEntry = (value, sourceBucket = '', defaults = {}) => {
      const normalized = normalizeMappedEquipmentEntry(value, defaults);
      if (!normalized.name) return;
      mappedEquipment[getMappedEquipmentBucket(normalized, sourceBucket)].push(normalized);
    };
    const findMappedEquipmentEntry = (name) => {
      const normalizedName = this.normalizeParsedEntryName(name || '');
      if (!normalizedName) return null;
      for (const bucket of ['weapons', 'armor', 'items', 'valuables']) {
        const index = mappedEquipment[bucket].findIndex((entry) => (
          this.normalizeParsedEntryName(entry?.name || '') === normalizedName
        ));
        if (index >= 0) return { bucket, index };
      }
      return null;
    };
    const upsertMappedEquipmentEntry = (value, defaults = {}) => {
      const sourceEntry = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
      const normalized = normalizeMappedEquipmentEntry(value, defaults);
      if (!normalized.name) return;
      const match = findMappedEquipmentEntry(normalized.name);
      if (!match) {
        mappedEquipment[getMappedEquipmentBucket(normalized)].push(normalized);
        return;
      }

      const existing = mappedEquipment[match.bucket][match.index];
      const hasOwn = (...keys) => keys.some((key) => Object.prototype.hasOwnProperty.call(sourceEntry, key));
      const merged = normalizeEquipmentInventoryEntry({
        ...existing,
        ...normalized,
        description: normalized.description || existing.description || '',
        type: hasOwn('type', 'kind', 'category', 'itemType', 'item_type')
          ? normalized.type
          : existing.type,
        quantity: hasOwn('quantity', 'qty', 'count')
          ? normalized.quantity
          : existing.quantity,
        weight: hasOwn('weight', 'weight_lbs', 'encumbrance')
          ? normalized.weight
          : existing.weight,
        rarity: normalized.rarity || existing.rarity || '',
        magical: existing.magical === true || normalized.magical === true,
        equipped: hasOwn('equipped', 'isEquipped', 'is_equipped', 'worn', 'wielded')
          ? normalized.equipped
          : existing.equipped,
        attunement: existing.requiresAttunement === true || normalized.requiresAttunement === true,
        requiresAttunement: existing.requiresAttunement === true || normalized.requiresAttunement === true,
        attuned: existing.attuned === true || normalized.attuned === true,
        parserMeta: this.mergeParserMeta(existing.parserMeta, normalized.parserMeta)
      }, defaults);
      const nextBucket = getMappedEquipmentBucket(merged);
      if (nextBucket === match.bucket) {
        mappedEquipment[match.bucket][match.index] = merged;
      } else {
        mappedEquipment[match.bucket].splice(match.index, 1);
        mappedEquipment[nextBucket].push(merged);
      }
    };

    // Exclude entries whose full text looks like narrative prose (biography / appearance sentences)
    const isNarrativeProseEntry = (value) => {
      const text = typeof value === 'string' ? value : (value?.name || value?.description || '');
      const trimmed = String(text).trim();
      // Long sentences with spaces that read like biography text are not equipment names
      return trimmed.length > 80 && trimmed.split(' ').length > 10 && !/\d+\s*(lb|lbs|kg|gp|sp|cp|pp)/i.test(trimmed);
    };

    if (equipmentSource && typeof equipmentSource === 'object' && !Array.isArray(equipmentSource)) {
      const hasCanonicalBuckets = ['weapons', 'armor', 'items', 'valuables', 'currency', 'other']
        .some((key) => equipmentSource[key] !== undefined);
      if (hasCanonicalBuckets) {
        toEquipmentList(equipmentSource.weapons)
          .filter((entry) => !isNarrativeProseEntry(entry))
          .forEach((entry) => appendMappedEquipmentEntry(entry, 'weapons', { type: 'weapon' }));
        toEquipmentList(equipmentSource.armor)
          .filter((entry) => !isNarrativeProseEntry(entry))
          .forEach((entry) => appendMappedEquipmentEntry(entry, 'armor', { type: 'armor' }));
        toEquipmentList(equipmentSource.items)
          .filter((entry) => !isNarrativeProseEntry(entry))
          .forEach((entry) => appendMappedEquipmentEntry(entry, 'items', { type: 'item' }));
        toEquipmentList(equipmentSource.valuables)
          .filter((entry) => !isNarrativeProseEntry(entry))
          .forEach((entry) => appendMappedEquipmentEntry(entry, 'valuables', { type: 'valuable' }));
        toEquipmentList(equipmentSource.other)
          .filter((entry) => !isNarrativeProseEntry(entry))
          .forEach((entry) => appendMappedEquipmentEntry(entry));
        mappedEquipment.currency = equipmentSource.currency && typeof equipmentSource.currency === 'object'
          ? { ...equipmentSource.currency }
          : {};
      } else {
        toEquipmentList(equipmentSource)
          .filter((entry) => !isNarrativeProseEntry(entry))
          .forEach((entry) => appendMappedEquipmentEntry(entry));
      }
    } else {
      toEquipmentList(equipmentSource)
        .filter((entry) => !isNarrativeProseEntry(entry))
        .forEach((entry) => appendMappedEquipmentEntry(entry));
    }

    // Magic Items
    const magicItemSource = json.magic_items ?? json.magicItems;
    toEquipmentList(magicItemSource).forEach((item) => {
      upsertMappedEquipmentEntry(item, {
        type: 'item',
        magical: true
      });
    });

    // Attuned Items
    const attunedItemSource = json.attuned_items ?? json.attunedItems;
    toEquipmentList(attunedItemSource).forEach((item) => {
      const sourceItem = typeof item === 'string'
        ? { name: item }
        : (item && typeof item === 'object' ? { ...item } : {});
      if (!sourceItem.name) {
        sourceItem.name = sourceItem.item_name || sourceItem.item_type;
      }
      if (
        !sourceItem.name
        && Array.isArray(sourceItem.properties)
        && typeof sourceItem.properties[0] === 'string'
        && sourceItem.properties[0].length < 40
        && !/stores|level|slot/i.test(sourceItem.properties[0])
      ) {
        sourceItem.name = sourceItem.properties[0];
      }
      upsertMappedEquipmentEntry({
        ...sourceItem,
        name: sourceItem.name || 'Magic Item'
      }, {
        type: 'item',
        magical: true,
        requiresAttunement: true,
        attuned: true
      });
    });

    if (
      mappedEquipment.weapons.length
      || mappedEquipment.armor.length
      || mappedEquipment.items.length
      || mappedEquipment.valuables.length
      || Object.keys(mappedEquipment.currency).length
    ) {
      data.equipment = mappedEquipment;
    }

    // Biography fields - handle both top-level and nested 'personality' object
    const biography = {};
    const biographyMeta = {};
    if (routedBackgroundNarrative) {
      biography.backstory = routedBackgroundNarrative;
      biographyMeta.backstory = this.buildParserMeta({
        sourceParser: 'mapJSONToCanonical',
        sourceSection: 'backstory',
        rawText: routedBackgroundNarrative,
        parseConfidence: 0.74,
        knowledgeSource: 'provided',
        knowledgeNote: 'Narrative JSON background value was routed to biography.backstory.'
      });
    }

    // Direct personality fields (PERSONALITY TRAITS, IDEALS, etc. in all caps or normal case)
    const personalityKeys = {
      'PERSONALITY TRAITS': 'personality',
      'personality_traits': 'personality',
      'IDEALS': 'ideals',
      'ideals': 'ideals',
      'BONDS': 'bonds',
      'bonds': 'bonds',
      'FLAWS': 'flaws',
      'flaws': 'flaws',
      'GOALS': 'goals',
      'goals': 'goals',
      'FEARS': 'fears',
      'fears': 'fears',
      'QUIRKS': 'quirks',
      'quirks': 'quirks',
      'VOICE/MANNERISMS': 'voiceMannerisms',
      'voice_mannerisms': 'voiceMannerisms'
    };

    for (const [jsonKey, bioKey] of Object.entries(personalityKeys)) {
      if (json[jsonKey]) {
        const val = json[jsonKey];
        biography[bioKey] = Array.isArray(val) ? val.join('\n') : val;
      }
    }

    // Nested personality object
    if (json.personality && typeof json.personality === 'object') {
      if (json.personality.traits) biography.personality = Array.isArray(json.personality.traits) ? json.personality.traits.join('\n') : json.personality.traits;
      if (json.personality.ideals) biography.ideals = Array.isArray(json.personality.ideals) ? json.personality.ideals.join('\n') : json.personality.ideals;
      if (json.personality.bonds) biography.bonds = Array.isArray(json.personality.bonds) ? json.personality.bonds.join('\n') : json.personality.bonds;
      if (json.personality.flaws) biography.flaws = Array.isArray(json.personality.flaws) ? json.personality.flaws.join('\n') : json.personality.flaws;
    }

    // Hooks (adventure hooks for NPCs)
    if (Array.isArray(json.hooks)) {
      biography.hooks = json.hooks.join('\n');
    }

    // Tactics
    if (json.tactics) {
      biography.tactics = json.tactics;
    }

    // Allies, Factions
    // date_of_change: 2025-12-19
    // rule_reference: D&D 5e character dossiers store relationships as readable narrative text
    if (Array.isArray(json.allies_friends)) {
      const alliesText = this.formatRelationshipEntries(json.allies_friends);
      if (alliesText) {
        data.allies = alliesText;
      }
    } else if (Array.isArray(json.allies)) {
      const alliesText = this.formatRelationshipEntries(json.allies);
      if (alliesText) {
        data.allies = alliesText;
      }
    } else if (typeof json.allies === 'string') {
      data.allies = json.allies;
    }

    if (Array.isArray(json.factions)) {
      const factionsText = this.formatRelationshipEntries(json.factions, { fieldPath: 'factions' });
      if (factionsText) {
        data.factions = factionsText;
      }
    } else if (typeof json.factions === 'string') {
      data.factions = json.factions;
    }

// ...
    // Location, Era
    if (json.location) data.location = json.location;
    if (json.era) data.era = json.era;

    if (Object.keys(biography).length) {
      data.biography = biography;
      if (Object.keys(biographyMeta).length) {
        data.biographyMeta = biographyMeta;
      }
    }

    // Title, Aliases, Role, Affiliation
    // date_of_change: 2026-02-12
    // rule_reference: NPCs/monsters often have titles and aliases that enrich the character dossier for GM reference
    if (json.title && json.title !== data.name) data.title = json.title;
    if (Array.isArray(json.aliases) && json.aliases.length > 0) data.aliases = json.aliases;
    if (json.role) data.role = json.role;
    if (json.affiliation) data.affiliation = json.affiliation;

    // Challenge Rating and XP
    // date_of_change: 2026-02-12
    // rule_reference: D&D 5e (2024) CR determines encounter difficulty and XP budget
    if (json.challenge_rating !== undefined) {
      data.challengeRating = typeof json.challenge_rating === 'number' ? json.challenge_rating : String(json.challenge_rating);
    }
    if (json.experience_points !== undefined) {
      data.xp = typeof json.experience_points === 'number' ? json.experience_points : parseInt(String(json.experience_points), 10);
    }

    // Legendary Actions
    // date_of_change: 2026-04-27
    // rule_reference: D&D 5e (2024) - Legendary actions are a core monster mechanic; preserve count, reset, and options.
    // Include parserMeta.rawText for SmartPaste UI preview consistency.
    const legendarySource = json.legendary_actions || (json._pipeline_stages && json._pipeline_stages['creator:_legendary'] && json._pipeline_stages['creator:_legendary'].legendary_actions);
    if (legendarySource && typeof legendarySource === 'object' && !Array.isArray(legendarySource)) {
      data.legendaryActions = {
        summary: legendarySource.summary || '',
        count: legendarySource.count != null ? legendarySource.count : 3,
        reset: legendarySource.reset || 'start of turn',
        options: Array.isArray(legendarySource.options) ? legendarySource.options.map(o => ({
          name: o.name,
          description: o.description,
          cost: o.cost != null ? o.cost : 1,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'legendaryActions',
            rawText: o.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        })) : []
      };
    }

    // Mythic Actions
    // date_of_change: 2026-04-27
    // rule_reference: D&D 5e mythic actions extend legendary creatures with a second phase triggered by a condition.
    // Include parserMeta.rawText for SmartPaste UI preview consistency.
    const mythicSource = json.mythic_actions || (json._pipeline_stages && json._pipeline_stages['creator:_legendary'] && json._pipeline_stages['creator:_legendary'].mythic_actions);
    if (mythicSource && typeof mythicSource === 'object' && !Array.isArray(mythicSource)) {
      data.mythicActions = {
        summary: mythicSource.summary || '',
        trigger: mythicSource.trigger || '',
        options: Array.isArray(mythicSource.options) ? mythicSource.options.map(o => ({
          name: o.name,
          description: o.description,
          uses: o.uses,
          notes: o.notes,
          parserMeta: this.buildParserMeta({
            sourceParser: 'mapJSONToCanonical',
            sourceSection: 'mythicActions',
            rawText: o.name || '',
            parseConfidence: 0.9,
            knowledgeSource: 'provided'
          })
        })) : []
      };
    }

    // Lair Actions
    // date_of_change: 2026-04-27
    // rule_reference: D&D 5e (2024) - Lair actions fire on initiative count 20 (losing ties).
    // Include parserMeta.rawText for SmartPaste UI preview consistency.
    const lairSource = json.lair_actions || (json._pipeline_stages && json._pipeline_stages['creator:_legendary'] && json._pipeline_stages['creator:_legendary'].lair_actions);
    if (Array.isArray(lairSource) && lairSource.length > 0) {
      data.lairActions = lairSource.map(la => {
        const name = typeof la === 'string' ? la : (la.action || la.name || '');
        const entry = typeof la === 'string'
          ? { action: la, initiative: 20, description: '' }
          : { initiative: la.initiative != null ? la.initiative : 20, action: la.action || la.name || '', description: la.description || '' };
        entry.parserMeta = this.buildParserMeta({
          sourceParser: 'mapJSONToCanonical',
          sourceSection: 'lairActions',
          rawText: name,
          parseConfidence: 0.9,
          knowledgeSource: 'provided'
        });
        return entry;
      });
    }

    // Regional Effects
    // date_of_change: 2026-04-27
    // rule_reference: D&D 5e (2024) - Regional effects describe the aura of a legendary creature's lair.
    // Include parserMeta.rawText for SmartPaste UI preview consistency.
    const regionalSource = json.regional_effects || (json._pipeline_stages && json._pipeline_stages['creator:_legendary'] && json._pipeline_stages['creator:_legendary'].regional_effects);
    if (Array.isArray(regionalSource) && regionalSource.length > 0) {
      data.regionalEffects = regionalSource.map(re => {
        const name = typeof re === 'string' ? re : (re.effect || re.name || '');
        const entry = typeof re === 'string'
          ? { effect: re, radius: '', description: '', duration: '' }
          : { radius: re.radius || '', effect: re.effect || re.name || '', description: re.description || '', duration: re.duration || '' };
        entry.parserMeta = this.buildParserMeta({
          sourceParser: 'mapJSONToCanonical',
          sourceSection: 'regionalEffects',
          rawText: name,
          parseConfidence: 0.9,
          knowledgeSource: 'provided'
        });
        return entry;
      });
    }

    // Minions
    // date_of_change: 2026-02-12
    // rule_reference: NPCs may command minions; preserve for GM reference and encounter planning
    if (Array.isArray(json.minions) && json.minions.length > 0) {
      data.minions = json.minions.map(function(m) {
        if (typeof m === 'string') return { name: m, type: '', quantity: 1, loyalty: '', notes: '' };
        return { name: m.name || 'Unknown', type: m.type || '', quantity: m.quantity != null ? m.quantity : 1, loyalty: m.loyalty || '', notes: m.notes || '' };
      });
    }

    // Multiclass Features
    // date_of_change: 2026-02-12
    // rule_reference: D&D 5e (2024) - Multiclass features arise from combinations of class levels
    if (Array.isArray(json.multiclass_features) && json.multiclass_features.length > 0) {
      data.multiclassFeatures = json.multiclass_features.map(function(f) {
        return { name: f.name, description: f.description, sourceClasses: f.source_classes, notes: f.notes };
      });
    }

    // Comprehensive Notes Catch-All
    // date_of_change: 2026-02-12
    // rule_reference: Gather all parsed-but-unplaced data into a single notes field for GM reference; nothing should be silently dropped
    const notesParts = [];

    if (data.title) notesParts.push('TITLE: ' + data.title);
    if (data.aliases && data.aliases.length > 0) notesParts.push('ALIASES: ' + data.aliases.join(', '));
    if (data.role) notesParts.push('ROLE: ' + data.role);
    if (data.affiliation) notesParts.push('AFFILIATION: ' + data.affiliation);
    if (data.challengeRating !== undefined) {
      notesParts.push('CHALLENGE RATING: ' + data.challengeRating + (data.xp ? ' (' + Number(data.xp).toLocaleString() + ' XP)' : ''));
    }

    if (data.legendaryActions && (data.legendaryActions.summary || data.legendaryActions.options.length > 0)) {
      notesParts.push('\n--- LEGENDARY ACTIONS ---');
      if (data.legendaryActions.summary) notesParts.push(data.legendaryActions.summary);
      notesParts.push('Uses: ' + data.legendaryActions.count + '/round, resets ' + data.legendaryActions.reset);
      for (var li = 0; li < data.legendaryActions.options.length; li++) {
        var opt = data.legendaryActions.options[li];
        notesParts.push('  ' + opt.name + ' (Cost ' + opt.cost + '): ' + opt.description);
      }
    }

    if (data.mythicActions && (data.mythicActions.summary || data.mythicActions.options.length > 0)) {
      notesParts.push('\n--- MYTHIC ACTIONS ---');
      if (data.mythicActions.summary) notesParts.push(data.mythicActions.summary);
      if (data.mythicActions.trigger) notesParts.push('Trigger: ' + data.mythicActions.trigger);
      for (var mi = 0; mi < data.mythicActions.options.length; mi++) {
        var mopt = data.mythicActions.options[mi];
        notesParts.push('  ' + mopt.name + ': ' + mopt.description + (mopt.uses ? ' (' + mopt.uses + ')' : ''));
      }
    }

    if (data.lairActions && data.lairActions.length > 0) {
      notesParts.push('\n--- LAIR ACTIONS ---');
      for (var lai = 0; lai < data.lairActions.length; lai++) {
        var la = data.lairActions[lai];
        notesParts.push('  [Init ' + la.initiative + '] ' + la.action + (la.description ? ': ' + la.description : ''));
      }
    }

    if (data.regionalEffects && data.regionalEffects.length > 0) {
      notesParts.push('\n--- REGIONAL EFFECTS ---');
      for (var rei = 0; rei < data.regionalEffects.length; rei++) {
        var re = data.regionalEffects[rei];
        notesParts.push('  ' + re.effect + (re.radius ? ' (' + re.radius + ')' : '') + (re.description ? ': ' + re.description : '') + (re.duration ? ' [' + re.duration + ']' : ''));
      }
    }

    if (data.minions && data.minions.length > 0) {
      notesParts.push('\n--- MINIONS ---');
      for (var mni = 0; mni < data.minions.length; mni++) {
        var mn = data.minions[mni];
        notesParts.push('  ' + mn.name + (mn.type ? ' (' + mn.type + ')' : '') + (mn.quantity > 1 ? ' x' + mn.quantity : '') + (mn.notes ? ': ' + mn.notes : ''));
      }
    }

    if (data.multiclassFeatures && data.multiclassFeatures.length > 0) {
      notesParts.push('\n--- MULTICLASS FEATURES ---');
      for (var mfi = 0; mfi < data.multiclassFeatures.length; mfi++) {
        var mf = data.multiclassFeatures[mfi];
        notesParts.push('  ' + mf.name + ': ' + (mf.description || '') + (mf.notes ? ' (' + mf.notes + ')' : ''));
      }
    }

    if (data.spells && data.spells.innateByFrequency && Object.keys(data.spells.innateByFrequency).length > 0) {
      notesParts.push('\n--- INNATE SPELLCASTING (LIMITED USE) ---');
      var innateFreqs = Object.entries(data.spells.innateByFrequency);
      for (var ifi = 0; ifi < innateFreqs.length; ifi++) {
        notesParts.push('  ' + innateFreqs[ifi][0] + ': ' + innateFreqs[ifi][1].map(function(s) { return s.name; }).join(', '));
      }
    }

    if (data.spellcasting && data.spellcasting.notes) {
      notesParts.push('\n--- SPELLCASTING NOTES ---\n' + data.spellcasting.notes);
    }

    if (Array.isArray(json.assumptions) && json.assumptions.length > 0) {
      notesParts.push('\n--- SOURCE ASSUMPTIONS ---');
      notesParts.push(json.assumptions.join('\n'));
    }

    if (notesParts.length > 0) {
      data.notes = notesParts.join('\n');
    }

    return data;
  }

  /**
   * Format an array of relationship-style entries into human-readable narrative text.
   *
   * date_of_change: 2025-12-24
   * rule_reference: D&D 5e (2014/2024) - relationships and affiliations are narrative dossier fields for GMs/players; display in UI as readable text.
   *
   * This is intentionally strict: if entries contain unsupported shapes, throw an error
   * so SmartPaste can surface a remediation message (no silent fallbacks).
   *
   * @param {Array<string|object>} entries
   * @param {object} [options]
   * @param {string} [options.fieldPath]
   * @returns {string}
   */
  static formatRelationshipEntries(entries, options = {}) {
    const fieldPath = options.fieldPath || 'relationships';

    if (!Array.isArray(entries)) {
      throw new Error(`Invalid ${fieldPath}: expected an array, got ${typeof entries}`);
    }

    const lines = [];
    for (let i = 0; i < entries.length; i++) {
      const entry = entries[i];
      const entryPath = `${fieldPath}[${i}]`;

      if (typeof entry === 'string') {
        const trimmed = entry.trim();
        if (trimmed) lines.push(trimmed);
        continue;
      }

      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
        throw new Error(
          `Invalid ${entryPath}: expected a string or object { name, ... }, got ${Array.isArray(entry) ? 'array' : typeof entry}`
        );
      }

      const name = typeof entry.name === 'string' ? entry.name.trim() : '';
      if (!name) {
        throw new Error(
          `Invalid ${entryPath}.name: expected non-empty string 'name' (e.g., { name: "King Derid Kendrick", ... })`
        );
      }

      const suffixParts = [];
      const addIfString = (key) => {
        if (entry[key] === undefined || entry[key] === null) return;
        if (typeof entry[key] !== 'string') {
          throw new Error(`Invalid ${entryPath}.${key}: expected string, got ${typeof entry[key]}`);
        }
        const trimmed = entry[key].trim();
        if (trimmed) suffixParts.push(trimmed);
      };

      addIfString('relationship');
      addIfString('role');
      addIfString('type');
      addIfString('standing');
      addIfString('location');

      let line = name;
      if (suffixParts.length > 0) {
        line += ` (${suffixParts.join(' | ')})`;
      }

      if (entry.notes !== undefined && entry.notes !== null) {
        if (typeof entry.notes !== 'string') {
          throw new Error(`Invalid ${entryPath}.notes: expected string, got ${typeof entry.notes}`);
        }
        const notesTrimmed = entry.notes.trim();
        if (notesTrimmed) {
          line += ` — ${notesTrimmed}`;
        }
      }

      lines.push(line);
    }

    return lines.join('\n');
  }

  /**
   * Parse multiclass text format like "Fighter 5 / Druid 3" or "Paladin 6, Warlock 2"
   * @date_of_change 2025-12-16 - Support text-based multiclass detection
   * @param {string} text - Text to parse
   * @returns {object|null} - { classes: [{name, level}], totalLevel } or null
   */
  static parseMulticlassText(text) {
    if (!text) return null;

    // date_of_change: 2026-01-03
    // rule_reference: SmartPaste must preserve multiclass strings from varied sources (including homebrew class names).
    const parts = String(text)
      .split(/\s*[/,]\s*/g)
      .map(p => p.trim())
      .filter(Boolean);

    const classes = [];
    let primarySubclass = null;

    for (const part of parts) {
      const m = part.match(/^([A-Za-z][A-Za-z\s'-]*?)(?:\s*\(([^)]+)\))?\s+(\d{1,2})\b/);
      if (!m) continue;
      const name = m[1].trim();
      const subclass = m[2] ? m[2].trim() : null;
      const level = parseInt(m[3], 10);
      if (!Number.isFinite(level) || level <= 0 || level > 20) continue;

      if (classes.length === 0 && subclass) {
        primarySubclass = subclass;
      }

      classes.push({ name, level, ...(subclass ? { subclass } : {}) });
    }

    if (classes.length === 0) return null;

    return {
      classes,
      totalLevel: classes.reduce((sum, c) => sum + c.level, 0),
      primaryClass: classes[0].name,
      ...(primarySubclass ? { primarySubclass } : {})
    };
  }

  /**
   * Determine whether a spell deals damage.
   *
   * date_of_change: 2026-01-04
   * rule_reference: CharacterSheetEditor renders a "Weapons"-style table that includes cantrips; only damage cantrips belong in that table.
   *
   * This helper is intentionally conservative and MUST NOT throw.
   * If we cannot confidently detect damage from structured fields or text, return false.
   *
   * @param {object|string} spell
   * @returns {boolean}
   */
  static isSpellDamaging(spell) {
    try {
      if (!spell) return false;

      const s = (typeof spell === 'string') ? { name: spell } : spell;
      if (!s || typeof s !== 'object') return false;

      // Structured spell data: FullSpellData / SpellRules / RefEntry.rules
      if (s.damage) return true;
      if (s.rules && typeof s.rules === 'object' && s.rules) {
        if (Array.isArray(s.rules.damage) && s.rules.damage.length > 0) return true;
      }

      const text = [s.description, s.desc, s.text]
        .filter(v => typeof v === 'string' && v.trim())
        .join('\n');

      if (!text) return false;
      const lower = text.toLowerCase();

      // Require BOTH an explicit damage keyword and a dice expression to avoid false positives (e.g., Guidance 1d4).
      if (!/\bdamage\b/.test(lower)) return false;
      if (!/\b\d{1,2}d\d{1,3}\b/.test(lower)) return false;

      // Avoid obvious negations.
      if (/\b(no damage|does not deal damage|without dealing damage)\b/.test(lower)) return false;

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Normalize ability scores from various formats to canonical {strength, dexterity, ...}
   * @date_of_change 2025-12-16 - Added to support JSON ability score parsing
   */
  static normalizeAbilityScores(scores) {
    if (!scores || typeof scores !== 'object') return null;

    const normalized = {};
    const mapping = {
      str: 'strength',
      dex: 'dexterity',
      con: 'constitution',
      int: 'intelligence',
      wis: 'wisdom',
      cha: 'charisma',
      strength: 'strength',
      dexterity: 'dexterity',
      constitution: 'constitution',
      intelligence: 'intelligence',
      wisdom: 'wisdom',
      charisma: 'charisma'
    };

    for (const [key, val] of Object.entries(scores)) {
      const normalKey = mapping[key.toLowerCase()];
      if (normalKey && typeof val === 'number') {
        normalized[normalKey] = val;
      }
    }

    // Only return if we have at least 3 ability scores
    return Object.keys(normalized).length >= 3 ? normalized : null;
  }

  /**
   * Normalize ability shorthand into canonical ability keys
   * @date_of_change 2026-02-25
   * rule_reference: D&D 5e ability names; used across SmartPaste parsing and structured JSON mapping.
   */
  static normalizeAbilityName(ability) {
    if (!ability) return '';
    const mapping = {
      str: 'strength',
      dex: 'dexterity',
      con: 'constitution',
      int: 'intelligence',
      wis: 'wisdom',
      cha: 'charisma'
    };
    const lower = String(ability).toLowerCase();
    return mapping[lower] || lower;
  }

  /**
   * Categorize features into feats, class features, and racial/monster traits
   * @date_of_change 2025-12-16 - Added to separate feats from class features from traits
   * 
   * Categories:
   * - feats: Character choices (Magic Initiate, War Caster, Telekinetic, etc.)
   * - classFeatures: Features from class levels (Extra Attack, Arcane Jolt, etc.)
   * - traits: Racial/species/monster traits (Regeneration, Spider Climb, Darkvision, etc.)
   */
  // date_of_change: 2026-03-11
  // rule_reference: SmartPaste ownership and deduplication must compare feature/action names conservatively across buckets.
  static normalizeParsedEntryName(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[\s\p{P}\p{S}]+/gu, ' ')
      .trim();
  }

  // date_of_change: 2026-09-06
  // rule_reference: SmartPaste may ingest flattened website text, but repeated character-sheet
  // paths are metadata, not a discrete D&D action even when the dump also contains attack numbers.
  static isCharacterSheetMetadataDump(entryOrText) {
    const source = typeof entryOrText === 'string'
      ? entryOrText
      : [
          entryOrText?.name,
          entryOrText?.description,
          entryOrText?.desc,
          entryOrText?.text,
          entryOrText?.notes,
          entryOrText?.rawLine,
          entryOrText?.parserMeta?.rawText
        ].filter(Boolean).join(' ');
    const normalized = String(source || '').replace(/\s+/g, ' ').trim();
    if (normalized.length < 180) return false;

    const skillPathCount = (normalized.match(
      /\bskills?\s+[a-z][a-z'’ -]{1,35}\s+(?:ability|modifier|proficient|expertise)\s*:/gi
    ) || []).length;
    const proficiencyPathCount = (normalized.match(
      /\bproficiencies?\s+(?:saving\s+throws?|skills?|armor|weapons?|tools?|languages?)\s*:/gi
    ) || []).length;
    const classPathCount = (normalized.match(
      /\bclasses?\s+[a-z][a-z'’ -]{1,35}\s+(?:hit\s+dice(?:\s+spent)?|primary\s+order|subclass|level)\s*:/gi
    ) || []).length;
    const sheetLabelCount = (normalized.match(
      /\b(?:ability|modifier|proficient|expertise|hit\s+dice(?:\s+spent)?|primary\s+order|saving\s+throws?|armor|weapons?|tools?|languages?)\s*:/gi
    ) || []).length;
    const sectionFamilyCount = [
      skillPathCount > 0,
      proficiencyPathCount > 0,
      classPathCount > 0
    ].filter(Boolean).length;

    return skillPathCount >= 3
      || proficiencyPathCount >= 3
      || (sheetLabelCount >= 8 && sectionFamilyCount >= 2);
  }

  // date_of_change: 2026-03-11
  // rule_reference: SmartPaste imports need a stable identity that survives full imports, granular review imports,
  // and repeated pastes of the same homebrew content.
  static buildParsedEntryFingerprint(entry, kind = 'entry') {
    const sourceKind = this.normalizeParsedEntryName(
      kind || entry?.sourceSection || entry?.parserMeta?.sourceSection || entry?.type || 'entry'
    );
    const type = this.normalizeParsedEntryName(entry?.type || '');
    const name = this.normalizeParsedEntryName(entry?.name || entry?.header || entry?.title || '');
    const description = this.normalizeParsedEntryName(
      entry?.description || entry?.content || entry?.statLine || entry?.rawLine || ''
    );
    const quantity = Number.isFinite(Number(entry?.quantity)) ? `qty:${Number(entry.quantity)}` : '';
    const fallback = description ? description.slice(0, 120) : '';

    return [sourceKind || 'entry', type, name || fallback, description.slice(0, 180), quantity]
      .filter(Boolean)
      .join('|');
  }

  // date_of_change: 2026-03-11
  // rule_reference: SmartPaste review needs stable provenance metadata so humans can understand where
  // a parsed or uncertain entry came from before approving an import.
  static buildParserMeta(meta = {}) {
    const cleaned = {
      sourceParser: meta.sourceParser || undefined,
      sourceSection: meta.sourceSection || undefined,
      lineStart: Number.isInteger(meta.lineStart) ? meta.lineStart : undefined,
      lineEnd: Number.isInteger(meta.lineEnd) ? meta.lineEnd : undefined,
      parseConfidence: typeof meta.parseConfidence === 'number' ? meta.parseConfidence : undefined,
      rawText: meta.rawText || undefined,
      reviewReason: meta.reviewReason || undefined,
      knowledgeSource: meta.knowledgeSource || undefined,
      knowledgeNote: meta.knowledgeNote || undefined,
      knowledgeConfirmed: meta.knowledgeConfirmed === true ? true : undefined,
      knowledgeConfirmationSource: meta.knowledgeConfirmationSource || undefined,
      knowledgeConfirmedAt: meta.knowledgeConfirmedAt || undefined
    };

    return Object.fromEntries(Object.entries(cleaned).filter(([, value]) => value !== undefined));
  }

  static mergeParserMeta(...sources) {
    const merged = sources.reduce((acc, source) => {
      if (!source || typeof source !== 'object') return acc;
      return { ...acc, ...source };
    }, {});

    return this.buildParserMeta(merged);
  }

  static classifyBackgroundValue(rawValue) {
    const value = typeof rawValue === 'string' ? rawValue.trim().replace(/\s+/g, ' ') : '';
    if (!value) {
      return { background: '', backstory: '' };
    }

    const wordCount = value.split(/\s+/).filter(Boolean).length;
    const looksLikeSentence = /[.!?]/.test(value);
    const looksClauseHeavy = /[,;:]/.test(value) && wordCount > 5;
    const looksNarrativeStart = /^(?:once|when|after|before|during|while|born|raised|framed|haunted|trained|served|grew|escaped|captured|rescued|banished|exiled|orphaned|seeking|seeks|as\s+a|as\s+an|in\s+the|from\s+the)\b/i.test(value);
    const hasNarrativeVerb = /\b(?:was|were|had|lost|found|rescued|captured|framed|served|trained|grew|seeks?|hunted|fled|escaped|survived|worked|promising)\b/i.test(value);
    const looksTitleLike = (
      value.length <= 40
      && wordCount <= 5
      && /^[A-Za-z0-9'’&()/ -]+$/.test(value)
      && !looksLikeSentence
      && !/[,;:]/.test(value)
    );

    const looksNarrative = !looksTitleLike && (
      value.length > 55
      || wordCount > 7
      || looksLikeSentence
      || looksClauseHeavy
      || looksNarrativeStart
      || (hasNarrativeVerb && wordCount > 5)
    );

    if (looksNarrative) {
      return { background: '', backstory: value };
    }

    return { background: value, backstory: '' };
  }

  // date_of_change: 2026-03-11
  // rule_reference: D&D 5e action economy should be derived from feature metadata when no explicit action section exists.
  static inferActivationTypeFromEntry(entry) {
    const name = String(entry?.name || '').trim();
    const description = String(entry?.description || '').trim();
    const sourceSection = String(entry?.sourceSection || '').trim().toLowerCase();
    const combined = `${name} ${description}`.toLowerCase();

    if (sourceSection === 'actions') return 'action';
    if (sourceSection === 'bonusActions') return 'bonus action';
    if (sourceSection === 'reactions') return 'reaction';

    const knownActions = /^(action surge|attack action|extra attack|multiattack)$/i;
    const knownBonusActions = /^(second wind|giant['’]s might|large form|cunning action|flurry of blows|patient defense|step of the wind|ki-fueled attack|quickened healing|bardic inspiration|healing word|hill rune)$/i;
    const knownReactions = /^(opportunity attack|shield|counterspell|absorb elements|hellish rebuke|feather fall|uncanny dodge|deflect missiles|slow fall|riposte|parry|brace|interception|protection|stone['’]s endurance|runic shield)$/i;

    if (knownBonusActions.test(name) || /\b(bonus action|use a bonus action|as a bonus action)\b/i.test(combined)) return 'bonus action';
    if (knownReactions.test(name) || /\b(use your reaction|as a reaction|use a reaction|when.*you can use your reaction|opportunity attack)\b/i.test(combined)) return 'reaction';
    if (knownActions.test(name) || /\b(use an action|take an action|action to|as an action|attack action|main action)\b/i.test(combined)) return 'action';
    return 'passive';
  }

  static buildSpellEntriesFromSpellData(spells = {}) {
    if (!spells || typeof spells !== 'object') return [];

    const entries = [];
    const seenKeys = new Set();
    const pushSpellEntry = (spell, { level = null, listType = 'known', sourceParser = 'parseSpellBlocks' } = {}) => {
      const baseEntry = typeof spell === 'string'
        ? { name: spell }
        : (spell && typeof spell === 'object' ? spell : null);
      if (!baseEntry?.name) return;

      const normalizedName = this.normalizeParsedEntryName(baseEntry.name);
      if (!normalizedName) return;

      const dedupeKey = [normalizedName, Number.isFinite(level) ? `level:${level}` : '', listType].filter(Boolean).join('|');
      if (seenKeys.has(dedupeKey)) return;
      seenKeys.add(dedupeKey);

      entries.push({
        ...baseEntry,
        level: Number.isFinite(level) ? level : (Number.isFinite(baseEntry.level) ? baseEntry.level : undefined),
        type: 'spell',
        listType,
        sourceSection: 'spells',
        parserMeta: this.mergeParserMeta(
          spells?.parserMeta,
          baseEntry?.parserMeta,
          {
            sourceParser,
            sourceSection: 'spells',
            knowledgeSource: baseEntry?.parserMeta?.knowledgeSource || spells?.parserMeta?.knowledgeSource || 'provided'
          }
        )
      });
    };

    Object.entries(spells.byLevel || {}).forEach(([levelKey, spellEntries]) => {
      const level = parseInt(levelKey, 10);
      (Array.isArray(spellEntries) ? spellEntries : []).forEach((spell) => {
        pushSpellEntry(spell, {
          level: Number.isFinite(level) ? level : null,
          listType: level === 0 ? 'cantrip' : 'byLevel'
        });
      });
    });
    (Array.isArray(spells.atWill) ? spells.atWill : []).forEach((spell) => pushSpellEntry(spell, { listType: 'atWill' }));
    (Array.isArray(spells.prepared) ? spells.prepared : []).forEach((spell) => pushSpellEntry(spell, { listType: 'prepared' }));
    (Array.isArray(spells.known) ? spells.known : []).forEach((spell) => pushSpellEntry(spell, { listType: 'known' }));
    (Array.isArray(spells.spells) ? spells.spells : []).forEach((spell) => pushSpellEntry(spell, {
      listType: spell?.listType || 'spellList',
      level: Number.isFinite(spell?.level) ? spell.level : null,
      sourceParser: spell?.parserMeta?.sourceParser || 'structuredSpellData'
    }));

    return entries;
  }

  static normalizeCombatActionEntry(entry, options = {}) {
    if (!entry || typeof entry !== 'object') return entry;

    const toFiniteNumber = (value) => {
      if (typeof value === 'number') return Number.isFinite(value) ? value : null;
      if (typeof value === 'string') {
        const match = value.match(/[+-]?\d+/);
        if (!match) return null;
        const parsed = Number(match[0]);
        return Number.isFinite(parsed) ? parsed : null;
      }
      return null;
    };
    const normalizeFormula = (value) => String(value || '').replace(/\s+/g, '');
    const normalizeRecharge = (value) => {
      const normalized = String(value || '').trim().toLowerCase();
      if (!normalized) return undefined;
      if (normalized.includes('short')) return 'shortRest';
      if (normalized.includes('long')) return 'longRest';
      if (normalized.includes('turn')) return 'turn';
      return normalized;
    };
    const combinedText = [
      entry?.statLine,
      entry?.description,
      entry?.content,
      entry?.parserMeta?.rawText
    ].filter(Boolean).join('\n');
    const addDamageComponent = (components, seen, formula, type = 'untyped', extra = {}) => {
      const normalizedFormula = normalizeFormula(formula);
      if (!normalizedFormula) return;
      const normalizedType = String(type || 'untyped').trim().toLowerCase() || 'untyped';
      const key = `${normalizedFormula}|${normalizedType}`;
      if (seen.has(key)) return;
      seen.add(key);
      components.push({
        dice: normalizedFormula,
        formula: normalizedFormula,
        type: normalizedType,
        ...extra
      });
    };
    const parseDamageComponents = () => {
      const components = [];
      const seen = new Set();
      const structuredDamage = Array.isArray(entry?.damage)
        ? entry.damage
        : (Array.isArray(entry?.attack?.damage) ? entry.attack.damage : []);
      structuredDamage.forEach((damageEntry) => {
        if (!damageEntry) return;
        if (typeof damageEntry === 'string') {
          addDamageComponent(components, seen, damageEntry, entry?.damageType || entry?.type || 'untyped');
          return;
        }
        addDamageComponent(
          components,
          seen,
          damageEntry?.dice || damageEntry?.formula || damageEntry?.amount,
          damageEntry?.type || damageEntry?.damageType || entry?.damageType || 'untyped',
          damageEntry?.bonus !== undefined ? { bonus: damageEntry.bonus } : {}
        );
      });

      if (typeof entry?.damage === 'string' && entry.damage.trim()) {
        addDamageComponent(components, seen, entry.damage, entry?.damageType || 'untyped');
      }

      const typedDamageRe = /(?:\(\s*(\d+d\d+(?:\s*[+-]\s*\d+)?)\s*\)|(\d+d\d+(?:\s*[+-]\s*\d+)?))\s*([a-z]+)?\s*(damage|healing)\b/gi;
      let damageMatch;
      while ((damageMatch = typedDamageRe.exec(combinedText)) !== null) {
        const formula = damageMatch[1] || damageMatch[2];
        const type = damageMatch[4].toLowerCase() === 'healing'
          ? 'healing'
          : (damageMatch[3] || 'untyped');
        addDamageComponent(components, seen, formula, type);
      }

      const shorthandDamageRe = /(?:^|[,(;]\s*)(\d+d\d+(?:\s*[+-]\s*\d+)?)\s*(slashing|piercing|bludgeoning|fire|cold|lightning|thunder|acid|poison|necrotic|radiant|force|psychic)\b/gi;
      let shorthandMatch;
      while ((shorthandMatch = shorthandDamageRe.exec(combinedText)) !== null) {
        addDamageComponent(components, seen, shorthandMatch[1], shorthandMatch[2]);
      }

      const regainHpRe = /\b(?:regain|restore|recover|heal)\s+(\d+d\d+(?:\s*[+-]\s*\d+)?)\s*(?:hit points?|hp)\b/gi;
      let regainMatch;
      while ((regainMatch = regainHpRe.exec(combinedText)) !== null) {
        addDamageComponent(components, seen, regainMatch[1], 'healing');
      }

      return components;
    };
    const parseSave = () => {
      const explicitAbility = this.normalizeAbilityName(entry?.save?.ability);
      const explicitDc = toFiniteNumber(entry?.save?.dc ?? entry?.saveDC ?? entry?.saveDc);
      if (explicitAbility && explicitDc !== null) {
        return { ability: explicitAbility, dc: explicitDc };
      }

      const dcFirstMatch = combinedText.match(/\bDC\s*(\d+)\s*([A-Za-z]+)\s*saving throw\b/i);
      if (dcFirstMatch) {
        const dc = toFiniteNumber(dcFirstMatch[1]);
        const ability = this.normalizeAbilityName(dcFirstMatch[2]);
        if (ability && dc !== null) return { ability, dc };
      }

      const abilityFirstMatch = combinedText.match(/\b([A-Za-z]+)\s*saving throw\s*\(DC\s*(\d+)\)\b/i);
      if (abilityFirstMatch) {
        const dc = toFiniteNumber(abilityFirstMatch[2]);
        const ability = this.normalizeAbilityName(abilityFirstMatch[1]);
        if (ability && dc !== null) return { ability, dc };
      }

      const mustSucceedMatch = combinedText.match(/\bmust succeed on a DC\s*(\d+)\s*([A-Za-z]+)\s*saving throw\b/i);
      if (mustSucceedMatch) {
        const dc = toFiniteNumber(mustSucceedMatch[1]);
        const ability = this.normalizeAbilityName(mustSucceedMatch[2]);
        if (ability && dc !== null) return { ability, dc };
      }

      return null;
    };
    const parseRange = () => {
      const explicitRange = entry?.range;
      if (explicitRange && typeof explicitRange === 'object' && explicitRange.type) {
        return {
          range: explicitRange,
          attackRange: entry?.attack?.range,
          reach: toFiniteNumber(entry?.reach ?? entry?.attack?.reach)
        };
      }

      if (/\bself\b/i.test(combinedText)) return { range: { type: 'self' } };
      if (/\btouch\b/i.test(combinedText)) return { range: { type: 'touch' } };

      const rangeMatch = combinedText.match(/\brange\s+(\d+)(?:\s*\/\s*(\d+))?\s*ft\b/i);
      if (rangeMatch) {
        const normal = toFiniteNumber(rangeMatch[1]);
        const long = toFiniteNumber(rangeMatch[2]);
        return {
          range: normal !== null
            ? { type: 'range', value: normal, units: 'feet' }
            : { type: 'special' },
          attackRange: normal !== null
            ? { normal, ...(long !== null ? { long } : {}) }
            : undefined
        };
      }

      const reachMatch = combinedText.match(/\breach\s+(\d+)\s*ft\b/i);
      if (reachMatch) {
        const reach = toFiniteNumber(reachMatch[1]);
        return reach !== null
          ? { range: { type: 'range', value: reach, units: 'feet' }, reach }
          : null;
      }

      const withinMatch = combinedText.match(/\bwithin\s+(\d+)\s*(?:feet|ft)\b/i);
      if (withinMatch) {
        const distance = toFiniteNumber(withinMatch[1]);
        return distance !== null
          ? { range: { type: 'special', value: distance, units: 'feet' } }
          : null;
      }

      if (/\b(?:cone|radius|sphere|cube|line)\b/i.test(combinedText)) {
        return { range: { type: 'special' } };
      }

      return null;
    };
    const parseUses = () => {
      if (entry?.uses && typeof entry.uses === 'object') return entry.uses;

      const usageText = [entry?.usage, combinedText].filter(Boolean).join(' ');
      const explicitUseMatch = usageText.match(/\b(\d+)\s*\/\s*(short|long)\s*rest\b/i);
      if (explicitUseMatch) {
        const max = toFiniteNumber(explicitUseMatch[1]);
        return max !== null
          ? { value: max, max, recharge: normalizeRecharge(explicitUseMatch[2]) }
          : null;
      }

      const oncePerRestMatch = usageText.match(/\bonce per (short|long) rest\b/i);
      if (oncePerRestMatch) {
        return { value: 1, max: 1, recharge: normalizeRecharge(oncePerRestMatch[1]) };
      }

      return null;
    };

    const structuredAttackBonus = [
      entry?.attackBonus,
      entry?.toHit,
      entry?.attack?.toHit,
      entry?.attack?.bonus
    ].map(toFiniteNumber).find((value) => value !== null);
    const parsedAttackBonus = structuredAttackBonus !== undefined
      ? structuredAttackBonus
      : toFiniteNumber(combinedText.match(/([+-]\d+)\s+to\s+hit\b/i)?.[1]);
    const attackHeaderMatch = combinedText.match(/\b(Melee|Ranged)\s+(Weapon|Spell)\s+Attack\b/i);
    const attackType = (() => {
      if (typeof entry?.attackType === 'string' && entry.attackType.trim()) return entry.attackType;
      if (typeof entry?.attack?.type === 'string' && entry.attack.type.trim()) return entry.attack.type;
      if (attackHeaderMatch) {
        return `${attackHeaderMatch[1].toLowerCase()} ${attackHeaderMatch[2].toLowerCase()} attack`;
      }
      return null;
    })();
    const save = parseSave();
    const damageComponents = parseDamageComponents();
    const usage = parseUses();
    const rangeInfo = parseRange();
    const primaryDamage = damageComponents[0] || null;
    const inferredCombatType = (() => {
      if (typeof entry?.type === 'string' && ['attack', 'spell', 'ability', 'movement'].includes(entry.type)) {
        return entry.type;
      }
      if (attackHeaderMatch?.[2]) {
        return attackHeaderMatch[2].toLowerCase() === 'spell' ? 'spell' : 'attack';
      }
      if (save || damageComponents.length > 0 || options?.kind === 'bonusActions' || options?.kind === 'reactions') {
        return 'ability';
      }
      return entry?.type;
    })();
    const inferredTarget = entry?.target || (() => {
      if (/\b(?:each creature|creatures? within|all creatures?|cone|radius|sphere|cube|line)\b/i.test(combinedText)) return 'area';
      if (/\byou regain\b|\byou gain\b|\bself\b/i.test(combinedText)) return 'self';
      if (/\bobject\b/i.test(combinedText)) return 'object';
      return 'creature';
    })();

    const nextAttack = {
      ...(entry?.attack && typeof entry.attack === 'object' ? entry.attack : {})
    };
    if (attackType) nextAttack.type = nextAttack.type || attackType;
    if (parsedAttackBonus !== null) {
      nextAttack.toHit = nextAttack.toHit ?? parsedAttackBonus;
      nextAttack.bonus = nextAttack.bonus ?? parsedAttackBonus;
    }
    if (rangeInfo?.reach !== undefined) nextAttack.reach = nextAttack.reach ?? rangeInfo.reach;
    if (rangeInfo?.attackRange) nextAttack.range = nextAttack.range || rangeInfo.attackRange;
    if (damageComponents.length > 0 && (!Array.isArray(nextAttack.damage) || nextAttack.damage.length === 0)) {
      nextAttack.damage = damageComponents;
    }

    const normalized = {
      ...entry,
      ...(inferredCombatType ? { type: inferredCombatType } : {}),
      ...(parsedAttackBonus !== null ? {
        attackBonus: entry?.attackBonus ?? parsedAttackBonus,
        toHit: entry?.toHit ?? parsedAttackBonus
      } : {}),
      ...(primaryDamage ? {
        damage: typeof entry?.damage === 'string' && entry.damage.trim()
          ? entry.damage
          : (Array.isArray(entry?.damage) ? entry.damage : primaryDamage.formula),
        damageFormula: entry?.damageFormula || primaryDamage.formula,
        damageType: entry?.damageType || primaryDamage.type
      } : {}),
      ...(rangeInfo?.range ? { range: entry?.range || rangeInfo.range } : {}),
      ...(save ? {
        save: entry?.save || save,
        saveDC: entry?.saveDC ?? entry?.saveDc ?? save.dc
      } : {}),
      ...(usage ? { uses: usage } : {}),
      ...(damageComponents.length > 0 ? { damageEntries: entry?.damageEntries || damageComponents } : {}),
      ...(inferredTarget ? { target: inferredTarget } : {})
    };

    if (Object.keys(nextAttack).length > 0) {
      normalized.attack = nextAttack;
    }

    return normalized;
  }

  static normalizeEquipmentWeaponEntry(entry) {
    if (!entry || typeof entry !== 'object' || entry.type !== 'weapon') return entry;

    const sourceSection = entry.sourceSection || entry?.parserMeta?.sourceSection || 'equipment';
    const combinedText = [
      entry.description,
      entry.statLine,
      entry.notes
    ].filter((part) => typeof part === 'string' && part.trim()).join('\n');

    if (!combinedText) return entry;

    const normalizedCombatView = this.normalizeCombatActionEntry({
      ...entry,
      type: entry.type,
      description: combinedText,
      sourceSection,
      parserMeta: this.mergeParserMeta(entry.parserMeta, { sourceSection })
    }, { kind: 'actions' });

    return {
      ...entry,
      type: 'weapon',
      ...(normalizedCombatView.attackBonus !== undefined ? {
        attackBonus: entry.attackBonus ?? normalizedCombatView.attackBonus,
        toHit: entry.toHit ?? normalizedCombatView.toHit ?? normalizedCombatView.attack?.toHit
      } : {}),
      ...(normalizedCombatView.damageFormula || normalizedCombatView.damageType || normalizedCombatView.damageEntries ? {
        damage: entry.damage ?? (
          typeof normalizedCombatView.damage === 'string' && normalizedCombatView.damage.trim()
            ? normalizedCombatView.damage
            : normalizedCombatView.damageFormula
        ),
        damageFormula: entry.damageFormula ?? normalizedCombatView.damageFormula,
        damageType: entry.damageType ?? normalizedCombatView.damageType,
        damageEntries: (Array.isArray(entry.damageEntries) && entry.damageEntries.length > 0)
          ? entry.damageEntries
          : normalizedCombatView.damageEntries
      } : {}),
      ...(normalizedCombatView.range ? { range: entry.range ?? normalizedCombatView.range } : {}),
      ...(normalizedCombatView.attack?.reach !== undefined ? { reach: entry.reach ?? normalizedCombatView.attack.reach } : {}),
      ...(normalizedCombatView.attack ? {
        attack: {
          ...(entry.attack && typeof entry.attack === 'object' ? entry.attack : {}),
          ...normalizedCombatView.attack
        }
      } : {})
    };
  }

  static parseFastCharacterWeaponAttacks(text) {
    const entries = [];
    String(text || '').split(/\r?\n/).forEach((rawLine, index) => {
      const line = rawLine.trim();
      const attackMatch = line.match(/^(.+?)\.\s*(?:(?:melee|ranged)(?:\s+or\s+(?:melee|ranged))?)\s+weapon\s+attack\s*:\s*(.+)$/i);
      if (!attackMatch) return;
      entries.push(this.normalizeEquipmentWeaponEntry({
        name: attackMatch[1].trim(),
        type: 'weapon',
        equipped: true,
        description: attackMatch[2].trim(),
        statLine: attackMatch[2].trim(),
        parserMeta: this.buildParserMeta({
          sourceParser: 'parseFastCharacterWeaponAttacks',
          sourceSection: 'weapons',
          lineStart: index + 1,
          lineEnd: index + 1,
          rawText: rawLine,
          parseConfidence: 0.96,
          reviewReason: 'Read from a weapon attack line',
          knowledgeSource: 'provided'
        })
      }));
    });
    return entries;
  }

  // date_of_change: 2026-03-11
  // rule_reference: Character import needs stable source metadata so SmartPaste can distinguish canonical source entries from derived action views.
  static buildParsedAbilityEntry(entry, overrides = {}) {
    if (!entry || !entry.name) return null;
    const merged = {
      ...entry,
      ...overrides
    };
    const sourceSection = overrides.sourceSection || merged.sourceSection || 'features';
    const parserMeta = this.mergeParserMeta(entry.parserMeta, overrides.parserMeta, { sourceSection });
    const activationType = overrides.activationType || merged.activationType || this.inferActivationTypeFromEntry(merged);
    const normalizedEntry = {
      ...merged,
      activationType,
      actionType: activationType,
      sourceSection,
      parserMeta
    };
    if (['actions', 'bonusActions', 'reactions'].includes(sourceSection) || normalizedEntry.derivedFromFeature === true) {
      return this.normalizeCombatActionEntry(normalizedEntry, { kind: sourceSection, activationType });
    }
    return normalizedEntry;
  }

  static annotateEntryKnowledge(entry, knowledgeSource, knowledgeNote) {
    if (!entry || !knowledgeSource) return entry;
    return {
      ...entry,
      parserMeta: this.mergeParserMeta(entry.parserMeta, {
        knowledgeSource,
        knowledgeNote
      })
    };
  }

  static categorizeFeatures(abilities, options = {}) {
    // Heuristic feat names used for inference only. These are NOT treated as authoritative SRD truth.
    const heuristicFeatNames = new Set([
      'alert', 'athlete', 'actor', 'charger', 'crossbow expert', 'defensive duelist',
      'dual wielder', 'dungeon delver', 'durable', 'elemental adept', 'grappler',
      'great weapon master', 'healer', 'heavily armored', 'heavy armor master',
      'inspiring leader', 'keen mind', 'lightly armored', 'linguist', 'lucky',
      'mage slayer', 'magic initiate', 'martial adept', 'medium armor master',
      'mobile', 'moderately armored', 'mounted combatant', 'observant', 'polearm master',
      'resilient', 'ritual caster', 'savage attacker', 'sentinel', 'sharpshooter',
      'shield master', 'skilled', 'skulker', 'spell sniper', 'tavern brawler',
      'tough', 'war caster', 'weapon master', 'fey touched', 'shadow touched',
      'telekinetic', 'telepathic', 'piercer', 'slasher', 'crusher', 'skill expert',
      'fighting initiate', 'metamagic adept', 'eldritch adept', 'artificer initiate',
      'chef', 'poisoner', 'gunner', 'feytouched', 'shadowtouched'
    ]);

    // Patterns indicating racial/monster traits
    const traitPatterns = [
      /regeneration/i, /resistance/i, /darkvision/i, /blindsight/i, /tremorsense/i,
      /spider climb/i, /legendary resistance/i, /magic resistance/i, /undead/i,
      /fey ancestry/i, /trance/i, /sunlight sensitivity/i, /pack tactics/i,
      /keen senses/i, /relentless/i, /savage attacks/i, /nimble escape/i,
      /brave/i, /halfling nimbleness/i, /lucky(?!=\s)/i, /stonecunning/i,
      /dwarven resilience/i, /gnome cunning/i, /forbiddance/i, /insatiable/i,
      /unnatural sight/i, /physique/i, /blueblood/i, /lineage/i,
      /powerful build/i, /large form/i
    ];

    const classFeaturePatterns = [
      /extra attack/i, /second wind/i, /action surge/i, /fighting style/i,
      /arcane jolt/i, /flash of genius/i, /steel defender/i, /infuse item/i,
      /sneak attack/i, /cunning action/i, /uncanny dodge/i, /evasion/i,
      /rage/i, /reckless attack/i, /danger sense/i, /brutal critical/i,
      /bardic inspiration/i, /song of rest/i, /magical secrets/i,
      /channel divinity/i, /divine smite/i, /lay on hands/i, /aura of/i,
      /wild shape/i, /natural recovery/i, /circle of/i,
      /ki point/i, /flurry of blows/i, /patient defense/i, /deflect missiles/i,
      /favored enemy/i, /natural explorer/i, /hunter's mark/i,
      /metamagic/i, /sorcery point/i, /font of magic/i,
      /eldritch invocation/i, /pact of the/i, /mystic arcanum/i,
      /arcane recovery/i, /spell mastery/i, /signature spell/i,
      /spell storing item/i, /attunement limit/i, /indomitable/i,
      // Rune Knight & fighter-specific features present in Glatham
      /giant['’]s might/i, /hill rune/i, /stone rune/i, /runic shield/i, /rune knight/i,
      /tactical shift/i, /tactical mind/i
    ];

    const entityClass = String(options?.entityClass || '').toLowerCase();
    const entitySpecies = String(options?.entitySpecies || '').toLowerCase();
    const result = { feats: [], classFeatures: [], traits: [], unresolved: [] };
    const pushCategorizedEntry = (bucket, entry, knowledgeSource, knowledgeNote) => {
      result[bucket].push(this.annotateEntryKnowledge(entry, knowledgeSource, knowledgeNote));
    };

    for (const ability of abilities) {
      if (!ability || !ability.name) continue;

      const entry = this.buildParsedAbilityEntry(ability, {
        sourceSection: ability?.sourceSection || options?.sourceSection || 'features'
      });
      if (!entry) continue;

      const name = entry.name.trim();
      const nameLower = name.toLowerCase();
      const hasRuneKeyword = /rune/i.test(nameLower);
      const hasFeatMarker = /(feat|boon|gift)/i.test(nameLower);
      const isClassFeature = classFeaturePatterns.some(p => p.test(name));
      const isKnownFeat = heuristicFeatNames.has(nameLower);
      const isLikelyFeat = isKnownFeat || hasFeatMarker || /(adept|initiate|prod|expert)/i.test(nameLower);
      const isTrait = traitPatterns.some(p => p.test(name));
      const activationType = entry.activationType || 'passive';
      const sectionSource = String(entry.sourceSection || '').toLowerCase();

      // Explicit fighter / rune knight routing to classFeatures
      // date_of_change: 2026-03-12
      // rule_reference: Attack Action is a core combat option, not a class feature; keep it out of feature ownership heuristics.
      const fighterHeaders = /(giant['’]s might|hill rune|stone rune|runic shield|tactical shift|tactical mind|action surge|second wind|indomitable|extra attack)/i;
      const speciesSpecificPatterns = [
        /stone['’]s endurance/i,
        /powerful build/i,
        /large form/i
      ];
      const likelySpeciesContext = /goliath|dwarf|elf|dragonborn|tiefling|human|orc|halfling|aasimar|genasi|tabaxi|kenku|firbolg/i.test(entitySpecies);
      const likelyClassContext = /fighter|wizard|rogue|cleric|ranger|barbarian|bard|druid|monk|paladin|sorcerer|warlock|artificer|blood hunter/i.test(entityClass);

      if (sectionSource === 'speciesTraits') {
        pushCategorizedEntry('traits', entry, 'provided', 'Placed in Species Traits section by pasted content');
      } else if (sectionSource === 'feats') {
        pushCategorizedEntry('feats', entry, 'provided', 'Placed in Feats section by pasted content');
      } else if (isClassFeature || fighterHeaders.test(name)) {
        pushCategorizedEntry('classFeatures', entry, 'inferred', 'Matched class feature heuristics');
      } else if (isLikelyFeat) {
        pushCategorizedEntry('feats', entry, 'inferred', 'Matched feat heuristics');
      } else if (isTrait) {
        pushCategorizedEntry('traits', entry, 'inferred', 'Matched trait heuristics');
      } else if (hasRuneKeyword) {
        pushCategorizedEntry('classFeatures', entry, 'inferred', 'Matched rune-class feature heuristics');
      } else if (speciesSpecificPatterns.some(p => p.test(name)) && likelySpeciesContext) {
        pushCategorizedEntry('traits', entry, 'inferred', 'Matched species-context heuristics');
      } else if (/interception|fighting style/i.test(name) && likelyClassContext) {
        pushCategorizedEntry('classFeatures', entry, 'inferred', 'Matched class-context heuristics');
      } else if (/ancestry|bloodline|heritage|lineage|subrace|clan|tribe|dragonborn|tiefling|genasi|goliath/i.test(nameLower)) {
        pushCategorizedEntry('traits', entry, 'inferred', 'Matched lineage/ancestry heuristics');
      } else if (/darkvision|blindsight|tremorsense|truesight/i.test(nameLower)) {
        pushCategorizedEntry('traits', entry, 'inferred', 'Matched sensory trait heuristics');
      } else if (sectionSource === 'features' && activationType !== 'passive' && likelyClassContext) {
        pushCategorizedEntry('classFeatures', entry, 'inferred', 'Matched action-economy class feature heuristics');
      } else {
        pushCategorizedEntry('unresolved', entry, 'inferred', 'Could not confidently classify beyond parser heuristics');
      }
    }

    return result;
  }

  /**
   * Return standard D&D 5e (2024) class features for the given class_levels array.
   * date_of_change: 2026-02-12
   * rule_reference: D&D 5e (2024) PHB class feature tables. Only key features listed;
   * the GM can always edit. Subclass features use the subclass name as source.
   * @param {Array<{class: string, level: number, subclass?: string}>} classLevels
   * @returns {Array<{name: string, description: string, source: string, level: number}>}
   */
  static getStandardClassFeatures(classLevels) {
    const classFeatureTable = {
      wizard: [
        { level: 1, name: 'Spellcasting', description: 'You can cast wizard spells using Intelligence as your spellcasting ability.' },
        { level: 1, name: 'Arcane Recovery', description: 'Once per long rest, during a short rest you can recover expended spell slots with a combined level equal to half your wizard level (rounded up).' },
        { level: 2, name: 'Arcane Tradition', description: 'You choose an arcane tradition (subclass) that shapes your practice of magic.' },
        { level: 18, name: 'Spell Mastery', description: 'Choose a 1st-level and a 2nd-level wizard spell. You can cast them at their lowest level without expending a spell slot.' },
        { level: 20, name: 'Signature Spells', description: 'Choose two 3rd-level wizard spells. You always have them prepared and can cast each once at 3rd level without expending a spell slot (regain on short or long rest).' },
      ],
      fighter: [
        { level: 1, name: 'Fighting Style', description: 'You adopt a particular style of fighting as your specialty.' },
        { level: 1, name: 'Second Wind', description: 'On your turn, use a bonus action to regain 1d10 + fighter level HP. Once per short or long rest.' },
        { level: 2, name: 'Action Surge', description: 'Take one additional action on your turn. Once per short or long rest (twice at 17th).' },
        { level: 5, name: 'Extra Attack', description: 'Attack twice when you take the Attack action (three at 11th, four at 20th).' },
        { level: 9, name: 'Indomitable', description: 'Reroll a failed saving throw. Once per long rest (twice at 13th, three at 17th).' },
      ],
      rogue: [
        { level: 1, name: 'Expertise', description: 'Double proficiency bonus for two chosen skill proficiencies.' },
        { level: 1, name: 'Sneak Attack', description: 'Once per turn, deal extra damage with a finesse or ranged weapon when you have advantage or an ally is within 5 ft of the target.' },
        { level: 2, name: 'Cunning Action', description: 'Use a bonus action to Dash, Disengage, or Hide.' },
        { level: 5, name: 'Uncanny Dodge', description: 'Use your reaction to halve damage from an attacker you can see.' },
        { level: 7, name: 'Evasion', description: 'Dex save for half damage: take none on success, half on failure.' },
        { level: 11, name: 'Reliable Talent', description: 'Treat any d20 roll of 9 or lower as a 10 for proficient skill checks.' },
        { level: 20, name: 'Stroke of Luck', description: 'Turn a miss into a hit, or a failed check into a 20. Once per short or long rest.' },
      ],
      cleric: [
        { level: 1, name: 'Spellcasting', description: 'You can cast cleric spells using Wisdom as your spellcasting ability.' },
        { level: 1, name: 'Divine Domain', description: 'Choose a divine domain (subclass) related to your deity.' },
        { level: 2, name: 'Channel Divinity', description: 'Channel divine energy: Turn Undead plus one domain effect. Once per short or long rest.' },
        { level: 10, name: 'Divine Intervention', description: 'Call on your deity to intervene. Roll percentile dice; success if roll <= cleric level.' },
      ],
      barbarian: [
        { level: 1, name: 'Rage', description: 'Advantage on Strength checks/saves, bonus rage damage, resistance to bludgeoning/piercing/slashing.' },
        { level: 1, name: 'Unarmored Defense', description: 'AC = 10 + Dex mod + Con mod while not wearing armor.' },
        { level: 2, name: 'Reckless Attack', description: 'Advantage on melee Strength attacks, but attacks against you have advantage.' },
        { level: 2, name: 'Danger Sense', description: 'Advantage on Dexterity saving throws against effects you can see.' },
        { level: 5, name: 'Extra Attack', description: 'Attack twice when you take the Attack action.' },
        { level: 20, name: 'Primal Champion', description: 'Strength and Constitution increase by 4, to a maximum of 24.' },
      ],
      bard: [
        { level: 1, name: 'Spellcasting', description: 'You can cast bard spells using Charisma as your spellcasting ability.' },
        { level: 1, name: 'Bardic Inspiration', description: 'Grant a creature a Bardic Inspiration die to add to one check, attack, or save.' },
        { level: 2, name: 'Jack of All Trades', description: 'Add half proficiency bonus to ability checks that do not already include it.' },
        { level: 2, name: 'Song of Rest', description: 'Allies who hear your performance during a short rest regain extra HP.' },
        { level: 10, name: 'Magical Secrets', description: 'Choose two spells from any class spell list; they count as bard spells.' },
        { level: 20, name: 'Superior Inspiration', description: 'Regain one Bardic Inspiration use when you roll initiative with none remaining.' },
      ],
      druid: [
        { level: 1, name: 'Spellcasting', description: 'You can cast druid spells using Wisdom as your spellcasting ability.' },
        { level: 2, name: 'Wild Shape', description: 'Magically assume the shape of a beast you have seen before.' },
        { level: 18, name: 'Beast Spells', description: 'Cast many druid spells in any Wild Shape form.' },
        { level: 20, name: 'Archdruid', description: 'Use Wild Shape an unlimited number of times.' },
      ],
      monk: [
        { level: 1, name: 'Unarmored Defense', description: 'AC = 10 + Dex mod + Wis mod while not wearing armor.' },
        { level: 1, name: 'Martial Arts', description: 'Use Dex for unarmed strikes and monk weapons; roll a Martial Arts die for damage.' },
        { level: 2, name: 'Ki', description: 'Harness ki energy. Ki points equal to your monk level.' },
        { level: 5, name: 'Extra Attack', description: 'Attack twice when you take the Attack action.' },
        { level: 5, name: 'Stunning Strike', description: 'Spend 1 ki point when you hit to attempt to stun the target.' },
        { level: 7, name: 'Evasion', description: 'Dex save for half damage: take none on success, half on failure.' },
        { level: 14, name: 'Diamond Soul', description: 'Proficiency in all saving throws. Spend 1 ki to reroll a failed save.' },
      ],
      paladin: [
        { level: 1, name: 'Divine Sense', description: 'Detect celestials, fiends, and undead within 60 feet.' },
        { level: 1, name: 'Lay on Hands', description: 'Healing pool equal to 5 x paladin level.' },
        { level: 2, name: 'Spellcasting', description: 'You can cast paladin spells using Charisma as your spellcasting ability.' },
        { level: 2, name: 'Divine Smite', description: 'Expend a spell slot on a melee hit to deal extra radiant damage.' },
        { level: 5, name: 'Extra Attack', description: 'Attack twice when you take the Attack action.' },
        { level: 6, name: 'Aura of Protection', description: 'You and allies within 10 ft add your Charisma modifier to saving throws.' },
      ],
      ranger: [
        { level: 1, name: 'Favored Enemy', description: 'Experience studying, tracking, and hunting a certain type of enemy.' },
        { level: 1, name: 'Natural Explorer', description: 'Adept at traveling and surviving in certain natural environments.' },
        { level: 2, name: 'Spellcasting', description: 'You can cast ranger spells using Wisdom as your spellcasting ability.' },
        { level: 2, name: 'Fighting Style', description: 'You adopt a particular style of fighting as your specialty.' },
        { level: 5, name: 'Extra Attack', description: 'Attack twice when you take the Attack action.' },
      ],
      sorcerer: [
        { level: 1, name: 'Spellcasting', description: 'You can cast sorcerer spells using Charisma as your spellcasting ability.' },
        { level: 1, name: 'Sorcerous Origin', description: 'Choose a sorcerous origin (subclass) describing the source of your innate power.' },
        { level: 2, name: 'Font of Magic', description: 'Sorcery points to create spell slots or fuel Metamagic.' },
        { level: 3, name: 'Metamagic', description: 'Twist your spells using Metamagic options.' },
        { level: 20, name: 'Sorcerous Restoration', description: 'Regain 4 sorcery points on a short rest.' },
      ],
      warlock: [
        { level: 1, name: 'Pact Magic', description: 'Cast warlock spells using Charisma. Slots refresh on short rest.' },
        { level: 1, name: 'Otherworldly Patron', description: 'You have struck a bargain with an otherworldly being (subclass).' },
        { level: 2, name: 'Eldritch Invocations', description: 'Gain invocations that grant special abilities.' },
        { level: 3, name: 'Pact Boon', description: 'Your patron bestows a gift (Blade, Chain, or Tome).' },
        { level: 11, name: 'Mystic Arcanum', description: 'Your patron grants a 6th-level spell you can cast once per long rest.' },
        { level: 20, name: 'Eldritch Master', description: 'Entreat your patron to regain all spell slots. Once per long rest.' },
      ],
      artificer: [
        { level: 1, name: 'Spellcasting', description: 'You can cast artificer spells using Intelligence as your spellcasting ability.' },
        { level: 1, name: 'Magical Tinkering', description: 'Invest a spark of magic into mundane objects.' },
        { level: 2, name: 'Infuse Item', description: 'Imbue mundane items with magical infusions.' },
        { level: 7, name: 'Flash of Genius', description: 'Add Intelligence modifier to a failed check or save within 30 ft.' },
        { level: 10, name: 'Magic Item Adept', description: 'Attune to up to four magic items at once.' },
        { level: 11, name: 'Spell-Storing Item', description: 'Store a spell in an object for others to cast.' },
        { level: 20, name: 'Soul of Artifice', description: '+1 to all saves per attuned magic item.' },
      ],
    };

    const subclassFeatureTable = {
      'wizard:divination': [
        { level: 2, name: 'Portent', description: 'After a long rest, roll two d20s. You can replace any attack roll, save, or check made by you or a creature you can see with one of these rolls.' },
        { level: 6, name: 'Expert Divination', description: 'Casting a divination spell of 2nd+ level regains one lower-level spell slot.' },
        { level: 10, name: 'The Third Eye', description: 'Use an action to gain darkvision 120 ft, ethereal sight 60 ft, read any language, or see invisible creatures within 10 ft.' },
        { level: 14, name: 'Greater Portent', description: 'Roll three d20s for Portent instead of two.' },
      ],
      'wizard:evocation': [
        { level: 2, name: 'Sculpt Spells', description: 'Choose creatures equal to 1 + spell level to auto-succeed on saves and take no damage from your evocation spells.' },
        { level: 6, name: 'Potent Cantrip', description: 'Creatures that succeed on saves against your cantrips take half damage.' },
        { level: 10, name: 'Empowered Evocation', description: 'Add Intelligence modifier to one damage roll of wizard evocation spells.' },
        { level: 14, name: 'Overchannel', description: 'Deal maximum damage with a 1st-5th level wizard damage spell.' },
      ],
      'wizard:abjuration': [
        { level: 2, name: 'Arcane Ward', description: 'Casting an abjuration spell of 1st+ level creates a ward with HP = 2x wizard level + Int mod.' },
        { level: 6, name: 'Projected Ward', description: 'Use reaction to have your Arcane Ward absorb damage for a creature within 30 ft.' },
        { level: 10, name: 'Improved Abjuration', description: 'Add proficiency bonus to ability checks for Counterspell, Dispel Magic, etc.' },
        { level: 14, name: 'Spell Resistance', description: 'Advantage on saves against spells, and resistance to spell damage.' },
      ],
    };

    const results = [];
    for (const cl of classLevels) {
      const className = (cl.class || cl.name || '').toLowerCase().trim();
      const level = cl.level || 0;
      const subclass = (cl.subclass || '').toLowerCase().trim();
      const displayClass = cl.class || cl.name || 'Unknown';

      const baseFeatures = classFeatureTable[className] || [];
      for (const f of baseFeatures) {
        if (f.level <= level) {
          results.push({ name: f.name, description: f.description, source: displayClass, level: f.level });
        }
      }

      if (subclass) {
        const subKey = `${className}:${subclass}`;
        const subFeatures = subclassFeatureTable[subKey] || [];
        for (const f of subFeatures) {
          if (f.level <= level) {
            results.push({ name: f.name, description: f.description, source: `${displayClass} (${cl.subclass || subclass})`, level: f.level });
          }
        }
      }
    }
    return results;
  }

  /**
   * Return SRD species traits for the given species name.
   * date_of_change: 2026-02-26
   * rule_reference: D&D 5e (2024) SRD — species traits for common PHB species.
   * Only SRD-legal traits are included. The GM can always edit or override.
   * @param {string} speciesName — e.g., "Goliath", "Human", "Elf"
   * @returns {Array<{name: string, description: string}>}
   */
  static getSRDSpeciesTraits(speciesName) {
    if (!speciesName) return [];
    const key = String(speciesName).toLowerCase().replace(/[^a-z]/g, '');

    const table = {
      goliath: [
        { name: 'Giant Ancestry', description: 'You are descended from Giants. Choose a lineage: Cloud, Fire, Frost, Hill, Stone, or Storm. You gain a related supernatural gift.' },
        { name: 'Large Form', description: 'Starting at 5th level, you can use a bonus action to become Large (if space permits) for 10 minutes. While Large, you have advantage on Strength checks and your speed increases by 10 ft.' },
        { name: 'Powerful Build', description: 'You count as one size larger when determining carrying capacity and the weight you can push, drag, or lift.' },
      ],
      human: [
        { name: 'Resourceful', description: 'You gain Heroic Inspiration whenever you finish a Long Rest.' },
        { name: 'Skillful', description: 'You gain proficiency in one skill of your choice.' },
        { name: 'Versatile', description: 'You gain an Origin feat of your choice.' },
      ],
      elf: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Fey Ancestry', description: 'You have advantage on saving throws against being charmed, and magic can\'t put you to sleep.' },
        { name: 'Keen Senses', description: 'You have proficiency in the Perception skill.' },
        { name: 'Trance', description: 'You don\'t need to sleep. Instead, you meditate deeply for 4 hours a day (Long Rest).' },
      ],
      dwarf: [
        { name: 'Darkvision', description: 'You can see in dim light within 120 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Dwarven Resilience', description: 'You have resistance to Poison damage and advantage on saving throws against the Poisoned condition.' },
        { name: 'Dwarven Toughness', description: 'Your Hit Point maximum increases by 1, and it increases by 1 again whenever you gain a level.' },
        { name: 'Stonecunning', description: 'As a Bonus Action, you gain Tremorsense with a range of 60 feet for 10 minutes. Once used, you can\'t use it again until you finish a Short or Long Rest.' },
      ],
      halfling: [
        { name: 'Brave', description: 'You have advantage on saving throws against being frightened.' },
        { name: 'Halfling Nimbleness', description: 'You can move through the space of any creature that is of a size larger than yours.' },
        { name: 'Lucky', description: 'When you roll a 1 on the d20 for an attack roll, ability check, or saving throw, you can reroll the die and must use the new roll.' },
        { name: 'Naturally Stealthy', description: 'You can attempt to hide even when you are obscured only by a creature that is at least one size larger than you.' },
      ],
      dragonborn: [
        { name: 'Draconic Ancestry', description: 'Choose a type of dragon from the Draconic Ancestry table. Your Breath Weapon and damage resistance are determined by the dragon type.' },
        { name: 'Breath Weapon', description: 'You can use your action to exhale destructive energy in an area determined by your ancestry. DC = 8 + CON mod + PB. Usable PB times per Long Rest.' },
        { name: 'Damage Resistance', description: 'You have resistance to the damage type associated with your draconic ancestry.' },
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
      ],
      gnome: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Gnomish Cunning', description: 'You have advantage on Intelligence, Wisdom, and Charisma saving throws.' },
        { name: 'Gnomish Lineage', description: 'Choose Forest Gnome (Minor Illusion cantrip, Speak with Small Beasts) or Rock Gnome (Mending cantrip, Tinker).' },
      ],
      halfelf: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Fey Ancestry', description: 'You have advantage on saving throws against being charmed, and magic can\'t put you to sleep.' },
        { name: 'Skill Versatility', description: 'You gain proficiency in two skills of your choice.' },
      ],
      halforc: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Relentless Endurance', description: 'When you are reduced to 0 HP but not killed outright, you can drop to 1 HP instead. Once per Long Rest.' },
        { name: 'Adrenaline Rush', description: 'You can take the Dash action as a Bonus Action. You gain temporary HP equal to your PB when you do. Usable PB times per Long Rest.' },
      ],
      tiefling: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Fiendish Legacy', description: 'Choose Abyssal (poison), Chthonic (necrotic), or Infernal (fire). You gain resistance to that damage type and related spells at 1st, 3rd, and 5th level.' },
        { name: 'Otherworldly Presence', description: 'You know the Thaumaturgy cantrip. Charisma is your spellcasting ability.' },
      ],
      orc: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Adrenaline Rush', description: 'You can take the Dash action as a Bonus Action. You gain temporary HP equal to your PB when you do. Usable PB times per Long Rest.' },
        { name: 'Relentless Endurance', description: 'When you are reduced to 0 HP but not killed outright, you can drop to 1 HP instead. Once per Long Rest.' },
      ],
      aasimar: [
        { name: 'Darkvision', description: 'You can see in dim light within 60 feet as if it were bright light, and in darkness as if it were dim light.' },
        { name: 'Celestial Resistance', description: 'You have resistance to Necrotic and Radiant damage.' },
        { name: 'Healing Hands', description: 'As an action, touch a creature to restore HP equal to your PB. Usable PB times per Long Rest.' },
        { name: 'Light Bearer', description: 'You know the Light cantrip. Charisma is your spellcasting ability.' },
      ],
    };

    // Try direct match, then strip common suffixes
    if (table[key]) return [...table[key]];
    // Handle "Stone Goliath" → goliath, "High Elf" → elf, "Hill Dwarf" → dwarf
    for (const [species, traits] of Object.entries(table)) {
      if (key.includes(species)) return [...traits];
    }
    return [];
  }

  /**
   * Return SRD languages for the given species name.
   * date_of_change: 2026-03-12
   * rule_reference: D&D 5e (2024) SRD — species languages are safe minimum defaults for import completion.
   * @param {string} speciesName
   * @returns {string[]}
   */
  static getSRDSpeciesLanguages(speciesName) {
    if (!speciesName) return [];
    const key = String(speciesName).toLowerCase().replace(/[^a-z]/g, '');

    const table = {
      goliath: ['Common', 'Giant'],
      human: ['Common'],
      elf: ['Common', 'Elvish'],
      drow: ['Common', 'Elvish'],
      dwarf: ['Common', 'Dwarvish'],
      halfling: ['Common', 'Halfling'],
      dragonborn: ['Common', 'Draconic'],
      gnome: ['Common', 'Gnomish'],
      halfelf: ['Common', 'Elvish'],
      halforc: ['Common', 'Orc'],
      tiefling: ['Common', 'Infernal'],
      orc: ['Common', 'Orc'],
      aasimar: ['Common', 'Celestial']
    };

    if (table[key]) return [...table[key]];
    for (const [species, languages] of Object.entries(table)) {
      if (key.includes(species)) return [...languages];
    }
    return [];
  }

  /**
   * Return SRD proficiencies for the given class name.
   * date_of_change: 2026-02-26
   * rule_reference: D&D 5e (2024) PHB — starting proficiencies by class.
   * @param {string} className
   * @returns {{ armor: string[], weapons: string[], tools: string[], savingThrows: string[] }}
   */
  static getSRDClassProficiencies(className) {
    if (!className) return { armor: [], weapons: [], tools: [], savingThrows: [] };
    const key = String(className).toLowerCase().trim();

    const table = {
      fighter:    { armor: ['Light Armor', 'Medium Armor', 'Heavy Armor', 'Shields'], weapons: ['Simple Weapons', 'Martial Weapons'], tools: [], savingThrows: ['strength', 'constitution'] },
      barbarian:  { armor: ['Light Armor', 'Medium Armor', 'Shields'], weapons: ['Simple Weapons', 'Martial Weapons'], tools: [], savingThrows: ['strength', 'constitution'] },
      paladin:    { armor: ['Light Armor', 'Medium Armor', 'Heavy Armor', 'Shields'], weapons: ['Simple Weapons', 'Martial Weapons'], tools: [], savingThrows: ['wisdom', 'charisma'] },
      ranger:     { armor: ['Light Armor', 'Medium Armor', 'Shields'], weapons: ['Simple Weapons', 'Martial Weapons'], tools: [], savingThrows: ['strength', 'dexterity'] },
      cleric:     { armor: ['Light Armor', 'Medium Armor', 'Shields'], weapons: ['Simple Weapons'], tools: [], savingThrows: ['wisdom', 'charisma'] },
      druid:      { armor: ['Light Armor', 'Medium Armor', 'Shields'], weapons: ['Simple Weapons'], tools: ["Herbalism Kit"], savingThrows: ['intelligence', 'wisdom'] },
      rogue:      { armor: ['Light Armor'], weapons: ['Simple Weapons', 'Hand Crossbows', 'Longswords', 'Rapiers', 'Shortswords'], tools: ["Thieves' Tools"], savingThrows: ['dexterity', 'intelligence'] },
      monk:       { armor: [], weapons: ['Simple Weapons', 'Martial Weapons (melee, no Heavy)'], tools: [], savingThrows: ['strength', 'dexterity'] },
      bard:       { armor: ['Light Armor'], weapons: ['Simple Weapons', 'Hand Crossbows', 'Longswords', 'Rapiers', 'Shortswords'], tools: ['Three Musical Instruments'], savingThrows: ['dexterity', 'charisma'] },
      wizard:     { armor: [], weapons: ['Daggers', 'Darts', 'Slings', 'Quarterstaffs', 'Light Crossbows'], tools: [], savingThrows: ['intelligence', 'wisdom'] },
      sorcerer:   { armor: [], weapons: ['Daggers', 'Darts', 'Slings', 'Quarterstaffs', 'Light Crossbows'], tools: [], savingThrows: ['constitution', 'charisma'] },
      warlock:    { armor: ['Light Armor'], weapons: ['Simple Weapons'], tools: [], savingThrows: ['wisdom', 'charisma'] },
    };

    return table[key] || { armor: [], weapons: [], tools: [], savingThrows: [] };
  }

  // date_of_change: 2026-03-11
  // rule_reference: SmartPaste should enrich character imports through a pure derivation step so
  // suggestion generation never mutates parser output in place.
  static deriveCharacterImportData(data = {}) {
    if (!data || typeof data !== 'object') return {};

    const derivedData = { ...data };
    const dedupeStrings = (values) => {
      const seen = new Set();
      return (Array.isArray(values) ? values : [])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .filter((value) => {
          const key = value.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    };
    const classNames = dedupeStrings([
      derivedData.class,
      ...(Array.isArray(derivedData.classes)
        ? derivedData.classes.flatMap((entry) => [entry?.name, entry?.className, entry?.class])
        : [])
    ]);
    const firstClassName = classNames[0] || '';
    const mergedSrdClassProficiencies = classNames.reduce((accumulator, className, index) => {
      const srdProf = this.getSRDClassProficiencies(className);
      if (!srdProf) return accumulator;

      accumulator.armor.push(...(srdProf.armor || []));
      accumulator.weapons.push(...(srdProf.weapons || []));
      accumulator.tools.push(...(srdProf.tools || []));
      if (index === 0) {
        accumulator.savingThrows.push(...(srdProf.savingThrows || []));
      }

      return accumulator;
    }, { armor: [], weapons: [], tools: [], savingThrows: [] });
    const buildSrdMeta = (sourceSection, knowledgeNote, reviewReason) => this.buildParserMeta({
      sourceParser: 'deriveCharacterImportData',
      sourceSection,
      parseConfidence: 1,
      reviewReason,
      knowledgeSource: 'srd',
      knowledgeNote
    });

    if (!Array.isArray(derivedData.speciesTraits) || derivedData.speciesTraits.length === 0) {
      const speciesName = derivedData.race || derivedData.species;
      if (speciesName) {
        const srdTraits = this.getSRDSpeciesTraits(speciesName);
        if (srdTraits.length > 0) {
          derivedData.speciesTraits = srdTraits.map((trait) => ({
            ...trait,
            parserMeta: this.mergeParserMeta(
              trait.parserMeta,
              buildSrdMeta(
                'speciesTraits',
                `Derived from SRD species trait table for ${speciesName}`,
                `Derived from SRD species traits for ${speciesName}`
              )
            )
          }));
        }
      }
    }

    if (!Array.isArray(derivedData.languages) || derivedData.languages.length === 0) {
      const speciesName = derivedData.race || derivedData.species;
      if (speciesName) {
        const srdLanguages = this.getSRDSpeciesLanguages(speciesName);
        if (srdLanguages.length > 0) {
          derivedData.languages = [...srdLanguages];
          derivedData.languagesMeta = this.mergeParserMeta(
            derivedData.languagesMeta,
            buildSrdMeta(
              'languages',
              `Derived from SRD species language table for ${speciesName}`,
              `Derived from SRD species languages for ${speciesName}`
            )
          );
        }
      }
    }

    const hasArmorProficiencies = Array.isArray(derivedData.proficiencies?.armor) && derivedData.proficiencies.armor.length > 0;
    const hasWeaponProficiencies = Array.isArray(derivedData.proficiencies?.weapons) && derivedData.proficiencies.weapons.length > 0;
    const hasToolProficiencies = Array.isArray(derivedData.proficiencies?.tools) && derivedData.proficiencies.tools.length > 0;
    const hasSavingThrowProficiencies = Array.isArray(derivedData.proficiencies?.savingThrows) && derivedData.proficiencies.savingThrows.length > 0;

    if (!derivedData.proficiencies || (!hasArmorProficiencies && !hasWeaponProficiencies)) {
      if (
        mergedSrdClassProficiencies.armor.length
        || mergedSrdClassProficiencies.weapons.length
        || mergedSrdClassProficiencies.tools.length
        || mergedSrdClassProficiencies.savingThrows.length
      ) {
        derivedData.proficiencies = {
          ...(derivedData.proficiencies || {}),
          armor: hasArmorProficiencies
            ? dedupeStrings(derivedData.proficiencies?.armor)
            : dedupeStrings(mergedSrdClassProficiencies.armor),
          weapons: hasWeaponProficiencies
            ? dedupeStrings(derivedData.proficiencies?.weapons)
            : dedupeStrings(mergedSrdClassProficiencies.weapons),
          tools: hasToolProficiencies
            ? dedupeStrings(derivedData.proficiencies?.tools)
            : dedupeStrings(mergedSrdClassProficiencies.tools),
          savingThrows: hasSavingThrowProficiencies
            ? dedupeStrings(derivedData.proficiencies?.savingThrows)
            : dedupeStrings(mergedSrdClassProficiencies.savingThrows)
        };
        derivedData.proficienciesMeta = this.mergeParserMeta(
          derivedData.proficienciesMeta,
          buildSrdMeta(
            'proficiencies',
            `Derived from SRD class proficiency table for ${classNames.join(', ') || firstClassName}`,
            `Derived from SRD class proficiencies for ${classNames.join(', ') || firstClassName}`
          )
        );
      }
    }

    if ((!Array.isArray(derivedData.toolProficiencies) || derivedData.toolProficiencies.length === 0)
      && Array.isArray(derivedData.proficiencies?.tools)
      && derivedData.proficiencies.tools.length > 0) {
      derivedData.toolProficiencies = [...derivedData.proficiencies.tools];
    }

    this.normalizeSpellcastingStructures(derivedData);

    return derivedData;
  }

  static normalizeSpellEntryPayload(entry, fallbackLevel = null) {
    if (entry === null || entry === undefined) return null;
    if (typeof entry === 'string') {
      const payload = { name: entry };
      if (fallbackLevel !== null) payload.level = fallbackLevel;
      return payload;
    }
    if (typeof entry === 'object') {
      const normalized = { ...entry };
      if (!normalized.name && typeof normalized.title === 'string') {
        normalized.name = normalized.title;
      }
      if (fallbackLevel !== null && (normalized.level === undefined || normalized.level === null)) {
        normalized.level = fallbackLevel;
      }
      return normalized;
    }
    return null;
  }

  static normalizeSpellArray(entries, fallbackLevel = null) {
    if (!Array.isArray(entries)) return [];
    const seen = new Set();
    const normalized = [];
    entries.forEach((entry) => {
      const payload = this.normalizeSpellEntryPayload(entry, fallbackLevel);
      if (!payload || !payload.name) return;
      const fingerprint = `${String(payload.level ?? fallbackLevel ?? 'unknown')}|${payload.name.toLowerCase()}`;
      if (seen.has(fingerprint)) return;
      seen.add(fingerprint);
      normalized.push(payload);
    });
    return normalized;
  }

  static normalizeSpellcastingStructures(derivedData) {
    if (!derivedData || typeof derivedData !== 'object') return;
    const spells = derivedData.spells;
    if (!spells || typeof spells !== 'object') return;

    const normalized = { ...spells };
    const byLevel = { ...(spells.byLevel || {}) };

    const mergeIntoLevel = (level, entries) => {
      if (!Array.isArray(entries) || entries.length === 0) return;
      const key = String(level);
      const existing = Array.isArray(byLevel[key]) ? [...byLevel[key]] : [];
      const seen = new Set(existing.map((entry) => `${key}|${(entry?.name || '').toLowerCase()}`));
      entries.forEach((entry) => {
        const payload = this.normalizeSpellEntryPayload(entry, level);
        if (!payload || !payload.name) return;
        const fingerprint = `${key}|${payload.name.toLowerCase()}`;
        if (seen.has(fingerprint)) return;
        seen.add(fingerprint);
        existing.push(payload);
      });
      byLevel[key] = existing;
    };

    const ingestContainer = (container) => {
      if (!container || typeof container !== 'object') return;
      const levelMap = [
        { level: 0, keys: ['cantrips', 'level0', '0'] },
        { level: 1, keys: ['level1', '1'] },
        { level: 2, keys: ['level2', '2'] },
        { level: 3, keys: ['level3', '3'] },
        { level: 4, keys: ['level4', '4'] },
        { level: 5, keys: ['level5', '5'] },
        { level: 6, keys: ['level6', '6'] },
        { level: 7, keys: ['level7', '7'] },
        { level: 8, keys: ['level8', '8'] },
        { level: 9, keys: ['level9', '9'] }
      ];
      levelMap.forEach(({ level, keys }) => {
        keys.forEach((key) => {
          if (Array.isArray(container[key])) {
            mergeIntoLevel(level, container[key]);
          }
        });
      });
      if (container.byLevel && typeof container.byLevel === 'object') {
        Object.entries(container.byLevel).forEach(([key, list]) => {
          const level = Number.isNaN(Number(key)) ? key : Number(key);
          mergeIntoLevel(level, Array.isArray(list) ? list : []);
        });
      }
    };

    ingestContainer(spells);
    ingestContainer(spells._rawSpells);

    const ensureSpellArray = (primary, fallback) => {
      const base = this.normalizeSpellArray(Array.isArray(primary) ? primary : []);
      const supplemental = this.normalizeSpellArray(Array.isArray(fallback) ? fallback : []);
      if (!supplemental.length) return base;
      const seen = new Set(base.map((entry) => `${entry.level ?? 'unknown'}|${(entry.name || '').toLowerCase()}`));
      supplemental.forEach((entry) => {
        const fingerprint = `${entry.level ?? 'unknown'}|${(entry.name || '').toLowerCase()}`;
        if (!seen.has(fingerprint)) {
          seen.add(fingerprint);
          base.push(entry);
        }
      });
      return base;
    };

    normalized.byLevel = byLevel;
    normalized.prepared = ensureSpellArray(spells.prepared, spells._rawSpells?.prepared);
    normalized.known = ensureSpellArray(spells.known, spells._rawSpells?.known);
    normalized.atWill = ensureSpellArray(spells.atWill, spells._rawSpells?.atWill);

    // @date_of_change: 2026-03-18
    // rule_reference: Legacy saved characters (pre-SmartPaste) stored per-level spell buckets like level1/level2.
    // Normalize those buckets into byLevel/prepared arrays so the sheet and combat engine can surface spell data.
    derivedData.spells = normalized;
  }

  // date_of_change: 2026-03-11
  // rule_reference: SmartPaste architecture should have a shared entry catalog so later UI and merge logic
  // can operate on one normalized set of parsed records instead of rescanning many arrays.
  static buildCharacterCatalogEntry(kind, entry, index = 0) {
    if (!entry || !entry.name) return null;

    const parserMeta = this.mergeParserMeta(entry.parserMeta, {
      sourceSection: entry.sourceSection || entry.parserMeta?.sourceSection || kind
    });
    const entryData = {
      ...entry,
      sourceSection: entry.sourceSection || parserMeta?.sourceSection || kind,
      parserMeta
    };

    if (kind === 'equipment') {
      return {
        id: `equipment:${this.normalizeParsedEntryName(entry.name) || index}:${index}`,
        kind: 'equipment',
        name: entry.name,
        description: entry.description || entry.statLine || '',
        activationType: null,
        type: entry.type || 'item',
        fingerprint: this.buildParsedEntryFingerprint(entry, 'equipment'),
        parserMeta,
        data: entryData
      };
    }

    return {
      id: `${kind}:${this.normalizeParsedEntryName(entry.name) || index}:${index}`,
      kind,
      name: entry.name,
      description: entry.description || '',
      activationType: entry.activationType || entry.actionType || null,
      type: entry.type || null,
      derivedFromFeature: entry.derivedFromFeature === true,
      fingerprint: this.buildParsedEntryFingerprint(entry, kind),
      parserMeta,
      data: entryData
    };
  }

  static buildCharacterCatalogEntriesForKind(kind, entries = []) {
    if (!Array.isArray(entries)) return [];

    return entries
      .map((entry, index) => this.buildCharacterCatalogEntry(kind, entry, index))
      .filter(Boolean);
  }

  static buildCharacterCatalogFromParsedEntries(parsedEntries = []) {
    const merged = [];
    const indexByKey = new Map();

    (Array.isArray(parsedEntries) ? parsedEntries : []).forEach(({ kind, entry }, index) => {
      const catalogEntry = this.buildCharacterCatalogEntry(kind, entry, index);
      if (!catalogEntry) return;

      const key = this.buildCharacterCatalogMergeKey(catalogEntry, kind);
      if (!key) return;

      if (!indexByKey.has(key)) {
        indexByKey.set(key, merged.length);
        merged.push(catalogEntry);
        return;
      }

      const existingIndex = indexByKey.get(key);
      merged[existingIndex] = this.mergeCharacterCatalogEntry(merged[existingIndex], catalogEntry);
    });

    return merged;
  }

  static buildCharacterCatalogMergeKey(entry, kind = 'entry') {
    const normalizedKind = this.normalizeParsedEntryName(kind || entry?.kind || entry?.data?.sourceSection || 'entry');
    const normalizedType = this.normalizeParsedEntryName(entry?.type || entry?.data?.type || '');
    const normalizedName = this.normalizeParsedEntryName(entry?.name || entry?.data?.name || '');
    const spellLevel = normalizedKind === 'spells' && Number.isFinite(Number(entry?.level ?? entry?.data?.level))
      ? `level:${Number(entry?.level ?? entry?.data?.level)}`
      : '';
    return normalizedName
      ? [normalizedKind || 'entry', normalizedType, normalizedName, spellLevel].filter(Boolean).join('|')
      : (entry?.fingerprint || this.buildParsedEntryFingerprint(entry?.data || entry, kind));
  }

  static scoreCharacterCatalogEntry(entry) {
    if (!entry) return 0;
    const description = String(entry?.description || entry?.data?.description || '').trim();
    const rawText = String(entry?.parserMeta?.rawText || entry?.data?.parserMeta?.rawText || '').trim();
    const statLine = String(entry?.data?.statLine || '').trim();
    const parseConfidence = Number.isFinite(entry?.parserMeta?.parseConfidence)
      ? entry.parserMeta.parseConfidence
      : 0;
    return description.length + rawText.length + statLine.length + parseConfidence * 100;
  }

  static mergeCharacterCatalogEntry(existing, incoming) {
    if (!existing) return incoming;
    if (!incoming) return existing;

    const keepIncoming = this.scoreCharacterCatalogEntry(incoming) > this.scoreCharacterCatalogEntry(existing);
    const preferred = keepIncoming ? incoming : existing;
    const secondary = keepIncoming ? existing : incoming;
    const mergedParserMeta = this.mergeParserMeta(
      secondary?.parserMeta,
      preferred?.parserMeta,
      { sourceSection: preferred?.parserMeta?.sourceSection || secondary?.parserMeta?.sourceSection || preferred?.kind }
    );
    const preferredData = preferred?.data && typeof preferred.data === 'object' ? preferred.data : {};
    const secondaryData = secondary?.data && typeof secondary.data === 'object' ? secondary.data : {};

    return {
      ...secondary,
      ...preferred,
      parserMeta: mergedParserMeta,
      data: {
        ...secondaryData,
        ...preferredData,
        sourceSection: preferredData.sourceSection || secondaryData.sourceSection || preferred?.kind,
        parserMeta: this.mergeParserMeta(secondaryData.parserMeta, preferredData.parserMeta, mergedParserMeta)
      }
    };
  }

  static mergeCharacterEntriesForKind(kind, ...entrySets) {
    const merged = [];
    const indexByKey = new Map();

    entrySets
      .map((entries) => this.buildCharacterCatalogEntriesForKind(kind, entries))
      .forEach((catalogEntries) => {
        catalogEntries.forEach((entry) => {
          const key = this.buildCharacterCatalogMergeKey(entry, kind);
          if (!key) return;

          if (!indexByKey.has(key)) {
            indexByKey.set(key, merged.length);
            merged.push(entry);
            return;
          }

          const existingIndex = indexByKey.get(key);
          merged[existingIndex] = this.mergeCharacterCatalogEntry(merged[existingIndex], entry);
        });
      });

    return merged
      .map((entry) => this.buildCatalogSuggestionEntry(entry))
      .filter(Boolean);
  }

  static buildCharacterEntryCatalog(data = {}) {
    const catalog = [];
    const pushEntries = (kind, entries = []) => {
      catalog.push(...this.buildCharacterCatalogEntriesForKind(kind, entries));
    };

    pushEntries('features', data.features);
    pushEntries('classFeatures', data.classFeatures);
    pushEntries('feats', data.feats);
    pushEntries('speciesTraits', data.speciesTraits);
    pushEntries('actions', data.actions);
    pushEntries('bonusActions', data.bonusActions);
    pushEntries('reactions', data.reactions);

    const equipmentEntries = [
      ...(Array.isArray(data.equipment?.weapons) ? data.equipment.weapons : []),
      ...(Array.isArray(data.equipment?.armor) ? data.equipment.armor : []),
      ...(Array.isArray(data.equipment?.items) ? data.equipment.items : []),
      ...(Array.isArray(data.equipment?.valuables) ? data.equipment.valuables : [])
    ];

    pushEntries('equipment', equipmentEntries);
    pushEntries('spells', this.buildSpellEntriesFromSpellData(data.spells));

    return catalog;
  }

  static getClaimedCharacterEntryCatalog(data = {}) {
    const finalCatalog = Array.isArray(data?.parsedEntryCatalog) && data.parsedEntryCatalog.length > 0
      ? data.parsedEntryCatalog
      : this.buildCharacterEntryCatalog(data);
    const provisionalCatalog = Array.isArray(data?.provisionalEntryCatalog) && data.provisionalEntryCatalog.length > 0
      ? data.provisionalEntryCatalog
      : this.buildCharacterCatalogFromParsedEntries(data?.parsedEntries);

    return this.mergeCharacterEntryCatalogs(finalCatalog, provisionalCatalog);
  }

  static scoreProvisionalCatalogMatch(finalEntry, provisionalEntry) {
    if (!finalEntry || !provisionalEntry) return -1;
    const finalName = this.normalizeParsedEntryName(finalEntry.name);
    const provisionalName = this.normalizeParsedEntryName(provisionalEntry.name);
    if (!finalName || !provisionalName || finalName !== provisionalName) return -1;

    const finalDescription = this.normalizeParsedEntryName(finalEntry.description || finalEntry?.data?.description || '');
    const provisionalDescription = this.normalizeParsedEntryName(provisionalEntry.description || provisionalEntry?.data?.description || '');
    const finalKind = this.normalizeParsedEntryName(finalEntry.kind);
    const provisionalKind = this.normalizeParsedEntryName(provisionalEntry.kind);
    const finalType = this.normalizeParsedEntryName(finalEntry.type || finalEntry?.data?.type || '');
    const provisionalType = this.normalizeParsedEntryName(provisionalEntry.type || provisionalEntry?.data?.type || '');
    const finalSourceSection = this.normalizeParsedEntryName(finalEntry?.parserMeta?.sourceSection || finalEntry?.data?.sourceSection || '');
    const provisionalSourceSection = this.normalizeParsedEntryName(provisionalEntry?.parserMeta?.sourceSection || provisionalEntry?.data?.sourceSection || '');

    let score = 100;
    if (finalKind && provisionalKind && finalKind === provisionalKind) score += 40;
    if (finalType && provisionalType && finalType === provisionalType) score += 15;
    if (finalSourceSection && provisionalSourceSection && finalSourceSection === provisionalSourceSection) score += 20;
    if (finalDescription && provisionalDescription) {
      if (finalDescription === provisionalDescription) score += 30;
      else if (finalDescription.includes(provisionalDescription) || provisionalDescription.includes(finalDescription)) score += 18;
    }

    return score;
  }

  static buildPublishedCharacterEntryCatalog(data = {}) {
    const finalCatalog = this.buildCharacterEntryCatalog(data);
    const provisionalCatalog = Array.isArray(data?.provisionalEntryCatalog) && data.provisionalEntryCatalog.length > 0
      ? data.provisionalEntryCatalog
      : this.buildCharacterCatalogFromParsedEntries(data?.parsedEntries);
    if (provisionalCatalog.length === 0) return finalCatalog;
    const normalizePublishedEntryName = (entry) => {
      let normalizedName = this.normalizeParsedEntryName(entry?.name);
      const entryType = this.normalizeParsedEntryName(entry?.type || entry?.data?.type || '');
      if (entry?.kind === 'equipment' && entryType === 'weapon') {
        normalizedName = normalizedName
          .replace(/^(?:one|two|three|four|five|six|\d+)(?:\s+\d+)?\s+/, '')
          .replace(/s\b$/, '');
      }
      return normalizedName;
    };

    const provisionalByName = new Map();
    provisionalCatalog.forEach((entry) => {
      const normalizedName = this.normalizeParsedEntryName(entry?.name);
      if (!normalizedName) return;
      if (!provisionalByName.has(normalizedName)) provisionalByName.set(normalizedName, []);
      provisionalByName.get(normalizedName).push(entry);
    });

    const enrichedCatalog = finalCatalog.map((entry) => {
      const normalizedName = this.normalizeParsedEntryName(entry?.name);
      const candidates = provisionalByName.get(normalizedName) || [];
      if (candidates.length === 0) return entry;

      let bestCandidate = null;
      let bestScore = -1;
      candidates.forEach((candidate) => {
        const score = this.scoreProvisionalCatalogMatch(entry, candidate);
        if (score > bestScore) {
          bestScore = score;
          bestCandidate = candidate;
        }
      });

      if (!bestCandidate || bestScore < 0) return entry;

      const mergedParserMeta = this.mergeParserMeta(
        bestCandidate?.parserMeta,
        bestCandidate?.data?.parserMeta,
        entry?.parserMeta,
        entry?.data?.parserMeta,
        { sourceSection: entry?.data?.sourceSection || entry?.parserMeta?.sourceSection || entry.kind }
      );
      const provisionalData = bestCandidate?.data && typeof bestCandidate.data === 'object' ? bestCandidate.data : {};
      const entryData = entry?.data && typeof entry.data === 'object' ? entry.data : {};

      return {
        ...bestCandidate,
        ...entry,
        parserMeta: mergedParserMeta,
        data: {
          ...provisionalData,
          ...entryData,
          sourceSection: entryData.sourceSection || provisionalData.sourceSection || entry.kind,
          parserMeta: this.mergeParserMeta(
            provisionalData.parserMeta,
            entryData.parserMeta,
            mergedParserMeta
          )
        }
      };
    });

    const publishedNames = new Set(
      enrichedCatalog
        .map(normalizePublishedEntryName)
        .filter(Boolean)
    );
    const unmatchedProvisional = provisionalCatalog.filter((entry) => {
      const normalizedName = normalizePublishedEntryName(entry);
      return normalizedName && !publishedNames.has(normalizedName);
    });

    return this.mergeCharacterEntryCatalogs(enrichedCatalog, unmatchedProvisional);
  }

  static buildCatalogSuggestionEntry(entry) {
    if (!entry || !entry.name) return null;

    const sourceSection = entry?.data?.sourceSection || entry?.parserMeta?.sourceSection || entry.kind || 'features';
    const parserMeta = this.mergeParserMeta(entry.parserMeta, entry?.data?.parserMeta, { sourceSection });
    const payload = entry.data && typeof entry.data === 'object'
      ? {
          ...entry.data,
          sourceSection: entry.data.sourceSection || sourceSection,
          parserMeta
        }
      : {
          name: entry.name,
          description: entry.description || '',
          activationType: entry.activationType || entry.actionType || null,
          actionType: entry.actionType || entry.activationType || null,
          sourceSection,
          parserMeta
        };

    if (entry.kind === 'equipment' && !payload.type) {
      payload.type = entry.type || 'item';
    }

    if (entry.derivedFromFeature === true) {
      payload.derivedFromFeature = true;
    }

    if (['actions', 'bonusActions', 'reactions'].includes(entry.kind)) {
      return this.normalizeCombatActionEntry(payload, { kind: entry.kind });
    }

    return payload;
  }

  static buildCatalogSuggestionBuckets(data = {}) {
    const catalog = Array.isArray(data.parsedEntryCatalog) && data.parsedEntryCatalog.length > 0
      ? data.parsedEntryCatalog
      : this.buildCharacterEntryCatalog(data);
    const buckets = {
      features: [],
      classFeatures: [],
      feats: [],
      speciesTraits: [],
      actions: [],
      bonusActions: [],
      reactions: [],
      equipment: [],
      spells: []
    };

    catalog.forEach((entry) => {
      if (!entry?.kind || !buckets[entry.kind]) return;
      const payload = this.buildCatalogSuggestionEntry(entry);
      if (payload) buckets[entry.kind].push(payload);
    });

    return buckets;
  }

  static mergeCharacterEntryCatalogs(...catalogs) {
    const merged = [];
    const indexByKey = new Map();

    catalogs.forEach((catalog) => {
      if (!Array.isArray(catalog)) return;

      catalog.forEach((entry, index) => {
        if (!entry || !entry.name) return;
        const kind = entry.kind || `entry-${index}`;
        const key = this.buildCharacterCatalogMergeKey(entry, kind)
          || entry.fingerprint
          || entry.id
          || this.buildParsedEntryFingerprint(entry.data || entry, kind);
        if (!indexByKey.has(key)) {
          indexByKey.set(key, merged.length);
          merged.push(entry);
          return;
        }
        const existingIndex = indexByKey.get(key);
        merged[existingIndex] = this.mergeCharacterCatalogEntry(merged[existingIndex], entry);
      });
    });

    return merged;
  }

  static buildParsedContentCoverage(parsedData = {}) {
    const catalog = this.getClaimedCharacterEntryCatalog(parsedData);
    const coveredLines = new Set();
    const coveredRawLines = new Set();
    const normalizeCoveredLine = (value) => String(value || '').trim().replace(/\s+/g, ' ');

    catalog.forEach((entry) => {
      const parserMeta = entry?.parserMeta || entry?.data?.parserMeta;
      if (Number.isInteger(parserMeta?.lineStart)) {
        const start = parserMeta.lineStart;
        const end = Number.isInteger(parserMeta?.lineEnd) ? parserMeta.lineEnd : start;
        for (let line = start; line <= end; line++) {
          coveredLines.add(line);
        }
      }

      String(parserMeta?.rawText || '')
        .split(/\r?\n/)
        .map(normalizeCoveredLine)
        .filter(Boolean)
        .forEach((line) => coveredRawLines.add(line));
    });

    return { coveredLines, coveredRawLines };
  }

  /**
   * Parse spell content from various formats
   */
  static parseSpell(text, context) {
    const spellPatterns = [
      // Standard spell format: "Name. Level school. Casting time..."
      /^([A-Z][a-z\s']+)\.\s*(\d+|cantrip)\w*\s+(\w+)\s*\.\s*(.+)/i,
      // Alternative: "Spell Name (Level X School)"
      /^([A-Z][a-z\s']+)\s*\(\s*(?:level\s*)?(\d+|cantrip)\s+(\w+)\s*\)\s*(.+)/i,
      // Simple: Just name and description starting with typical spell words
      /^([A-Z][a-z\s']+)\s*[.:]?\s*((?:Casting time|Range|Components|Duration|Attack roll|Save).+)/is
    ];

    for (const pattern of spellPatterns) {
      const match = text.match(pattern);
      if (match) {
        const [, name, level, school, description] = match;

        // Create spell data following FullSpellData interface from CanonicalSchemas.ts
        const spellData = {
          name: name.trim(),
          level: level.toLowerCase() === 'cantrip' ? 0 : parseInt(level) || 0,
          school: school ? school.toLowerCase() : 'universal',
          description: description.trim(),
          castingTime: '1 action', // Default values
          range: 'Self',
          components: 'V, S',
          duration: 'Instantaneous'
        };

        // Extract additional details from description
        this.extractSpellDetails(spellData, description);

        return {
          type: 'spell',
          confidence: 0.85,
          data: spellData,
          suggestions: [`Add "${spellData.name}" to spells list`]
        };
      }
    }

    // Check for spell-like keywords
    if (/\b(casting time|components|duration|concentration|spell(?:casting)?|cantrip)\b/i.test(text)) {
      return {
        type: 'spell',
        confidence: 0.6,
        data: { name: 'Unknown Spell', description: text },
        suggestions: ['Detected spell-like content - please review and edit']
      };
    }

    return { type: 'unknown', confidence: 0 };
  }

  /**
   * Parse standalone ability score lines into an abilities object
   * Accepts formats like: "STR 16 DEX 14 CON 12 INT 10 WIS 8 CHA 18"
   * or multi-line bullet/table variants.
   * @date_of_change 2025-10-12 - Implemented to support Smart Paste abilities
   */
  static parseAbilityScore(text, context) {
    const abilRegexGlobal = /(STR|DEX|CON|INT|WIS|CHA|Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma)\b\s*:?-?\s*(\d{1,2})/gi;
    let m; const found = {};
    while ((m = abilRegexGlobal.exec(text)) !== null) {
      const key = this.normalizeAbilityName(m[1]);
      const val = parseInt(m[2]);
      if (!Number.isNaN(val)) found[key] = val;
    }

    const count = Object.keys(found).length;
    if (count >= 3) {
      // @date_of_change 2025-10-15 - Reduce abilities confidence when paste looks like a full stat block
      // This prevents standalone abilities from overshadowing complete character/monster detection.
      const statblockSignals = /(armor\s*class|hit\s*points|\bHP\b|challenge\s*rating|legendary\s*actions|level\s+\d+|\bclass\b|background)/i;
      const looksLikeStatblock = statblockSignals.test(text);
      const confidence = count >= 5 ? (looksLikeStatblock ? 0.6 : 0.85) : (looksLikeStatblock ? 0.5 : 0.65);
      return {
        type: 'abilities',
        confidence,
        data: found,
        suggestions: ['Update ability scores']
      };
    }

    return { type: 'unknown', confidence: 0 };
  }

  /**
   * Parse a simple list (bulleted, numbered, or comma-separated)
   * @date_of_change 2025-10-12 - Implemented to classify narrative lists safely
   */
  static parseSimpleList(text, context) {
    // @date_of_change 2025-10-16 - Support bracketed quoted arrays and equipment/features/feats/traits hinting
    // Bulleted/numbered lines
    const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const bulletItems = lines
      .map(l => l.replace(/^(?:[-*•\u2022]\s+|\d+[).]\s+)/, '').trim())
      .filter(l => l.length > 0 && l.split(' ').length <= 20); // allow slightly longer

    // Try to extract items from a bracketed list: ['Foo', 'Bar'] or ["Foo", "Bar"]
    let items = [];
    const bracketMatch = text.match(/\[([\s\S]*?)\]/);
    if (bracketMatch) {
      const inner = bracketMatch[1];
      const singleQuoted = Array.from(inner.matchAll(/'([^']+)'/g)).map(m => m[1].trim());
      const doubleQuoted = Array.from(inner.matchAll(/"([^"]+)"/g)).map(m => m[1].trim());
      if (singleQuoted.length > 0) items = singleQuoted;
      else if (doubleQuoted.length > 0) items = doubleQuoted;
      else {
        // Fallback: split by comma (best effort)
        items = inner.split(',').map(s => s.replace(/^['"]|['"]$/g, '').trim()).filter(Boolean);
      }
    }

    // If no bracket form, try commas on a single line
    if (items.length === 0 && lines.length === 1 && /,/.test(lines[0])) {
      items = lines[0].split(',').map(s => s.trim()).filter(Boolean);
    }

    // Merge with bullet-derived items if bracket/commas produced too few
    if (items.length < 2 && bulletItems.length >= 2) {
      items = bulletItems;
    }

    // Reject if looks like a stat block header
    // date_of_change: 2026-02-25
    // rule_reference: D&D 5e ability blocks and AC/HP shorthands are character stat indicators, not narrative lists.
    if (/(armor class|\bAC\b|hit points|\bHP\b|challenge rating|spell|attack|saving throw|\bSTR\b|\bDEX\b|\bCON\b|\bINT\b|\bWIS\b|\bCHA\b)/i.test(text)) {
      return { type: 'unknown', confidence: 0 };
    }

    if (items.length >= 2) {
      // Heuristic hinting
      const looksLikeEquipment = /(magic\s*items?|equipment|gear|inventory)/i.test(text);
      const looksLikeClassFeatures = /class\s+features/i.test(text);
      const looksLikeFeats = /\bfeats?\b/i.test(text);
      const looksLikeSpeciesTraits = /(species|racial)\s+traits/i.test(text);
      let meta = {};
      if (looksLikeEquipment) meta = { category: 'equipment' };
      else if (looksLikeClassFeatures) meta = { category: 'class_features' };
      else if (looksLikeFeats) meta = { category: 'feats' };
      else if (looksLikeSpeciesTraits) meta = { category: 'species_traits' };
      const data = Object.keys(meta).length ? { items, meta } : { items };
      return {
        type: 'list',
        confidence: items.length >= 3 ? 0.7 : 0.55,
        data,
        suggestions: ['Append items to appropriate field']
      };
    }

    return { type: 'unknown', confidence: 0 };
  }

  static parseStructuredEntryLine(raw, options = {}) {
    const {
      allowBulletSeparator = false,
      allowParenDescription = false,
      fallbackShortHeaderMaxLength = null,
      stripNumberPrefix = false,
      returnBareLine = true
    } = options;
    const prefixRe = stripNumberPrefix
      ? /^[-*•\u2022]\s+|\d+[).]\s+/
      : /^[-*•\u2022]\s+/;
    const cleaned = String(raw || '').trim().replace(prefixRe, '').trim();
    if (!cleaned) return null;

    const subtypeHeaderRe = /^[\p{Lu}][\p{L}'’\- ]+\s*[–—-]\s*[\p{Lu}][\p{L}'’\- ]+(?:\s*\([^)]*\))?\s*$/u;
    if (subtypeHeaderRe.test(cleaned)) return { name: cleaned, description: '' };

    const mColon = cleaned.match(/^([^:]+):\s*(.+)$/);
    if (mColon) return { name: mColon[1].trim(), description: mColon[2].trim() };

    if (allowBulletSeparator) {
      const bulletParts = cleaned.split(/[•\u2022]+/).map(s => s.trim()).filter(Boolean);
      if (bulletParts.length >= 2) {
        const name = bulletParts.shift();
        return { name: name.trim(), description: bulletParts.join(' • ').trim() };
      }
    }

    const mDash = cleaned.match(/^(.+?)\s*[–—-]\s+(.+)$/);
    if (mDash) return { name: mDash[1].trim(), description: mDash[2].trim() };

    if (allowParenDescription) {
      const mParen = cleaned.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
      if (mParen) return { name: mParen[1].trim(), description: mParen[2].trim() };
    }

    if (
      Number.isFinite(fallbackShortHeaderMaxLength) &&
      cleaned.length <= fallbackShortHeaderMaxLength &&
      /^[A-Z]/.test(cleaned)
    ) {
      return { name: cleaned, description: '' };
    }

    return returnBareLine ? { name: cleaned, description: '' } : null;
  }

  static looksLikeStructuredEntryHeader(raw, parsed = null, options = {}) {
    const {
      isBullet = false,
      maxLength = 120,
      allowExplicitDescription = false,
      allowParentheticalUsageHeader = true,
      allowUpperHeader = true,
      rejectSentenceLike = true,
      rejectParenThenText = false,
      rejectTerminalPunctuation = true
    } = options;
    const textValue = String(raw || '').trim();
    if (!textValue || isBullet || textValue.length > maxLength) return false;
    if (rejectTerminalPunctuation && /[.!?]$/.test(textValue)) return false;
    if (rejectParenThenText && /\([^)]+\)\s+[a-z]/.test(textValue)) return false;
    if (rejectSentenceLike && /\b(?:you|your|when|while|whenever|choose|gain|once|twice|after|before|if|then|until|can|may|must|creature|target|attack|hit)\b/i.test(textValue) && /\s/.test(textValue)) {
      return false;
    }

    const subtypeHeaderRe = /^[\p{Lu}][\p{L}'’\- ]+\s*[–—-]\s*[\p{Lu}][\p{L}'’\- ]+(?:\s*\([^)]*\))?\s*$/u;
    const titleHeaderRe = /^[\p{Lu}][\p{L}'’-]+(?:\s+(?:[\p{Lu}][\p{L}'’-]+|of|the|and|or|to|for|in|on|at|a|an|per|with|without|from))*(?:\s*\([^)]*\))?\s*$/u;
    const upperHeaderRe = /^[\p{Lu}\d][\p{Lu}\d'’ -]+(?:\s*\([^)]*\))?\s*$/u;
    const usageOnlyHeaderRe = /^[\p{Lu}][\p{L}'’\- ]+\s*\([^)]*\)\s*$/u;

    if (parsed?.description && !allowExplicitDescription) {
      if (!(allowParentheticalUsageHeader && usageOnlyHeaderRe.test(textValue))) {
        return false;
      }
    }

    if (subtypeHeaderRe.test(textValue)) return true;
    if (titleHeaderRe.test(textValue)) return true;
    if (allowUpperHeader && upperHeaderRe.test(textValue)) return true;
    return allowParentheticalUsageHeader && usageOnlyHeaderRe.test(textValue);
  }

  static buildStructuredSectionEntry(parsed, { baseMeta = {}, lineNumber, rawText, extraFields = {} } = {}) {
    if (!parsed || !parsed.name) return null;
    return {
      name: parsed.name,
      description: parsed.description || '',
      ...extraFields,
      parserMeta: this.buildParserMeta({
        ...baseMeta,
        lineStart: lineNumber,
        lineEnd: lineNumber,
        rawText
      })
    };
  }

  static appendStructuredSectionEntryLine(entry, lineNumber, rawText, cleanedText) {
    if (!entry) return entry;
    entry.description = entry.description
      ? `${entry.description}\n${cleanedText}`
      : cleanedText;
    entry.parserMeta = this.mergeParserMeta(entry.parserMeta, {
      lineEnd: lineNumber,
      rawText: entry.parserMeta?.rawText ? `${entry.parserMeta.rawText}\n${rawText}` : rawText
    });
    return entry;
  }

  static collectGroupedSectionEntries(lines, options = {}) {
    const {
      startIndex = 0,
      lineOffset = 0,
      stopRe = null,
      headerRe = null,
      breakOnBlankCount = null,
      handleLine
    } = options;
    const state = {
      entries: [],
      currentEntry: null
    };
    const api = {
      getCurrentEntry: () => state.currentEntry,
      getEntries: () => state.entries,
      setCurrentEntry: (entry) => {
        state.currentEntry = entry || null;
      },
      flushEntry: () => {
        if (!state.currentEntry) return;
        state.entries.push({ ...state.currentEntry });
        state.currentEntry = null;
      },
      pushEntry: (entry) => {
        if (entry) state.entries.push(entry);
      },
      appendToCurrent: (lineNumber, rawText, cleanedText) => {
        state.currentEntry = this.appendStructuredSectionEntryLine(
          state.currentEntry,
          lineNumber,
          rawText,
          cleanedText
        );
        return state.currentEntry;
      },
      appendToLastEntry: (lineNumber, rawText, cleanedText) => {
        if (!state.entries.length) return null;
        const lastIndex = state.entries.length - 1;
        state.entries[lastIndex] = this.appendStructuredSectionEntryLine(
          state.entries[lastIndex],
          lineNumber,
          rawText,
          cleanedText
        );
        return state.entries[lastIndex];
      }
    };

    let blankCount = 0;
    for (let i = startIndex; i < lines.length; i++) {
      const raw = lines[i] || '';
      const trimmed = raw.trim();

      if (!trimmed) {
        blankCount += 1;
        api.flushEntry();
        if (breakOnBlankCount && blankCount >= breakOnBlankCount) break;
        continue;
      }

      blankCount = 0;
      if (stopRe && stopRe.test(trimmed) && !(headerRe && headerRe.test(trimmed))) {
        api.flushEntry();
        break;
      }

      handleLine?.({
        raw,
        trimmed,
        lineNumber: i + 1 + lineOffset,
        lineIndex: i
      }, api);
    }

    api.flushEntry();
    return state.entries;
  }

  static collectStructuredSectionRecords(lines, options = {}) {
    const {
      startIndex = 0,
      lineOffset = 0,
      stopRe = null,
      headerRe = null,
      breakOnBlankCount = null,
      parseLine,
      createEntry,
      createHeaderEntry = null,
      cleanLine = ({ trimmed }) => trimmed.replace(/^[-*•\u2022]\s+|\d+[).]\s+/, '').trim(),
      recordLine = null,
      isHeaderLine = null,
      handleSpecialLine = null,
      appendToCurrentByDefault = true,
      appendBulletsToLastEntry = false
    } = options;

    return this.collectGroupedSectionEntries(lines, {
      startIndex,
      lineOffset,
      stopRe,
      headerRe,
      breakOnBlankCount,
      handleLine: ({ raw, trimmed, lineNumber }, api) => {
        const isBullet = /^\s*[-*•\u2022]\s+/.test(raw);
        const cleaned = cleanLine({ raw, trimmed, lineNumber, isBullet });
        if (!cleaned) return;

        recordLine?.({ raw, trimmed, cleaned, lineNumber, isBullet });

        const parsed = parseLine ? parseLine(trimmed, { raw, cleaned, lineNumber, isBullet }) : null;
        const context = {
          raw,
          trimmed,
          cleaned,
          lineNumber,
          isBullet,
          parsed,
          current: api.getCurrentEntry(),
          entries: api.getEntries()
        };

        if (handleSpecialLine?.(context, api)) {
          return;
        }

        if (isHeaderLine?.(context)) {
          api.flushEntry();
          const headerParsed = parsed || { name: cleaned, description: '' };
          const entry = createHeaderEntry
            ? createHeaderEntry({ ...context, parsed: headerParsed }, api)
            : createEntry?.(headerParsed, lineNumber, raw, context);
          if (entry) {
            api.setCurrentEntry(entry);
          }
          return;
        }

        if (appendToCurrentByDefault && api.getCurrentEntry()) {
          api.appendToCurrent(lineNumber, raw, cleaned);
          return;
        }

        if (appendBulletsToLastEntry && isBullet && api.getEntries().length) {
          api.appendToLastEntry(lineNumber, raw, cleaned);
          return;
        }

        const entry = createEntry?.(parsed || { name: cleaned, description: '' }, lineNumber, raw, context);
        if (entry) {
          api.pushEntry(entry);
        }
      }
    });
  }

  static buildStructuredSectionParsedEntries(entriesByKind = {}) {
    return Object.entries(entriesByKind).flatMap(([kind, entries]) => (
      Array.isArray(entries)
        ? entries
          .filter((entry) => entry && entry.name)
          .map((entry) => ({ kind, entry }))
        : []
    ));
  }

  /**
   * Parse common character-sheet sections into canonical list/text fields.
   *
   * date_of_change: 2026-02-25
   * rule_reference: SmartPaste should not throw on partial/\"garbage\" pastes; section extraction must be
   * conservative and only parse explicit headers (Features/Feats/Species Traits) to support extras dedup.
   */
  // date_of_change: 2026-02-25
  // rule_reference: Parse Features, Feats, Species Traits sections into structured arrays of {name, description}
  // objects so the CharacterSheetEditor can display both name and description for each entry.
  // Uses sub-header + description grouping (same pattern as parseCombatSections): a line that starts with
  // an uppercase word is a new entry name; subsequent bullet/indented lines are its description.
  static parseCharacterSections(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const lines = String(text || '').split(/\r?\n/);
    if (!lines.length) return {};

    const out = {};
    const parsedEntries = [];
    const sections = [
      { listKey: 'featuresList', textKey: 'classFeaturesText', sourceSection: 'features', headerRe: /^\s*(?:passive(?:\s*\/?\s*triggered)?\s+features?|class\s*features?|features?)\s*:?\s*(.*)$/i },
      { listKey: 'featsList', textKey: 'featsText', sourceSection: 'feats', headerRe: /^\s*feats?\s*:?\s*(.*)$/i },
      { listKey: 'speciesTraitsList', textKey: 'speciesTraitsText', sourceSection: 'speciesTraits', headerRe: /^\s*(?:species|racial)\s*traits?\s*:?\s*(.*)$/i }
    ];

    // Section headers that signal end of a features/feats block.
    // Uses end-of-line anchor to prevent "Action Surge" matching "actions?".
    const stopRe = /^\s*(?:passive(?:\s*\/?\s*triggered)?\s+features?|class\s*features?|features?|feats?|(?:species|racial)\s*traits?|actions?|bonus\s*actions?|reactions?|legendary\s*actions?|equipment|inventory|gear|(?:equipped\s+)?weapons?|armor|magic\s*items?(?:\s*&\s*gear)?|spells?|senses|languages|saving\s*throws|skills?|ability\s*scores?|combat\s*stats|personality|backstory|appearance|proficiencies|allies)\s*:?\s*$/i;
    const parseSectionEntryLine = (raw) => this.parseStructuredEntryLine(raw, {
      allowParenDescription: true,
      stripNumberPrefix: true
    });

    for (let i = 0; i < lines.length; i++) {
      const trimmed = (lines[i] || '').trim();
      if (!trimmed) continue;
      if (/^features\s*,\s*traits\s*,\s*special\s+abilities\b/i.test(trimmed)) continue;

      const section = sections.find(s => s.headerRe.test(trimmed));
      if (!section) continue;

      const m = trimmed.match(section.headerRe);
      const inline = (m && typeof m[1] === 'string') ? m[1].trim() : '';
      const textLines = [];
      const baseMeta = {
        sourceParser: 'parseCharacterSections',
        sourceSection: section.sourceSection,
        parseConfidence: 0.82,
        reviewReason: `Detected in ${(lines[i] || '').trim() || section.sourceSection}`
      };

      const createEntry = (parsed, startLine, rawText) => {
        return this.buildStructuredSectionEntry(parsed, {
          baseMeta,
          lineNumber: startLine,
          rawText
        });
      };

      // Handle inline content after colon (e.g., "Features: Strike of the Giants – Storm")
      const inlineEntries = [];
      if (inline) {
        const inlineItems = inline.split(/[,;]+/).map(s => s.trim()).filter(Boolean);
        for (const it of inlineItems) {
          const parsed = parseSectionEntryLine(it);
          const entry = createEntry(parsed, i + 1 + lineOffset, it);
          if (entry) inlineEntries.push(entry);
        }
        textLines.push(inline);
      }

      const entries = [
        ...inlineEntries,
        ...this.collectStructuredSectionRecords(lines, {
          startIndex: i + 1,
          lineOffset,
          stopRe,
          headerRe: section.headerRe,
          breakOnBlankCount: 2,
          parseLine: (sectionLine) => parseSectionEntryLine(sectionLine),
          createEntry: (parsed, lineNumber, raw) => createEntry(parsed, lineNumber, raw),
          recordLine: ({ cleaned }) => {
            textLines.push(cleaned);
          },
          isHeaderLine: ({ cleaned, parsed, isBullet }) => this.looksLikeStructuredEntryHeader(cleaned, parsed, {
            isBullet,
            maxLength: 120
          }),
          handleSpecialLine: ({ cleaned, current, lineNumber, raw }, api) => {
            if (cleaned.endsWith(':') && current) {
              api.appendToCurrent(lineNumber, raw, cleaned);
              return true;
            }
            return false;
          }
        })
      ];

      // Produce outputs: structured list + raw text
      const nameList = entries.map(e => e.name).filter(Boolean);
      if (nameList.length) out[section.listKey] = Array.from(new Set(nameList));
      if (textLines.length) out[section.textKey] = textLines.join('\n');
      // Store structured entries with descriptions for features/feats arrays
      if (entries.length) {
        out[section.listKey + 'Structured'] = entries;
        parsedEntries.push(...this.buildStructuredSectionParsedEntries({
          [section.sourceSection]: entries
        }));
      }
    }

    if (parsedEntries.length) out.parsedEntries = parsedEntries;
    return out;
  }

  // FastCharacter uses one broad features container with named class/species subheaders.
  // Preserve each bullet as its own editable sheet entry instead of collapsing the block.
  static parseFastCharacterSections(text) {
    const lines = String(text || '').split(/\r?\n/);
    const containerIndex = lines.findIndex((line) => /^\s*features\s*,\s*traits\s*,\s*special\s+abilities\b/i.test(line));
    if (containerIndex === -1) return null;

    const buckets = { classFeatures: [], feats: [], speciesTraits: [] };
    const featCandidates = [];
    const classHeaderRe = /^\s*.+?\s+class\s+features(?:\s*\[[^\]]+\])?\s*$/i;
    const speciesHeaderRe = /^\s*.+?\s+traits(?:\s*\[[^\]]+\])?\s*$/i;
    const boundaryRe = /^\s*(?:spellcasting|prepared\s+spells|equipment(?:\s*&\s*treasure)?|magic\s+items|coins\s*&\s*gems)\b/i;
    const proficiencyHeaderRe = /^\s*proficiencies\s*&\s*languages\s*$/i;
    let activeKind = null;

    const buildEntry = (rawValue, kind, lineNumber, description = '') => {
      const cleaned = String(rawValue || '')
        .replace(/^\s*[-*•\u2022]\s*/, '')
        .replace(/\s*\[[^\]]+\]\s*$/, '')
        .trim();
      if (!cleaned) return null;
      const parentheticalMatch = cleaned.match(/^(.+?)\s*\((.+)\)\s*$/);
      const name = parentheticalMatch ? parentheticalMatch[1].trim() : cleaned;
      const parsedDescription = description || (parentheticalMatch ? parentheticalMatch[2].trim() : '');
      return this.buildParsedAbilityEntry({
        name,
        description: parsedDescription,
        sourceSection: kind,
        parserMeta: this.buildParserMeta({
          sourceParser: 'parseFastCharacterSections',
          sourceSection: kind,
          lineStart: lineNumber,
          lineEnd: lineNumber,
          rawText: lines[lineNumber - 1],
          parseConfidence: 0.94,
          reviewReason: `Read from the pasted ${kind === 'classFeatures' ? 'class features' : kind === 'speciesTraits' ? 'species traits' : 'feats'} section`,
          knowledgeSource: 'provided'
        })
      }, { sourceSection: kind });
    };

    for (let index = containerIndex + 1; index < lines.length; index += 1) {
      const raw = lines[index] || '';
      const trimmed = raw.trim();
      if (!trimmed) continue;
      if (boundaryRe.test(trimmed)) break;

      if (classHeaderRe.test(trimmed)) {
        activeKind = 'classFeatures';
        continue;
      }
      if (speciesHeaderRe.test(trimmed) && !/^\s*(?:features|special)\b/i.test(trimmed)) {
        activeKind = 'speciesTraits';
        continue;
      }
      if (proficiencyHeaderRe.test(trimmed)) {
        activeKind = null;
        continue;
      }

      const isBullet = /^\s*[-*•\u2022]/.test(raw);
      if (isBullet && activeKind) {
        const entry = buildEntry(trimmed, activeKind, index + 1);
        if (entry) buckets[activeKind].push(entry);
        continue;
      }

      if (!isBullet) {
        if (/\b(?:spell|weapon)\s+attack\s*:/i.test(trimmed)) continue;
        const titledLine = trimmed.match(/^(.+?)\s*:\s*(.+)$/);
        if (!titledLine) continue;
        const candidate = buildEntry(titledLine[1], 'feats', index + 1, titledLine[2].trim());
        if (candidate) featCandidates.push(candidate);
      }
    }

    if (featCandidates.length) {
      const categorized = this.categorizeFeatures(featCandidates);
      buckets.feats.push(...categorized.feats);
    }

    const parsedEntries = this.buildStructuredSectionParsedEntries(buckets);
    if (!parsedEntries.length) return null;
    return { ...buckets, parsedEntries };
  }

  static isLikelyMajorCharacterHeader(line) {
    return /^\s*(?:passive(?:\s*\/?\s*triggered)?\s+features?|class\s*features?|features?|feats?|(?:species|racial)\s*traits?|actions?|bonus\s*actions?|reactions?|legendary\s*actions?|equipment|inventory|gear|(?:equipped\s+)?weapons?|armor|magic\s*items?(?:\s*&\s*gear)?|spells?|spellcasting|senses|languages|saving\s*throws|skills?|ability\s*scores?|combat\s*stats|personality|backstory|appearance|proficiencies|allies|relationships?|biography|notes?)(?:\s*\[[^\]]+\])?\s*:?\s*$/i.test(String(line || '').trim());
  }

  static looksLikeLooseCharacterSummaryLine(line) {
    const trimmed = String(line || '').trim();
    if (!trimmed) return false;

    const tokenCount = trimmed
      .split(/[,;]+/)
      .map((token) => token.trim())
      .filter(Boolean)
      .length;

    if (tokenCount < 4) return false;

    const summarySignals = [
      /\b(?:level|lvl\.?)\s*\d{1,2}\b/i,
      /\b(?:fighter|wizard|rogue|cleric|ranger|barbarian|bard|druid|monk|paladin|sorcerer|warlock|artificer|blood\s*hunter|mystic)\b/i,
      /\b(?:str(?:ength)?|dex(?:terity)?|con(?:stitution)?|int(?:elligence)?|wis(?:dom)?|cha(?:risma)?)\b\s*:?\s*\d{1,2}\b/i,
      /\b(?:\d+\s*(?:hp|hit\s*points?|ac|armor\s*class)|(?:hp|hit\s*points?|ac|armor\s*class)\s*:?\s*\d+)\b/i,
      /\b(?:armor|shield|mail|plate|chain(?:mail| shirt)?|staff|sword|axe|bow|crossbow|dagger|wand|rod|orb|boots?|cloak|ring|amulet|helm(?:et)?|gauntlets?|bracers?|belt|pearl|spellbook|tome)\b/i
    ];

    return summarySignals.filter((pattern) => pattern.test(trimmed)).length >= 2;
  }

  static isSpellMetadataLikeLine(line) {
    const trimmed = String(line || '').trim();
    if (!trimmed) return false;
    if (this.looksLikeLooseCharacterSummaryLine(trimmed)) return false;
    if (/^\s*(?:cantrips?|prepared\s*spells?|at\s*will|spell\s*slots|spellcasting\s*ability|spell\s*save\s*dc|spell\s*attack(?:\s*(?:bonus|modifier))?|level\s*:\s*slots)\b/i.test(trimmed)) return true;
    if (/^\s*(?:casting\s*time|range|components?|duration|school|save|saving\s*throw|attack\s*roll|ritual|concentration|materials?)\s*:?\s*/i.test(trimmed)) return true;
    if (/\b(?:casting\s*time|range|components?|duration|school|save|saving\s*throw|level\s*text|spell\s*attack(?:\s*(?:bonus|modifier))?)\b\s*:?\s*/i.test(trimmed)) return true;
    if (/^\s*(?:\d\s*(?:st|nd|rd|th)[-\s]*level\b|cantrip\b)/i.test(trimmed)) return true;
    if (/\b(?:abjuration|conjuration|divination|enchantment|evocation|illusion|necromancy|transmutation)\b/i.test(trimmed) && /\b(?:cantrip|level)\b/i.test(trimmed)) return true;
    if (/\b(?:casting\s*time|duration|components?|concentration|at\s+higher\s+levels?)\b/i.test(trimmed)) return true;
    return false;
  }

  static looksLikeSpellTitleLine(line) {
    const trimmed = String(line || '').trim();
    if (!trimmed) return false;
    if (trimmed.length > 80) return false;
    if (this.isLikelyMajorCharacterHeader(trimmed)) return false;
    if (this.isSpellMetadataLikeLine(trimmed)) return false;
    if (/[:]/.test(trimmed)) return false;
    if (/^[•*\-\d]/.test(trimmed)) return false;
    return /^[A-Z][A-Za-z'’\-(),/ ]+$/.test(trimmed);
  }

  static hasNearbySpellContext(lines = [], index = 0, claimedLines = new Set()) {
    for (let offset = -2; offset <= 2; offset += 1) {
      if (offset === 0) continue;
      const lineIndex = index + offset;
      if (lineIndex < 0 || lineIndex >= lines.length) continue;
      if (claimedLines.has(lineIndex + 1)) continue;
      const nearby = String(lines[lineIndex] || '').trim();
      if (this.isSpellMetadataLikeLine(nearby)) return true;
    }
    return false;
  }

  static buildSpellKeywordClaims(text, existingClaimedLines = new Set()) {
    const lines = String(text || '').split(/\r?\n/);
    if (!lines.length) return [];

    const claimedLines = new Set(existingClaimedLines instanceof Set ? [...existingClaimedLines] : []);
    const claims = [];

    const isClaimed = (lineIndex) => claimedLines.has(lineIndex + 1);
    const markClaim = (startIndex, endIndex) => {
      for (let index = startIndex; index <= endIndex; index += 1) {
        claimedLines.add(index + 1);
      }
    };

    for (let index = 0; index < lines.length; index += 1) {
      if (isClaimed(index)) continue;

      const trimmed = String(lines[index] || '').trim();
      if (!this.isSpellMetadataLikeLine(trimmed)) continue;

      let startIndex = index;
      let endIndex = index;
      let metadataCount = 0;

      for (let backIndex = index - 1; backIndex >= Math.max(0, index - 3); backIndex -= 1) {
        if (isClaimed(backIndex)) break;
        const backTrimmed = String(lines[backIndex] || '').trim();
        if (!backTrimmed) {
          startIndex = backIndex;
          continue;
        }
        if (this.looksLikeSpellTitleLine(backTrimmed) || this.isSpellMetadataLikeLine(backTrimmed)) {
          startIndex = backIndex;
          continue;
        }
        break;
      }

      for (let forwardIndex = startIndex; forwardIndex < lines.length; forwardIndex += 1) {
        if (isClaimed(forwardIndex) && forwardIndex !== startIndex) break;
        const forwardTrimmed = String(lines[forwardIndex] || '').trim();

        if (!forwardTrimmed) {
          endIndex = forwardIndex;
          const nextTrimmed = String(lines[forwardIndex + 1] || '').trim();
          if (!nextTrimmed || this.isLikelyMajorCharacterHeader(nextTrimmed)) break;
          continue;
        }

        if (forwardIndex > index && this.isLikelyMajorCharacterHeader(forwardTrimmed)) {
          break;
        }

        if (
          forwardIndex > startIndex
          && this.looksLikeSpellTitleLine(forwardTrimmed)
          && this.hasNearbySpellContext(lines, forwardIndex, claimedLines)
        ) {
          break;
        }

        if (this.isSpellMetadataLikeLine(forwardTrimmed)) {
          metadataCount += 1;
          endIndex = forwardIndex;
          continue;
        }

        if (this.looksLikeSpellTitleLine(forwardTrimmed) || metadataCount > 0) {
          endIndex = forwardIndex;
          continue;
        }

        break;
      }

      if (metadataCount === 0) continue;

      const claimText = lines.slice(startIndex, endIndex + 1).join('\n').trim();
      if (!claimText) continue;

      claims.push({
        kind: 'spells',
        startLine: startIndex + 1,
        endLine: endIndex + 1,
        text: claimText
      });
      markClaim(startIndex, endIndex);
      index = endIndex;
    }

    return claims;
  }

  static buildCharacterSectionClaims(text) {
    const lines = String(text || '').split(/\r?\n/);
    if (!lines.length) return [];

    const explicitHeaderDefinitions = [
      {
        kind: 'characterSections',
        re: /^\s*(?:class\s*features?|features?|feats?|(?:species|racial)\s*traits?)\s*:?.*$/i
      },
      {
        kind: 'abilitiesTraits',
        re: /^\s*(?:abilities\s*(?:&|and)\s*traits|passive\s*(?:\/|and)?\s*triggered\s*features?)\s*:?.*$/i
      },
      {
        kind: 'combat',
        re: /^\s*(?:actions|bonus\s*actions?|reactions?)\s*:?\s*$/i
      },
      {
        kind: 'equipment',
        re: /^\s*(?:(?:equipped\s+)?weapons?|armor(?!\s*class\b)(?:\s*&\s*shields?)?|equipment(?:\s*&\s*treasure)?|inventory|(?:carried\s+)?gear|magic\s*items?(?:\s*&\s*gear)?|potions?)(?:\s*\[[^\]]+\])?(?:\s*:\s*.*)?\s*$/i
      },
      {
        kind: 'spells',
        re: /^\s*(?:spellcasting|spells?)(?:\s*\[[^\]]+\])?\s*:?\s*$/i
      },
      {
        kind: 'biography',
        re: /^\s*(?:personality(?:\s+traits?)?|ideals?|bonds?|flaws?|backstory|history|background\s+story|physical\s+description|appearance|looks?|notes?|allies(?:\s*(?:&|and)\s*(?:organizations?|foes))?|relationships?)\s*:?.*$/i
      }
    ];
    const spellLineRe = /^\s*(?:cantrips?|prepared\s*spells?(?:\s+include[sd]?(?:\s*\([^)]*\))?)?|at\s*will|spell\s*slots|spellcasting\s*ability|spell\s*save\s*dc|spell\s*attack(?:\s*(?:modifier|bonus))?|level\s*:\s*slots\s*=|\d\s*(?:st|nd|rd|th)\s*level)\b/i;
    const getExplicitHeaderKind = (value) => {
      const trimmed = String(value || '').trim();
      if (!trimmed) return null;
      const matchedDefinition = explicitHeaderDefinitions.find((definition) => definition.re.test(trimmed));
      return matchedDefinition ? matchedDefinition.kind : null;
    };
    const claims = [];
    const buildClaimedLineSet = (claimList = []) => {
      const claimed = new Set();
      claimList.forEach((claim) => {
        const start = Number(claim?.startLine || 0);
        const end = Number(claim?.endLine || start);
        if (!Number.isFinite(start) || !Number.isFinite(end)) return;
        for (let line = start; line <= end; line += 1) {
          claimed.add(line);
        }
      });
      return claimed;
    };
    const sortClaims = (claimList = []) => claimList
      .filter((claim) => String(claim?.text || '').trim())
      .sort((left, right) => (left.startLine - right.startLine) || (left.endLine - right.endLine));

    let lineIndex = 0;
    while (lineIndex < lines.length) {
      const trimmed = String(lines[lineIndex] || '').trim();
      if (!trimmed) {
        lineIndex += 1;
        continue;
      }

      const explicitKind = getExplicitHeaderKind(trimmed);
      if (explicitKind) {
        let endIndex = lineIndex;
        for (let nextIndex = lineIndex + 1; nextIndex < lines.length; nextIndex += 1) {
          const nextTrimmed = String(lines[nextIndex] || '').trim();
          if (!nextTrimmed) {
            endIndex = nextIndex;
            continue;
          }
          if (getExplicitHeaderKind(nextTrimmed)) break;
          endIndex = nextIndex;
        }

        claims.push({
          kind: explicitKind,
          startLine: lineIndex + 1,
          endLine: endIndex + 1,
          text: lines.slice(lineIndex, endIndex + 1).join('\n')
        });
        lineIndex = endIndex + 1;
        continue;
      }

      if (spellLineRe.test(trimmed)) {
        let endIndex = lineIndex;
        for (let nextIndex = lineIndex + 1; nextIndex < lines.length; nextIndex += 1) {
          const nextTrimmed = String(lines[nextIndex] || '').trim();
          if (!nextTrimmed) {
            endIndex = nextIndex;
            continue;
          }
          if (getExplicitHeaderKind(nextTrimmed)) break;
          if (!spellLineRe.test(nextTrimmed)) break;
          endIndex = nextIndex;
        }

        claims.push({
          kind: 'spells',
          startLine: lineIndex + 1,
          endLine: endIndex + 1,
          text: lines.slice(lineIndex, endIndex + 1).join('\n')
        });
        lineIndex = endIndex + 1;
        continue;
      }

      lineIndex += 1;
    }

    const claimedLines = buildClaimedLineSet(claims);
    const spellKeywordClaims = this.buildSpellKeywordClaims(text, claimedLines);
    return sortClaims([...claims, ...spellKeywordClaims]);
  }

  static buildClaimSeparatedText(text, claims = []) {
    const lines = String(text || '').split(/\r?\n/);
    if (!lines.length || !Array.isArray(claims) || claims.length === 0) {
      return String(text || '');
    }

    const maskedLines = [...lines];
    claims.forEach((claim) => {
      const startIndex = Math.max(0, Number(claim?.startLine || 1) - 1);
      const endIndex = Math.min(maskedLines.length - 1, Number(claim?.endLine || claim?.startLine || 1) - 1);
      for (let index = startIndex; index <= endIndex; index += 1) {
        maskedLines[index] = '';
      }
    });

    return maskedLines.join('\n');
  }

  // date_of_change: 2026-04-24
  // rule_reference: SmartPaste intake must recover core character fields from loose one-line summaries
  // such as "Rogar, fighter, level 11, Strength 18, 134 HP, 18 AC, chainmail, longsword" even when
  // the user does not provide formal labels or section headers.
  static parseLooseCharacterSummary(text) {
    const source = String(text || '').trim();
    if (!source) return null;

    const tokens = source
      .split(/[\n,;]+/)
      .map((rawValue, index) => ({
        index,
        rawValue: String(rawValue || '').trim(),
        value: String(rawValue || '').trim().replace(/\s+/g, ' ')
      }))
      .filter((token) => token.value && token.value !== '__SMARTPASTE_CLAIM__');

    if (tokens.length < 2) return null;

    const classPattern = '(artificer|barbarian|bard|blood\\s*hunter|cleric|druid|fighter|monk|paladin|ranger|rogue|sorcerer|warlock|wizard|mystic)';
    const speciesPattern = '(human|elf|dwarf|halfling|dragonborn|gnome|half-elf|half-orc|tiefling|vampire|aasimar|genasi|goliath|tabaxi|kenku|firbolg|lizardfolk|changeling|shifter|kalashtar|warforged)';
    const classTokenRe = new RegExp(`^${classPattern}$`, 'i');
    const classWithLevelRe = new RegExp(`^${classPattern}(?:\\s*(?:level|lvl\\.?))?\\s*(\\d{1,2})$`, 'i');
    const levelThenClassRe = new RegExp(`^(?:level|lvl\\.?)\\s*(\\d{1,2})\\s*${classPattern}$`, 'i');
    const levelOnlyRe = /^(?:level|lvl\.?)\s*(\d{1,2})$/i;
    const speciesTokenRe = new RegExp(`^${speciesPattern}$`, 'i');
    const alignmentTokenRe = /^(lawful good|neutral good|chaotic good|lawful neutral|neutral|true neutral|chaotic neutral|lawful evil|neutral evil|chaotic evil)$/i;
    const abilityTokenRe = /^(str(?:ength)?|dex(?:terity)?|con(?:stitution)?|int(?:elligence)?|wis(?:dom)?|cha(?:risma)?)\s*:?\s*(\d{1,2})$/i;
    const reverseAbilityTokenRe = /^(\d{1,2})\s*(str(?:ength)?|dex(?:terity)?|con(?:stitution)?|int(?:elligence)?|wis(?:dom)?|cha(?:risma)?)$/i;
    const hpTokenRe = /^(?:hp|hit\s*points?)\s*:?\s*(\d+)(?:\s*\(([^)]+)\))?$/i;
    const reverseHpTokenRe = /^(\d+)\s*(?:hp|hit\s*points?)$/i;
    const acTokenRe = /^(?:ac|armor\s*class)\s*:?\s*(\d+)(?:\s*\(([^)]+)\))?$/i;
    const reverseAcTokenRe = /^(\d+)\s*(?:ac|armor\s*class)$/i;
    const speedTokenRe = /^(?:speed|movement)\s*:?\s*(\d+)(?:\s*ft\.?)?$/i;
    const initiativeTokenRe = /^initiative\s*[:=]?\s*([+-]?\d+)$/i;
    const equipmentKeywordRe = /\b(armor|shield|mail|plate|chain(?:mail| shirt)?|breastplate|half[- ]plate|ring mail|splint|scale|hide|padded|studded|leather|sword|axe|bow|crossbow|dagger|dart|flail|glaive|halberd|hammer|handaxe|javelin|lance|longbow|longsword|mace|maul|morningstar|pike|quarterstaff|rapier|scimitar|shortbow|shortsword|sickle|sling|spear|staff|trident|war ?pick|warhammer|whip|club|potion|scroll|wand|rod|orb|focus|kit|pack|boots?|cloak|ring|amulet|helm(?:et)?|gauntlets?|bracers?|belt|tool|pearl|spellbook|tome)\b/i;
    const weaponKeywordRe = /\b(axe|bow|club|crossbow|dagger|dart|flail|glaive|halberd|hammer|handaxe|javelin|lance|longbow|longsword|mace|maul|morningstar|pike|quarterstaff|rapier|scimitar|shortbow|shortsword|sickle|sling|spear|staff|sword|trident|war ?pick|warhammer|whip)\b/i;
    const armorKeywordRe = /\b(armor|shield|mail|plate|chain(?:mail| shirt)?|breastplate|half[- ]plate|ring mail|splint|scale|hide|padded|studded|leather)\b/i;
    const titleCase = (value) => String(value || '')
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .replace(/\s+/g, ' ')
      .trim();
    const buildSummaryMeta = (token, reviewReason, parseConfidence = 0.7) => this.buildParserMeta({
      sourceParser: 'parseLooseCharacterSummary',
      sourceSection: 'summary',
      lineStart: 1,
      lineEnd: 1,
      rawText: token?.rawValue || token?.value || source,
      parseConfidence,
      reviewReason
    });
    const looksLikeNameToken = (value) => {
      if (!value) return false;
      if (/\d/.test(value) || /[:=]/.test(value)) return false;
      if (classTokenRe.test(value) || speciesTokenRe.test(value) || alignmentTokenRe.test(value)) return false;
      if (classWithLevelRe.test(value) || levelThenClassRe.test(value) || levelOnlyRe.test(value)) return false;
      if (abilityTokenRe.test(value) || reverseAbilityTokenRe.test(value) || hpTokenRe.test(value) || reverseHpTokenRe.test(value) || acTokenRe.test(value) || reverseAcTokenRe.test(value)) {
        return false;
      }
      if (equipmentKeywordRe.test(value)) return false;
      const words = value.split(/\s+/).filter(Boolean);
      return words.length >= 1 && words.length <= 4 && /^[A-Za-z][A-Za-z'’\- ]*[A-Za-z]$|^[A-Za-z]$/.test(value);
    };
    const looksLikeEquipmentToken = (value) => {
      if (!value) return false;
      if (abilityTokenRe.test(value) || reverseAbilityTokenRe.test(value) || hpTokenRe.test(value) || reverseHpTokenRe.test(value) || acTokenRe.test(value) || reverseAcTokenRe.test(value)) {
        return false;
      }
      if (classTokenRe.test(value) || speciesTokenRe.test(value) || alignmentTokenRe.test(value) || classWithLevelRe.test(value) || levelThenClassRe.test(value) || levelOnlyRe.test(value)) {
        return false;
      }
      if (/^(?:proficiency\s*bonus|pb|passive\s*perception|initiative|speed|movement)\b/i.test(value)) return false;
      return equipmentKeywordRe.test(value);
    };

    const data = {};
    const pendingTokens = [];
    const equipment = {
      weapons: [],
      armor: [],
      items: [],
      valuables: []
    };
    let matchedTokenCount = 0;

    const pushEquipmentEntry = (token) => {
      const quantityMatch = token.value.match(/^(\d+)\s*[x×]?\s+(.+)$/i);
      const normalizedName = quantityMatch?.[2] ? quantityMatch[2].trim() : token.value;
      const quantity = quantityMatch?.[1] ? parseInt(quantityMatch[1], 10) : null;
      const baseEntry = {
        name: normalizedName,
        type: 'item',
        ...(quantity ? { quantity } : {}),
        parserMeta: buildSummaryMeta(token, 'Recovered from loose character summary equipment list', 0.68)
      };

      if (weaponKeywordRe.test(normalizedName)) {
        equipment.items.push(this.normalizeEquipmentWeaponEntry({
          ...baseEntry,
          type: 'weapon'
        }));
        return;
      }

      if (armorKeywordRe.test(normalizedName)) {
        equipment.armor.push({
          ...baseEntry,
          type: /\bshield\b/i.test(normalizedName) ? 'shield' : 'armor'
        });
        return;
      }

      equipment.items.push(baseEntry);
    };

    tokens.forEach((token) => {
      const { value } = token;
      let match = value.match(abilityTokenRe);
      if (match) {
        const abilityKey = this.normalizeAbilityName(match[1]);
        const abilityValue = parseInt(match[2], 10);
        if (!Number.isNaN(abilityValue)) {
          data.abilities = {
            ...(data.abilities || {}),
            [abilityKey]: abilityValue
          };
          matchedTokenCount += 1;
          return;
        }
      }

      match = value.match(reverseAbilityTokenRe);
      if (match) {
        const abilityKey = this.normalizeAbilityName(match[2]);
        const abilityValue = parseInt(match[1], 10);
        if (!Number.isNaN(abilityValue)) {
          data.abilities = {
            ...(data.abilities || {}),
            [abilityKey]: abilityValue
          };
          matchedTokenCount += 1;
          return;
        }
      }

      match = value.match(hpTokenRe) || value.match(reverseHpTokenRe);
      if (match) {
        const hpValue = parseInt(match[1], 10);
        if (!Number.isNaN(hpValue)) {
          data.maxHp = hpValue;
          data.hp = {
            average: hpValue,
            ...(match[2] ? { formula: match[2] } : {}),
            parserMeta: buildSummaryMeta(token, 'Recovered hit points from loose character summary', 0.72)
          };
          matchedTokenCount += 1;
          return;
        }
      }

      match = value.match(acTokenRe) || value.match(reverseAcTokenRe);
      if (match) {
        const acValue = parseInt(match[1], 10);
        if (!Number.isNaN(acValue)) {
          data.ac = {
            value: acValue,
            notes: match[2] || '',
            parserMeta: buildSummaryMeta(token, 'Recovered armor class from loose character summary', 0.72)
          };
          matchedTokenCount += 1;
          return;
        }
      }

      match = value.match(classWithLevelRe);
      if (match) {
        data.class = titleCase(match[1]);
        if (!data.level) {
          const levelValue = parseInt(match[2], 10);
          if (!Number.isNaN(levelValue)) data.level = levelValue;
        }
        matchedTokenCount += 1;
        return;
      }

      match = value.match(levelThenClassRe);
      if (match) {
        const levelValue = parseInt(match[1], 10);
        if (!Number.isNaN(levelValue)) data.level = levelValue;
        data.class = titleCase(match[2]);
        matchedTokenCount += 1;
        return;
      }

      match = value.match(levelOnlyRe);
      if (match) {
        const levelValue = parseInt(match[1], 10);
        if (!Number.isNaN(levelValue)) {
          data.level = levelValue;
          matchedTokenCount += 1;
          return;
        }
      }

      if (!data.class && classTokenRe.test(value)) {
        data.class = titleCase(value);
        matchedTokenCount += 1;
        return;
      }

      if (!data.species && speciesTokenRe.test(value)) {
        data.species = titleCase(value);
        data.race = titleCase(value);
        matchedTokenCount += 1;
        return;
      }

      if (!data.alignment && alignmentTokenRe.test(value)) {
        data.alignment = titleCase(value);
        matchedTokenCount += 1;
        return;
      }

      match = value.match(speedTokenRe);
      if (match) {
        const speedValue = parseInt(match[1], 10);
        if (!Number.isNaN(speedValue)) {
          data.speed = { walk: speedValue };
          matchedTokenCount += 1;
          return;
        }
      }

      match = value.match(initiativeTokenRe);
      if (match) {
        const initiativeValue = parseInt(match[1], 10);
        if (!Number.isNaN(initiativeValue)) {
          data.initiative = initiativeValue;
          matchedTokenCount += 1;
          return;
        }
      }

      pendingTokens.push(token);
    });

    if (!data.name) {
      const nameToken = pendingTokens.find((token) => looksLikeNameToken(token.value));
      if (nameToken) {
        data.name = nameToken.value;
        nameToken.claimed = true;
        matchedTokenCount += 1;
      }
    }

    pendingTokens
      .filter((token) => !token.claimed)
      .forEach((token) => {
        if (!looksLikeEquipmentToken(token.value)) return;
        pushEquipmentEntry(token);
        matchedTokenCount += 1;
      });

    const hasEquipment = equipment.weapons.length || equipment.armor.length || equipment.items.length || equipment.valuables.length;
    if (hasEquipment) {
      data.equipment = {
        weapons: equipment.weapons,
        armor: equipment.armor,
        items: equipment.items,
        valuables: equipment.valuables
      };
    }

    const hasStructuredData = Boolean(
      data.name ||
      data.class ||
      data.level ||
      (data.abilities && Object.keys(data.abilities).length > 0) ||
      data.ac ||
      data.maxHp ||
      data.hp ||
      hasEquipment
    );

    if (!hasStructuredData || matchedTokenCount < 2) {
      return null;
    }

    return {
      data,
      matchedTokenCount,
      recoveredFromLooseSummary: true
    };
  }

  /**
   * Parse character stat block
   */
  static parseCharacterStatBlock(text, context) {
    // date_of_change: 2026-02-25
    // rule_reference: SmartPaste should tolerate partial/"garbage" pastes; prefer extracting explicit labeled stats (ability scores + AC/HP)
    // without inferring mechanics from prose.
    const sectionClaims = this.buildCharacterSectionClaims(text);
    const claimStrippedText = this.buildClaimSeparatedText(text, sectionClaims);
    const rawSummaryText = String(text || '')
      .replace(/__SMARTPASTE_CLAIM__/g, '')
      .trim();
    const summaryExtractionText = String(sectionClaims.length > 0 ? claimStrippedText : text)
      .replace(/__SMARTPASTE_CLAIM__/g, '')
      .trim() || rawSummaryText;
    const extracted = this.extractBasicStats(summaryExtractionText);
    const fullTextDetails = sectionClaims.length > 0
      ? this.extractBasicStats(rawSummaryText)
      : null;
    const summaryLineCount = summaryExtractionText.split(/\r?\n/).filter((line) => line.trim()).length;
    const looseSummary = summaryLineCount <= 2
      ? this.parseLooseCharacterSummary(summaryExtractionText)
      : null;

    // Look for character-specific patterns
    // date_of_change: 2026-02-25
    // rule_reference: Expanded character indicators to detect more class names, species, and Level: N format.
    const characterIndicators = [
      /level\s*:?\s*\d+/i,
      /\b(fighter|wizard|rogue|cleric|ranger|barbarian|bard|druid|monk|paladin|sorcerer|warlock|artificer|blood\s*hunter|mystic)\b/i,
      /\b(human|elf|dwarf|halfling|dragonborn|gnome|half-elf|half-orc|tiefling|vampire|aasimar|genasi|goliath|tabaxi|kenku|firbolg|lizardfolk|changeling|shifter|kalashtar|warforged)\b/i,
      /background\s*:/i,
      /class\s+features/i,
      // date_of_change: 2026-02-27
      // rule_reference: Narrative stat blocks often use an "Abilities & Traits" section instead of explicit ability score lines.
      // Treat this as a character indicator so SmartPaste doesn't reject these pastes as unknown.
      /^\s*abilities\s*(?:&|and)\s*traits\s*:?(?:\s*)$/im,
      /\bclass\s*:\s*[A-Z]/i
    ];

    const hasCharacterIndicators = characterIndicators.some(pattern => pattern.test(text));
    const hasCharacterCoreStats = Boolean(
      extracted && extracted.abilities && (extracted.ac || extracted.hp || extracted.maxHp)
    );

    if (!hasCharacterIndicators && !hasCharacterCoreStats) {
      return { type: 'unknown', confidence: 0 };
    }

    const characterData = extracted || {};
    if (fullTextDetails) {
      [
        'proficiencyBonus',
        'languages',
        'toolProficiencies',
        'proficiencies',
        'savingThrows',
        'skills',
        'skillsDetailed'
      ].forEach((field) => {
        const current = characterData[field];
        const isCurrentEmpty = current === undefined
          || current === null
          || (Array.isArray(current) && current.length === 0)
          || (typeof current === 'object' && !Array.isArray(current) && Object.keys(current).length === 0);
        if (isCurrentEmpty && fullTextDetails[field] !== undefined) {
          characterData[field] = fullTextDetails[field];
        }
      });
    }
    if (looseSummary?.data) {
      if (
        looseSummary.data.name &&
        (
          !characterData.name ||
          characterData.name === summaryExtractionText.trim() ||
          /[,;]/.test(characterData.name)
        )
      ) {
        characterData.name = looseSummary.data.name;
      }

      ['class', 'subclass', 'background', 'alignment', 'species', 'race'].forEach((field) => {
        if (
          looseSummary.data[field] !== undefined &&
          looseSummary.data[field] !== null &&
          looseSummary.data[field] !== '' &&
          !characterData[field]
        ) {
          characterData[field] = looseSummary.data[field];
        }
      });

      if (typeof looseSummary.data.level === 'number' && !characterData.level) {
        characterData.level = looseSummary.data.level;
      }

      if (looseSummary.data.abilities && Object.keys(looseSummary.data.abilities).length > 0) {
        characterData.abilities = {
          ...(characterData.abilities || {}),
          ...looseSummary.data.abilities
        };
      }

      if (looseSummary.data.ac && !characterData.ac) {
        characterData.ac = looseSummary.data.ac;
      }

      if (!characterData.maxHp && looseSummary.data.maxHp) {
        characterData.maxHp = looseSummary.data.maxHp;
      }

      if (!characterData.hp && looseSummary.data.hp) {
        characterData.hp = looseSummary.data.hp;
      }

      if (!characterData.speed && looseSummary.data.speed) {
        characterData.speed = looseSummary.data.speed;
      }

      if (characterData.initiative === undefined && looseSummary.data.initiative !== undefined) {
        characterData.initiative = looseSummary.data.initiative;
      }
    }
    characterData.type = 'character';
    const biographyParse = this.parseBiographyFields(text);
    if (biographyParse?.biography && Object.keys(biographyParse.biography).length > 0) {
      characterData.biography = {
        ...(characterData.biography || {}),
        ...biographyParse.biography
      };
      if (biographyParse.fieldMeta && Object.keys(biographyParse.fieldMeta).length > 0) {
        characterData.biographyMeta = {
          ...(characterData.biographyMeta || {}),
          ...biographyParse.fieldMeta
        };
      }
    }

    const warnings = [];
    if (!hasCharacterIndicators && hasCharacterCoreStats) {
      warnings.push('Detected partial character-like stats (ability scores + AC/HP) without a full stat block header; review extracted fields carefully.');
    }

    const parseBlocksWithFallback = (claimKinds, parser) => {
      const kinds = Array.isArray(claimKinds) ? claimKinds : [claimKinds];
      const matchingClaims = sectionClaims.filter((claim) => kinds.includes(claim.kind));
      if (!matchingClaims.length) {
        const fallbackText = sectionClaims.length > 0 ? claimStrippedText : text;
        const parsed = parser.call(this, fallbackText);
        return parsed ? [parsed] : [];
      }

      return matchingClaims
        .map((claim) => parser.call(this, claim.text, { lineOffset: claim.startLine - 1 }))
        .filter(Boolean);
    };
    const dedupeStrings = (values = []) => {
      const seen = new Set();
      return values
        .map((value) => String(value || '').trim())
        .filter(Boolean)
        .filter((value) => {
          const key = value.toLowerCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
    };
    const mergeTextField = (current, incoming) => {
      const normalizedIncoming = String(incoming || '').trim();
      if (!normalizedIncoming) return current || '';
      return current ? `${current}\n${normalizedIncoming}` : normalizedIncoming;
    };
    const mergeByFingerprint = (existing = [], incoming = [], kind = 'entry') => {
      const merged = [];
      const seen = new Set();
      [...(Array.isArray(existing) ? existing : []), ...(Array.isArray(incoming) ? incoming : [])].forEach((entry) => {
        if (!entry) return;
        const key = this.buildParsedEntryFingerprint(entry, kind);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(entry);
      });
      return merged;
    };
    const mergeEquipmentBuckets = (existing = {}, incoming = {}) => ({
      weapons: mergeByFingerprint(existing?.weapons, incoming?.weapons, 'equipment'),
      armor: mergeByFingerprint(existing?.armor, incoming?.armor, 'equipment'),
      items: mergeByFingerprint(existing?.items, incoming?.items, 'equipment'),
      valuables: mergeByFingerprint(existing?.valuables, incoming?.valuables, 'equipment'),
      currency: { ...(existing?.currency || {}), ...(incoming?.currency || {}) }
    });
    const mergeCharacterSectionBlocks = (blocks = []) => blocks.reduce((accumulator, block) => {
      if (!block || typeof block !== 'object') return accumulator;

      accumulator.featuresList = dedupeStrings([...(accumulator.featuresList || []), ...(block.featuresList || [])]);
      accumulator.featsList = dedupeStrings([...(accumulator.featsList || []), ...(block.featsList || [])]);
      accumulator.speciesTraitsList = dedupeStrings([...(accumulator.speciesTraitsList || []), ...(block.speciesTraitsList || [])]);
      accumulator.classFeaturesText = mergeTextField(accumulator.classFeaturesText, block.classFeaturesText);
      accumulator.featsText = mergeTextField(accumulator.featsText, block.featsText);
      accumulator.speciesTraitsText = mergeTextField(accumulator.speciesTraitsText, block.speciesTraitsText);
      accumulator.featuresListStructured = this.mergeCharacterEntriesForKind('features', accumulator.featuresListStructured, block.featuresListStructured);
      accumulator.featsListStructured = this.mergeCharacterEntriesForKind('feats', accumulator.featsListStructured, block.featsListStructured);
      accumulator.speciesTraitsListStructured = this.mergeCharacterEntriesForKind('speciesTraits', accumulator.speciesTraitsListStructured, block.speciesTraitsListStructured);
      if (Array.isArray(block.parsedEntries) && block.parsedEntries.length) {
        accumulator.parsedEntries = [...(accumulator.parsedEntries || []), ...block.parsedEntries];
      }
      return accumulator;
    }, {});
    const dedupeSpellEntries = (entries = []) => {
      const merged = [];
      const seen = new Set();
      (Array.isArray(entries) ? entries : []).forEach((entry) => {
        if (!entry) return;
        const key = [
          this.normalizeParsedEntryName(entry.name),
          Number.isFinite(Number(entry.level)) ? `level:${Number(entry.level)}` : '',
          entry.prepared ? 'prepared' : '',
          entry.alwaysPrepared ? 'alwaysPrepared' : '',
          entry.innateFrequency || '',
          entry.listType || ''
        ].filter(Boolean).join('|');
        if (!key || seen.has(key)) return;
        seen.add(key);
        merged.push(entry);
      });
      return merged;
    };
    const mergeSpellBlocks = (blocks = []) => {
      const mergedSpells = {
        byLevel: {},
        prepared: [],
        atWill: [],
        known: [],
        slots: {},
        innateByFrequency: {},
        alwaysPrepared: {}
      };
      let spellcastingMeta = null;
      const warningsList = [];
      const parsedEntries = [];

      blocks.forEach((block) => {
        if (!block?.spells || typeof block.spells !== 'object') return;
        const spells = block.spells;
        if (spells.byLevel && typeof spells.byLevel === 'object') {
          Object.entries(spells.byLevel).forEach(([levelKey, spellEntries]) => {
            mergedSpells.byLevel[levelKey] = dedupeSpellEntries([
              ...(mergedSpells.byLevel[levelKey] || []),
              ...(Array.isArray(spellEntries) ? spellEntries : [])
            ]);
          });
        }
        mergedSpells.prepared = dedupeSpellEntries([...(mergedSpells.prepared || []), ...(spells.prepared || [])]);
        mergedSpells.atWill = dedupeSpellEntries([...(mergedSpells.atWill || []), ...(spells.atWill || [])]);
        mergedSpells.known = dedupeSpellEntries([...(mergedSpells.known || []), ...(spells.known || [])]);
        if (spells.ability && !mergedSpells.ability) mergedSpells.ability = spells.ability;
        if (spells.saveDC !== undefined && mergedSpells.saveDC === undefined) mergedSpells.saveDC = spells.saveDC;
        if (spells.attackBonus !== undefined && mergedSpells.attackBonus === undefined) mergedSpells.attackBonus = spells.attackBonus;
        if (spells.parserMeta) {
          mergedSpells.parserMeta = this.mergeParserMeta(mergedSpells.parserMeta, spells.parserMeta);
        }
        if (spells.slots && typeof spells.slots === 'object') {
          mergedSpells.slots = { ...mergedSpells.slots, ...spells.slots };
        }
        if (spells.innateByFrequency && typeof spells.innateByFrequency === 'object') {
          Object.entries(spells.innateByFrequency).forEach(([frequency, spellEntries]) => {
            mergedSpells.innateByFrequency[frequency] = dedupeSpellEntries([
              ...(mergedSpells.innateByFrequency[frequency] || []),
              ...(Array.isArray(spellEntries) ? spellEntries : [])
            ]);
          });
        }
        if (spells.alwaysPrepared && typeof spells.alwaysPrepared === 'object') {
          Object.entries(spells.alwaysPrepared).forEach(([sourceKey, spellEntries]) => {
            mergedSpells.alwaysPrepared[sourceKey] = dedupeSpellEntries([
              ...(mergedSpells.alwaysPrepared[sourceKey] || []),
              ...(Array.isArray(spellEntries) ? spellEntries : [])
            ]);
          });
        }

        spellcastingMeta = this.mergeParserMeta(spellcastingMeta, block.spellcastingMeta);
        warningsList.push(...(Array.isArray(block.warnings) ? block.warnings : []));
        parsedEntries.push(...(Array.isArray(block.parsedEntries) ? block.parsedEntries : []));
      });

      if (Object.keys(mergedSpells.byLevel).length === 0) delete mergedSpells.byLevel;
      if (!mergedSpells.prepared.length) delete mergedSpells.prepared;
      if (!mergedSpells.atWill.length) delete mergedSpells.atWill;
      if (!mergedSpells.known.length) delete mergedSpells.known;
      if (!Object.keys(mergedSpells.slots).length) delete mergedSpells.slots;
      if (!Object.keys(mergedSpells.innateByFrequency).length) delete mergedSpells.innateByFrequency;
      if (!Object.keys(mergedSpells.alwaysPrepared).length) delete mergedSpells.alwaysPrepared;

      if (!Object.keys(mergedSpells).length) return null;
      return {
        spells: mergedSpells,
        warnings: warningsList,
        spellcastingMeta,
        parsedEntries
      };
    };
    const mergeInventoryBlocks = (blocks = []) => {
      const merged = {
        items: [],
        currency: {},
        weaponRiders: [],
        equipmentRiders: [],
        parsedEntries: []
      };

      blocks.forEach((block) => {
        if (!block || typeof block !== 'object') return;
        merged.items = mergeByFingerprint(merged.items, block.items, 'equipment');
        merged.currency = { ...merged.currency, ...(block.currency || {}) };
        merged.weaponRiders = mergeByFingerprint(merged.weaponRiders, block.weaponRiders, 'weapon-rider');
        merged.equipmentRiders = mergeByFingerprint(merged.equipmentRiders, block.equipmentRiders, 'equipment-rider');
        if (Array.isArray(block.parsedEntries) && block.parsedEntries.length) {
          merged.parsedEntries = [...merged.parsedEntries, ...block.parsedEntries];
        }
      });

      if (!merged.items.length && !merged.weaponRiders.length && !merged.equipmentRiders.length && !Object.keys(merged.currency).length) return null;
      return merged;
    };
    const mergeFeatureBlocks = (blocks = []) => {
      const merged = {
        features: [],
        parsedEntries: []
      };

      blocks.forEach((block) => {
        if (!block || typeof block !== 'object') return;
        merged.features = this.mergeCharacterEntriesForKind('features', merged.features, block.features);
        if (Array.isArray(block.parsedEntries) && block.parsedEntries.length) {
          merged.parsedEntries = [...merged.parsedEntries, ...block.parsedEntries];
        }
      });

      return merged.features.length ? merged : null;
    };
    const mergeCombatBlocks = (blocks = []) => {
      const merged = {
        actions: [],
        bonusActions: [],
        reactions: [],
        parsedEntries: []
      };

      blocks.forEach((block) => {
        if (!block || typeof block !== 'object') return;
        merged.actions = this.mergeCharacterEntriesForKind('actions', merged.actions, block.actions);
        merged.bonusActions = this.mergeCharacterEntriesForKind('bonusActions', merged.bonusActions, block.bonusActions);
        merged.reactions = this.mergeCharacterEntriesForKind('reactions', merged.reactions, block.reactions);
        if (Array.isArray(block.parsedEntries) && block.parsedEntries.length) {
          merged.parsedEntries = [...merged.parsedEntries, ...block.parsedEntries];
        }
      });

      return (merged.actions.length || merged.bonusActions.length || merged.reactions.length) ? merged : null;
    };

    // Extract structured sections for features/traits/feats
    // date_of_change: 2026-02-25
    // rule_reference: parseCharacterSections now produces 'Structured' arrays of {name, description}
    // objects. Use those for the features/feats arrays so descriptions flow to the UI.
    const sections = mergeCharacterSectionBlocks(parseBlocksWithFallback('characterSections', this.parseCharacterSections));
    Object.assign(characterData, sections);
    const mergeCategorizedEntries = (categorized) => {
      if (!categorized) return;
      if (categorized.classFeatures.length) {
        characterData.classFeatures = this.mergeCharacterEntriesForKind('classFeatures', characterData.classFeatures, categorized.classFeatures);
      }
      if (categorized.feats.length) {
        characterData.feats = this.mergeCharacterEntriesForKind('feats', characterData.feats, categorized.feats);
      }
      if (categorized.traits.length) {
        characterData.speciesTraits = this.mergeCharacterEntriesForKind('speciesTraits', characterData.speciesTraits, categorized.traits);
      }
      if (categorized.unresolved.length) {
        characterData.features = this.mergeCharacterEntriesForKind('features', characterData.features, categorized.unresolved);
      }
    };
    const provisionalParsedEntries = [];
    const collectParsedEntryRecords = (records = []) => {
      if (!Array.isArray(records) || records.length === 0) return;
      provisionalParsedEntries.push(...records.filter((record) => record?.kind && record?.entry?.name));
    };
    const mapParsedSectionRecords = (records = [], overridesByKind = {}) => (
      (Array.isArray(records) ? records : [])
        .map(({ kind, entry }) => this.buildParsedAbilityEntry(entry, {
          sourceSection: kind,
          ...(overridesByKind[kind] || {})
        }))
        .filter(Boolean)
    );

    collectParsedEntryRecords(sections?.parsedEntries);
    const categorizedSectionEntries = this.categorizeFeatures(mapParsedSectionRecords(sections?.parsedEntries), {
      entityClass: characterData.class,
      entitySpecies: characterData.race || characterData.species
    });
    mergeCategorizedEntries(categorizedSectionEntries);

    const fastCharacterSections = this.parseFastCharacterSections(text);
    if (fastCharacterSections) {
      collectParsedEntryRecords(fastCharacterSections.parsedEntries);
      characterData.classFeatures = this.mergeCharacterEntriesForKind(
        'classFeatures',
        characterData.classFeatures,
        fastCharacterSections.classFeatures
      );
      characterData.feats = this.mergeCharacterEntriesForKind(
        'feats',
        characterData.feats,
        fastCharacterSections.feats
      );
      characterData.speciesTraits = this.mergeCharacterEntriesForKind(
        'speciesTraits',
        characterData.speciesTraits,
        fastCharacterSections.speciesTraits
      );
    }

    // Extract spells
    const spellBlock = mergeSpellBlocks(parseBlocksWithFallback('spells', this.parseSpellBlocks));
    if (spellBlock && spellBlock.spells) {
      collectParsedEntryRecords(spellBlock.parsedEntries);
      characterData.spells = spellBlock.spells;
      // Also mirror high-level meta into spellcasting for UI if present
      // @date_of_change 2025-12-05 - Use canonical saveDC casing so UI and handlers see the parsed DC
      if (spellBlock.spells.ability || spellBlock.spells.saveDC || spellBlock.spells.attackBonus) {
        characterData.spellcasting = {
          ...(characterData.spellcasting || {}),
          ability: spellBlock.spells.ability,
          saveDC: spellBlock.spells.saveDC,
          attackBonus: spellBlock.spells.attackBonus,
          parserMeta: this.mergeParserMeta(
            characterData.spellcasting?.parserMeta,
            spellBlock.spellcastingMeta,
            spellBlock.spells?.parserMeta
          )
        };
      }
      if (Array.isArray(spellBlock.warnings) && spellBlock.warnings.length) warnings.push(...spellBlock.warnings);
    }

    // Provide a conservative default spellcasting ability from class when not explicitly stated
    // @date_of_change 2025-12-05 - Map core 5e classes to their standard spellcasting abilities
    if (!characterData.spellcasting?.ability && characterData.class) {
      const cls = String(characterData.class).toLowerCase();
      const classAbilityMap = {
        artificer: 'intelligence',
        wizard: 'intelligence',
        warlock: 'charisma',
        sorcerer: 'charisma',
        bard: 'charisma',
        cleric: 'wisdom',
        druid: 'wisdom',
        ranger: 'wisdom',
        paladin: 'charisma'
      };
      const inferred = classAbilityMap[cls];
      if (inferred) {
        const existingSpellcastingMeta = characterData.spellcasting?.parserMeta;
        const inferredKnowledgeSource = existingSpellcastingMeta?.knowledgeSource
          ? 'mixed'
          : 'inferred';
        const inferredKnowledgeNote = existingSpellcastingMeta?.knowledgeSource
          ? `Spellcasting ability inferred from class ${characterData.class}; other spellcasting data came from explicit pasted text.`
          : `Spellcasting ability inferred from class ${characterData.class} using SRD class defaults.`;
        characterData.spellcasting = {
          ...(characterData.spellcasting || {}),
          ability: inferred,
          parserMeta: this.mergeParserMeta(
            existingSpellcastingMeta,
            {
              sourceParser: 'parseCharacterStatBlock',
              sourceSection: 'spellcasting',
              parseConfidence: 0.44,
              knowledgeSource: inferredKnowledgeSource,
              knowledgeNote: inferredKnowledgeNote
            }
          )
        };
      }
    }

    // Extract inventory/equipment
    // date_of_change: 2026-02-25
    // rule_reference: parseInventoryFromText now returns { items, weaponRiders }. Weapon riders are properties/effects
    // that should be associated with a parent weapon. Pass them through for UI association prompts.
    const inv = mergeInventoryBlocks(parseBlocksWithFallback('equipment', this.parseInventoryFromText));
    if (inv) {
      collectParsedEntryRecords(inv.parsedEntries);
      if (inv.items && inv.items.length) {
        characterData.equipment = {
          ...(characterData.equipment || {}),
          items: inv.items
        };
      }
      if (inv.currency && Object.keys(inv.currency).length) {
        characterData.equipment = {
          ...(characterData.equipment || {}),
          currency: { ...(characterData.equipment?.currency || {}), ...inv.currency }
        };
      }
      if (inv.weaponRiders && inv.weaponRiders.length) {
        characterData.weaponRiders = inv.weaponRiders;
      }
      // date_of_change: 2026-02-27
      // rule_reference: Some items list their effects as standalone lines after the item name (e.g., Root Boots).
      // Pass these through for SmartPaste UI association prompts.
      if (inv.equipmentRiders && inv.equipmentRiders.length) {
        characterData.equipmentRiders = inv.equipmentRiders;
      }
    }
    const fastCharacterWeapons = this.parseFastCharacterWeaponAttacks(text);
    if (fastCharacterWeapons.length) {
      const quantityWords = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 };
      const normalizeCarriedWeaponName = (value) => String(value || '')
        .toLowerCase()
        .replace(/^\s*(?:one|two|three|four|five|six|\d+)(?:\s*\(\d+\))?\s+/, '')
        .replace(/s\b$/, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
      const carriedItems = Array.isArray(characterData.equipment?.items) ? characterData.equipment.items : [];
      const enrichedWeapons = fastCharacterWeapons.map((weapon) => {
        const normalizedWeaponName = normalizeCarriedWeaponName(weapon.name);
        const carriedMatch = carriedItems.find((item) => normalizeCarriedWeaponName(item?.name) === normalizedWeaponName);
        if (!carriedMatch) return weapon;
        const quantityToken = String(carriedMatch.name || '').match(/^\s*(one|two|three|four|five|six|\d+)/i)?.[1]?.toLowerCase();
        const quantity = quantityToken ? (quantityWords[quantityToken] || parseInt(quantityToken, 10)) : null;
        return Number.isFinite(quantity) ? { ...weapon, quantity } : weapon;
      });
      const fastWeaponNames = new Set(enrichedWeapons.map((weapon) => normalizeCarriedWeaponName(weapon.name)));
      if (characterData.equipment?.items) {
        characterData.equipment = {
          ...characterData.equipment,
          items: carriedItems.filter((item) => !fastWeaponNames.has(normalizeCarriedWeaponName(item?.name)))
        };
      }
      collectParsedEntryRecords(this.buildStructuredSectionParsedEntries({ equipment: enrichedWeapons }));
      characterData.equipment = mergeEquipmentBuckets(characterData.equipment, {
        weapons: enrichedWeapons
      });
    }
    if (looseSummary?.data?.equipment) {
      characterData.equipment = mergeEquipmentBuckets(characterData.equipment, looseSummary.data.equipment);
    }

    // Abilities & Traits block
    const featsBlock = mergeFeatureBlocks(parseBlocksWithFallback('abilitiesTraits', this.parseAbilitiesTraits));
    if (featsBlock && Array.isArray(featsBlock.features) && featsBlock.features.length) {
      collectParsedEntryRecords(featsBlock.parsedEntries);
      // Categorize parsed features into classFeatures/feats/speciesTraits buckets
      const categorized = this.categorizeFeatures(
        mapParsedSectionRecords(featsBlock.parsedEntries),
        {
          entityClass: characterData.class,
          entitySpecies: characterData.race || characterData.species
        }
      );
      mergeCategorizedEntries(categorized);
    }

    // date_of_change: 2026-03-01
    // rule_reference: D&D 5e (2024) — Deduplicate by name ACROSS all feature-like buckets to prevent duplicates
    // (e.g., Powerful Build appearing in both features and speciesTraits).
    // Once an entry is claimed by one category, remove it from all others to ensure single-category placement.
    const dedupeAcrossCategories = () => {
      const allBuckets = [
        { key: 'classFeatures', arr: characterData.classFeatures },
        { key: 'feats', arr: characterData.feats },
        { key: 'speciesTraits', arr: characterData.speciesTraits },
        { key: 'features', arr: characterData.features }
      ];
      
      const globalSeen = new Set();
      
      // Process in priority order: classFeatures > feats > speciesTraits > features (legacy fallback)
      allBuckets.forEach(bucket => {
        if (!Array.isArray(bucket.arr)) return;
        const dedupedArr = [];
        bucket.arr.forEach(entry => {
          const key = this.normalizeParsedEntryName(entry?.name);
          if (!key || globalSeen.has(key)) return;
          globalSeen.add(key);
          dedupedArr.push(entry);
        });
        characterData[bucket.key] = dedupedArr;
      });
    };
    
    // date_of_change: 2026-03-12
    // rule_reference: Character combat sections primarily describe the activation timing of features and traits.
    // Claim recognizable entries into their owning buckets first, then keep only true standalone combat entries here.
    const combat = mergeCombatBlocks(parseBlocksWithFallback('combat', this.parseCombatSections));
    if (combat) {
      collectParsedEntryRecords(combat.parsedEntries);
      const parsedCombatEntries = mapParsedSectionRecords(combat.parsedEntries, {
        actions: { activationType: 'action', derivedFromFeature: false },
        bonusActions: { activationType: 'bonus action', derivedFromFeature: false },
        reactions: { activationType: 'reaction', derivedFromFeature: false }
      });
      const categorizedCombatEntries = this.categorizeFeatures(parsedCombatEntries, {
        entityClass: characterData.class || '',
        entitySpecies: characterData.species || characterData.race || ''
      });
      const claimedCombatEntryNames = new Set(
        [
          ...(categorizedCombatEntries.classFeatures || []),
          ...(categorizedCombatEntries.feats || []),
          ...(categorizedCombatEntries.traits || [])
        ]
          .map((entry) => this.normalizeParsedEntryName(entry?.name))
          .filter(Boolean)
      );
      const filterStandaloneCombatEntries = (entries = []) => entries.filter((entry) => {
        const normalizedName = this.normalizeParsedEntryName(entry?.name);
        return !normalizedName || !claimedCombatEntryNames.has(normalizedName);
      });

      if (categorizedCombatEntries.classFeatures.length) {
        characterData.classFeatures = this.mergeCharacterEntriesForKind(
          'classFeatures',
          characterData.classFeatures,
          categorizedCombatEntries.classFeatures
        );
      }
      if (categorizedCombatEntries.feats.length) {
        characterData.feats = this.mergeCharacterEntriesForKind(
          'feats',
          characterData.feats,
          categorizedCombatEntries.feats
        );
      }
      if (categorizedCombatEntries.traits.length) {
        characterData.speciesTraits = this.mergeCharacterEntriesForKind(
          'speciesTraits',
          characterData.speciesTraits,
          categorizedCombatEntries.traits
        );
      }
      if (Array.isArray(combat.actions) && combat.actions.length) {
        characterData.actions = this.mergeCharacterEntriesForKind(
          'actions',
          characterData.actions,
          filterStandaloneCombatEntries(parsedCombatEntries.filter(entry => entry.sourceSection === 'actions'))
        );
      }
      if (Array.isArray(combat.bonusActions) && combat.bonusActions.length) {
        characterData.bonusActions = this.mergeCharacterEntriesForKind(
          'bonusActions',
          characterData.bonusActions,
          filterStandaloneCombatEntries(parsedCombatEntries.filter(entry => entry.sourceSection === 'bonusActions'))
        );
      }
      if (Array.isArray(combat.reactions) && combat.reactions.length) {
        characterData.reactions = this.mergeCharacterEntriesForKind(
          'reactions',
          characterData.reactions,
          filterStandaloneCombatEntries(parsedCombatEntries.filter(entry => entry.sourceSection === 'reactions'))
        );
      }
    }

    dedupeAcrossCategories();

    // date_of_change: 2026-03-12
    // rule_reference: Character action economy is a derived view over canonical feature buckets.
    // Merge derived entries with any true standalone combat actions instead of treating whole explicit sections as authoritative.
    const deriveActionEconomy = () => {
      const allFeatures = [
        ...(characterData.classFeatures || []),
        ...(characterData.feats || []),
        ...(characterData.speciesTraits || [])
      ];

      const actions = Array.isArray(characterData.actions) ? [...characterData.actions] : [];
      const bonusActions = Array.isArray(characterData.bonusActions) ? [...characterData.bonusActions] : [];
      const reactions = Array.isArray(characterData.reactions) ? [...characterData.reactions] : [];
      const seenNames = {
        actions: new Set(actions.map((entry) => this.normalizeParsedEntryName(entry?.name)).filter(Boolean)),
        bonusActions: new Set(bonusActions.map((entry) => this.normalizeParsedEntryName(entry?.name)).filter(Boolean)),
        reactions: new Set(reactions.map((entry) => this.normalizeParsedEntryName(entry?.name)).filter(Boolean))
      };

      for (const feature of allFeatures) {
        if (!feature || !feature.name) continue;
        const derived = this.buildParsedAbilityEntry(feature, {
          sourceSection: feature.sourceSection || 'features',
          derivedFromFeature: true
        });
        if (!derived) continue;
        const normalizedName = this.normalizeParsedEntryName(derived.name);
        if (derived.activationType === 'bonus action' && !seenNames.bonusActions.has(normalizedName)) {
          bonusActions.push(derived);
          if (normalizedName) seenNames.bonusActions.add(normalizedName);
        } else if (derived.activationType === 'reaction' && !seenNames.reactions.has(normalizedName)) {
          reactions.push(derived);
          if (normalizedName) seenNames.reactions.add(normalizedName);
        } else if (derived.activationType === 'action' && !seenNames.actions.has(normalizedName)) {
          actions.push(derived);
          if (normalizedName) seenNames.actions.add(normalizedName);
        }
      }

      if (actions.length) characterData.actions = actions;
      if (bonusActions.length) characterData.bonusActions = bonusActions;
      if (reactions.length) characterData.reactions = reactions;
    };

    deriveActionEconomy();

    if (provisionalParsedEntries.length > 0) {
      characterData.parsedEntries = provisionalParsedEntries;
      characterData.provisionalEntryCatalog = this.buildCharacterCatalogFromParsedEntries(provisionalParsedEntries);
    }

    const claimedEntryCatalog = this.getClaimedCharacterEntryCatalog(characterData);
    characterData.parsedEntryCatalog = this.buildPublishedCharacterEntryCatalog(characterData);

    // @date_of_change 2025-10-15 - Boost confidence when multiple key signals are present
    const signals = [
      Boolean(characterData.abilities),
      Boolean(characterData.ac),
      Boolean(characterData.hp),
      Boolean(characterData.level || characterData.class)
    ];
    const strongSignalCount = signals.filter(Boolean).length;
    const baseConfidence = hasCharacterIndicators ? 0.8 : 0.55;
    const boostedConfidence = strongSignalCount >= 2
      ? Math.min(0.95, baseConfidence + strongSignalCount * 0.05)
      : Math.min(0.7, baseConfidence);

    if (looseSummary?.recoveredFromLooseSummary) {
      warnings.push('Recovered fields from a loose character summary. Review the suggested sections before applying them.');
    }

    // @date_of_change 2025-12-06 - Extract uncategorized content for human-in-the-loop review
    const uncategorized = this.extractUncategorizedBlocks(text, characterData);
    const claimedEntryNames = new Set([
      ...claimedEntryCatalog.map(entry => this.normalizeParsedEntryName(entry?.name)),
      this.normalizeParsedEntryName(characterData.name)
    ].filter(Boolean));
    const filterClaimedEntries = (items = []) => items.filter((item) => {
      const normalizedName = this.normalizeParsedEntryName(item?.name);
      if (!normalizedName) return true;
      if (claimedEntryNames.has(normalizedName)) return false;
      if (normalizedName === this.normalizeParsedEntryName(characterData.name)) return false;
      return true;
    });
    uncategorized.potentialFeatures = filterClaimedEntries(uncategorized.potentialFeatures);
    uncategorized.potentialTraits = filterClaimedEntries(uncategorized.potentialTraits);
    uncategorized.potentialActions = filterClaimedEntries(uncategorized.potentialActions);
    uncategorized.potentialEquipment = filterClaimedEntries(uncategorized.potentialEquipment);
    uncategorized.totalUncategorized =
      uncategorized.potentialFeatures.length +
      uncategorized.potentialEquipment.length +
      uncategorized.potentialTraits.length +
      uncategorized.potentialActions.length +
      (uncategorized.sectionFragments || []).length +
      uncategorized.rawTextBlocks.length;
    if (uncategorized.totalUncategorized > 0) {
      characterData.uncategorized = uncategorized;
      // Add a warning to prompt human review
      if (uncategorized.potentialFeatures.length > 0) {
        warnings.push(`Found ${uncategorized.potentialFeatures.length} potential feature(s)/ability(ies) that need review`);
      }
      if (uncategorized.potentialTraits.length > 0) {
        warnings.push(`Found ${uncategorized.potentialTraits.length} potential trait(s) that need review`);
      }
      if (uncategorized.potentialEquipment.length > 0) {
        warnings.push(`Found ${uncategorized.potentialEquipment.length} potential equipment item(s) that need review`);
      }
      if ((uncategorized.sectionFragments || []).length > 0) {
        warnings.push(`Found ${(uncategorized.sectionFragments || []).length} leftover section fragment(s) that need review`);
      }
      if (uncategorized.rawTextBlocks.length > 0) {
        warnings.push(`Found ${uncategorized.rawTextBlocks.length} unrecognized text block(s) - please review`);
      }
    }

    return {
      type: 'character',
      confidence: boostedConfidence,
      data: characterData,
      suggestions: ['Import as new character', 'Merge with existing character'],
      warnings
    };
  }

  /**
   * Parse monster stat block
   */
  static parseMonsterStatBlock(text, context) {
    // Look for monster-specific patterns
    const monsterIndicators = [
      /challenge rating?\s+(\d+(?:\/\d+)?)/i,
      /\b(tiny|small|medium|large|huge|gargantuan)\s+(beast|humanoid|undead|fiend|dragon|fey|elemental|aberration)\b/i,
      /armor class\s+\d+/i,
      /hit points?\s+\d+/i,
      /legendary actions?/i,
      /saving throws?/i
    ];

    const hasMonsterIndicators = monsterIndicators.some(pattern => pattern.test(text));

    // date_of_change: 2026-02-25
    // rule_reference: Avoid misclassifying character sheets as monsters simply because they contain "Armor Class"/"Hit Points".
    // Require strong monster signals (CR / size+type / legendary actions) plus basic combat stats.
    const hasStrongMonsterSignal =
      /challenge rating?\s+(\d+(?:\/\d+)?)/i.test(text) ||
      /\b(tiny|small|medium|large|huge|gargantuan)\s+(beast|humanoid|undead|fiend|dragon|fey|elemental|aberration)\b/i.test(text) ||
      /legendary actions?/i.test(text);
    const hasBasicCombatStats = /armor class\s+\d+/i.test(text) && /hit points?\s+\d+/i.test(text);
    const isLikelyMonster = hasMonsterIndicators && hasStrongMonsterSignal && hasBasicCombatStats;

    if (isLikelyMonster) {
      const monsterData = this.extractBasicStats(text);
      monsterData.type = 'monster';

      // Extract CR
      const crMatch = text.match(/challenge rating?\s+(\d+(?:\/\d+)?)/i);
      if (crMatch) monsterData.cr = crMatch[1];

      // Extract size and type
      const sizeTypeMatch = text.match(/\b(tiny|small|medium|large|huge|gargantuan)\s+(beast|humanoid|undead|fiend|dragon|fey|elemental|aberration)\b/i);
      if (sizeTypeMatch) {
        monsterData.size = sizeTypeMatch[1].toLowerCase();
        monsterData.monsterType = sizeTypeMatch[2].toLowerCase();
      }

      // @date_of_change 2025-10-15 - Boost confidence when multiple key signals are present
      const signals = [
        Boolean(monsterData.abilities),
        Boolean(monsterData.ac),
        Boolean(monsterData.hp),
        Boolean(monsterData.cr)
      ];
      const strongSignalCount = signals.filter(Boolean).length;
      const baseConfidence = 0.8;
      const boostedConfidence = strongSignalCount >= 2 ? Math.min(0.95, baseConfidence + strongSignalCount * 0.05) : baseConfidence;

      return {
        type: 'monster',
        confidence: boostedConfidence,
        data: monsterData,
        suggestions: ['Import as new monster', 'Merge with existing monster']
      };
    }

    return { type: 'unknown', confidence: 0 };
  }

  static extractBasicStats(text) {
    const data = {};
    const cleanedText = String(text || '').replace(/__SMARTPASTE_CLAIM__/g, '');

    // Name (first non-empty line)
    const lines = cleanedText
      .split('\n')
      .map((line) => String(line || '').trim())
      .filter((line) => line && line !== '__SMARTPASTE_CLAIM__');
    if (lines.length > 0) {
      const firstLine = lines[0].trim();
      const looksLikeDelimitedSummary = /[,;]/.test(firstLine) && firstLine.split(/[,;]/).filter(Boolean).length >= 3;
      if (!looksLikeDelimitedSummary && firstLine !== '__SMARTPASTE_CLAIM__') {
        data.name = firstLine;
      }
    }

    // @date_of_change 2025-12-05 - Prefer explicit "Name: X" label when present over the first heading line
    const explicitNameMatch = cleanedText.match(/^\s*name\s*:\s*([^\n]+)/im);
    if (
      explicitNameMatch &&
      explicitNameMatch[1].trim() &&
      explicitNameMatch[1].trim() !== '__SMARTPASTE_CLAIM__'
    ) {
      data.name = explicitNameMatch[1].trim();
    }

    const experienceMatch = cleanedText.match(/^\s*(?:experience|xp)\s*:\s*([\d,]+)(?:\s*xp)?\s*$/im);
    if (experienceMatch) data.experience = parseInt(experienceMatch[1].replace(/,/g, ''), 10);

    // Ability scores
    // @date_of_change 2025-12-05 - Prefer explicit "Ability Scores:" line to avoid capturing companion stat blocks later in the text
    let abilitiesFromLine = null;
    const abilityScoresLineMatch = cleanedText.match(/ability\s*scores?\s*:\s*([^\n]+)/i);
    if (abilityScoresLineMatch && abilityScoresLineMatch[1]) {
      const body = abilityScoresLineMatch[1];
      const lineRe = /\b(STR|DEX|CON|INT|WIS|CHA|Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma)\b\s*[:=]?\s*(\d{1,2})/gi;
      const found = {};
      let m;
      while ((m = lineRe.exec(body)) !== null) {
        const key = this.normalizeAbilityName(m[1]);
        const val = parseInt(m[2], 10);
        if (!Number.isNaN(val)) found[key] = val;
      }
      if (Object.keys(found).length >= 3) {
        abilitiesFromLine = found;
      }
    }

    if (abilitiesFromLine) {
      data.abilities = abilitiesFromLine;
    } else {
      // Grid with modifiers (monster-style table)
      const abilityPattern = /STR\s+DEX\s+CON\s+INT\s+WIS\s+CHA\s+(\d+)\s*\([^)]*\)\s+(\d+)\s*\([^)]*\)\s+(\d+)\s*\([^)]*\)\s+(\d+)\s*\([^)]*\)\s+(\d+)\s*\([^)]*\)\s+(\d+)\s*\([^)]*\)/i;
      let abilityMatch = cleanedText.match(abilityPattern);
      if (abilityMatch) {
        data.abilities = {
          strength: parseInt(abilityMatch[1]),
          dexterity: parseInt(abilityMatch[2]),
          constitution: parseInt(abilityMatch[3]),
          intelligence: parseInt(abilityMatch[4]),
          wisdom: parseInt(abilityMatch[5]),
          charisma: parseInt(abilityMatch[6])
        };
      } else {
        // Compact list anywhere in the text: "STR 16 DEX 14 ..."
        // Keep this as a last resort to avoid picking companion stat blocks when a main "Ability Scores" line exists
        const abilRegexGlobal = /\b(STR|DEX|CON|INT|WIS|CHA|Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma)\b\s*:?\s*(\d{1,2})/gi;
        let m; const found = {};
        while ((m = abilRegexGlobal.exec(cleanedText)) !== null) {
          const key = this.normalizeAbilityName(m[1]);
          const val = parseInt(m[2], 10);
          if (!Number.isNaN(val)) found[key] = val;
        }
        if (Object.keys(found).length >= 3) data.abilities = { ...(data.abilities || {}), ...found };
      }
    }

    // AC
    const acMatch = cleanedText.match(/(?:armor\s+class|\bAC\b)\s*:?\s*(\d+)(?:\s*\(([^)]+)\))?/i);
    if (acMatch) data.ac = { value: parseInt(acMatch[1]), notes: acMatch[2] || '' };
    if (!data.ac) {
      const reverseAcMatch = cleanedText.match(/\b(\d+)\s*(?:armor\s+class|\bAC\b)\b/i);
      if (reverseAcMatch) data.ac = { value: parseInt(reverseAcMatch[1], 10), notes: '' };
    }

    // HP
    const hpMatch = cleanedText.match(/(?:hit\s+points|\bHP\b)\s*:?\s*(\d+)(?:\s*\(([^)]+)\))?/i);
    if (hpMatch) {
      const hpValue = parseInt(hpMatch[1]);
      data.maxHp = hpValue;
      if (hpMatch[2]) {
        // For objects, provide an average to satisfy character/monster validators
        data.hp = { average: hpValue, formula: hpMatch[2] };
      } else {
        data.hp = hpValue;
      }
    }
    if (!data.maxHp && !data.hp) {
      const reverseHpMatch = cleanedText.match(/\b(\d+)\s*(?:hit\s+points|\bHP\b)\b/i);
      if (reverseHpMatch) {
        const hpValue = parseInt(reverseHpMatch[1], 10);
        data.maxHp = hpValue;
        data.hp = hpValue;
      }
    }

    // FastCharacter and similar printable sheets put hit dice after hit points on the same line.
    const reverseHitDiceMatch = cleanedText.match(/\bhit\s+points\b[^\n]*?\b(\d+d\d+(?:\s*[+-]\s*\d+)?)\s+hit\s+dice\b/i);
    if (reverseHitDiceMatch) {
      data.hitDice = reverseHitDiceMatch[1].replace(/\s+/g, '');
      const hitDiceTotal = parseInt(reverseHitDiceMatch[1], 10);
      if (!Number.isNaN(hitDiceTotal)) data.hitDiceTotal = hitDiceTotal;
    }

    // @date_of_change 2025-12-05 - Capture Hit Dice lines, including total and per-class breakdown
    const hitDiceMatch = cleanedText.match(/^\s*hit\s*dice\s*:\s*([^\n]+)$/im);
    if (!data.hitDice && hitDiceMatch) {
      const rawHd = hitDiceMatch[1].trim();
      const totalMatch = rawHd.match(/^(\d+)\s*\(([^)]+)\)/);
      if (totalMatch) {
        const total = parseInt(totalMatch[1], 10);
        if (!Number.isNaN(total)) data.hitDiceTotal = total;
        data.hitDice = totalMatch[2].trim();
      } else {
        data.hitDice = rawHd;
      }
    }

    // Speed (supports multiple modes: walk, fly, climb, swim, burrow)
    const speedBlock = cleanedText.match(/(?:speed|movement)\s*:?:?\s*([^\n.]+)/i);
    if (speedBlock) {
      const block = speedBlock[1];
      const modes = {};
      // walk 30 ft., fly 60 ft., swim 20 ft.
      const modeRegex = /(walk|fly|climb|swim|burrow)\s*(\d+)\s*ft/gi;
      let mm;
      while ((mm = modeRegex.exec(block)) !== null) {
        modes[mm[1].toLowerCase()] = parseInt(mm[2]);
      }
      if (Object.keys(modes).length === 0) {
        const num = block.match(/(\d+)\s*ft/);
        if (num) modes.walk = parseInt(num[1]);
      }
      if (Object.keys(modes).length > 0) data.speed = modes;
    }
    const reverseSpeedMatch = cleanedText.match(/^\s*(\d+)\s*(?:ft\.?|feet)\s+speed\b/im);
    if (reverseSpeedMatch) data.speed = { walk: parseInt(reverseSpeedMatch[1], 10) };

    // Alignment
    // @date_of_change 2025-12-05 - Limit alignment capture to a single line to avoid swallowing following labels like "Hit Dice"
    const alignMatch = cleanedText.match(/alignment\s*:?\s*([A-Za-z -]+)/i);

    if (alignMatch) data.alignment = alignMatch[1].trim();

    // Initiative
    const initMatch = cleanedText.match(/initiative(?:\s+modifier)?\s*[:=]?\s*([+-]?\d+)/i);
    if (initMatch) data.initiative = parseInt(initMatch[1], 10);
    if (data.initiative === undefined) {
      const reverseInitiativeMatch = cleanedText.match(/^\s*([+-]?\d+)\s*\.\.\.\s*initiative(?:\s+modifier)?\b/im);
      if (reverseInitiativeMatch) data.initiative = parseInt(reverseInitiativeMatch[1], 10);
    }

    // PB
    const pbMatch = cleanedText.match(/(?:proficiency\s*bonus|\bPB\b)\s*[:+]?\s*(\+?\d+)/i);
    if (pbMatch) {
      const val = parseInt(pbMatch[1]);
      if (!Number.isNaN(val)) data.proficiencyBonus = val;
    }

    // Level & Class
    const classAndLevelMatch = cleanedText.match(/^\s*class\s*(?:&|and)\s*level\s*:\s*([A-Za-z][A-Za-z '-]*?)\s+(\d{1,2})(?:\s*\(([^)]+)\))?\s*$/im);
    const levelClass1 = cleanedText.match(/level\s*(\d+)\s*(?:of\s+)?(fighter|wizard|rogue|cleric|ranger|barbarian|bard|druid|monk|paladin|sorcerer|warlock)/i);
    const levelClass2 = cleanedText.match(/(fighter|wizard|rogue|cleric|ranger|barbarian|bard|druid|monk|paladin|sorcerer|warlock)\s*(\d+)/i);
    if (classAndLevelMatch) {
      data.class = classAndLevelMatch[1].trim();
      data.level = parseInt(classAndLevelMatch[2], 10);
      if (classAndLevelMatch[3]) data.subclass = classAndLevelMatch[3].trim();
      data.classes = [{
        name: data.class,
        level: data.level,
        ...(data.subclass ? { subclass: data.subclass } : {})
      }];
    } else if (levelClass1) {
      data.level = parseInt(levelClass1[1]);
      data.class = levelClass1[2].charAt(0).toUpperCase() + levelClass1[2].slice(1).toLowerCase();
    } else if (levelClass2) {
      data.class = levelClass2[1].charAt(0).toUpperCase() + levelClass2[1].slice(1).toLowerCase();
      data.level = parseInt(levelClass2[2]);
    }

    // date_of_change: 2026-02-25
    // rule_reference: Support standalone "Level: 25" lines that don't include a class name.
    if (!data.level) {
      const standaloneLevelMatch = text.match(/^\s*level\s*:\s*(\d+)/im);
      if (standaloneLevelMatch) {
        data.level = parseInt(standaloneLevelMatch[1], 10);
      }
    }

    // @date_of_change 2025-12-05 - Support multiclass "Classes:" lines (e.g., "Artificer (Battle Smith) 16 / Vampire 9")
    // @date_of_change 2026-02-26 - Also handle "Class: Fighter (Rune Knight)" where level is on a separate line.
    // rule_reference: PHB 2024 — class and subclass are core identity fields; must parse even when level is elsewhere.
    const classesLineMatch = cleanedText.match(/classes?\s*:?\s*([^\n]+)/i);
    if (classesLineMatch && classesLineMatch[1]) {
      const raw = classesLineMatch[1];
      const parts = raw.split(/[/;,]+/).map(s => s.trim()).filter(Boolean);
      const classes = [];
      for (const part of parts) {
        // Match: ClassName (Subclass) 16   OR   ClassName 16
        const m = part.match(/([A-Za-z][A-Za-z '-]+?)(?:\s*\(([^)]+)\))?\s*(\d{1,2})\b/);
        if (m) {
          const name = m[1].trim();
          const subclass = m[2] ? m[2].trim() : undefined;
          const lvl = parseInt(m[3], 10);
          if (Number.isNaN(lvl)) continue;
          const cls = { name, level: lvl };
          if (subclass) cls.subclass = subclass;
          classes.push(cls);
          continue;
        }
        // Fallback: ClassName (Subclass) without level number on same line
        const mNoLevel = part.match(/^([A-Za-z][A-Za-z '-]+?)(?:\s*\(([^)]+)\))\s*$/);
        if (mNoLevel) {
          const name = mNoLevel[1].trim();
          const subclass = mNoLevel[2] ? mNoLevel[2].trim() : undefined;
          const cls = { name, level: data.level || 1 };
          if (subclass) cls.subclass = subclass;
          classes.push(cls);
          continue;
        }
        // Fallback: bare class name without subclass or level (e.g., "Fighter")
        const mBare = part.match(/^([A-Za-z][A-Za-z '-]+?)\s*$/);
        if (mBare && mBare[1].trim().length >= 3) {
          classes.push({ name: mBare[1].trim(), level: data.level || 1 });
        }
      }
      if (classes.length) {
        data.classes = classes;
        const totalLevel = classes.reduce((sum, c) => sum + (c.level || 0), 0);
        if (!data.level && totalLevel > 0) data.level = totalLevel;
        if (!data.class) data.class = classes[0].name;
        if (!data.subclass && classes[0].subclass) data.subclass = classes[0].subclass;
      }
    }

    // date_of_change: 2026-02-27
    // rule_reference: Many sheets provide subclass as its own labeled field (e.g., "Subclass: Rune Knight").
    // Parse it explicitly so identity stays consistent when level/class are on separate lines.
    if (!data.subclass) {
      const subclassLabelMatch = text.match(/^\s*subclass\s*:\s*([^\n]+)/im);
      if (subclassLabelMatch && subclassLabelMatch[1] && subclassLabelMatch[1].trim()) {
        data.subclass = subclassLabelMatch[1].trim();
      }
    }
    if (!data.subclass) {
      const archetypeMatch = text.match(/^\s*(?:martial\s+archetype|archetype)\s*:\s*([^\n]+)/im);
      if (archetypeMatch && archetypeMatch[1] && archetypeMatch[1].trim()) {
        data.subclass = archetypeMatch[1].trim();
      }
    }

    // Race/Species
    // Race/Species (accept any value after label)
    const raceMatch = cleanedText.match(/^\s*(?:race\s*\/\s*ancestry\s*\/\s*heritage|race|species|ancestry|heritage)\s*:\s*([^\n]+)/im);
    if (raceMatch) {
      const r = raceMatch[1].trim();
      data.race = r;
      data.species = r;
    }

    // Background
    const bgLineIndex = lines.findIndex((line) => /^\s*background\s*:?\s*(.+)$/i.test(line));
    if (bgLineIndex !== -1) {
      const bgMatch = lines[bgLineIndex].match(/^\s*background\s*:?\s*(.+)$/i);
      const classifiedBackground = this.classifyBackgroundValue(bgMatch?.[1] || '');
      if (classifiedBackground.background) {
        data.background = classifiedBackground.background;
      }
      if (classifiedBackground.backstory) {
        data.biography = {
          ...(data.biography || {}),
          backstory: data.biography?.backstory || classifiedBackground.backstory
        };
        if (!data.biographyMeta?.backstory) {
          data.biographyMeta = {
            ...(data.biographyMeta || {}),
            backstory: this.buildParserMeta({
              sourceParser: 'parseCharacterStatBlock',
              sourceSection: 'backstory',
              lineStart: bgLineIndex + 1,
              lineEnd: bgLineIndex + 1,
              rawText: lines[bgLineIndex],
              parseConfidence: 0.74,
              knowledgeSource: 'provided',
              knowledgeNote: 'Narrative text under Background label was routed to backstory.'
            })
          };
        }
      }
    }

    // @date_of_change 2025-12-05 - Parse biographical fields (backstory, personality, ideals, bonds, flaws, physical description)
    const biographyParse = this.parseBiographyFields(text);
    if (biographyParse?.biography && Object.keys(biographyParse.biography).length > 0) {
      data.biography = {
        ...(data.biography || {}),
        ...biographyParse.biography
      };
      if (biographyParse.fieldMeta && Object.keys(biographyParse.fieldMeta).length > 0) {
        data.biographyMeta = {
          ...(data.biographyMeta || {}),
          ...biographyParse.fieldMeta
        };
      }
    }

    // Multi-line Senses and Languages sections
    const collectBlock = (headerRegex, stopRegexes = []) => {
      const idx = lines.findIndex(l => headerRegex.test(l));
      if (idx === -1) return [];
      const out = [];
      for (let i = idx + 1; i < lines.length; i++) {
        const ln = lines[i].trim();
        if (!ln) break;
        if (stopRegexes.some(re => re.test(ln))) break;
        out.push(ln);
      }
      return out;
    };

    // Senses
    const sensesBlock = collectBlock(/^\s*senses\s*$/i, [/^\s*languages\s*$/i, /^\s*damage\s*&\s*condition\s*traits/i, /^\s*skills?/i]);
    if (sensesBlock.length) {
      const senses = { ...(data.senses || {}) };
      for (const ln of sensesBlock) {
        const m1 = ln.match(/(darkvision|blindsight|tremorsense|truesight)\s*(\d+)\s*ft/i);
        if (m1) {
          senses[m1[1].toLowerCase()] = parseInt(m1[2], 10);
        }
        const pp = ln.match(/passive\s*perception\s*(\d+)/i);
        if (pp) data.passivePerception = parseInt(pp[1], 10);
      }
      if (Object.keys(senses).length) data.senses = senses;
    }

    // Languages
    const langsBlock = collectBlock(/^\s*languages\s*$/i, [/^\s*damage\s*&\s*condition\s*traits/i, /^\s*skills?/i, /^\s*abilities?\s*&\s*traits/i]);
    if (langsBlock.length) {
      const langs = langsBlock.map(s => s.replace(/^[-*•\u2022]\s+/, '').trim()).filter(Boolean);
      if (langs.length) data.languages = langs;
    }

    // Saving Throws (multi-line section)
    const savesBlock = collectBlock(/^\s*saving\s*throws\s*$/i, [/^\s*skill/i, /^\s*senses/i, /^\s*languages/i]);
    if (savesBlock.length) {
      const st = {};
      for (const ln of savesBlock) {
        const mm = ln.match(/(STR|DEX|CON|INT|WIS|CHA|Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma)\s*([+-]?\d+)/i);
        if (mm) {
          const key = this.normalizeAbilityName(mm[1]);
          const val = parseInt(mm[2], 10);
          if (!Number.isNaN(val)) st[key] = val;
        }
      }
      if (Object.keys(st).length) data.savingThrows = st;
    }

    // @date_of_change 2025-12-05 - Inline saving throws line: "Saving Throws: Con (+11), Int (+12)..."
    const inlineSavesMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*saving\s*throws?\s*:\s*([^\n]+)$/im);
    if (inlineSavesMatch && inlineSavesMatch[1]) {
      const st = data.savingThrows || {};
      const savesText = inlineSavesMatch[1];
      const pairRe = /(Strength|Dexterity|Constitution|Intelligence|Wisdom|Charisma|STR|DEX|CON|INT|WIS|CHA)\s*\(?\s*([+-]?\d+)/gi;
      let m2;
      while ((m2 = pairRe.exec(savesText)) !== null) {
        const key = this.normalizeAbilityName(m2[1]);
        const val = parseInt(m2[2], 10);
        if (!Number.isNaN(val)) st[key] = val;
      }
      if (Object.keys(st).length) data.savingThrows = st;
    }

    // @date_of_change 2025-12-05 - Inline skills line: "Skills: Arcana (+12), History (+12)..."
    // @date_of_change 2026-02-25 - Also support space-separated skills: "Athletics +9 Perception +5 Survival +5"
    // rule_reference: Skill lists can appear without commas in some sheet exports; parse repeated "SkillName +N" pairs.
    const parseSkillPairs = (body) => {
      const out = [];
      if (!body) return out;
      const pairRe = /([A-Za-z][A-Za-z\s']{1,30}?)\s*\(?\s*([+-]\d+)\s*\)?/g;
      let m;
      while ((m = pairRe.exec(body)) !== null) {
        const name = (m[1] || '').trim();
        const bonus = parseInt(m[2], 10);
        if (!name) continue;
        if (Number.isNaN(bonus)) continue;
        out.push({ name, bonus });
      }
      return out;
    };

    const skillsInlineMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*skills?\s*:\s*([^\n]+)$/im);
    if (skillsInlineMatch && skillsInlineMatch[1]) {
      const body = skillsInlineMatch[1].trim();
      const parsedPairs = parseSkillPairs(body);
      if (parsedPairs.length >= 2) {
        const skillNames = [];
        const skillsDetailed = [];
        for (const pair of parsedPairs) {
          if (skillNames.includes(pair.name)) continue;
          skillNames.push(pair.name);
          skillsDetailed.push({ name: pair.name, proficient: true, expertise: false, bonus: pair.bonus });
        }
        data.skills = skillNames;
        data.skillsDetailed = skillsDetailed;
      } else {
        const skillNames = [];
        const skillsDetailed = [];
        const segments = body.split(/[,;]/);
        for (const segRaw of segments) {
          const seg = segRaw.trim();
          if (!seg) continue;
          // Match: "Arcana (+12)" or "Arcana +12" or "Arcana (expertise) (+12)" or just "Arcana"
          const detailedMatch = seg.match(/([A-Za-z][A-Za-z\s']*?)(?:\s*\((expertise)\))?\s*(?:\(([+-]?\d+)\)|([+-]\d+))?\s*$/i);
          if (detailedMatch) {
            const name = detailedMatch[1].trim();
            if (!name) continue;
            const hasExpertise = Boolean(detailedMatch[2]);
            const bonus = detailedMatch[3] || detailedMatch[4];
            if (!skillNames.includes(name)) {
              skillNames.push(name);
              skillsDetailed.push({
                name,
                proficient: true,
                expertise: hasExpertise,
                bonus: bonus ? parseInt(bonus, 10) : null
              });
            }
          }
        }
        if (skillNames.length) {
          data.skills = skillNames;
          data.skillsDetailed = skillsDetailed;
        }
      }
    }

    // Multi-line Skills section: "Skills" on its own line, then one or more lines of skill pairs.
    // date_of_change: 2026-02-25
    // rule_reference: Some exports use a standalone Skills header without a colon.
    const skillsBlock = collectBlock(/^\s*skills\s*$/i, [/^\s*(senses|languages|damage\s*&\s*condition\s*traits|abilities\s*&\s*traits|actions|bonus\s*actions|reactions)\b/i]);
    if (skillsBlock.length) {
      const body = skillsBlock.join(' ');
      const parsedPairs = parseSkillPairs(body);
      if (parsedPairs.length) {
        const existing = Array.isArray(data.skillsDetailed) ? data.skillsDetailed : [];
        const seen = new Set(existing.map(s => (s.name || '').toLowerCase()));
        const nextDetailed = [...existing];
        const nextNames = Array.isArray(data.skills) ? [...data.skills] : [];
        for (const pair of parsedPairs) {
          const key = pair.name.toLowerCase();
          if (seen.has(key)) continue;
          nextDetailed.push({ name: pair.name, proficient: true, expertise: false, bonus: pair.bonus });
          nextNames.push(pair.name);
          seen.add(key);
        }
        if (nextNames.length) data.skills = nextNames;
        if (nextDetailed.length) data.skillsDetailed = nextDetailed;
      }
    }

    // date_of_change: 2026-02-26
    // rule_reference: Passive Perception appears as a standalone line in many character sheets (e.g., "Passive Perception 14").
    // Must extract it even when not inside a "Senses" block header.
    if (!data.passivePerception) {
      const ppStandalone = cleanedText.match(/passive\s*perception\s*[:=]?\s*(\d+)/i);
      if (ppStandalone) {
        data.passivePerception = parseInt(ppStandalone[1], 10);
      }
    }
    if (!data.passivePerception) {
      const reversePassiveMatch = cleanedText.match(/^\s*(\d+)\s*\.\.\.\s*passive\s+perception\b/im);
      if (reversePassiveMatch) data.passivePerception = parseInt(reversePassiveMatch[1], 10);
    }

    // Inline senses without a dedicated "Senses" header
    // date_of_change: 2026-02-25
    // rule_reference: Some character sheets list darkvision directly ("Darkvision 120 ft").
    const inlineSense = text.match(/\b(darkvision|blindsight|tremorsense|truesight)\s*(\d+)\s*ft\b/i);
    if (inlineSense) {
      const senses = { ...(data.senses || {}) };
      senses[String(inlineSense[1]).toLowerCase()] = parseInt(inlineSense[2], 10);
      data.senses = senses;
    }

    // Damage & Condition Traits subsections (Resistances, Immunities, Vulnerabilities, Condition Immunities)
    const traitsStart = lines.findIndex(l => /damage\s*&\s*condition\s*traits/i.test(l));
    if (traitsStart !== -1) {
      const subCollect = (labelOrRe) => {
        const headerRe = labelOrRe instanceof RegExp ? labelOrRe : new RegExp('^\\s*' + labelOrRe + '\\b', 'i');
        const idx = lines.findIndex(l => headerRe.test(l));
        if (idx === -1) return [];
        const out = [];
        for (let i = idx + 1; i < lines.length; i++) {
          const ln = lines[i].trim();
          if (!ln) break;
          // Stop at any next main header
          if (/^\s*(bonus\s*actions|reactions|equipment|magic\s*items|notes|lair|abilities\s*&\s*traits|relationships|allies\s*&\s*foes)\b/i.test(ln)) break;
          const line = ln.replace(/^[-*•\u2022]\s+/, '');
          const m = line.match(/^([^:]+):\s*(.+)$/);
          if (m) out.push(m[2].trim());
        }
        return out;
      };
      const resistBlock = subCollect('resistances');
      if (resistBlock.length) {
        const list = resistBlock.map(s => s.replace(/^[-*•\u2022]\s+/, '').trim()).filter(Boolean);
        if (list.length) data.resistances = list;
      }
      const immBlock = subCollect('immunities');
      if (immBlock.length) {
        const list = immBlock.map(s => s.replace(/^[-*•\u2022]\s+/, '').trim()).filter(Boolean);
        if (list.length) data.immunities = list;
      }
      const vulBlock = subCollect('vulnerabilities');
      if (vulBlock.length) {
        const list = vulBlock.map(s => s.replace(/^[-*•\u2022]\s+/, '').trim()).filter(Boolean);
        if (list.length) data.vulnerabilities = list;
      }
      const cImmBlock = subCollect(/^\s*condition\s*immunities\s*$/i);
      if (cImmBlock.length) {
        const list = cImmBlock.map(s => s.replace(/^[-*•\u2022]\s+/, '').trim()).filter(Boolean);
        if (list.length) data.conditionImmunities = list;
      }
    }

    // @date_of_change 2025-12-05 - Inline damage/condition trait lines without a dedicated header section
    const parseInlineList = (labelRegex, key) => {
      const match = text.match(labelRegex);
      if (!match || !match[1]) return;
      const rawList = match[1];
      const parts = /[•\u2022]/.test(rawList)
        ? rawList
          .split(/[•\u2022]+/)
          .map(s => s.replace(/^[-*•\u2022]\s+/, '').replace(/\.+$/, '').trim())
          .filter(Boolean)
        : rawList
          .split(/[,;]+/)
          .map(s => s.replace(/\.+$/, '').trim())
          .filter(Boolean);
      if (!parts.length) return;
      const existing = Array.isArray(data[key]) ? data[key] : [];
      const merged = existing.concat(parts.filter(p => !existing.includes(p)));
      if (merged.length) data[key] = merged;
    };

    parseInlineList(/resistances?\s*:?\s*([^\n]+)/i, 'resistances');
    parseInlineList(/immunities?\s*:?\s*([^\n]+)/i, 'immunities');
    parseInlineList(/vulnerabilities?\s*:?\s*([^\n]+)/i, 'vulnerabilities');
    parseInlineList(/condition\s+immunities?\s*:?\s*([^\n]+)/i, 'conditionImmunities');

    // date_of_change: 2026-02-25
    // rule_reference: D&D character sheets often list resistances/immunities as multi-line bullet lists.
    // After the single-line inline parse, scan for "Damage Resistances:" followed by bullet lines.
    const parseMultiLineBulletList = (headerRe, key) => {
      const allLines = text.split(/\r?\n/);
      const headerIdx = allLines.findIndex(l => headerRe.test(l.trim()));
      if (headerIdx === -1) return;
      const collected = [];
      for (let j = headerIdx + 1; j < allLines.length; j++) {
        const ln = allLines[j].trim();
        if (!ln) break;
        // Stop at next labeled section
        if (/^\s*[A-Z][A-Za-z\s]+:/.test(ln) && !/^[•\u2022\-*]/.test(ln)) break;
        const cleaned = ln.replace(/^[-*•\u2022]\s+/, '').replace(/\.+$/, '').trim();
        if (cleaned) collected.push(cleaned);
      }
      if (!collected.length) return;
      const existing = Array.isArray(data[key]) ? data[key] : [];
      const merged = existing.concat(collected.filter(p => !existing.includes(p)));
      if (merged.length) data[key] = merged;
    };

    parseMultiLineBulletList(/^\s*damage\s+resistances?\s*:?\s*$/i, 'resistances');
    parseMultiLineBulletList(/^\s*damage\s+immunities?\s*:?\s*$/i, 'immunities');
    parseMultiLineBulletList(/^\s*damage\s+vulnerabilities?\s*:?\s*$/i, 'vulnerabilities');
    parseMultiLineBulletList(/^\s*condition\s+immunities?\s*:?\s*$/i, 'conditionImmunities');

    // date_of_change: 2026-02-25
    // rule_reference: Parse proficiencies (armor, weapon, tool) and languages from stat block text.
    // CharacterSheetEditor expects character.proficiencies = { armor: [], weapons: [] },
    // character.toolProficiencies = [], character.languages = [].
    const proficiencies = {};

    // Armor proficiencies — inline "Armor: Light, Medium, Shields" or "Armor Training: Light, Medium, Heavy, Shields"
    const armorProfMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*armor\s*(?:training|proficienc\w*)?\s*:\s*([^\n]+)/im);
    if (armorProfMatch) {
      const armorList = armorProfMatch[1].split(/[,;]+/).map(s => s.trim().toLowerCase()).filter((value) => value && value !== 'none');
      if (armorList.length) proficiencies.armor = armorList;
    }

    // Weapon proficiencies — "Weapon Proficiencies: Simple, Martial" or "Weapons: Simple Weapons, Martial Weapons"
    const weaponProfMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*weapons?\s*(?:proficienc\w*)?\s*:\s*([^\n]+)/im);
    if (weaponProfMatch) {
      const weaponList = weaponProfMatch[1].split(/[,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
      if (weaponList.length) proficiencies.weapons = weaponList;
    }

    // Tool proficiencies — "Tool Proficiencies: Thieves' Tools, Smith's Tools"
    const toolProfMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*tools?(?:\s+proficienc\w*)?\s*:\s*([^\n]+)/im);
    if (toolProfMatch) {
      const toolList = toolProfMatch[1].split(/[,;]+/).map(s => s.trim()).filter(Boolean);
      if (toolList.length) {
        data.toolProficiencies = toolList;
        proficiencies.tools = toolList;
      }
    }

    const saveProfMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*saving\s+throws?\s*:\s*([^\n]+)/im);
    if (saveProfMatch) {
      const savingThrows = saveProfMatch[1]
        .split(/[,;]+/)
        .map((value) => this.normalizeAbilityName(value.trim()))
        .filter(Boolean);
      if (savingThrows.length) proficiencies.savingThrows = savingThrows;
    }

    const skillProfMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*skills?\s*:\s*([^\n]+)/im);
    if (skillProfMatch) {
      const skillNames = skillProfMatch[1].split(/[,;]+/).map(s => s.trim()).filter(Boolean);
      if (skillNames.length) {
        const existingDetailed = Array.isArray(data.skillsDetailed) ? data.skillsDetailed : [];
        const detailedByName = new Map(existingDetailed.map((entry) => [String(entry?.name || '').toLowerCase(), entry]));
        skillNames.forEach((name) => {
          const key = name.toLowerCase();
          if (!detailedByName.has(key)) {
            detailedByName.set(key, { name, proficient: true, expertise: false, bonus: null });
          }
        });
        data.skills = skillNames;
        data.skillsDetailed = [...detailedByName.values()];
      }
    }

    // FastCharacter prints calculated skill/save rows with one star for proficiency and two for expertise.
    const scoreRowRe = /^\s*([+-]?\d+)\s*\.{2,}\s*(.+?)\s+(saving\s+throws?|skill)\s*(\*{1,2})?\s*$/gim;
    const detailedSkills = new Map((data.skillsDetailed || []).map((entry) => [String(entry?.name || '').toLowerCase(), entry]));
    const savingThrowBonuses = { ...(data.savingThrows || {}) };
    let scoreRowMatch;
    while ((scoreRowMatch = scoreRowRe.exec(cleanedText)) !== null) {
      const bonus = parseInt(scoreRowMatch[1], 10);
      const label = scoreRowMatch[2].trim();
      const stars = scoreRowMatch[4] || '';
      if (/saving/i.test(scoreRowMatch[3])) {
        const ability = this.normalizeAbilityName(label);
        if (ability && stars.length >= 1) savingThrowBonuses[ability] = bonus;
        continue;
      }
      if (stars.length >= 1) {
        detailedSkills.set(label.toLowerCase(), {
          name: label,
          proficient: true,
          expertise: stars.length >= 2,
          bonus
        });
      }
    }
    if (Object.keys(savingThrowBonuses).length) data.savingThrows = savingThrowBonuses;
    if (detailedSkills.size) {
      data.skillsDetailed = [...detailedSkills.values()];
      data.skills = data.skillsDetailed.map((entry) => entry.name);
    }

    // Languages — standalone "Languages: Common, Elvish, Giant" (only if not already parsed by collectBlock above)
    if (!data.languages) {
      const langInlineMatch = cleanedText.match(/^\s*[-*•\u2022]?\s*languages?\s*:\s*([^\n]+)/im);
      if (langInlineMatch) {
        const langList = langInlineMatch[1].split(/[,;]+/).map(s => s.trim()).filter(Boolean);
        if (langList.length) data.languages = langList;
      }
    }

    if (Object.keys(proficiencies).length) data.proficiencies = proficiencies;

    return data;
  }

  static parseDetailedSpellBlock(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const lines = String(text || '')
      .split(/\r?\n/)
      .map((line, index) => ({
        raw: line,
        trimmed: String(line || '').trim(),
        lineNumber: index + 1 + lineOffset
      }))
      .filter((line) => line.trimmed);

    if (lines.length < 2) return null;

    const titleIndex = lines.findIndex((line) => this.looksLikeSpellTitleLine(line.trimmed));
    if (titleIndex === -1) return null;

    const name = lines[titleIndex].trimmed;
    let level = null;
    let school = 'universal';
    const labeledFields = {};
    const descriptionLines = [];
    const metadataLineNumbers = new Set([lines[titleIndex].lineNumber]);
    const metadataRawLines = [lines[titleIndex].raw];
    const captureMetadataLine = (line) => {
      metadataLineNumbers.add(line.lineNumber);
      if (!metadataRawLines.includes(line.raw)) {
        metadataRawLines.push(line.raw);
      }
    };

    lines.forEach((line, index) => {
      if (index === titleIndex) return;
      const trimmed = line.trimmed;

      const cantripSchoolMatch = trimmed.match(/^(abjuration|conjuration|divination|enchantment|evocation|illusion|necromancy|transmutation)\s+cantrip\b/i);
      if (cantripSchoolMatch) {
        level = 0;
        school = cantripSchoolMatch[1].toLowerCase();
        captureMetadataLine(line);
        return;
      }

      const levelSchoolMatch = trimmed.match(/^(\d)\s*(?:st|nd|rd|th)[-\s]*level\b(?:\s+(abjuration|conjuration|divination|enchantment|evocation|illusion|necromancy|transmutation))?/i);
      if (levelSchoolMatch) {
        level = Number.parseInt(levelSchoolMatch[1], 10);
        if (levelSchoolMatch[2]) {
          school = levelSchoolMatch[2].toLowerCase();
        }
        captureMetadataLine(line);
        return;
      }

      const reverseLevelSchoolMatch = trimmed.match(/^(abjuration|conjuration|divination|enchantment|evocation|illusion|necromancy|transmutation)\s+(\d)\s*(?:st|nd|rd|th)[-\s]*level\b/i);
      if (reverseLevelSchoolMatch) {
        school = reverseLevelSchoolMatch[1].toLowerCase();
        level = Number.parseInt(reverseLevelSchoolMatch[2], 10);
        captureMetadataLine(line);
        return;
      }

      const labeledMatch = trimmed.match(/^(level(?:\s*text)?|casting\s*time|range|components?|duration|save|saving\s*throw|spell\s*attack(?:\s*(?:bonus|modifier))?|school)\s*:?\s*(.+)$/i);
      if (labeledMatch) {
        const normalizedLabel = labeledMatch[1].toLowerCase();
        const normalizedValue = labeledMatch[2].trim();
        labeledFields[normalizedLabel] = normalizedValue;
        if (/^level(?:\s*text)?$/.test(normalizedLabel)) {
          const labeledLevelMatch = normalizedValue.match(/^(\d)\s*(?:st|nd|rd|th)[-\s]*level\b(?:\s+(abjuration|conjuration|divination|enchantment|evocation|illusion|necromancy|transmutation))?/i);
          const labeledCantripMatch = normalizedValue.match(/^(abjuration|conjuration|divination|enchantment|evocation|illusion|necromancy|transmutation)\s+cantrip\b/i);
          if (labeledLevelMatch) {
            level = Number.parseInt(labeledLevelMatch[1], 10);
            if (labeledLevelMatch[2]) {
              school = labeledLevelMatch[2].toLowerCase();
            }
          } else if (labeledCantripMatch) {
            level = 0;
            school = labeledCantripMatch[1].toLowerCase();
          }
        }
        captureMetadataLine(line);
        return;
      }

      if (this.isSpellMetadataLikeLine(trimmed)) {
        captureMetadataLine(line);
        return;
      }

      descriptionLines.push(trimmed);
    });

    if (level === null && lines.some((line) => /\bcantrip\b/i.test(line.trimmed))) {
      level = 0;
    }

    const spell = {
      name,
      level: level !== null ? level : 0,
      school,
      castingTime: labeledFields['casting time'] || '1 action',
      range: labeledFields.range || 'Self',
      components: labeledFields.components || labeledFields.component || 'V, S',
      duration: labeledFields.duration || 'Instantaneous',
      description: descriptionLines.join('\n').trim() || metadataRawLines.join('\n').trim()
    };

    if (/concentration/i.test(spell.duration)) {
      spell.concentration = true;
    }

    const saveMatch = String(labeledFields.save || labeledFields['saving throw'] || '').match(/\b(str(?:ength)?|dex(?:terity)?|con(?:stitution)?|int(?:elligence)?|wis(?:dom)?|cha(?:risma)?)\b/i);
    if (saveMatch) {
      spell.save = {
        ability: this.normalizeAbilityName(saveMatch[1])
      };
    }

    spell.parserMeta = this.buildParserMeta({
      sourceParser: 'parseSpellBlocks',
      sourceSection: 'spells',
      lineStart: Math.min(...metadataLineNumbers),
      lineEnd: Math.max(...metadataLineNumbers),
      rawText: metadataRawLines.join('\n'),
      parseConfidence: metadataRawLines.length >= 4 ? 0.84 : 0.72,
      knowledgeSource: 'provided',
      knowledgeNote: 'Parsed from keyword-and-context spell block text.'
    });

    return spell;
  }

  /**
   * Parse spell blocks from stat text: Cantrips, Nth-level (X slots), Prepared Spells, At will
   * Emits canonical spells object: { byLevel, prepared, atWill, slots, ability, attackBonus, saveDC }
   * Adds a warning when we infer slots.used = 0 from totals, per AGENTS.md no-silent-defaults.
   */
  static parseSpellBlocks(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const lines = text.split(/\r?\n/);
    const byLevel = {};
    const prepared = [];
    const atWill = [];
    const slots = {};
    const warnings = [];
    const matchedSpellLineNumbers = new Set();
    const matchedSpellLines = [];
    const matchedSpellcastingLineNumbers = new Set();
    const matchedSpellcastingLines = [];
    const recordMatchedLine = (lineNumber, rawLine, lineNumbers, rawLines) => {
      if (!Number.isInteger(lineNumber)) return;
      const trimmedRaw = String(rawLine || '').trim();
      if (!trimmedRaw) return;
      if (!lineNumbers.has(lineNumber)) {
        lineNumbers.add(lineNumber);
      }
      if (!rawLines.includes(trimmedRaw)) {
        rawLines.push(trimmedRaw);
      }
    };
    const buildSpellParserMeta = ({
      sourceSection = 'spells',
      lineNumbers = [],
      rawLines = [],
      parseConfidence = 0.74,
      knowledgeSource = 'provided',
      knowledgeNote
    } = {}) => {
      const numericLines = Array.from(lineNumbers).filter(Number.isInteger);
      if (!numericLines.length && !(Array.isArray(rawLines) && rawLines.length)) return null;
      return this.buildParserMeta({
        sourceParser: 'parseSpellBlocks',
        sourceSection,
        lineStart: numericLines.length ? Math.min(...numericLines) : undefined,
        lineEnd: numericLines.length ? Math.max(...numericLines) : undefined,
        rawText: Array.isArray(rawLines) && rawLines.length ? rawLines.join('\n') : undefined,
        parseConfidence,
        knowledgeSource,
        knowledgeNote
      });
    };

    const cantripRe = /^\s*cantrips?(?:\s+(?:known|prepared))?\s*(?:\(.*?\))?\s*:\s*(.+)$/i;
    const levelLineRe = /^\s*(\d)\s*(?:st|nd|rd|th)\s*level\s*(?:\((\d+)\s*slots?\))?\s*:\s*(.+)$/i;
    const slotsSummaryRe = /(?:^|\b)(\d)(?:st|nd|rd|th)\s*(\d+)\b/g; // "1st 4, 2nd 3"
    // @date_of_change 2025-12-05 - Support variants like "Prepared Spells Include (13): ..."
    const preparedHeaderRe = /^\s*prepared\s*spells?(?:\s+include[sd]?(?:\s*\([^)]*\))?)?\s*:\s*(.*)$/i;

    const atWillRe = /^\s*at\s*will\s*:\s*(.+)$/i;

    for (let lineIndex = 0; lineIndex < lines.length; lineIndex += 1) {
      const raw = lines[lineIndex];
      const line = raw.trim();
      const actualLineNumber = lineIndex + 1 + lineOffset;
      if (!line) continue;

      let m;
      if ((m = line.match(cantripRe))) {
        recordMatchedLine(actualLineNumber, raw, matchedSpellLineNumbers, matchedSpellLines);
        const list = m[1].split(/[,;]|\band\b/i).map(s => s.trim()).filter(Boolean);
        if (list.length) byLevel[0] = list.map(n => ({ name: n }));
        continue;
      }
      if ((m = line.match(levelLineRe))) {
        recordMatchedLine(actualLineNumber, raw, matchedSpellLineNumbers, matchedSpellLines);
        const lvl = parseInt(m[1], 10);
        const totalSlots = m[2] ? parseInt(m[2], 10) : null;
        const list = (m[3] || '')
          .split(/[,;]|\band\b/i)
          .map(s => s.trim())
          .filter(Boolean);
        if (list.length) byLevel[lvl] = list.map(n => ({ name: n }));
        if (Number.isFinite(totalSlots)) {
          slots[lvl] = { total: totalSlots, used: 0 };
          warnings.push(`Spell slots for level ${lvl} set with used=0; adjust if needed.`);
        }
        continue;
      }
      if ((m = line.match(preparedHeaderRe))) {
        recordMatchedLine(actualLineNumber, raw, matchedSpellLineNumbers, matchedSpellLines);
        const body = m[1] || '';
        // @date_of_change 2025-12-05 - Support inline per-level prepared lists (e.g., "1st: Witch Bolt, heroism. 2nd: ...")
        let foundLeveled = false;
        const levelSegmentRe = /(\d)\s*(?:st|nd|rd|th)?\s*:\s*([^.;]+)/gi;
        let lm;
        while ((lm = levelSegmentRe.exec(body)) !== null) {
          foundLeveled = true;
          const lvl = parseInt(lm[1], 10);
          if (Number.isNaN(lvl)) continue;
          const names = lm[2]
            .split(/[,;]/)
            .map(s => s.replace(/\([^)]*\)/g, '').trim())
            .filter(Boolean);
          if (!names.length) continue;
          const arr = Array.isArray(byLevel[lvl]) ? byLevel[lvl] : [];
          byLevel[lvl] = arr.concat(names.map(n => ({ name: n })));
        }

        if (!foundLeveled) {
          const list = body
            .split(/[,;]|\band\b/i)
            .map(s => s.trim())
            .filter(Boolean);
          if (list.length) prepared.push(...list.map(n => ({ name: n })));
        } else {
          // Mirror into prepared for convenience when per-level parsing succeeded
          const flatPrepared = Object.values(byLevel).flat();
          if (flatPrepared.length) prepared.push(...flatPrepared);
        }
        continue;
      }
      if ((m = line.match(atWillRe))) {
        recordMatchedLine(actualLineNumber, raw, matchedSpellLineNumbers, matchedSpellLines);
        const list = (m[1] || '')
          .split(/[,;]|\band\b/i)
          .map(s => s.trim())
          .filter(Boolean);
        if (list.length) atWill.push(...list.map(n => ({ name: n })));
        continue;
      }
      // Slots summary on a single line
      if (/spell\s*slots\s*:/i.test(line)) {
        recordMatchedLine(actualLineNumber, raw, matchedSpellLineNumbers, matchedSpellLines);
        let sm;
        while ((sm = slotsSummaryRe.exec(line)) !== null) {
          const lvl = parseInt(sm[1], 10);
          const total = parseInt(sm[2], 10);
          if (Number.isFinite(lvl) && Number.isFinite(total)) {
            slots[lvl] = { total, used: 0 };
            warnings.push(`Spell slots for level ${lvl} set with used=0; adjust if needed.`);
          }
        }
      }

      // @date_of_change 2025-12-05 - Support "Level:Slots = 1:4, 2:3, ..." summaries
      if (/level\s*:\s*slots\s*=\s*/i.test(line)) {
        recordMatchedLine(actualLineNumber, raw, matchedSpellLineNumbers, matchedSpellLines);
        const summaryMatch = line.match(/level\s*:\s*slots\s*=\s*([^\n]+)/i);
        if (summaryMatch && summaryMatch[1]) {
          const pairs = summaryMatch[1].split(/[,;]+/).map(s => s.trim()).filter(Boolean);
          for (const pair of pairs) {
            const pm = pair.match(/(\d)\s*[:]\s*(\d+)/);
            if (!pm) continue;
            const lvl = parseInt(pm[1], 10);
            const total = parseInt(pm[2], 10);
            if (Number.isFinite(lvl) && Number.isFinite(total)) {
              slots[lvl] = { total, used: 0 };
              warnings.push(`Spell slots for level ${lvl} set with used=0; adjust if needed.`);
            }
          }
        }
      }
    }

    // Ability/DC/Attack bonus commonly appear near spellcasting text
    let abilityMatch = null;
    let saveDcMatch = null;
    let atkMatch = null;
    const spellAbilityRe = /spell\s*casting\s*ability\s*:?:?\s*([A-Za-z]+)/i;
    const spellSaveDcRe = /spell\s*save\s*dc\s*:?:?\s*(\d+)/i;
    const spellAtkSpecificRe = /spell\s*attack\s*(?:modifier|bonus)?\s*:?:?\s*[+]?\s*(\d+)/i;
    const spellAtkGenericRe = /\b\+\s*(\d+)\s*to\s*hit\s*(?:with\s*spell\s*attacks)?/i;
    lines.forEach((rawLine, lineIndex) => {
      const line = rawLine.trim();
      const actualLineNumber = lineIndex + 1 + lineOffset;
      if (!line) return;

      if (!abilityMatch) {
        const nextAbilityMatch = line.match(spellAbilityRe);
        if (nextAbilityMatch) {
          abilityMatch = nextAbilityMatch;
          recordMatchedLine(actualLineNumber, rawLine, matchedSpellcastingLineNumbers, matchedSpellcastingLines);
        }
      }

      if (!saveDcMatch) {
        const nextSaveDcMatch = line.match(spellSaveDcRe);
        if (nextSaveDcMatch) {
          saveDcMatch = nextSaveDcMatch;
          recordMatchedLine(actualLineNumber, rawLine, matchedSpellcastingLineNumbers, matchedSpellcastingLines);
        }
      }

      if (!atkMatch) {
        const nextAtkMatch = line.match(spellAtkSpecificRe) || line.match(spellAtkGenericRe);
        if (nextAtkMatch) {
          atkMatch = nextAtkMatch;
          recordMatchedLine(actualLineNumber, rawLine, matchedSpellcastingLineNumbers, matchedSpellcastingLines);
        }
      }
    });

    const detailedSpellClaims = this.buildSpellKeywordClaims(text);
    const appendSpellEntry = (entry, listKey = 'known') => {
      if (!entry?.name) return;
      const spellLevel = Number.isFinite(Number(entry.level)) ? Number(entry.level) : 0;
      byLevel[spellLevel] = Array.isArray(byLevel[spellLevel]) ? byLevel[spellLevel] : [];
      const existingEntries = byLevel[spellLevel];
      const duplicate = existingEntries.find((existing) => (
        this.normalizeParsedEntryName(existing?.name) === this.normalizeParsedEntryName(entry.name)
      ));
      if (!duplicate) {
        existingEntries.push(entry);
      }
      if (listKey === 'prepared') {
        prepared.push(entry);
      } else if (listKey === 'atWill') {
        atWill.push(entry);
      }
    };

    detailedSpellClaims.forEach((claim) => {
      const parsedSpell = this.parseDetailedSpellBlock(claim.text, { lineOffset: claim.startLine - 1 });
      if (!parsedSpell) return;
      appendSpellEntry(parsedSpell, parsedSpell.level === 0 ? 'atWill' : 'known');
      recordMatchedLine(parsedSpell.parserMeta?.lineStart, claim.text, matchedSpellLineNumbers, matchedSpellLines);
    });

    const spells = {};
    if (Object.keys(byLevel).length) spells.byLevel = byLevel;
    if (prepared.length) spells.prepared = prepared;
    if (atWill.length) spells.atWill = atWill;
    if (Object.keys(slots).length) spells.slots = slots;
    if (abilityMatch) spells.ability = this.normalizeAbilityName(abilityMatch[1]);
    if (saveDcMatch) spells.saveDC = parseInt(saveDcMatch[1], 10);
    if (atkMatch) spells.attackBonus = parseInt(atkMatch[1], 10);

    const spellListMeta = buildSpellParserMeta({
      sourceSection: 'spells',
      lineNumbers: matchedSpellLineNumbers,
      rawLines: matchedSpellLines,
      parseConfidence: 0.78,
      knowledgeSource: 'provided',
      knowledgeNote: 'Parsed from explicit spell list text.'
    });
    if (spellListMeta) {
      spells.parserMeta = spellListMeta;
    }

    const spellcastingMeta = buildSpellParserMeta({
      sourceSection: 'spellcasting',
      lineNumbers: matchedSpellcastingLineNumbers,
      rawLines: matchedSpellcastingLines,
      parseConfidence: 0.8,
      knowledgeSource: 'provided',
      knowledgeNote: 'Parsed from explicit spellcasting text.'
    });

    if (Object.keys(spells).length === 0) return null;
    return {
      spells,
      warnings,
      spellcastingMeta,
      parsedEntries: this.buildStructuredSectionParsedEntries({
        spells: this.buildSpellEntriesFromSpellData(spells)
      })
    };
  }

  /**
   * Parse biographical fields from character text
   * @date_of_change 2025-12-05 - Added to support backstory, personality, ideals, bonds, flaws, physical description
   */
  static parseBiographyFields(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const biography = {};
    const fieldMeta = {};
    const lines = text.split(/\r?\n/);
    const buildBiographyFieldMeta = ({
      fieldKey,
      lineStart,
      lineEnd,
      rawText,
      parseConfidence = 0.68,
      knowledgeSource = 'provided',
      knowledgeNote
    } = {}) => this.buildParserMeta({
      sourceParser: 'parseBiographyFields',
      sourceSection: fieldKey || 'biography',
      lineStart,
      lineEnd,
      rawText,
      parseConfidence,
      knowledgeSource,
      knowledgeNote
    });
    const setBiographyField = (fieldKey, value, meta = {}) => {
      const normalizedValue = typeof value === 'string' ? value.trim() : String(value || '').trim();
      if (!fieldKey || !normalizedValue) return;
      biography[fieldKey] = normalizedValue;
      const parserMeta = buildBiographyFieldMeta({ fieldKey, ...meta });
      if (parserMeta && Object.keys(parserMeta).length > 0) {
        fieldMeta[fieldKey] = parserMeta;
      }
    };
    const findFirstMatchingLine = (regex) => {
      for (let i = 0; i < lines.length; i += 1) {
        const match = lines[i].match(regex);
        if (match) {
          return {
            match,
            lineNumber: i + 1 + lineOffset,
            rawText: lines[i]
          };
        }
      }
      return null;
    };

    // Helper to collect multi-line content under a header until another header or blank
    const collectSection = (headerRe, stopRe = /^\s*(personality|ideals?|bonds?|flaws?|backstory|history|physical|appearance|description|notes?|allies|relationships?|equipment|spells?|features?|actions?)\s*:?/i) => {
      const idx = lines.findIndex(l => headerRe.test(l));
      if (idx === -1) return null;
      const content = [];
      const rawLines = [];
      rawLines.push(lines[idx]);
      let endLine = idx + 1 + lineOffset;
      // Check if header line has inline content
      const headerMatch = lines[idx].match(headerRe);
      if (headerMatch && headerMatch[1] && headerMatch[1].trim()) {
        content.push(headerMatch[1].trim());
      }
      for (let i = idx + 1; i < lines.length; i++) {
        const ln = lines[i].trim();
        if (!ln) {
          if (content.length > 0) break; // End at first blank after content
          continue;
        }
        if (stopRe.test(ln)) break;
        content.push(ln);
        rawLines.push(lines[i]);
        endLine = i + 1 + lineOffset;
      }
      if (content.length === 0) return null;
      return {
        text: content.join('\n'),
        lineStart: idx + 1 + lineOffset,
        lineEnd: endLine,
        rawText: rawLines.join('\n')
      };
    };

    // Personality traits
    const personality = collectSection(/^\s*personality(?:\s+traits?)?\s*:?\s*(.*)$/i);
    if (personality) setBiographyField('personality', personality.text, personality);

    // Ideals
    const ideals = collectSection(/^\s*ideals?\s*:?\s*(.*)$/i);
    if (ideals) setBiographyField('ideals', ideals.text, ideals);

    // Bonds
    const bonds = collectSection(/^\s*bonds?\s*:?\s*(.*)$/i);
    if (bonds) setBiographyField('bonds', bonds.text, bonds);

    // Flaws
    const flaws = collectSection(/^\s*flaws?\s*:?\s*(.*)$/i);
    if (flaws) setBiographyField('flaws', flaws.text, flaws);

    // FastCharacter prints background roleplay fields as labeled bullets rather than section headers.
    [
      { fieldKey: 'personality', re: /^\s*[-*•\u2022]\s*traits?\s*:\s*(.+)$/i },
      { fieldKey: 'ideals', re: /^\s*[-*•\u2022]\s*ideals?\s*:\s*(.+)$/i },
      { fieldKey: 'bonds', re: /^\s*[-*•\u2022]\s*bonds?\s*:\s*(.+)$/i },
      { fieldKey: 'flaws', re: /^\s*[-*•\u2022]\s*flaws?\s*:\s*(.+)$/i }
    ].forEach(({ fieldKey, re }) => {
      if (biography[fieldKey]) return;
      const fieldMatch = findFirstMatchingLine(re);
      if (!fieldMatch) return;
      setBiographyField(fieldKey, fieldMatch.match[1], {
        lineStart: fieldMatch.lineNumber,
        lineEnd: fieldMatch.lineNumber,
        rawText: fieldMatch.rawText,
        parseConfidence: 0.9
      });
    });

    // Backstory / History
    const backstory = collectSection(/^\s*(?:backstory|history|background\s+story)\s*:?\s*(.*)$/i);
    if (backstory) setBiographyField('backstory', backstory.text, backstory);

    // Physical description / Appearance
    const appearance = collectSection(/^\s*(?:physical\s+description|appearance|looks?)\s*:?\s*(.*)$/i);
    if (appearance) setBiographyField('appearance', appearance.text, appearance);

    // Notes
    const notes = collectSection(/^\s*notes?\s*:?\s*(.*)$/i);
    if (notes) setBiographyField('notes', notes.text, notes);

    // date_of_change: 2026-02-25
    // rule_reference: Allies & Organizations — separate section in many stat blocks
    const allies = collectSection(/^\s*(?:allies(?:\s*(?:&|and)\s*(?:organizations?|foes))?|relationships?)\s*:?\s*(.*)$/i);
    if (allies) setBiographyField('allies', allies.text, allies);

    // date_of_change: 2026-02-25
    // rule_reference: Many character sheet exports mash personality, ideals, bonds, and flaws into a single
    // bullet-separated block like "• trait1 • trait2 Ideals • ideal1 Bond • bond1 Flaw • flaw1".
    // When we got personality but NOT ideals/bonds/flaws from headed sections, attempt to split inline keywords.
    if (biography.personality && !biography.ideals && !biography.bonds && !biography.flaws) {
      const raw = biography.personality;
      // Split on keyword boundaries: "Ideals", "Ideal", "Bond", "Bonds", "Flaw", "Flaws"
      // These keywords may be preceded by a bullet separator or stand alone.
      const keywordRe = /(?:^|\s+|[•\u2022]\s*|\.\s+|\n\s*)\b(Ideals?|Bonds?|Flaws?)\b\s*[•\u2022:.\-—]?\s*/i;
      if (keywordRe.test(raw)) {
        // Use a global split that captures the keyword
        const splitRe = /(?:\s+|[•\u2022]\s*|\.\s+|\n\s*)?\b(Ideals?|Bonds?|Flaws?)\b\s*[•\u2022:.\-—]?\s*/gi;
        const parts = [];
        let lastIndex = 0;
        let m;
        while ((m = splitRe.exec(raw)) !== null) {
          if (m.index > lastIndex) {
            parts.push({ key: null, text: raw.slice(lastIndex, m.index) });
          }
          parts.push({ key: m[1].toLowerCase().replace(/s$/, ''), marker: m.index });
          lastIndex = splitRe.lastIndex;
        }
        if (lastIndex < raw.length) {
          parts.push({ key: null, text: raw.slice(lastIndex) });
        }

        // Reassemble: text after a keyword marker belongs to that field
        let currentKey = 'personality';
        const buckets = { personality: [], ideal: [], bond: [], flaw: [] };
        for (const part of parts) {
          if (part.key) {
            currentKey = part.key;
          } else if (part.text) {
            const cleaned = part.text
              .split(/[•\u2022]+/)
              .map(s => s.trim())
              .filter(Boolean)
              .join('\n• ');
            if (cleaned) {
              const bucket = buckets[currentKey] || buckets.personality;
              bucket.push(cleaned);
            }
          }
        }

        if (buckets.personality.length) biography.personality = buckets.personality.join('\n• ');
        const personalityParserMeta = fieldMeta.personality;
        const buildInlineSplitMeta = (fieldKey) => this.mergeParserMeta(
          personalityParserMeta,
          {
            sourceParser: 'parseBiographyFields',
            sourceSection: fieldKey,
            parseConfidence: 0.55,
            knowledgeSource: 'inferred',
            knowledgeNote: 'Split from combined personality text using inline biography keywords.'
          }
        );
        if (buckets.ideal.length) {
          biography.ideals = buckets.ideal.join('\n• ');
          fieldMeta.ideals = buildInlineSplitMeta('ideals');
        }
        if (buckets.bond.length) {
          biography.bonds = buckets.bond.join('\n• ');
          fieldMeta.bonds = buildInlineSplitMeta('bonds');
        }
        if (buckets.flaw.length) {
          biography.flaws = buckets.flaw.join('\n• ');
          fieldMeta.flaws = buildInlineSplitMeta('flaws');
        }
      }
    }

    // Age, Height, Weight, Eyes, Hair, Skin
    // @date_of_change 2025-12-06 - Require colon after label to avoid false positives (e.g., "Eyebite" spell)
    const ageMatch = findFirstMatchingLine(/\bage\s*:\s*(\d+(?:\s*years?)?)/i);
    if (ageMatch) setBiographyField('age', ageMatch.match[1].trim(), {
      lineStart: ageMatch.lineNumber,
      lineEnd: ageMatch.lineNumber,
      rawText: ageMatch.rawText,
      parseConfidence: 0.72
    });

    const heightMatch = findFirstMatchingLine(/\bheight\s*:\s*([^\n,;]+)/i);
    if (heightMatch) setBiographyField('height', heightMatch.match[1].trim(), {
      lineStart: heightMatch.lineNumber,
      lineEnd: heightMatch.lineNumber,
      rawText: heightMatch.rawText,
      parseConfidence: 0.72
    });

    const weightMatch = findFirstMatchingLine(/\bweight\s*:\s*([^\n,;]+)/i);
    if (weightMatch) setBiographyField('weight', weightMatch.match[1].trim(), {
      lineStart: weightMatch.lineNumber,
      lineEnd: weightMatch.lineNumber,
      rawText: weightMatch.rawText,
      parseConfidence: 0.72
    });

    // Require "eyes:" or "eye color:" to avoid matching spell names like "Eyebite"
    const eyesMatch = findFirstMatchingLine(/\b(?:eyes?|eye\s+color)\s*:\s*([A-Za-z]+(?: [A-Za-z]+)?)/i);
    if (eyesMatch) setBiographyField('eyes', eyesMatch.match[1].trim(), {
      lineStart: eyesMatch.lineNumber,
      lineEnd: eyesMatch.lineNumber,
      rawText: eyesMatch.rawText,
      parseConfidence: 0.72
    });

    const hairMatch = findFirstMatchingLine(/\bhair\s*:\s*([^\n,;]+)/i);
    if (hairMatch) setBiographyField('hair', hairMatch.match[1].trim(), {
      lineStart: hairMatch.lineNumber,
      lineEnd: hairMatch.lineNumber,
      rawText: hairMatch.rawText,
      parseConfidence: 0.72
    });

    const skinMatch = findFirstMatchingLine(/\bskin\s*:\s*([^\n,;]+)/i);
    if (skinMatch) setBiographyField('skin', skinMatch.match[1].trim(), {
      lineStart: skinMatch.lineNumber,
      lineEnd: skinMatch.lineNumber,
      rawText: skinMatch.rawText,
      parseConfidence: 0.72
    });

    if (Object.keys(biography).length === 0) return null;
    return {
      biography,
      fieldMeta
    };
  }

  /**
   * Parse inventory/equipment blocks (Equipment, Inventory, Gear).
   * Emits { items: [{ name, type:'item' }] }
   * @date_of_change 2025-12-05 - Enhanced to also capture inline equipment lists (e.g., "Equipment: sword, shield")
   */
  // date_of_change: 2026-02-25
  // rule_reference: Character sheets have multiple equipment sections (WEAPONS, ARMOR & SHIELD, MAGIC ITEMS & GEAR, Potions).
  // Each section should be parsed independently with appropriate item types.
  static parseInventoryFromText(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const lines = text.split(/\r?\n/);
    const items = [];
    const currency = {};
    const buildEquipmentMeta = ({ sourceSection = 'equipment', lineStart, lineEnd, rawText, parseConfidence = 0.74, reviewReason } = {}) => this.buildParserMeta({
      sourceParser: 'parseInventoryFromText',
      sourceSection,
      lineStart,
      lineEnd: lineEnd ?? lineStart,
      rawText,
      parseConfidence,
      reviewReason
    });

    const wealthLineMatch = text.match(/^\s*coins\s*&\s*gems\s*:\s*([^\n]+)/im);
    if (wealthLineMatch) {
      const coinKeyMap = { cp: 'copper', sp: 'silver', ep: 'electrum', gp: 'gold', pp: 'platinum' };
      const coinRe = /([\d,]+)\s+(?:copper|silver|electrum|gold|platinum)\s+pieces?\s*\((cp|sp|ep|gp|pp)\)/gi;
      let coinMatch;
      while ((coinMatch = coinRe.exec(wealthLineMatch[1])) !== null) {
        currency[coinKeyMap[coinMatch[2].toLowerCase()]] = parseInt(coinMatch[1].replace(/,/g, ''), 10);
      }
      wealthLineMatch[1]
        .split(/;+/)
        .map((value) => value.trim())
        .filter((value) => value && !/\bpieces?\s*\((?:cp|sp|ep|gp|pp)\)/i.test(value) && /\b(?:gems?|jewels?|art\s+objects?)\b/i.test(value))
        .forEach((value) => {
          const quantityMatch = value.match(/^(\d+)\s+(.+)$/);
          items.push({
            name: quantityMatch ? quantityMatch[2].trim() : value,
            type: 'valuable',
            ...(quantityMatch ? { quantity: parseInt(quantityMatch[1], 10) } : {}),
            parserMeta: buildEquipmentMeta({
              sourceSection: 'Coins & Gems',
              rawText: value,
              parseConfidence: 0.92,
              reviewReason: 'Read from the pasted wealth line'
            })
          });
        });
    }

    // All recognized equipment-like headers and their default item types
    const equipHeaders = [
      { re: /^\s*(?:equipped\s+)?weapons?(?:\s*\[[^\]]+\])?\s*:?\s*$/i, type: 'weapon' },
      { re: /^\s*armor\s*(?:&\s*shields?)?(?:\s*\[[^\]]+\])?\s*:?\s*$/i, type: 'armor' },
      { re: /^\s*(?:equipment(?:\s*&\s*treasure)?|inventory|(?:carried\s+)?gear)(?:\s*\[[^\]]+\])?\s*:?\s*$/i, type: 'item' },
      { re: /^\s*magic\s*items?(?:\s*&\s*gear)?(?:\s*\[[^\]]+\])?\s*:?\s*$/i, type: 'item' },
      { re: /^\s*potions?(?:\s*\[[^\]]+\])?\s*:?\s*$/i, type: 'consumable' }
    ];

    // Inline header with items on the same line
    const inlineHeaderRe = /^\s*(equipment|inventory|(?:carried\s+)?gear|magic\s*items?(?:\s*&\s*gear)?|(?:equipped\s+)?weapons?|armor|potions?)(?:\s*\[[^\]]+\])?\s*:\s*(.+)$/im;

    const splitInventoryList = (value) => {
      const entries = [];
      let current = '';
      let parenthesisDepth = 0;
      String(value || '').split('').forEach((character) => {
        if (character === '(') parenthesisDepth += 1;
        if (character === ')') parenthesisDepth = Math.max(0, parenthesisDepth - 1);
        if ((character === ',' || character === ';') && parenthesisDepth === 0) {
          if (current.trim()) entries.push(current.trim());
          current = '';
          return;
        }
        current += character;
      });
      if (current.trim()) entries.push(current.trim());
      return entries;
    };

    // Stop when we hit a non-equipment major section
    const stopRe = /^\s*(features|class\s+features|feats|species|traits|spells?|actions?|bonus\s*actions?|reactions?|passive|triggered|personality|ideals?|bonds?|flaws?|backstory|appearance|allies|combat\s*stats|ability\s*scores?|saving\s*throws?|skills?|action\s*economy|coins\s*&\s*gems)\b/i;

    // Weapon stat line patterns ("+9 to hit", "1d6 + 5 piercing", etc.)
    const weaponStatRe = /^\s*(?:\+\d+\s+to\s+hit|\d+d\d+(?:\s*[+-]\s*\d+)?\s+\w+|\d+\s+bolts?|range\s+\d+)\s*/i;

    // Inline equipment list on a single line
    const inlineMatch = text.match(inlineHeaderRe);
    if (inlineMatch && inlineMatch[2]) {
      const inlineItems = splitInventoryList(inlineMatch[2]);
      const inlineMagicSection = /^magic\s*items?/i.test(inlineMatch[1]);
      for (const rawName of inlineItems) {
        const name = rawName.replace(/\.\s*\(This load\b.*$/i, '').trim();
        if (name && name.length > 1) {
          const explicitWeaponHeader = /^(?:equipped\s+)?weapons?$/i.test(inlineMatch[1]);
          let inlineItemType = explicitWeaponHeader ? 'weapon' : 'item';
          if (/sword|axe|bow|crossbow|dagger|mace|trident|spear|glaive|halberd|pike|javelin|hammer|flail|whip|lance|quarterstaff/i.test(name)) inlineItemType = 'weapon';
          else if (/armor|shield|helm|plate|chain|leather|studded/i.test(name)) inlineItemType = 'armor';
          else if (/potion|scroll|elixir/i.test(name)) inlineItemType = 'consumable';
          items.push({
            name,
            type: inlineItemType,
            ...(explicitWeaponHeader ? { equipped: true } : {}),
            ...(inlineMagicSection ? { magical: true } : {}),
            parserMeta: buildEquipmentMeta({
              sourceSection: inlineMatch[1],
              rawText: name,
              parseConfidence: 0.68,
              reviewReason: `Parsed from inline ${inlineMatch[1]} list`
            })
          });
        }
      }
    }

    // Multi-block scan: find each equipment header and collect items until the next section
    let inBlock = false;
    let blockType = 'item';
    let blockSection = 'equipment';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (inlineHeaderRe.test(line)) {
        inBlock = true;
        blockSection = line.split(':')[0].trim();
        continue;
      }

      // Check for equipment header
      const matchedHeader = equipHeaders.find(h => h.re.test(line));
      if (matchedHeader) {
        inBlock = true;
        blockType = matchedHeader.type;
        blockSection = line || matchedHeader.type;
        continue;
      }

      if (!inBlock) continue;

      // Blank line: keep scanning (entries may be separated by blanks)
      if (!line) continue;

      // Stop at non-equipment section headers
      if (stopRe.test(line)) {
        inBlock = false;
        continue;
      }

      // Check for another equipment header (switch block type)
      const switchHeader = equipHeaders.find(h => h.re.test(line));
      if (switchHeader) {
        blockType = switchHeader.type;
        blockSection = line || switchHeader.type;
        continue;
      }

      // Skip weapon stat lines ("+9 to hit", "1d6+5 piercing") — attach to previous item
      if (weaponStatRe.test(line)) {
        if (items.length > 0) {
          const prev = items[items.length - 1];
          prev.statLine = prev.statLine ? prev.statLine + '; ' + line : line;
          prev.parserMeta = this.mergeParserMeta(prev.parserMeta, {
            lineEnd: i + 1 + lineOffset,
            rawText: prev.parserMeta?.rawText ? `${prev.parserMeta.rawText}\n${lines[i]}` : lines[i]
          });
          // Auto-detect weapon type from stat lines
          if (/to\s+hit/i.test(line) || /\d+d\d+.*(?:slashing|piercing|bludgeoning)/i.test(line)) {
            prev.type = 'weapon';
          }
          items[items.length - 1] = this.normalizeEquipmentWeaponEntry(prev);
        }
        continue;
      }

      // Strip bullet prefix
      const cleaned = line.replace(/^[-*•\u2022]\s+|\d+[).]\s+/, '').trim();
      if (!cleaned || cleaned.length < 2) continue;

      // date_of_change: 2026-02-25
      // rule_reference: Many stat blocks use tab-separated columns for weapons (Name \t Atk \t Damage \t Notes).
      // If a line has tabs, use the first column as the item name (the rest are stats/notes).
      let itemName = cleaned;
      let inlineStatLine = '';
      if (cleaned.includes('\t')) {
        const cols = cleaned.split('\t').map(s => s.trim()).filter(Boolean);
        if (cols.length >= 2) {
          itemName = cols[0];
          inlineStatLine = cols.slice(1).join(', ');
          // Skip table header rows ("Name", "Item", "Atk Bonus")
          if (/^name$/i.test(itemName) || /^item$/i.test(itemName) || /^atk/i.test(itemName)) continue;
        }
      }

      // Split comma-delimited lines (common for simple item lists)
      const segments = splitInventoryList(itemName);
      for (const seg of segments) {
        // Detect consumable keywords
        let itemType = blockType;
        if (/potion|scroll|elixir/i.test(seg)) itemType = 'consumable';
        else if (/sword|axe|bow|crossbow|dagger|mace|trident|spear|glaive|halberd|pike|javelin|hammer|flail|whip|lance/i.test(seg)) itemType = 'weapon';
        else if (/armor|shield|helm|plate|chain|leather|studded/i.test(seg)) itemType = 'armor';
        const nextItem = {
          name: seg,
          type: itemType,
          ...(itemType === 'weapon' && /^(?:equipped\s+)?weapons?\s*:?$/i.test(blockSection) ? { equipped: true } : {}),
          ...(/^magic\s*items?/i.test(blockSection) ? { magical: true } : {}),
          parserMeta: buildEquipmentMeta({
            sourceSection: blockSection,
            lineStart: i + 1 + lineOffset,
            rawText: lines[i],
            reviewReason: `Parsed from ${blockSection}`
          })
        };
        if (inlineStatLine) {
          nextItem.statLine = inlineStatLine;
        }
        items.push(this.normalizeEquipmentWeaponEntry(nextItem));
      }
    }

    // date_of_change: 2026-02-25
    // rule_reference: Filter out action/feature names that got mixed into weapon blocks (e.g., "Action Surge",
    // "Attack Action") and detect weapon property/rider lines ("+2d6 necrotic per turn", "Vicious rider")
    // that should be associated with a parent weapon rather than listed as standalone items.
    // date_of_change: 2026-02-25
    // rule_reference: Expanded filter to catch more class features that appear in weapon blocks,
    // weapon mastery descriptions ("Vex Mastery", "gain advantage"), and tabular header rows.
    const actionFeatureNames = /^(action\s*surge|attack\s*action|extra\s*attack|second\s*wind|uncanny\s*dodge|sneak\s*attack|cunning\s*action|multiattack|legendary\s*resistance|lair\s*actions?|tactical\s*(shift|master|mastery)|indomitable|relentless\s*endurance|evasion|giant'?s?\s*might|fire\s*rune|rune\s*knight|strike\s*of\s*the\s*giants?)\b/i;
    const weaponPropertyRe = /^[+-]?\d+d\d+\s+\w+|^vicious\s+rider|^vorpal\s+rider|^\w+\s+rider$|^extra\s+\d+d\d+|per\s+turn\s*$/i;
    // Weapon mastery descriptions and short generic description lines that aren't real equipment
    const weaponMasteryRe = /^(vex|sap|push|slow|graze|cleave|topple|nick)\s+mastery/i;
    const genericDescRe = /^(gain\s+advantage|on\s+hit\s+and\s+damage|apply\s+weapon\s+mastery|once\s+per\s+turn|may\s+trigger|do\s+\d+\s+extra)/i;

    const filtered = [];
    const weaponRiders = [];
    const equipmentRiders = [];
    let lastRealWeapon = null;
    let lastRealItem = null;

    // date_of_change: 2026-02-27
    // rule_reference: Some exports list an item and then list its effects as plain lines (e.g., "Root Boots" then
    // "no difficult terrain"). These should be associated to a parent item, not treated as separate equipment.
    const equipmentEffectRe = /(difficult\s+terrain|jump|speed|advantage|disadvantage|resistance|immune|immunity|vulnerab|bonus\s+action|reaction|once\s+per\s+turn|per\s+(?:short|long)\s*rest|\d+\s*\/\s*(?:short|long)\s*rest|attunement)/i;
    const equipmentNameLikeRe = /\b(potion|scroll|boots?|ring|cloak|armor|shield|sword|axe|bow|crossbow|dagger|mace|trident|spear|helm|helmet|gloves?|gauntlets?|belt|bracers?|amulet|wand|staff|rod)\b/i;
    const isLikelyEquipmentEffectLine = (rawName) => {
      const s = String(rawName || '').trim();
      if (!s) return false;
      const startsLowerOrPunct = /^[a-z(+-]/.test(s);
      const hasSpace = s.includes(' ');
      if (equipmentNameLikeRe.test(s)) return false;
      return equipmentEffectRe.test(s) || (startsLowerOrPunct && hasSpace);
    };

    for (const sourceItem of items) {
      let item = sourceItem;
      let name = item.name || '';

      if (/^\(a\)\s*item\s+attuned\s+to\s+character\.?$/i.test(name)) continue;
      if (/\s*\(a\)\s*$/i.test(name) && /\(a\)\s*item\s+attuned\s+to\s+character/i.test(text)) {
        name = name.replace(/\s*\(a\)\s*$/i, '').trim();
        item = { ...item, name, attuned: true, requiresAttunement: true };
      }

      // Skip action/feature entries that aren't actual items
      if (actionFeatureNames.test(name)) continue;

      // Skip weapon mastery descriptions and generic action descriptions
      if (weaponMasteryRe.test(name) || genericDescRe.test(name)) {
        // Attach as rider/note to the last real weapon if present
        if (lastRealWeapon) {
          const rider = {
            name,
            type: 'weaponProperty',
            suggestedParent: lastRealWeapon.name,
            suggestedParentFingerprint: this.buildParsedEntryFingerprint(lastRealWeapon, 'equipment'),
            needsAssociation: true,
            parserMeta: buildEquipmentMeta({
              sourceSection: item?.parserMeta?.sourceSection || blockSection,
              lineStart: item?.parserMeta?.lineStart,
              lineEnd: item?.parserMeta?.lineEnd,
              rawText: item?.parserMeta?.rawText || name,
              parseConfidence: 0.58,
              reviewReason: 'Looks like a weapon rider/property rather than a standalone equipment item'
            })
          };
          weaponRiders.push(rider);
        }
        continue;
      }

      // Detect weapon property/rider lines
      if (weaponPropertyRe.test(name) || /^\+\d+d\d+\s+\w+/.test(name)) {
        const rider = {
          name,
          type: 'weaponProperty',
          suggestedParent: lastRealWeapon ? lastRealWeapon.name : null,
          suggestedParentFingerprint: lastRealWeapon ? this.buildParsedEntryFingerprint(lastRealWeapon, 'equipment') : '',
          needsAssociation: true,
          parserMeta: buildEquipmentMeta({
            sourceSection: item?.parserMeta?.sourceSection || blockSection,
            lineStart: item?.parserMeta?.lineStart,
            lineEnd: item?.parserMeta?.lineEnd,
            rawText: item?.parserMeta?.rawText || name,
            parseConfidence: 0.56,
            reviewReason: 'Looks like a weapon rider/property rather than a standalone equipment item'
          })
        };
        weaponRiders.push(rider);
        continue;
      }

      // Detect non-weapon equipment effect lines that should be attached to the last real item.
      const isExplicitInventoryItem = /Parsed from inline\b/i.test(item?.parserMeta?.reviewReason || '')
        || /^\s*[-*•\u2022]\s*/.test(item?.parserMeta?.rawText || '');
      if (lastRealItem && !isExplicitInventoryItem && isLikelyEquipmentEffectLine(name)) {
        equipmentRiders.push({
          name,
          type: 'equipmentEffect',
          suggestedParent: lastRealItem.name,
          suggestedParentFingerprint: this.buildParsedEntryFingerprint(lastRealItem, 'equipment'),
          needsAssociation: true,
          parserMeta: buildEquipmentMeta({
            sourceSection: item?.parserMeta?.sourceSection || blockSection,
            lineStart: item?.parserMeta?.lineStart,
            lineEnd: item?.parserMeta?.lineEnd,
            rawText: item?.parserMeta?.rawText || name,
            parseConfidence: 0.55,
            reviewReason: 'Looks like an equipment effect that should attach to a parent item'
          })
        });
        continue;
      }

      filtered.push(item);
      // Track the last real weapon for rider association
      if (item.type === 'weapon') lastRealWeapon = item;
      lastRealItem = item;
    }

    // Attach riders to the result for UI prompting
    if (!filtered.length && !weaponRiders.length && !equipmentRiders.length && !Object.keys(currency).length) return null;
    const result = {};
    if (filtered.length) result.items = filtered;
    if (filtered.length) {
      result.parsedEntries = this.buildStructuredSectionParsedEntries({
        equipment: filtered
      });
    }
    if (weaponRiders.length) result.weaponRiders = weaponRiders;
    if (equipmentRiders.length) result.equipmentRiders = equipmentRiders;
    if (Object.keys(currency).length) result.currency = currency;
    return (result.items || result.weaponRiders || result.equipmentRiders || result.currency) ? result : null;
  }

  /**
   * Parse Abilities & Traits block into feature objects { name, description }
   * @date_of_change 2025-11-12 - Added to support narrative stat blocks like Goran Varus
   */
  static parseAbilitiesTraits(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const lines = String(text || '').split(/\r?\n/);
    // date_of_change: 2026-02-27
    // rule_reference: Narrative stat blocks vary the "Abilities & Traits" header ("and", optional colon)
    // and often insert blank lines between entries. The parser must tolerate these while still
    // stopping at the next major section header (Actions/Equipment/Spells/etc.).
    // date_of_change: 2026-03-01
    // rule_reference: Narrative blocks often label passive abilities as "Passive / Triggered Features"
    const headerRe = /^\s*(abilities\s*(?:&|and)\s*traits|passive\s*(?:\/|and)?\s*triggered\s*features?)\s*:?(?:\s*)$/i;
    const headerIdx = lines.findIndex(l => headerRe.test(l));
    if (headerIdx === -1) return null;
    const headerLabel = (lines[headerIdx] || '').trim() || 'Abilities & Traits';

    const sectionStopRe = /^\s*(?:actions|bonus\s*actions?|reactions?|(?:equipped\s+)?weapons?|armor|equipment|inventory|gear|magic\s*items|potions?|spellcasting|spells?|combat\s*stats|ability\s*scores?|saving\s*throws?|skills?|senses|languages|appearance|backstory|personality|notes?)\s*:?\s*$/i;

    const parseFeatureLine = (raw) => this.parseStructuredEntryLine(raw, {
      allowBulletSeparator: true,
      returnBareLine: true
    });
    const baseMeta = {
      sourceParser: 'parseAbilitiesTraits',
      sourceSection: 'features',
      parseConfidence: 0.76,
      reviewReason: `Detected in ${headerLabel}`
    };
    const createFeatureEntry = (parsed, startLine, rawText) => {
      return this.buildStructuredSectionEntry(parsed, {
        baseMeta,
        lineNumber: startLine,
        rawText,
        extraFields: {
          actionType: 'passive'
        }
      });
    };
    const features = this.collectStructuredSectionRecords(lines, {
      startIndex: headerIdx + 1,
      lineOffset,
      stopRe: sectionStopRe,
      headerRe,
      parseLine: (raw) => parseFeatureLine(raw),
      createEntry: (parsed, lineNumber, raw) => createFeatureEntry(parsed, lineNumber, raw),
      createHeaderEntry: ({ parsed, cleaned, lineNumber, raw }) => {
        const headerParsed = parsed || { name: cleaned, description: '' };
        const usageMatch = headerParsed.name.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        return usageMatch
          ? createFeatureEntry({ name: usageMatch[1].trim(), description: `(${usageMatch[2].trim()})` }, lineNumber, raw)
          : createFeatureEntry(headerParsed, lineNumber, raw);
      },
      cleanLine: ({ trimmed }) => trimmed.replace(/^[-*•\u2022]\s+/, '').trim(),
      isHeaderLine: ({ trimmed, parsed, isBullet }) => this.looksLikeStructuredEntryHeader(trimmed, parsed, {
        isBullet,
        maxLength: 120
      }),
      handleSpecialLine: ({ trimmed, parsed, current, lineNumber, raw }, api) => {
        const hasExplicitDelimiter = /:|[•\u2022]|\s[–—-]\s/.test(trimmed);

        if (hasExplicitDelimiter && !parsed?.description && current) {
          api.appendToCurrent(lineNumber, raw, trimmed);
          return true;
        }

        if (parsed?.description && hasExplicitDelimiter) {
          if (current && !current.description) {
            api.appendToCurrent(lineNumber, raw, `${parsed.name}${parsed.description ? ': ' + parsed.description : ''}`);
            return true;
          }
          api.flushEntry();
          api.setCurrentEntry(createFeatureEntry(parsed, lineNumber, raw));
          return true;
        }
        return false;
      }
    });

    return features.length
      ? {
          features,
          parsedEntries: this.buildStructuredSectionParsedEntries({ features })
        }
      : null;
  }

  /**
   * Parse Actions / Bonus Actions / Reactions sections into arrays of { name, description }
   * @date_of_change 2025-11-12 - Added to support narrative stat blocks
   */
  static parseCombatSections(text, options = {}) {
    const lineOffset = Number.isInteger(options?.lineOffset) ? options.lineOffset : 0;
    const lines = text.split(/\r?\n/);

    const parseActionLikeLine = (raw) => this.parseStructuredEntryLine(raw, {
      allowBulletSeparator: true,
      fallbackShortHeaderMaxLength: 80,
      returnBareLine: false
    });

    // date_of_change: 2026-02-25
    // rule_reference: D&D character sheets often separate action entries with blank lines.
    // The collector must NOT break on blank lines; it groups sub-headers + bullet descriptions
    // into entries and only stops at the next major section header.
    const sectionStopRe = /^\s*(?:actions|bonus\s*actions?|reactions?|(?:equipped\s+)?weapons?|armor|equipment|magic\s*items|potions?|notes|lair|abilities?\s*(?:&|and)\s*traits?|passive(?:\s*\/|and)?\s*triggered\s*features?|features|feats?|personality|ideals?|bonds?|flaws?|backstory|appearance|relationships|allies|combat\s*stats|ability\s*scores?|saving\s*throws?|skills?)\s*:?\s*$/i;

    const collect = (header) => {
      const headerRe = header instanceof RegExp ? header : new RegExp('^\\s*' + header + '\\b', 'i');
      const idx = lines.findIndex(l => headerRe.test(l));
      if (idx === -1) return [];
      const headerLabel = (lines[idx] || '').trim() || 'Action Section';
      const normalizeSectionName = () => {
        if (/bonus\s*actions?/i.test(headerLabel)) return 'bonusActions';
        if (/reactions?/i.test(headerLabel)) return 'reactions';
        return 'actions';
      };
      const sectionName = normalizeSectionName();
      const baseMeta = {
        sourceParser: 'parseCombatSections',
        sourceSection: sectionName,
        parseConfidence: 0.84,
        reviewReason: `Detected in ${headerLabel}`
      };
      const createActionEntry = (parsed, lineNumber, rawText) => {
        return this.buildStructuredSectionEntry(parsed, {
          baseMeta,
          lineNumber,
          rawText
        });
      };

      return this.collectStructuredSectionRecords(lines, {
        startIndex: idx + 1,
        lineOffset,
        stopRe: sectionStopRe,
        headerRe,
        parseLine: (raw) => parseActionLikeLine(raw),
        createEntry: (parsed, lineNumber, raw) => createActionEntry(parsed, lineNumber, raw),
        createHeaderEntry: ({ cleaned, lineNumber, raw }) => {
          const usageMatch = cleaned.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
          return usageMatch
            ? createActionEntry({ name: usageMatch[1].trim(), description: `(${usageMatch[2].trim()})` }, lineNumber, raw)
            : createActionEntry({ name: cleaned.trim(), description: '' }, lineNumber, raw);
        },
        cleanLine: ({ trimmed }) => trimmed.replace(/^[-*•\u2022]\s+/, '').trim(),
        isHeaderLine: ({ trimmed, parsed, isBullet }) => this.looksLikeStructuredEntryHeader(trimmed, parsed, {
          isBullet,
          maxLength: 60,
          rejectParenThenText: true
        }),
        appendBulletsToLastEntry: true
      });
    };

    // date_of_change: 2026-03-01
    // rule_reference: Merge continuation lines (e.g., bullets under Giant's Might) into the previous entry
    const foldContinuationEntries = (entries) => {
      if (!Array.isArray(entries)) return entries;
      const usagePattern = /(short|long)\s+rest|per\s+rest|per\s+short|per\s+long|per\s+turn|per\s+round/i;
      const knownHeaders = /(giant['’]s might|second wind|hill rune|stone rune|runic shield|tactical shift|tactical mind|large form|action surge|indomitable)/i;
      const shouldMergeWithPrev = (prev, cur) => {
        if (!prev) return false;
        const name = String(cur?.name || '').trim();
        const desc = String(cur?.description || '').trim();
        if (!name) return false;
        // If current looks like its own ability header (has usage markers or is a known header), do not merge
        if (usagePattern.test(name) || usagePattern.test(desc) || knownHeaders.test(name)) return false;
        // If current already has description content, treat as standalone
        if (desc) return false;
        // Merge when previous is a known multi-line ability header or has usage markers
        const prevName = String(prev.name || '').toLowerCase();
        const prevDesc = String(prev.description || '');
        const prevHasUsage = usagePattern.test(prevName) || usagePattern.test(prevDesc) || knownHeaders.test(prevName);
        return prevHasUsage;
      };
      const out = [];
      for (const entry of entries) {
        const last = out[out.length - 1];
        if (shouldMergeWithPrev(last, entry)) {
          const line = entry.description ? `${entry.name} — ${entry.description}` : entry.name;
          last.description = last.description ? `${last.description}\n${line}` : line;
          last.parserMeta = this.mergeParserMeta(last.parserMeta, {
            lineEnd: entry?.parserMeta?.lineEnd,
            rawText: [last?.parserMeta?.rawText, entry?.parserMeta?.rawText].filter(Boolean).join('\n')
          });
        } else {
          out.push(entry);
        }
      }
      return out;
    };

      const keepDiscreteActions = (entries) => (
        (Array.isArray(entries) ? entries : []).filter((entry) => !this.isCharacterSheetMetadataDump(entry))
      );
      const actions = keepDiscreteActions(collect('actions'));
      const bonusActions = keepDiscreteActions(foldContinuationEntries(collect(/^\s*bonus\s*actions\b/i)));
      const reactions = keepDiscreteActions(foldContinuationEntries(collect('reactions')));
      const result = {};
      if (actions.length) result.actions = actions;
      if (bonusActions.length) result.bonusActions = bonusActions;
      if (reactions.length) result.reactions = reactions;
      const parsedEntries = this.buildStructuredSectionParsedEntries({
        actions,
        bonusActions,
        reactions
      });
      if (parsedEntries.length) result.parsedEntries = parsedEntries;
      return Object.keys(result).length ? result : null;
  }

  /**
   * Extract uncategorized/unrecognized text blocks for human review
   * @date_of_change 2025-12-08 - Enhanced to be more thorough and capture more content
   * 
   * This method identifies text sections that weren't fully parsed by the standard parsers.
   * Each block is presented to the user for manual categorization.
   */
  // date_of_change: 2026-02-25
  // rule_reference: Suppress sections already extracted by upstream parsers (parseCombatSections, parseInventoryFromText,
  // extractBasicStats). Use keyword heuristics (save DCs, advantage, X/rest, damage dice) to classify remaining blocks
  // as features vs equipment vs actions. Recognize PASSIVE/TRIGGERED FEATURES, ACTION ECONOMY SUMMARY, Damage Reduction.
  static extractUncategorizedBlocks(text, parsedData = {}) {
    const lines = text.split(/\r?\n/);
    const parsedEntryCatalog = this.getClaimedCharacterEntryCatalog(parsedData);
    const { coveredLines, coveredRawLines } = this.buildParsedContentCoverage(parsedData);
    const claimedEntryNames = new Set(
      parsedEntryCatalog
        .map((entry) => this.normalizeParsedEntryName(entry?.name))
        .filter(Boolean)
    );
    const normalizeCoveredLine = (value) => String(value || '').trim().replace(/\s+/g, ' ');
    const isCoveredByParsedCatalog = (lineNumber, rawLine) => (
      coveredLines.has(lineNumber + 1) ||
      coveredRawLines.has(normalizeCoveredLine(rawLine))
    );
    const isClaimedEntryName = (name) => claimedEntryNames.has(this.normalizeParsedEntryName(name));

    // ── Sections fully handled by upstream parsers → suppress entirely ──
    // These headers AND their content should not appear in uncategorized output.
    const suppressedSections = [
      /^\s*(?:ability\s*scores?|ABILITY\s*SCORES?)\s*:?\s*$/i,
      /^\s*(?:saving\s*throws?)\s*:?\s*$/i,
      /^\s*skills?\s*:?\s*$/i,
      /^\s*(?:combat\s*stats)\s*:?\s*$/i,
      /^\s*(?:damage\s+resistances?)\s*:?\s*$/i,
      /^\s*(?:damage\s+immunities?)\s*:?\s*$/i,
      /^\s*(?:damage\s+vulnerabilities?)\s*:?\s*$/i,
      /^\s*(?:condition\s+immunities?)\s*:?\s*$/i,
      /^\s*(?:damage\s+reduction)\s*:?\s*$/i,
      /^\s*(?:action\s+economy(?:\s+summary)?)\s*:?\s*$/i,
      /^\s*senses?\s*:?\s*$/i,
      /^\s*languages?\s*:?\s*$/i,
      /^\s*hit\s*dice\s*:?\s*$/i,
      /^\s*passive\s+perception\b/i,
      /^\s*initiative\b/i,
    ];

    // Also suppress if parseCombatSections already extracted the data
    const hasActions = Array.isArray(parsedData.actions) && parsedData.actions.length > 0;
    const hasBonusActions = Array.isArray(parsedData.bonusActions) && parsedData.bonusActions.length > 0;
    const hasReactions = Array.isArray(parsedData.reactions) && parsedData.reactions.length > 0;
    const hasEquipment = parsedData.equipment && Array.isArray(parsedData.equipment.items) && parsedData.equipment.items.length > 0;
    const hasAbilityScores = parsedData.abilities && Object.keys(parsedData.abilities).length > 0;
    const hasCombatStats = Boolean(parsedData.ac && (parsedData.hp || parsedData.maxHp));
    const hasSpellList = this.hasStructuredSpellCoverage(parsedData.spells);
    const hasFastCharacterFeatures = [
      parsedData.classFeatures,
      parsedData.feats,
      parsedData.speciesTraits
    ].some((entries) => Array.isArray(entries) && entries.length > 0);

    if (hasAbilityScores) {
      suppressedSections.push(/^\s*ability\s+scores?(?:\s*&\s*abilities)?\s*:?\s*$/i);
    }
    if (hasCombatStats) {
      suppressedSections.push(/^\s*combat(?:\s*\[[^\]]+\])?\s*:?\s*$/i);
    }
    if (hasFastCharacterFeatures) {
      suppressedSections.push(/^\s*features\s*,\s*traits\s*,\s*special\s+abilities\s*$/i);
    }
    if (hasSpellList) {
      suppressedSections.push(/^\s*(?:spellcasting|spells?)(?:\s*\[[^\]]+\])?\s*:?\s*$/i);
    }

    if (hasActions) suppressedSections.push(/^\s*(?:actions?)\s*:?\s*$/i);
    if (hasBonusActions) suppressedSections.push(/^\s*(?:bonus\s*actions?)\s*:?\s*$/i);
    if (hasReactions) suppressedSections.push(/^\s*(?:reactions?)\s*:?\s*$/i);
    if (hasEquipment) {
      suppressedSections.push(/^\s*(?:weapons?)\s*:?\s*$/i);
      suppressedSections.push(/^\s*(?:armor\s*(?:&\s*shields?)?)\s*:?\s*$/i);
      suppressedSections.push(/^\s*(?:equipment(?:\s*&\s*treasure)?|inventory|(?:carried\s+)?gear)(?:\s*\[[^\]]+\])?(?:\s*:\s*.*)?\s*$/i);
      suppressedSections.push(/^\s*(?:magic\s*items?(?:\s*&\s*gear)?)(?:\s*\[[^\]]+\])?\s*:?\s*$/i);
      suppressedSections.push(/^\s*potions?\s*:?\s*$/i);
    }
    if (parsedData.biography) {
      suppressedSections.push(/^\s*(?:appearance)\s*:?\s*$/i);
      suppressedSections.push(/^\s*(?:backstory)\s*:?\s*$/i);
      suppressedSections.push(/^\s*(?:personality)\b/i);
      suppressedSections.push(/^\s*(?:allies)\b/i);
    }

    // ── Structural one-line headers already handled by extractBasicStats ──
    const knownOneLiners = [
      /^\s*name\s*:/i,
      /^\s*class(?:es)?\s*:/i,
      /^\s*class\s*&\s*level\s*:/i,
      /^\s*level\s*:/i,
      /^\s*(?:race|species|race\s*\/\s*ancestry\s*\/\s*heritage)\s*:/i,
      /^\s*background\s*:/i,
      /^\s*experience\s*:/i,
      /^\s*(?:armor\s+class|AC)\s*:/i,
      /^\s*(?:hit\s+points|HP)\s*:/i,
      /^\s*(?:speed|movement)\s*:/i,
      /^\s*alignment\s*:/i,
      /^\s*proficiency\s*bonus\s*:/i,
      /^\s*size\s*:/i,
      /^\s*player\s*:/i,
      /^\s*darkvision\b/i,
    ];

    // ── Content headers: NOT suppressed, but their lines get classified ──
    const contentSectionHeaders = [
      { re: /^\s*(?:passive\s*[/&]\s*triggered\s*features?|passive\s*features?|triggered\s*features?)\s*:?\s*$/i, category: 'features' },
      { re: /^\s*(?:class\s*features?|features?)\s*:?\s*$/i, category: 'features' },
      { re: /^\s*feats?\s*:?\s*$/i, category: 'feats' },
      { re: /^\s*(?:species|racial)\s*traits?\s*:?\s*$/i, category: 'speciesTraits' },
      { re: /^\s*(?:legendary\s*actions?)\s*:?\s*$/i, category: 'legendaryActions' },
      { re: /^\s*(?:abilities?\s*&\s*traits?|special\s*abilities?)\s*:?\s*$/i, category: 'features' },
      { re: /^\s*(?:notes?)\s*:?\s*$/i, category: 'biography' },
    ];

    // ── Keyword detectors ──
    const equipmentKeywords = /sword|armor|shield|ring|cloak|boots|weapon|wand|staff|rod|potion|scroll|amulet|bracers?|helm|gloves?|tool|focus|bag|pack|belt|hat|robe|invisibility|all-purpose|bloodforged|trident|crossbow|manacles|crowbar|orb|moonstone/i;
    const traitKeywords = /trait|resistance|immunity|darkvision|blindsight|tremorsense|truesight|regenerat|undead|vampir|fey|celestial|fiend|dragon/i;
    const attackKeywords = /\+\d+\s*to\s*hit|weapon\s*attack|melee\s*attack|ranged\s*attack|spell\s*attack|\d+d\d+\s*(?:\+\d+)?\s*(?:slashing|piercing|bludgeoning|fire|cold|lightning|thunder|poison|acid|necrotic|radiant|force|psychic)/i;
    // date_of_change: 2026-02-25
    // rule_reference: D&D 5e feature keywords — save DCs, advantage/disadvantage, rerolls, X/rest usage, bonus damage
    const featureKeywords = /save\s*DC\s*\d+|DC\s*\d+|advantage\s+on|disadvantage\s+on|reroll|(?:\d+\/(?:short|long)\s*rest)|(?:\d+\s*uses?)|bonus\s*(?:action|damage)|extra\s+(?:attack|die|damage)|resistance\s+to|speed\s+(?:increases?|decreases?)|incapacitated|charmed|frightened|restrained|prone|grappled/i;

    const potentialFeatures = [];
    const potentialEquipment = [];
    const potentialTraits = [];
    const potentialActions = [];
    const sectionFragments = [];
    const rawTextBlocks = [];
    const buildReviewMeta = ({ sourceSection, lineStart, lineEnd, rawText, parseConfidence = 0.55, reviewReason } = {}) => this.buildParserMeta({
      sourceParser: 'extractUncategorizedBlocks',
      sourceSection: sourceSection || currentCategory || 'uncategorized',
      lineStart,
      lineEnd: lineEnd ?? lineStart,
      rawText,
      parseConfidence,
      reviewReason
    });

    let inSuppressed = false;  // true when inside a fully-suppressed section
    let currentCategory = null; // non-null when inside a content section
    let currentSectionHasCatalogCoverage = false;
    let currentBlock = { header: '', lines: [], startLine: 0, category: null, parserMeta: null, blockType: 'raw' };
    const flushCurrentBlock = (nextStartLine = 0) => {
      if (currentBlock.lines.length > 0) {
        const snapshot = { ...currentBlock, lines: [...currentBlock.lines] };
        if (snapshot.blockType === 'sectionFragment') {
          sectionFragments.push(snapshot);
        } else {
          rawTextBlocks.push(snapshot);
        }
      }
      currentBlock = { header: '', lines: [], startLine: nextStartLine, category: null, parserMeta: null, blockType: 'raw' };
    };
    const ensureSectionDiffBlock = (lineNumber, rawLine) => {
      if (currentBlock.lines.length > 0) return;

      currentBlock = {
        header: '',
        lines: [],
        startLine: lineNumber,
        category: currentCategory,
        blockType: 'sectionFragment',
        parserMeta: buildReviewMeta({
          sourceSection: currentCategory || 'uncategorized',
          lineStart: lineNumber + 1,
          rawText: rawLine,
          parseConfidence: 0.42,
          reviewReason: currentCategory
            ? `Recovered leftover lines from ${currentCategory} section after parser-consumed spans`
            : 'Recovered leftover lines after parser-consumed spans'
        })
      };
    };

    // Helper: classify a block by name+description keywords and push to the right bucket
    const classifyAndPush = (name, description, rawLine, lineNumber, hintCategory) => {
      if (isClaimedEntryName(name)) return;

      const combined = (name + ' ' + description);
      const parserMeta = buildReviewMeta({
        sourceSection: hintCategory || currentCategory || 'uncategorized',
        lineStart: lineNumber + 1,
        rawText: rawLine || `${name}: ${description}`,
        reviewReason: hintCategory
          ? `Needs review from ${hintCategory} section`
          : 'Recovered by fallback heuristics after primary parsing'
      });
      const entry = { name, description, rawLine, lineNumber, parserMeta };
      if (this.isCharacterSheetMetadataDump(entry)) {
        const retainedText = [name, description].filter(Boolean).join('\n');
        currentBlock.lines.push(retainedText);
        currentBlock.parserMeta = this.mergeParserMeta(currentBlock.parserMeta, parserMeta, {
          sourceSection: 'uncategorized',
          rawText: retainedText,
          reviewReason: 'Flattened character-sheet fields retained for review instead of becoming a combat action'
        });
        return;
      }
      // Use hint from content section first
      if (hintCategory === 'features' || hintCategory === 'feats') {
        potentialFeatures.push(entry);
        return;
      }
      if (hintCategory === 'equipment') {
        // date_of_change: 2026-05-01
        // rule_reference: Filter out narrative text that contains equipment keywords but lacks equipment stats.
        // Descriptions longer than 150 chars without damage/AC/rarity/bonus stats are likely narrative text, not equipment.
        const hasEquipmentStats = /\d+d\d+|\+\d+\s+(to\s+hit|bonus)|AC\s*\d+|rare|legendary|uncommon|common|attunement/i.test(combined);
        const isLikelyNarrative = description.length > 150 && !hasEquipmentStats;
        if (!isLikelyNarrative) {
          potentialEquipment.push(entry);
        } else {
          // Treat as raw text block instead
          currentBlock.lines.push(rawLine || `${name}: ${description}`);
          currentBlock.parserMeta = this.mergeParserMeta(currentBlock.parserMeta, {
            lineEnd: lineNumber + 1,
            rawText: currentBlock.parserMeta?.rawText
              ? `${currentBlock.parserMeta.rawText}\n${rawLine || `${name}: ${description}`}`
              : (rawLine || `${name}: ${description}`)
          });
        }
        return;
      }
      if (hintCategory === 'actions' || hintCategory === 'bonusActions' || hintCategory === 'reactions' || hintCategory === 'legendaryActions') {
        potentialActions.push(entry);
        return;
      }
      if (hintCategory === 'speciesTraits') {
        potentialTraits.push(entry);
        return;
      }
      // Keyword-based fallback
      if (attackKeywords.test(combined)) {
        potentialActions.push(entry);
      } else if (equipmentKeywords.test(combined)) {
        // date_of_change: 2026-05-01
        // rule_reference: Filter out narrative text that contains equipment keywords but lacks equipment stats.
        // Descriptions longer than 150 chars without damage/AC/rarity/bonus stats are likely narrative text, not equipment.
        const hasEquipmentStats = /\d+d\d+|\+\d+\s+(to\s+hit|bonus)|AC\s*\d+|rare|legendary|uncommon|common|attunement/i.test(combined);
        const isLikelyNarrative = description.length > 150 && !hasEquipmentStats;
        if (!isLikelyNarrative) {
          potentialEquipment.push(entry);
        } else {
          // Treat as raw text block instead
          currentBlock.lines.push(rawLine || `${name}: ${description}`);
          currentBlock.parserMeta = this.mergeParserMeta(currentBlock.parserMeta, {
            lineEnd: lineNumber + 1,
            rawText: currentBlock.parserMeta?.rawText
              ? `${currentBlock.parserMeta.rawText}\n${rawLine || `${name}: ${description}`}`
              : (rawLine || `${name}: ${description}`)
          });
        }
      } else if (traitKeywords.test(combined)) {
        potentialTraits.push(entry);
      } else if (featureKeywords.test(combined)) {
        potentialFeatures.push(entry);
      } else if (description.length > 10) {
        potentialFeatures.push(entry);
      } else {
        // Too short to classify confidently → raw block
        currentBlock.lines.push(rawLine || `${name}: ${description}`);
        currentBlock.parserMeta = this.mergeParserMeta(currentBlock.parserMeta, {
          lineEnd: lineNumber + 1,
          rawText: currentBlock.parserMeta?.rawText
            ? `${currentBlock.parserMeta.rawText}\n${rawLine || `${name}: ${description}`}`
            : (rawLine || `${name}: ${description}`)
        });
      }
    };

    for (let i = 0; i < lines.length; i++) {
      const trimmed = lines[i].trim();

      // ── Blank line: flush current block, reset section state ──
      if (!trimmed) {
        flushCurrentBlock(i + 1);
        // Don't reset suppression on blank lines — multi-line suppressed sections continue until next header
        continue;
      }

      // Respect parser/catalog provenance first: if another parser already claimed this exact line or span,
      // do not re-add it through fallback uncategorized extraction.
      if (isCoveredByParsedCatalog(i, lines[i])) {
        if (currentCategory) {
          currentSectionHasCatalogCoverage = true;
        }
        flushCurrentBlock(i + 1);
        continue;
      }

      // ── Check: suppressed section header ──
      if (suppressedSections.some(re => re.test(trimmed))) {
        flushCurrentBlock(i + 1);
        inSuppressed = true;
        currentCategory = null;
        currentSectionHasCatalogCoverage = false;
        continue;
      }

      // ── Check: known one-liner header (skip line itself, don't suppress content) ──
      if (knownOneLiners.some(re => re.test(trimmed))) {
        inSuppressed = false;
        currentCategory = null;
        currentSectionHasCatalogCoverage = false;
        continue;
      }

      // ── Check: content section header ──
      const contentSection = contentSectionHeaders.find(h => h.re.test(trimmed));
      if (contentSection) {
        flushCurrentBlock(i + 1);
        currentBlock = {
          header: trimmed,
          lines: [],
          startLine: i,
          category: contentSection.category,
          blockType: 'raw',
          parserMeta: buildReviewMeta({
            sourceSection: contentSection.category,
            lineStart: i + 1,
            rawText: lines[i],
            parseConfidence: 0.5,
            reviewReason: `Section header "${trimmed}" was not fully consumed by primary parsers`
          })
        };
        inSuppressed = false;
        currentCategory = contentSection.category;
        currentSectionHasCatalogCoverage = false;
        continue;
      }

      // ── Any ALL-CAPS header that's a new major section resets suppression ──
      if (/^[A-Z][A-Z\s&/]{2,50}$/.test(trimmed)) {
        inSuppressed = false;
        currentCategory = null;
        currentSectionHasCatalogCoverage = false;
        // If it's a recognizable header, start a new block
        flushCurrentBlock(i + 1);
        currentBlock = {
          header: trimmed,
          lines: [],
          startLine: i,
          category: null,
          blockType: 'raw',
          parserMeta: buildReviewMeta({
            sourceSection: 'uncategorized',
            lineStart: i + 1,
            rawText: lines[i],
            parseConfidence: 0.45,
            reviewReason: `Encountered a standalone section header "${trimmed}" with leftover content`
          })
        };
        continue;
      }

      // ── If inside a suppressed section, skip content lines ──
      if (inSuppressed) continue;

      // ── Check: sub-header with description ("Name: description" or "Name. description") ──
      const colonMatch = trimmed.match(/^([A-Za-z][A-Za-z'\s-]+):\s*(.+)$/);
      if (colonMatch) {
        const name = colonMatch[1].trim();
        const content = colonMatch[2].trim();
        classifyAndPush(name, content, trimmed, i, currentCategory);
        continue;
      }

      // ── Check: sub-header line (title case, optionally with "(usage)") ──
      const subHeaderMatch = trimmed.match(/^([\p{Lu}][\p{L}'’ -]+(?:\s*[–—-]\s*[\p{Lu}][\p{L}'’ -]+)?(?:'s\s+[\p{L}'’ -]+)?)(?:\s*\(([^)]+)\))?\s*$/u);
      if (subHeaderMatch && !inSuppressed) {
        // Collect following bullet/description lines until blank or next sub-header
        const name = subHeaderMatch[1].trim();
        const usage = subHeaderMatch[2] || '';
        const descLines = [];
        let j = i + 1;
        for (; j < lines.length; j++) {
          const nxt = lines[j].trim();
          if (!nxt) break;
          if (isCoveredByParsedCatalog(j, lines[j])) break;
          // Stop at next sub-header or major section header
          if (/^[\p{Lu}][\p{L}'’ -]+(?:\s*[–—-]\s*[\p{Lu}][\p{L}'’ -]+)?(?:\s*\([^)]*\))?\s*$/u.test(nxt) && !/^[-*•\u2022]/.test(nxt)) break;
          if (suppressedSections.some(re => re.test(nxt))) break;
          if (contentSectionHeaders.some(h => h.re.test(nxt))) break;
          descLines.push(nxt.replace(/^[-*•\u2022]\s+/, '').trim());
        }
        const description = (usage ? `(${usage}) ` : '') + descLines.join('\n');
        if (description.trim()) {
          classifyAndPush(name, description.trim(), trimmed, i, currentCategory);
          i = j - 1; // skip processed lines
        }
        // If no description and it's a short name, it's a standalone entry (e.g., "Opportunity Attack")
        else if (currentCategory) {
          classifyAndPush(name, usage, trimmed, i, currentCategory);
        }
        continue;
      }

      // ── Bullet line inside a content section ──
      if (currentCategory && /^[•\u2022\-*]\s+/.test(trimmed)) {
        const cleaned = trimmed.replace(/^[-*•\u2022]\s+/, '').trim();
        if (cleaned.length > 2) {
          if (currentSectionHasCatalogCoverage) {
            ensureSectionDiffBlock(i, lines[i]);
            currentBlock.lines.push(trimmed);
            currentBlock.parserMeta = this.mergeParserMeta(currentBlock.parserMeta, {
              lineEnd: i + 1,
              rawText: currentBlock.parserMeta?.rawText
                ? `${currentBlock.parserMeta.rawText}\n${lines[i]}`
                : lines[i]
            });
          } else {
            classifyAndPush(cleaned, '', trimmed, i, currentCategory);
          }
        }
        continue;
      }

      // ── Accumulate into current raw block (fallback) ──
      if (trimmed.length > 3) {
        if (currentCategory && currentSectionHasCatalogCoverage) {
          ensureSectionDiffBlock(i, lines[i]);
        }
        currentBlock.lines.push(trimmed);
        currentBlock.parserMeta = this.mergeParserMeta(currentBlock.parserMeta, {
          sourceParser: 'extractUncategorizedBlocks',
          sourceSection: currentBlock.category || currentCategory || 'uncategorized',
          lineStart: currentBlock.parserMeta?.lineStart ?? i + 1,
          lineEnd: i + 1,
          parseConfidence: currentBlock.parserMeta?.parseConfidence ?? 0.45,
          rawText: currentBlock.parserMeta?.rawText ? `${currentBlock.parserMeta.rawText}\n${lines[i]}` : lines[i],
          reviewReason: currentBlock.parserMeta?.reviewReason || 'Fallback block retained for human review'
        });
      }
    }

    // Save final block
    flushCurrentBlock(lines.length);

    // Filter noise blocks (very short or single-word blocks)
    const meaningfulSectionFragments = sectionFragments.filter(block => {
      const content = block.lines.join(' ');
      return content.length > 10 && block.lines.length >= 1;
    });
    const meaningfulBlocks = rawTextBlocks.filter(block => {
      const content = block.lines.join(' ');
      return content.length > 15 && block.lines.length >= 1;
    });

    return {
      potentialFeatures,
      potentialEquipment,
      potentialTraits,
      potentialActions,
      sectionFragments: meaningfulSectionFragments,
      rawTextBlocks: meaningfulBlocks,
      totalUncategorized: potentialFeatures.length + potentialEquipment.length + potentialTraits.length + potentialActions.length + meaningfulSectionFragments.length + meaningfulBlocks.length
    };
  }

/**
 * Get suggestions for where to place content based on type and context
 */
static getPlacementSuggestions(parsedContent, context) {
  const { type, data = {} } = parsedContent || {};
  if (!type) return [];

  const suggestions = [];
  const sanitizeSuggestionScalar = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim().replace(/^["']|["']$/g, '');
    if (!trimmed) return '';
    if (trimmed === '__SMARTPASTE_CLAIM__') return '';
    if (/^"?data"?\s*:?\s*$/i.test(trimmed)) return '';
    if (/^[:",\s]+$/.test(trimmed)) return '';
    if (/^(name|species|race|class|background)\s*:?$/i.test(trimmed)) return '';
    return trimmed;
  };
  const suggestionData = (() => {
    if (type === 'character') {
      const derivedData = this.deriveCharacterImportData(data);
      const refreshedCatalog = this.buildCharacterEntryCatalog(derivedData);
      return {
        ...derivedData,
        parsedEntryCatalog: this.mergeCharacterEntryCatalogs(data.parsedEntryCatalog, refreshedCatalog)
      };
    }
    if (type === 'monster') {
      const clonedData = { ...data };
      const refreshedCatalog = this.buildCharacterEntryCatalog(clonedData);
      return {
        ...clonedData,
        parsedEntryCatalog: this.mergeCharacterEntryCatalogs(data.parsedEntryCatalog, refreshedCatalog)
      };
    }
    return data;
  })();

  switch (type) {
    case 'spell':
      suggestions.push({
        field: 'spells',
        action: 'append',
        preview: `Add spell: ${data.name || 'Spell'}`,
        data
      });
      break;

    case 'action':
      suggestions.push({
        field: 'actions',
        action: 'append',
        preview: `Add action: ${data.name || 'Action'}`,
        data
      });
      break;

    case 'item':
      suggestions.push({
        field: 'equipment',
        action: 'append',
        preview: `Add equipment: ${data.name || 'Item'}`,
        data
      });
      break;

    case 'feature':
      suggestions.push({
        field: 'features',
        action: 'append',
        preview: `Add feature: ${data.name || 'Feature'}`,
        data
      });
      break;

    case 'ability':
    case 'abilities':
      suggestions.push({
        field: 'abilities',
        action: 'merge',
        preview: `Update ability scores`,
        data: data
      });
      break;

    case 'list':
      // Suggest different fields based on context and meta hints
      if (parsedContent?.data?.meta?.category === 'equipment') {
        // @date_of_change 2025-10-16 - Convert strings to equipment objects for proper merge/display
        const equipArray = (data.items || []).map(it => (
          typeof it === 'string' ? { name: it, type: 'item' } : it
        ));
        suggestions.push({
          field: 'equipment',
          action: 'append',
          preview: `Add equipment: ${(data.items || []).join(', ')}`,
          data: equipArray
        });
      } else if (parsedContent?.data?.meta?.category === 'class_features') {
        suggestions.push({
          field: 'features',
          action: 'append',
          preview: `Add ${data.items.length} class features`,
          data: (data.items || []).map(n => ({ name: n }))
        });
        suggestions.push({
          field: 'classFeaturesText',
          action: 'replace',
          preview: 'Set Class Features text',
          data: (data.items || []).join('\n')
        });
      } else if (parsedContent?.data?.meta?.category === 'feats') {
        suggestions.push({
          field: 'feats',
          action: 'append',
          preview: `Add ${data.items.length} feats`,
          data: (data.items || []).map(n => ({ name: n }))
        });
        suggestions.push({
          field: 'featsText',
          action: 'replace',
          preview: 'Set Feats text',
          data: (data.items || []).join('\n')
        });
      } else if (parsedContent?.data?.meta?.category === 'species_traits') {
        suggestions.push({
          field: 'speciesTraitsText',
          action: 'replace',
          preview: 'Set Species Traits text',
          data: (data.items || []).join('\n')
        });
      } else if (context?.nearField === 'skills' || /skill/i.test(context?.nearField || '')) {
        suggestions.push({
          field: 'skills',
          action: 'append',
          preview: `Add skills: ${(data.items || []).join(', ')}`,
          data: data.items || []
        });
      } else if (context?.nearField === 'equipment' || /equipment|item/i.test(context?.nearField || '')) {
        const equipArray = (data.items || []).map(it => (
          typeof it === 'string' ? { name: it, type: 'item' } : it
        ));
        suggestions.push({
          field: 'equipment',
          action: 'append',
          preview: `Add equipment: ${(data.items || []).join(', ')}`,
          data: equipArray
        });
      } else {
        // Generic suggestions
        suggestions.push(
          {
            field: 'skills',
            action: 'append',
            preview: `Add as skills: ${(data.items || []).join(', ')}`,
            data: data.items || []
          },
          {
            field: 'equipment',
            action: 'append',
            preview: `Add as equipment: ${(data.items || []).join(', ')}`,
            data: data.items || []
          },
          {
            field: 'features',
            action: 'append',
            preview: `Add as features: ${(data.items || []).join(', ')}`,
            data: (data.items || []).map(n => (typeof n === 'string' ? { name: n } : n))
          }
        );
      }
      break;

    case 'character':
    case 'monster': {
      const data = suggestionData;
      const catalogBuckets = this.buildCatalogSuggestionBuckets(data);
      const parsedSpellCount = catalogBuckets.spells.length;
      // Always offer full import
      suggestions.push({
        field: 'all',
        action: 'replace',
        preview: data.sourceFormat === DND_BEYOND_SOURCE_FORMAT
          ? 'Add the complete D&D Beyond character to this sheet'
          : `Replace current sheet with imported ${type} (all detected data)`,
        data
      });
      // date_of_change: 2026-08-24
      // rule_reference: A recognized D&D Beyond export is already a complete source-owned
      // import plan. Do not make players assemble it from granular suggestions, because that
      // previously omitted the multiclass array and duplicated full-character content.
      if (data.sourceFormat === DND_BEYOND_SOURCE_FORMAT && data.completeCharacterImport === true) {
        break;
      }
      // Granular suggestions for partial merges
      const sanitizedName = sanitizeSuggestionScalar(data.name);
      const sanitizedClass = sanitizeSuggestionScalar(data.class);
      const sanitizedSubclass = sanitizeSuggestionScalar(data.subclass);
      const sanitizedBackground = sanitizeSuggestionScalar(data.background);
      const sanitizedSpecies = sanitizeSuggestionScalar(data.race || data.species);
      if (sanitizedName) suggestions.push({ field: 'name', action: 'replace', preview: `Set name: ${sanitizedName}`, data: sanitizedName });
      if (Array.isArray(data.classes) && data.classes.length > 1) {
        const classSummary = data.classes
          .map((entry) => `${entry.name}${entry.subclass ? ` (${entry.subclass})` : ''} ${entry.level}`)
          .join(' / ');
        suggestions.push({
          field: 'classes',
          action: 'replace',
          preview: `Set classes: ${classSummary}`,
          data: data.classes
        });
      } else if (sanitizedClass) {
        suggestions.push({ field: 'class', action: 'replace', preview: `Set class: ${sanitizedClass}`, data: sanitizedClass });
      }
      // @date_of_change 2025-12-05 - Add subclass suggestion
      if (sanitizedSubclass) suggestions.push({ field: 'subclass', action: 'replace', preview: `Set subclass: ${sanitizedSubclass}`, data: sanitizedSubclass });
      if (typeof data.level === 'number') suggestions.push({ field: 'level', action: 'replace', preview: `Set level: ${data.level}`, data: data.level });
      if (typeof data.experience === 'number') suggestions.push({ field: 'experience', action: 'replace', preview: `Set XP: ${data.experience.toLocaleString()}`, data: data.experience });
      if (sanitizedBackground) suggestions.push({ field: 'background', action: 'replace', preview: `Set background: ${sanitizedBackground}`, data: sanitizedBackground });
      if (sanitizedSpecies) {
        suggestions.push({ field: 'race', action: 'replace', preview: `Set species: ${sanitizedSpecies}`, data: sanitizedSpecies });
      }
      if (Array.isArray(data.featuresList) && data.featuresList.length > 0 && catalogBuckets.features.length === 0 && catalogBuckets.classFeatures.length === 0) {
        suggestions.push({
          field: 'features',
          action: 'append',
          preview: `Add ${data.featuresList.length} class features`,
          data: data.featuresList.map(n => ({ name: n }))
        });
      }
      if (Array.isArray(data.featsList) && data.featsList.length > 0 && catalogBuckets.feats.length === 0) {
        suggestions.push({
          field: 'feats',
          action: 'append',
          preview: `Add ${data.featsList.length} feats`,
          data: data.featsList.map(n => ({ name: n }))
        });
      }

      // date_of_change: 2026-02-26
      // rule_reference: D&D 5e (2024) SRD — auto-populate species traits from SRD when not explicitly parsed.
      if (!Array.isArray(data.speciesTraits) || data.speciesTraits.length === 0) {
        const speciesName = data.race || data.species;
        if (speciesName) {
          const srdTraits = this.getSRDSpeciesTraits(speciesName);
          if (srdTraits.length > 0) {
            data.speciesTraits = srdTraits;
            // date_of_change: 2026-02-27
            // rule_reference: Generic speciesTraits suggestion block below will pick this up; no inline suggestion needed.
          }
        }
      }

      // date_of_change: 2026-03-11
      // rule_reference: Suggest imported proficiencies from the pure derived model rather than mutating parser output.
      if (data.proficiencies && (data.proficiencies.armor?.length || data.proficiencies.weapons?.length || data.proficiencies.tools?.length)) {
        suggestions.push({ field: 'proficiencies', action: 'replace', preview: `Add ${data.class} armor, weapon, and tool training`, data: data.proficiencies });
      }

      if (data.abilities) suggestions.push({ field: 'abilities', action: 'merge', preview: 'Update ability scores', data: data.abilities });
      if (data.ac) {
        const acVal = (typeof data.ac === 'object' && data.ac) ? data.ac.value : data.ac;
        const acNotes = (typeof data.ac === 'object' && data.ac && typeof data.ac.notes === 'string') ? data.ac.notes.trim() : '';
        const preview = acNotes ? `Set AC: ${acVal} (${acNotes})` : `Set AC: ${acVal}`;
        suggestions.push({ field: 'ac', action: 'replace', preview, data: data.ac });
      }
      if (data.hp) suggestions.push({ field: 'hp', action: 'replace', preview: `Set HP: ${data.maxHp || data.hp.average || data.hp}`, data: data.maxHp || data.hp.average || data.hp });
      // date_of_change: 2026-05-02
      // rule_reference: Hit dice display (e.g. 1d8, 8d10+3d8) must be imported as a suggestion so the
      // character sheet header shows the correct hit dice formula rather than the blank-character default.
      if (data.hitDice) suggestions.push({ field: 'hitDice', action: 'replace', preview: `Set Hit Dice: ${data.hitDice}`, data: data.hitDice });
      if (data.speed) {
        const preview = (typeof data.speed === 'object' && data.speed)
          ? `Set speed: ${Object.entries(data.speed).map(([k, v]) => `${k} ${v}`).join(', ')}`
          : `Set speed: ${data.speed}`;
        suggestions.push({ field: 'speed', action: 'replace', preview, data: data.speed });
      }
      if (typeof data.proficiencyBonus === 'number') suggestions.push({ field: 'proficiencyBonus', action: 'replace', preview: `Set PB: +${data.proficiencyBonus}`, data: data.proficiencyBonus });
      if (typeof data.initiative === 'number') suggestions.push({ field: 'initiative', action: 'replace', preview: `Set Initiative: ${data.initiative}`, data: data.initiative });
      if (Array.isArray(data.languages) && data.languages.length) suggestions.push({ field: 'languages', action: 'append', preview: `Add languages: ${data.languages.join(', ')}`, data: data.languages });
      if (Array.isArray(data.toolProficiencies) && data.toolProficiencies.length) suggestions.push({ field: 'toolProficiencies', action: 'append', preview: `Add tool proficiencies: ${data.toolProficiencies.join(', ')}`, data: data.toolProficiencies });
      // @date_of_change 2025-12-05 - Use skillsDetailed if available for proficiency/expertise info
      if (Array.isArray(data.skills) && data.skills.length) {
        const skillData = Array.isArray(data.skillsDetailed) && data.skillsDetailed.length > 0 
          ? data.skillsDetailed 
          : data.skills;
        suggestions.push({ field: 'skills', action: 'append', preview: `Add skills: ${data.skills.join(', ')}`, data: skillData });
      }
      if (typeof data.passivePerception === 'number') suggestions.push({ field: 'passivePerception', action: 'replace', preview: `Set Passive Perception: ${data.passivePerception}`, data: data.passivePerception });
      if (data.savingThrows && Object.keys(data.savingThrows).length) suggestions.push({ field: 'savingThrows', action: 'replace', preview: 'Set saving throws', data: data.savingThrows });
      if (data.spellcasting && (data.spellcasting.ability || data.spellcasting.saveDC || data.spellcasting.attackBonus)) {
        suggestions.push({ field: 'spellcasting', action: 'replace', preview: 'Set spellcasting stats', data: data.spellcasting });
      }
      if (data.senses && Object.keys(data.senses).length) suggestions.push({ field: 'senses', action: 'replace', preview: 'Set senses', data: data.senses });
      if (Array.isArray(data.resistances) && data.resistances.length) suggestions.push({ field: 'resistances', action: 'replace', preview: `Set resistances: ${data.resistances.join(', ')}`, data: data.resistances });
      if (Array.isArray(data.immunities) && data.immunities.length) suggestions.push({ field: 'immunities', action: 'replace', preview: `Set immunities: ${data.immunities.join(', ')}`, data: data.immunities });
      if (Array.isArray(data.vulnerabilities) && data.vulnerabilities.length) suggestions.push({ field: 'vulnerabilities', action: 'replace', preview: `Set vulnerabilities: ${data.vulnerabilities.join(', ')}`, data: data.vulnerabilities });
      if (Array.isArray(data.conditionImmunities) && data.conditionImmunities.length) suggestions.push({ field: 'conditionImmunities', action: 'replace', preview: `Set condition immunities: ${data.conditionImmunities.join(', ')}`, data: data.conditionImmunities });
      if (typeof data.alignment === 'string' && data.alignment) suggestions.push({ field: 'alignment', action: 'replace', preview: `Set alignment: ${data.alignment}`, data: data.alignment });
      if (data.spells && (data.spells.byLevel || data.spells.prepared || data.spells.slots || data.spells.atWill)) {
        suggestions.push({
          field: 'spells',
          action: 'replace',
          preview: parsedSpellCount > 0
            ? `Set spells (${parsedSpellCount} parsed spells, byLevel/prepared/slots)`
            : 'Set spells (byLevel, prepared, slots)',
          data: data.spells
        });
      }
      if (catalogBuckets.equipment.length > 0) {
        suggestions.push({ field: 'equipment', action: 'append', preview: `Add ${catalogBuckets.equipment.length} inventory items`, data: catalogBuckets.equipment });
      }
      if (data.equipment?.currency && Object.keys(data.equipment.currency).length > 0) {
        const coinLabels = { platinum: 'pp', gold: 'gp', electrum: 'ep', silver: 'sp', copper: 'cp' };
        const coinSummary = Object.entries(data.equipment.currency)
          .filter(([, value]) => Number.isFinite(Number(value)))
          .map(([coin, value]) => `${Number(value).toLocaleString()} ${coinLabels[coin] || coin}`)
          .join(', ');
        suggestions.push({
          field: 'currency',
          action: 'replace',
          preview: `Set coins: ${coinSummary}`,
          data: data.equipment.currency
        });
      }
      const explicitActions = catalogBuckets.actions.filter(entry => entry && entry.derivedFromFeature !== true);
      const explicitBonusActions = catalogBuckets.bonusActions.filter(entry => entry && entry.derivedFromFeature !== true);
      const explicitReactions = catalogBuckets.reactions.filter(entry => entry && entry.derivedFromFeature !== true);
      if (explicitActions.length > 0) suggestions.push({ field: 'actions', action: 'append', preview: `Add ${explicitActions.length} actions`, data: explicitActions });
      if (explicitBonusActions.length > 0) suggestions.push({ field: 'bonusActions', action: 'append', preview: `Add ${explicitBonusActions.length} bonus actions`, data: explicitBonusActions });
      if (explicitReactions.length > 0) suggestions.push({ field: 'reactions', action: 'append', preview: `Add ${explicitReactions.length} reactions`, data: explicitReactions });
      if (catalogBuckets.features.length > 0) {
        suggestions.push({ field: 'features', action: 'append', preview: `Add ${catalogBuckets.features.length} features/traits`, data: catalogBuckets.features });
      }
      // date_of_change: 2026-02-27
      // rule_reference: D&D 5e (2024) — keep class features distinct from feats/species traits for SmartPaste routing
      if (catalogBuckets.classFeatures.length > 0) {
        suggestions.push({ field: 'classFeatures', action: 'append', preview: `Add ${catalogBuckets.classFeatures.length} class features`, data: catalogBuckets.classFeatures });
      }
      // date_of_change: 2026-02-27
      // rule_reference: D&D 5e (2024) — Structured feats array from parseCharacterSections or categorizeFeatures
      // must generate its own SmartPaste suggestion so users can apply feats independently of features.
      if (catalogBuckets.feats.length > 0) {
        suggestions.push({ field: 'feats', action: 'append', preview: `Add ${catalogBuckets.feats.length} feats`, data: catalogBuckets.feats });
      }
      // date_of_change: 2026-02-27
      // rule_reference: D&D 5e (2024) — Structured speciesTraits array from parseCharacterSections or
      // getSRDSpeciesTraits must generate its own SmartPaste suggestion so users can apply species traits.
      if (catalogBuckets.speciesTraits.length > 0) {
        suggestions.push({ field: 'speciesTraits', action: 'append', preview: `Add ${catalogBuckets.speciesTraits.length} species traits`, data: catalogBuckets.speciesTraits });
      }
      // @date_of_change 2025-12-05 - Add biography field suggestions (human-in-the-loop for backstory, personality, etc.)
      if (data.biography && typeof data.biography === 'object') {
        const bio = data.biography;
        const biographyMeta = data.biographyMeta && typeof data.biographyMeta === 'object' ? data.biographyMeta : {};
        const pushBiographySuggestion = (field, preview, value) => {
          if (!value) return;
          suggestions.push({
            field,
            action: 'replace',
            preview,
            data: value,
            parserMeta: biographyMeta[field]
          });
        };
        if (bio.personality) pushBiographySuggestion('personality', `Set personality: ${bio.personality.substring(0, 50)}${bio.personality.length > 50 ? '...' : ''}`, bio.personality);
        if (bio.ideals) pushBiographySuggestion('ideals', `Set ideals: ${bio.ideals.substring(0, 50)}${bio.ideals.length > 50 ? '...' : ''}`, bio.ideals);
        if (bio.bonds) pushBiographySuggestion('bonds', `Set bonds: ${bio.bonds.substring(0, 50)}${bio.bonds.length > 50 ? '...' : ''}`, bio.bonds);
        if (bio.flaws) pushBiographySuggestion('flaws', `Set flaws: ${bio.flaws.substring(0, 50)}${bio.flaws.length > 50 ? '...' : ''}`, bio.flaws);
        if (bio.backstory) pushBiographySuggestion('backstory', `Set backstory: ${bio.backstory.substring(0, 50)}${bio.backstory.length > 50 ? '...' : ''}`, bio.backstory);
        if (bio.appearance) pushBiographySuggestion('appearance', `Set appearance: ${bio.appearance.substring(0, 50)}${bio.appearance.length > 50 ? '...' : ''}`, bio.appearance);
        if (bio.notes) pushBiographySuggestion('notes', `Set notes: ${bio.notes.substring(0, 50)}${bio.notes.length > 50 ? '...' : ''}`, bio.notes);
        pushBiographySuggestion('age', `Set age: ${bio.age}`, bio.age);
        pushBiographySuggestion('height', `Set height: ${bio.height}`, bio.height);
        pushBiographySuggestion('weight', `Set weight: ${bio.weight}`, bio.weight);
        pushBiographySuggestion('eyes', `Set eyes: ${bio.eyes}`, bio.eyes);
        pushBiographySuggestion('hair', `Set hair: ${bio.hair}`, bio.hair);
        pushBiographySuggestion('skin', `Set skin: ${bio.skin}`, bio.skin);
      }

      // Unified Character suggestion (for UnifiedDataManager) - only when critical fields present
      try {
        const hasCritical = Boolean(data && data.abilities && data.ac && (data.maxHp || data.hp));
        if (!hasCritical) break;
        const now = new Date().toISOString();
        const nameSlug = (data.name || 'character').toLowerCase().replace(/[^a-z0-9]+/g, '_');
        const id = `imported_character_${nameSlug}_${Date.now()}`;
        const abilFull = data.abilities || {};
        const abil = {
          str: abilFull.strength ?? 10,
          dex: abilFull.dexterity ?? 10,
          con: abilFull.constitution ?? 10,
          int: abilFull.intelligence ?? 10,
          wis: abilFull.wisdom ?? 10,
          cha: abilFull.charisma ?? 10,
        };
        const unifiedEntity = {
          schemaVersion: '1.0',
          type: 'character',
          exportDate: now,
          id,
          source: { name: 'SmartPaste Import', edition: '5e', license: 'Custom' },
          data: {
            identity: {
              name: data.name || 'Imported Character',
              race: data.race || data.species || 'Human',
              background: data.background || '',
              alignment: data.alignment || 'neutral'
            },
            level: data.level || 1,
            // date_of_change: 2026-08-24
            // rule_reference: D&D 5e multiclass progression is canonical source data. Library
            // creation must preserve every class instead of rebuilding a one-class placeholder.
            classes: Array.isArray(data.classes) && data.classes.length > 0
              ? data.classes.map((entry) => ({
                  ...entry,
                  name: entry?.name || entry?.className || entry?.class || 'Unknown Class',
                  level: Number.isFinite(Number(entry?.level)) ? Number(entry.level) : 0,
                  subclass: entry?.subclass || entry?.archetype || entry?.path || entry?.domain || ''
                }))
              : [{ name: data.class || 'Fighter', level: data.level || 1, subclass: data.subclass || '', hitDie: data.hitDie || 'd10' }],
            abilities: abil,
            proficiencyBonus: typeof data.proficiencyBonus === 'number' ? data.proficiencyBonus : Math.ceil((data.level || 1) / 4) + 1,
            hp: {
              current: data.maxHp || (typeof data.hp === 'object' ? (data.hp.current || data.hp.average) : data.hp) || 10,
              maximum: data.maxHp || (typeof data.hp === 'object' ? (data.hp.average || data.hp.maximum) : data.hp) || 10,
              temporary: data.tempHp || 0
            },
            ac: { value: (typeof data.ac === 'object' ? data.ac.value : data.ac) || 10, source: (typeof data.ac === 'object' ? (data.ac.notes || 'import') : 'import') },
            speed: (() => {
              if (typeof data.speed === 'object' && data.speed) {
                const { walk, fly, climb, swim, burrow } = data.speed;
                return {
                  walk: walk || 30,
                  ...(fly ? { fly } : {}),
                  ...(climb ? { climb } : {}),
                  ...(swim ? { swim } : {}),
                  ...(burrow ? { burrow } : {})
                };
              }
              const base = (typeof data.speed === 'number') ? data.speed : (parseInt(data.speed) || 30);
              return { walk: base };
            })(),
            saves: data.savingThrows || {},
            skills: Array.isArray(data.skills) ? data.skills.reduce((acc, n) => { acc[n] = true; return acc; }, {}) : {},
            features: (Array.isArray(data.features) ? data.features : (Array.isArray(data.featuresList) ? data.featuresList.map(n => ({ name: n })) : [])),
            inventory: { coins: { cp: 0, sp: 0, ep: 0, gp: 0, pp: 0 }, equipment: Array.isArray(data.equipment?.items) ? data.equipment.items : [] },
            equipment: data.equipment || null,
            actions: Array.isArray(data.actions) ? data.actions : [],
            bonusActions: Array.isArray(data.bonusActions) ? data.bonusActions : [],
            reactions: Array.isArray(data.reactions) ? data.reactions : [],
            spells: data.spells || null,
            spellcasting: data.spellcasting || null
          },
          _meta: { createdAt: now, lastModified: now, artwork: data.artwork || null }
        };
        suggestions.push({
          field: 'unifiedEntity',
          action: 'create',
          preview: 'Save as a new character in your library',
          data: unifiedEntity
        });
      } catch (e) {
        // Non-fatal if unified suggestion build fails
      }
      break;
    }
    default:
      break;
  }

  return suggestions;
}
}

export default IntelligentContentParser;
